/* ============================================================
   hoh-contract-data.js — THE CONTRACT data layer (theme asset)
   The only Contract file that talks to the network or the bank.

   Server truth: runs, seeds, skill rolls, pack grants (Supabase
   RPCs + the settle worker). Client bank: hearts move through
   the same localStorage bank the whole Hall uses, every move
   announced with hohHeartHint so the R275 ledger sees it.

   Depends on globals the Hall already ships:
     window.hohYourHallEmail()         — signed-in email
     window.hohHeartHint(reason, id)   — ledger hint (section-core)
   PIN comes from the shop's stored credential, same as get_shop_state.
   ============================================================ */
(function () {
  'use strict';
  if (window.HOHContractData) return;

  var WORKER_URL = (window.HOH_CONFIG && window.HOH_CONFIG.contractWorkerUrl) || 'https://quest-ai.fondlyframed.workers.dev';
  /* R500/V76 deployment handshake. Engine version proves the browser and
     judge simulate the same fight; the minimum Worker build proves the
     lifecycle fix plus the generation-aware atomic cutover bridge.
     Check before spending a ticket and again before playback, so a tab left
     open across a deploy fails before showing a fight that cannot be saved. */
  /* R787: untimed choices require the matching manual-choice authority. */
  var MIN_WORKER_BUILD = 141;
  /* Engine 82 widens the persistent bench to 100. Reaching that rail is no
     longer a dead end: incoming recruits deterministically replace existing
     recruit entries in place. Main-party heroes and items are stored in
     separate fields and can never be selected by this helper. Engine 81 and
     earlier retain the deployed 40-seat persistence refusal exactly. */
  var LEGACY_PERSISTENT_BENCH = 40;
  var MAX_PERSISTENT_BENCH = 100;
  var RECRUIT_REPLACE_ENGINE = 82;
  function persistentBenchEngineVersion(explicit) {
    var v = explicit | 0;
    if (v > 0) return v;
    try { v = (window.__hohLocalRun && (window.__hohLocalRun.engine_ver | 0)) || 0; } catch (_) { v = 0; }
    if (!v) try { v = (window.__hohLocalLadder && (window.__hohLocalLadder.engine_ver | 0)) || 0; } catch (_) { v = 0; }
    if (!v) try { v = (window.HOHContract && (window.HOHContract.ENGINE_VERSION | 0)) || 0; } catch (_) { v = 0; }
    return v;
  }
  function persistentBenchLimitAt(engineVer) {
    return persistentBenchEngineVersion(engineVer) >= RECRUIT_REPLACE_ENGINE
      ? MAX_PERSISTENT_BENCH : LEGACY_PERSISTENT_BENCH;
  }
  function persistentBenchRoom(list, engineVer) {
    return Math.max(0, persistentBenchLimitAt(engineVer) - (Array.isArray(list) ? list.length : 0));
  }
  function persistentBenchReplacementIndexes(list, count, cap) {
    var ranked = [], i;
    for (i = 0; i < list.length && i < cap; i++) {
      var row = list[i], state = !row ? 0
        : (row.dead || row.gone || row.fell) ? 1
        : (row.temp || (row.ttl | 0) > 0) ? 2 : 3;
      ranked.push({ i:i, state:state, rar:Math.max(0, (row && row.rar) | 0),
        lvl:Math.max(0, (row && row.lvl) | 0) });
    }
    ranked.sort(function (a, b) {
      return a.state - b.state || a.rar - b.rar || a.lvl - b.lvl || a.i - b.i;
    });
    return ranked.slice(0, Math.max(0, count | 0)).map(function (row) { return row.i; });
  }
  function addPersistentBench(list, additions, engineVer) {
    list = Array.isArray(list) ? list : [];
    additions = Array.isArray(additions) ? additions : (additions ? [additions] : []);
    var ver = persistentBenchEngineVersion(engineVer), cap = persistentBenchLimitAt(ver);
    if (ver < RECRUIT_REPLACE_ENGINE) {
      var take = additions.slice(0, persistentBenchRoom(list, ver));
      return { list:list.concat(take), added:take.length,
        refused:additions.length - take.length, replaced:[] };
    }
    var out = list.slice(), accepted = additions.slice();
    var append = Math.min(Math.max(0, cap - out.length), accepted.length);
    for (var a = 0; a < append; a++) out.push(accepted[a]);
    var replaceN = accepted.length - append;
    /* Ordinary doors add at most three bodies, but keep the primitive total:
       even a future bulk award above 100 consumes every incoming recruit.
       A batch reserves every ranked seat once before cycling in the same
       deterministic order, so no capacity-only refusal can reappear. */
    var indexes = persistentBenchReplacementIndexes(out, cap, cap);
    var replaced = [];
    for (var r = 0; r < replaceN && indexes.length; r++) {
      var at = indexes[r % indexes.length], incoming = accepted[append + r];
      replaced.push({ i:at, out:out[at], incoming:incoming });
      out[at] = incoming;
    }
    var added = append + (indexes.length ? replaceN : 0);
    return { list:out, added:added, refused:additions.length - added, replaced:replaced };
  }
  function persistentBenchLimitError(engineVer) {
    var limit = persistentBenchLimitAt(engineVer);
    var err = new Error('Your bench has reached its ' + limit + '-recruit safety limit.');
    err.reason = 'bench_limit'; err.limit = limit;
    return err;
  }
  var _workerGatePromise = null;
  var _workerGateGoodAt = 0;
  function workerGate(force, runVersion) {
    if (localMode()) return Promise.resolve({ ok: true, local: true });
    /* R750: a newer browser may replay an older release, but the judge must
       also support the exact saved run. Validate each caller after the shared
       probe, including cached and concurrent checks. */
    if ((runVersion | 0) > 0) return workerGate(force).then(function (result) {
      if ((result.supported_versions || []).indexOf(runVersion | 0) !== -1) return result;
      var mismatch = new Error('The battle server is being updated. Your run is saved. Try again shortly.');
      mismatch.__hohWorkerGate = true;
      mismatch.reason = 'worker_run_version_mismatch';
      throw mismatch;
    });
    var localEngine = 0;
    try { localEngine = (window.HOHContract && window.HOHContract.ENGINE_VERSION) | 0; } catch (_) {}
    if (localEngine < 1) {
      var noEngine = new Error('The battle game is still loading. Reload this page and try again.');
      noEngine.__hohReload = true;
      return Promise.reject(noEngine);
    }
    if (!force && _workerGateGoodAt && Date.now() - _workerGateGoodAt < 10000) {
      return Promise.resolve(window.__hohWorkerGate || { ok: true, cached: true });
    }
    if (_workerGatePromise) return _workerGatePromise;
    var ctl = null, timer = null;
    var init = { method: 'GET', cache: 'no-store' };
    try {
      if (typeof AbortController === 'function') {
        ctl = new AbortController(); init.signal = ctl.signal;
        timer = setTimeout(function () { try { ctl.abort(); } catch (_) {} }, 6000);
      }
    } catch (_) {}
    var url = WORKER_URL.replace(/\/+$/, '') + '/contract-settle?gate=' +
      encodeURIComponent(String(localEngine) + '-V' + MIN_WORKER_BUILD + '-' + Date.now());
    _workerGatePromise = fetch(url, init)
      .then(function (r) {
        return r.text().then(function (text) {
          var json = null;
          try { json = text ? JSON.parse(text) : null; } catch (_) {}
          if (!r.ok || !json || json.ok !== true) throw new Error('battle server check failed');
          var workerEngine = json.worker_ver | 0;
          var match = /^V([0-9]+)$/.exec(String(json.build || ''));
          var workerBuild = match ? (parseInt(match[1], 10) || 0) : 0;
          /* R518: THE NEWER BROWSER CAN SPEAK THE LIVE OLDER GAME.
             Engine 64 deliberately ships the saved-version reducers for 62
             and 63. Requiring the probe's LATEST engine to equal the asset's
             latest engine made a theme-first rollout impossible: Engine 64 +
             live V77/62 displayed "reload" forever, and a reload could never
             change either number. Accept the Worker only when THIS loaded
             engine explicitly declares the Worker's exact engine supported.
             This is one-way on purpose: an old browser still cannot enter a
             newer game it does not know. The minimum complete-Worker build
             remains mandatory, so this does not reopen pre-lifecycle Workers. */
          var supportedByBrowser = false;
          try {
            supportedByBrowser = !!(workerEngine > 0 && window.HOHContract &&
              window.HOHContract.SUPPORTED_ENGINE_VERSIONS &&
              window.HOHContract.SUPPORTED_ENGINE_VERSIONS[workerEngine] === true);
          } catch (_) { supportedByBrowser = false; }
          var result = { ok: supportedByBrowser && workerBuild >= MIN_WORKER_BUILD,
            local_engine: localEngine, worker_engine: workerEngine,
            supported_versions: Array.isArray(json.supported_versions) ? json.supported_versions.slice() : [workerEngine],
            worker_build: workerBuild, minimum_build: MIN_WORKER_BUILD,
            compatibility: workerEngine === localEngine ? 'exact' : 'saved_version', at: Date.now() };
          try { window.__hohWorkerGate = result; } catch (_) {}
          if (!result.ok) {
            var stale = new Error('The game was updated. Reload this page before starting the battle.');
            stale.__hohReload = true; stale.worker = result;
            throw stale;
          }
          _workerGateGoodAt = Date.now();
          return result;
        });
      })
      .catch(function (error) {
        if (error && error.__hohReload) throw error;
        var unavailable = new Error('We could not check the battle server. Check your connection and try again.');
        unavailable.__hohWorkerGate = true;
        throw unavailable;
      })
      .then(function (result) {
        _workerGatePromise = null;
        if (timer) clearTimeout(timer);
        return result;
      }, function (error) {
        _workerGatePromise = null;
        if (timer) clearTimeout(timer);
        throw error;
      });
    return _workerGatePromise;
  }
  var BANK_KEY = 'hoh-heart-bank-v1';
  var DAY_KEY = 'hoh-contract-day-v1';

  /* ---------- account ---------- */
  function email() {
    try { return (window.hohYourHallEmail && window.hohYourHallEmail()) || ''; } catch (e) { return ''; }
  }
  function pin() {
    /* the account pin lives in the your-hall token, exactly where the shop
       reads it for get_shop_state (hoh-shop.js pin()). Reuse it verbatim. */
    try { var t = JSON.parse(localStorage.getItem('hoh-your-hall-token') || 'null'); return (t && t.pin) || ''; } catch (e) { return ''; }
  }
  function signedIn() { return !!(email() && pin()); }

  /* ---------- daily gate (once a day, mirrors the server UNIQUE) ---------- */
  function today() { try { return new Date().toISOString().slice(0, 10); } catch (e) { return ''; } }
  function playedToday() { try { return localStorage.getItem(DAY_KEY) === today() && !!today(); } catch (e) { return false; } }
  function stampPlayed() { try { localStorage.setItem(DAY_KEY, today()); } catch (e) {} }
  /* R301: the last run id, so a paid replay can continue the day's thread when
     the server refuses a second depth-0 start. */
  var RUN_KEY = 'hoh-contract-lastrun-v1';
  function rememberRun(id) { try { if (id) localStorage.setItem(RUN_KEY, String(id)); } catch (e) {} }
  function lastRunId() { try { return localStorage.getItem(RUN_KEY) || ''; } catch (e) { return ''; } }
  function forgetRunIf(id) {
    try {
      if (id != null && String(localStorage.getItem(RUN_KEY) || '') === String(id)) {
        localStorage.removeItem(RUN_KEY);
      }
    } catch (e) {}
  }
  /* R334: REPLAY_COST is gone. It was the 300 heart entry fee, and it was
     charged ON TOP of the wager the player chose inside — two prices for one
     fight. An extra run costs one ticket now. The constant is deleted rather
     than zeroed so nobody can resurrect the fee by editing a number. */
  /* R328: THE GATES CLOSE AFTER A LOSS. Five minutes to walk it off before the
     Battlegrounds will take you again — it turns a loss into a pause rather
     than an instant re-tilt, which the research flagged as the healthier shape
     for a wagering loop. Local to the device on purpose: it is a cooling-off
     kindness, not an anti-cheat measure, so it must never block a legitimate
     player who switched phones. */
  var LOSS_KEY = 'hoh-contract-loss-at-v1';
  /* v39 (Jan): two minutes, not five. Five was long enough that the honest
     move after a loss was to close the tab, which is the opposite of a
     cooling off period's job. CHECKED FIRST, because the twelve hour timer
     taught this the hard way: nothing on the server enforces this. It is
     stampLoss writing localStorage and lossLockMsLeft reading it, so this
     constant IS the rule and there is no second copy to drift from. */
  var LOSS_LOCK_MS = 2 * 60 * 1000;
  function stampLoss() { try { localStorage.setItem(LOSS_KEY, String(Date.now())); } catch (e) {} }
  function lossLockMsLeft() {
    try {
      var t = parseInt(localStorage.getItem(LOSS_KEY) || '0', 10) || 0;
      if (!t) return 0;
      var left = LOSS_LOCK_MS - (Date.now() - t);
      return left > 0 ? left : 0;
    } catch (e) { return 0; }
  }
  function clearLossLock() { try { localStorage.removeItem(LOSS_KEY); } catch (e) {} }

  /* ---------- jangela local test mode (god mode only) --------------------
     A secret, client-only sandbox so The Contract is playable on any device
     the moment god mode is on: no RPCs, no settle worker, no daily lock, no
     ledger pollution. The REAL engine and the REAL UI still run, so the whole
     experience and every mechanic gets exercised. Normal players never reach
     this branch (godMode() gates it), so the live path is byte-for-byte the
     same for them. Force flags live on window.__hohContractForce
     ({ raise:bool, outcome:'win'|'loss', fast:bool }) and are driven by the
     contract debug panel. */
  function godMode() { try { return typeof window.hohIsGodMode === 'function' && window.hohIsGodMode(); } catch (e) { return false; } }
  var NEW_PLAYER_PREVIEW_KEY = 'hoh-bg-new-player-preview-v1';
  var newPlayerPreviewMemoryAccount = '';
  function newPlayerPreviewAccount() {
    try { return String(collectionAccount() || 'signed-out'); } catch (_) { return 'signed-out'; }
  }
  function isNewPlayerPreview() {
    if (!godMode()) return false;
    var account = newPlayerPreviewAccount();
    try { return window.sessionStorage.getItem(NEW_PLAYER_PREVIEW_KEY) === account; }
    catch (_) { return newPlayerPreviewMemoryAccount === account; }
  }
  /* R410 (Jan): GOD MODE PLAYS THE REAL GAME. The sandbox used to be the
     DEFAULT for god mode, so every run Jan played was judged in his own
     browser, wrote no row, told the worker nothing, and paid hearts and packs
     that did not exist. Testing "the real experience" was the one thing god
     mode could not do. It is inverted now: a signed-in god plays the same
     path a player does. The browser judge survives ONLY as the fallback for
     a god with no account to authenticate with, because there is no server
     call to make without an email and a pin. It is not a mode anyone picks.
     The old 'hoh-contract-local-v1' key is deliberately ignored so a stale
     value from the sandbox-by-default era cannot strand anyone. */
  function localMode() {
    if (!godMode()) return false;
    /* A reversible new-player preview must use the real local judge. A visual
       one-slot lie would be refused by the server, whose saved allowance is
       deliberately authoritative. Session scope means closing the tab always
       restores the real account, and the account-shaped value prevents a
       sign-in switch from inheriting somebody else's preview. */
    if (isNewPlayerPreview()) return true;
    try { if (localStorage.getItem('hoh-contract-sandbox-v2') === '1') return true; } catch (e) {}
    return !signedIn();
  }
  /* v39: THE SANDBOX WAS INVISIBLE, AND THAT COST A WEEK.
     god mode silently routes every Contract run through settleLocal. No run
     row is created, the worker is never called, and the hearts, the recruits
     and the win are all fabricated in this file. Then the pack screen asks the
     REAL vault what it is owed, the vault correctly answers nothing, and the
     screen reports a broken prize. Three rounds of worker rebuilds and two
     migrations were spent looking at a server that was never in the story.
     A debug mode that is pixel identical to production is not a debug mode, it
     is a trap. Anything can ask now, and the run bar wears a badge. */
  window.hohContractIsLocal = function () { try { return !!localMode(); } catch (e) { return false; } };
  function forceFlags() { return window.__hohContractForce || {}; }
  function setNewPlayerPreview(on) {
    if (!godMode()) return Promise.resolve({ ok:false, reason:'debug_only' });
    on = !!on;
    if (!on) {
      try { window.sessionStorage.removeItem(NEW_PLAYER_PREVIEW_KEY); } catch (_) {}
      newPlayerPreviewMemoryAccount = '';
      /* Every preview run is intentionally fake. Drop only those browser
         objects, invalidate the mirror, and fetch the untouched live ledger. */
      try { window.__hohLocalUnlimited = null; window.__hohLocalRun = null; } catch (_) {}
      unlimitedForget();
      return Promise.resolve(unlimitedState(true)).then(function (state) {
        return { ok:true, preview:false, state:state || null };
      }, function () {
        /* Preview is already off. The ordinary entry recovery owns a failed
           live read and will offer Retry instead of trapping debug mode on. */
        return { ok:true, preview:false, state:null };
      });
    }
    if (isNewPlayerPreview()) {
      return Promise.resolve({ ok:true, preview:true, state:localUnlim() });
    }
    var uiState = null, pending = false;
    try { uiState = window.HOHContractUI && window.HOHContractUI.state ? window.HOHContractUI.state() : null; } catch (_) {}
    try { pending = !!(typeof hasPendingSettlement === 'function' && hasPendingSettlement()); } catch (_) {}
    if (pending) return Promise.resolve({ ok:false, reason:'settlement_pending' });
    if (uiState && (uiState.run || uiState.settling || uiState._battlePreparing)) {
      return Promise.resolve({ ok:false, reason:'active_run' });
    }
    /* Check server truth before switching localMode. This prevents the debug
       preview from hiding a live run that still needs to settle or resume. */
    return Promise.resolve(unlimitedState(true)).then(function (state) {
      if (state && state.live) return { ok:false, reason:'active_run' };
      var account = newPlayerPreviewAccount();
      newPlayerPreviewMemoryAccount = account;
      try { window.sessionStorage.setItem(NEW_PLAYER_PREVIEW_KEY, account); } catch (_) {}
      try { window.__hohLocalUnlimited = null; window.__hohLocalRun = null; } catch (_) {}
      unlimitedForget();
      return Promise.resolve(unlimitedState(true)).then(function (fresh) {
        return { ok:true, preview:true, state:fresh || localUnlim() };
      });
    });
  }
  function rhex(n) { var s = ''; n = n || 32; for (var i = 0; i < n; i++) s += '0123456789abcdef'[Math.floor(Math.random() * 16)]; return s; }
  function localSeed() {
    var f = forceFlags(), tail = rhex(26);
    if (f.raise === true) return rhex(4) + '10' + tail;   /* byte slice(4,6)=0x10 < 0x40 -> enemy raises */
    if (f.raise === false) return rhex(4) + 'aa' + tail;  /* byte slice(4,6)=0xaa >= 0x40 -> no raise    */
    return rhex(4) + rhex(2) + tail;
  }
  /* Jan's ceiling: the prize never exceeds 600, double the 300 max bet.
     Base pays ~1.2x at low stakes rising to ~1.5x at max, so the second
     wager and the enemy raise still have room to climb toward the cap. */
  var POT_CAP = 600;
  /* v28 THE PURSE COMES FROM THE ENGINE. This file used to carry its own copy
     of the payout formula, the worker carried a third, and three copies of one
     number is exactly how a client and its judge drift into settling every run
     as a mismatch. There is one table now and it lives in hoh-contract-engine,
     which is the block spliced byte-identical into the worker. The fallbacks
     below only ever fire if the engine failed to load, in which case nothing
     settles anyway. */
  function _E() { try { return window.HOHContract || null; } catch (_) { return null; } }
  function purseWin(depth) { var e = _E(); return e && e.purseWin ? e.purseWin(depth | 0) : [100, 125, 150, 200, 250][Math.max(0, Math.min(4, depth | 0))]; }
  function pursePacks(depth) { var e = _E(); return e && e.pursePacks ? e.pursePacks(depth | 0) : [0, 1, 1, 1, 5][Math.max(0, Math.min(4, depth | 0))]; }
  function purseEntry() { var e = _E(); return (e && e.PURSE && e.PURSE.ENTRY) || 100; }
  /* v35: what walking away is worth, off the SAME table the worker settles
     against. It answers 0 at every depth past the first by design, because a
     continue is free and refunding a table nobody paid for is a mint. */
  function purseFlee(depth, mid) {
    var e = _E();
    if (e && e.purseFlee) return e.purseFlee(depth | 0, !!mid) | 0;
    var d = Math.max(0, Math.min(4, depth | 0));
    return (mid ? [25, 0, 0, 0, 0] : [50, 0, 0, 0, 0])[d];
  }
  /* kept as the name every call site already uses. It answers from the purse
     now and ignores the stake entirely, because there is only one stake. */
  function localPot(stake, depth) { return purseWin(depth | 0); }
  /* R358: mirrors public.hoh_contract_foe_scale exactly. Recalibrated for
     v22 CLASS SIGNATURES (every class owns a felt battle mechanic, both
     sides; the full class deck now also fights for the house): 1.31 at 100
     (~70-72%), 1.40 at 200 (~60-63%), 1.49 at 300 (~52-54%) across seed
     families. Continuous at 100. */
  /* R359: v23 anchors — the boon-less early house threatens less, so the
     dial rises: 1.45/1.53/1.61 measured ~72-74/62/53. Continuous at 100. */
  /* v23b: the dial is a STAT scale now (the early pool is honest 100+rolls;
     pools only scale once the boons return at battle three, gently derived
     in-engine). Anchors 2.4/2.7/3.0 measured ~71/62/52 across families. */
  /* v24: THE RARITY LADDER carries the run's difficulty (foe dice capped
     uncommon/rare/epic/legendary across fights 1-4, real rival at 5).
     The dial is a modest stat scale again — 1.20/1.35/1.50 — and the
     stake's real meaning shifts to the late tables. */
  function localScale(stake) {
    var s0 = Math.max(0, Math.min(300, stake || 0));
    return +((s0 <= 100 ? 1.00 + 0.20 * s0 / 100 : 1.05 + 0.15 * s0 / 100).toFixed(3));
  }
  /* v28: packs are a property of WHICH TABLE fell, not of the pot's size */
  function localPacks(stake, pot, depth) { return pursePacks(depth | 0); }
  var localGear = {};
  /* build the frozen hero snapshot from roster keys, exactly like the server's
     contract_start now does: name, class, hearts, race, and equipped gear at
     start time. Storing this (not the keys) is what lets the worker and the
     client re-simulate the identical party. */
  function localSnapshot(keys) {
    var cards = ownedCards();
    /* R334: the sandbox has to resolve HIRELINGS too. A 'hall:<uuid>' key is
       not in the player's collection by definition, so it is looked up in the
       Hall instead — the same row contract_start reads server side. Without
       this a god-mode party of one card plus four hirelings would simulate as
       five blank Level-1 Fighters and the local test would be worthless. */
    var hall = null;
    function hallHero(id) {
      if (!hall) {
        hall = {};
        try {
          var all = (typeof window.hohGetAllCharacters === 'function') ? (window.hohGetAllCharacters() || []) : [];
          for (var i = 0; i < all.length; i++) hall[String(all[i].id)] = all[i];
        } catch (_) {}
      }
      return hall[String(id)] || {};
    }
    return (keys || []).map(function (k) {
      var c;
      if (String(k).indexOf('hall:') === 0) c = hallHero(String(k).slice(5));
      else {
        /* R422: own: keys resolve too — match on the bare id, whatever the
           prefix. The old ff:-only compare left every own: card a nameless
           "Hero" in sandbox fights. */
        var idk = String(k).replace(/^(ff|own):/, '');
        c = cards.filter(function (x) { return String(x.id || x.card_id || x.character_id) === idk; })[0] || {};
      }
      var g = gearFor(k);
      /* v2 (Jan's dice law): RARITY (the hero's real hall hearts — the gem)
         owns the battle die; the EFFECTIVE LEVEL feeds team power only.
         Snapshot carries all three so the engine and the worker agree.
         Older server snapshots without `rar` fall back inside the engine. */
      var E2 = window.HOHContract;
      var isHire = String(k).indexOf('hall:') === 0;
      var classTitle = c.character_class || c.cls || 'Fighter';
      var baseClass = E2 && E2.ENGINE_VERSION >= 99 && E2.primaryClass ? E2.primaryClass(classTitle) : '';
      return { name: c.name || 'Hero', cls: baseClass || classTitle.split(' ')[0],
               hearts: c.hearts || 0,
               rar: E2 ? E2.tierOf(c.hearts || 0) : 0,
               /* a hireling owns no card, so it can hold no card_xp row and
                  is level 1 forever — mirroring the server join exactly */
               lvl: isHire ? 1 : effectiveLevel(k),
               /* Engine 78 freezes claimed card-crafting milestones beside
                  the level. A fight never reads a claim that changes after
                  Start, so replay and the Worker judge the same hero. */
               progression_claims: isHire ? 0 : cardProgressionClaims(k),
               hire: isHire,
               race: c.race || '', skill: isHire ? null : g.active, passive: isHire ? null : g.passive };
    });
  }

  /* ---------- the bank (client hearts, ledgered by the interceptor) ---------- */
  function bankGet() {
    try { return parseInt(localStorage.getItem(BANK_KEY) || '0', 10) || 0; } catch (e) { return 0; }
  }
  var HEART_BANK_MAX = 9999;
  function bankBump(delta, reason) {
    try { if (window.hohHeartHint) window.hohHeartHint(reason || (delta > 0 ? 'earn_other' : 'spend_other')); } catch (_) {}
    try { document.dispatchEvent(new CustomEvent('hoh:hearts-bumped', { detail: delta < 0 ? { spent: -delta } : { gained: delta } })); } catch (_) {}
    try { if (typeof window.hohRenderHeaderCounter === 'function') window.hohRenderHeaderCounter(); } catch (_) {}
  }
  function bankApply(delta, reason) {
    /* ⛔ R594: the Battlegrounds side goes through the same funnel as the shop,
       so one move cannot be server-backed on one screen and device-local on
       another. The funnel writes the mirror synchronously before it returns,
       which is what keeps bankGet() honest for the caller on the very next
       line, and it fires hoh:hearts-bumped itself.
       The old body stays as the fallback for the case where hoh-wallet.js did
       not load: a battle that cannot pay out is worse than one that pays out
       locally. */
    if (window.HOHWallet && typeof window.HOHWallet.apply === 'function') {
      try {
        window.HOHWallet.apply('hearts', delta, reason || (delta < 0 ? 'spend_other' : 'earn_other'));
        return bankGet();
      } catch (_) { /* fall through to the local path below */ }
    }
    try {
      var v = Math.max(0, Math.min(HEART_BANK_MAX, bankGet() + delta));
      localStorage.setItem(BANK_KEY, String(v));
      /* The Hall refreshes its corner heart counter and clears the cached
         wallet off the hoh:hearts-bumped event (section-core invalidates
         __hohBankCache on it, fresh-faces re-syncs wallet state). Fire it and
         repaint the header exactly like the shop's bankAdd, or every counter
         on the page stays frozen after a stake or a payout. */
      bankBump(delta, reason);
      return v;
    } catch (e) { return bankGet(); }
  }
  /* Settlement claims need a stronger contract than legacy wallet callers:
     write the exact expected balance, read it back, and fail closed if storage
     refused it. This still cannot create cross-device atomicity while Hearts
     are device-local, but it prevents the UI from announcing a credit that was
     never durably written on this device. */
  function bankApplyVerified(delta, reason, expectedBefore) {
    var before = bankGet();
    if (expectedBefore != null && before !== (expectedBefore | 0)) {
      throw new Error('Your saved reward is waiting because the Heart balance changed. Reopen Battlegrounds to retry it safely.');
    }
    var after = before + (delta | 0);
    if (after < 0 || after > HEART_BANK_MAX) {
      var capErr = new Error('Your saved reward is waiting because the Heart bank is full. Spend Hearts, then reopen Battlegrounds.');
      capErr.__hohRewardPending = true;
      throw capErr;
    }
    localStorage.setItem(BANK_KEY, String(after));
    if (bankGet() !== after) throw new Error('Your saved reward could not be written on this device. Reopen Battlegrounds to retry it.');
    bankBump(delta, reason);
    return after;
  }
  function canAfford(n) { return bankGet() >= n; }

  /* ---------- rpc ---------- */
  /* Route through the Hall's own Supabase client (window.hohSupabaseClient),
     the same client the Shop and Fresh Faces use. It already carries the
     project URL, anon key, and auth headers, so the Contract never hardcodes
     credentials. supabase-js resolves to { data, error }; unwrap to the data
     payload and throw on error so the callers below stay unchanged. */
  function rpc(fn, args) {
    var c = window.hohSupabaseClient;
    if (!c || typeof c.rpc !== 'function') return Promise.reject(new Error('The hall is still waking up. Try again in a moment.'));
    return Promise.resolve(c.rpc(fn, args || {})).then(function (res) {
      if (res && res.error) {
        /* Keep PostgREST's machine-readable refusal. A rollout compatibility
           path may stand in only for a positively missing RPC (PGRST202/42883),
           never for a permission error, timeout, or ambiguous network failure. */
        var raw = res.error || {};
        var err = new Error(raw.message || raw.hint || ('rpc ' + fn));
        err.__hohRpcCode = String(raw.code || '');
        err.__hohRpcStatus = res.status == null ? null : (res.status | 0);
        err.__hohRpcDetails = String(raw.details || '');
        err.__hohRpcHint = String(raw.hint || '');
        err.__hohRpcName = String(fn || '');
        throw err;
      }
      return res ? res.data : null;
    });
  }
  function rpcDefinitelyMissing(err, fn) {
    var code = String(err && (err.__hohRpcCode || err.code) || '').toUpperCase();
    var name = String(fn || '');
    if (!name) return false;
    var safeName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    var named = new RegExp('(?:^|[^a-z0-9_])(?:public\\.)?' + safeName + '(?:[^a-z0-9_]|$)', 'i');
    if (code === 'PGRST202') {
      /* PGRST202 is PostgREST's schema-cache miss. It may put the signature in
         details instead of message, but both the requested RPC name and an
         explicit cache/not-found phrase must be present. */
      var text = [err && err.message, err && err.__hohRpcDetails, err && err.__hohRpcHint].join(' ');
      return named.test(text) && /could not find|schema cache/i.test(text);
    }
    if (code === '42883') {
      /* 42883 can also mean an INSTALLED RPC called a missing helper. In that
         failure Postgres puts the outer RPC name in the context/details. Do
         not search those fields: require the primary message itself to say
         that this exact requested function is the one that does not exist. */
      var message = String(err && err.message || '');
      var missingRequested = new RegExp('\\bfunction\\s+(?:(?:"?public"?)\\.)?"?' + safeName +
        '"?(?:\\s*\\([^\\n]{0,512}\\))?\\s+does not exist\\b', 'i');
      return missingRequested.test(message);
    }
    return false;
  }
  /* R496: Supabase's promise has no useful UI deadline. A stalled mobile
     handover must not leave Battle, plan choice, or a reordered party inert
     forever. This deadline does not pretend to cancel a write already at the
     server; callers therefore send only idempotent full-state replacements
     and reject every late reply through their frozen account generation. */
  function rpcDeadline(promise, ms, message) {
    return new Promise(function (resolve, reject) {
      var done = false;
      var timer = setTimeout(function () {
        if (done) return;
        done = true;
        var e = new Error(message || 'The Hall took too long to answer. Try again.');
        e.__hohTimeout = true;
        reject(e);
      }, Math.max(1000, ms | 0));
      Promise.resolve(promise).then(function (value) {
        if (done) return;
        done = true; clearTimeout(timer); resolve(value);
      }, function (err) {
        if (done) return;
        done = true; clearTimeout(timer); reject(err);
      });
    });
  }

  /* ============ R791 · ONE READING OF A FAILURE ============================
     Jan hit a run that could not be continued, settled or ended, and every
     screen on the way chose its words from WHERE it was called rather than
     from WHAT failed: an engine throw read "Reconnect to your run" and its
     Retry replayed the same throw forever. One reader now, used by every
     recovery path:
       account    the signed-in account changed, or the server wants a sign in
       reload     the page is older than the run (the reload card)
       engine     E.run threw. Tagged at the source (__hohEngine), or a known
                  engine code in the message. Replays the same way every time.
       server     the Worker or database refused this exact request and will
                  refuse its retry the same way: its own deterministic flag,
                  V142's engine_refused, an engine or upgrade code in the
                  detail, or a named upgrade refusal
       transient  everything else: timeouts, a dropped connection, a bare 5xx
     Only transient failures ever offer a plain Retry. */
  var RUN_ENGINE_CODE_RE = /^(?:upgrade\d+:[a-z0-9_]+|invalid_[a-z0-9_]+|special_reward_[a-z0-9_]+|combat_termination_[a-z0-9_]+|encounter_(?:queue|phase)_limit|engine_not_released|unresolved_rule[a-z0-9_]*|shell_reward_[a-z0-9_]+|vanguard_reward_[a-z0-9_]+|unknown_(?:unit_archetype|card_op|ability_op|unlimited_buff_fx)|unsupported_(?:battleground_[a-z_]+|mode_for_engine)|unlimited_segment_crosses_stop)/i;
  var THEME_BUILD = 'R813';
  function settleTransportFailure(err) {
    if (!err) return false;
    var msg = String(err.message || '');
    if (err.name === 'AbortError' || err.__hohTimeout || /signal is aborted/i.test(msg)) return true;
    if (err.name === 'TypeError' && /fetch|network|load failed/i.test(msg)) return true;
    if (/^bad settle body/.test(msg)) return true;
    var s = err.httpStatus | 0;
    return (s === 408 || s === 425 || s === 429 || s >= 500) && !err.settleDetail && !err.__hocDeterministic;
  }
  /* R791 review: two named refusals have a repair of their own, so they go to
     the recovery screen and never to the fault card. R789's
     repairUpgradeSettlement114 rebuilds a changed card history, and an
     unchosen upgrade is chosen there. Sending them to End run would give up a
     segment that can still be saved. */
  var RUN_RECOVERABLE_RE = /^upgrade_(?:committed_plays_changed|choice_required)$/i;
  function runServerDeterministic(e) {
    if (!e) return false;
    if (RUN_RECOVERABLE_RE.test(String(e.settleCode || '')) || RUN_RECOVERABLE_RE.test(String(e.settleDetail || '')) ||
        RUN_RECOVERABLE_RE.test(String(e.__hocRefused || ''))) return false;
    if (e.__hocDeterministic === true || e.__hohServerDeterministic === true) return true;
    if (e.settleCode === 'engine_refused' || e.__hocRefused === 'engine_refused') return true;
    var d = e.__hocDetailRemembered ? '' : String(e.settleDetail || ''), c = String(e.settleCode || '');
    if (d && (RUN_ENGINE_CODE_RE.test(d) || /^upgrade_[a-z0-9_]+/i.test(d))) return true;
    if (c === 'upgrade_checkpoint_temporarily_unavailable') return !!d;
    return /^upgrade_[a-z0-9_]+$/i.test(c);
  }
  /* the leading code of a machine string: 'upgrade114:dice_work_budget:
     class:A:2:38' is 'upgrade114:dice_work_budget', the same trim V142 uses */
  function trimRunCode(value) {
    /* BG-SYNC (2026-10-10): a team the engine refused keeps the body it named, so the code says which hero to look at
       ('invalid_world_body:recruits:0'); every other code trims exactly as before */
    var m = /^([a-z][a-z0-9_]*)(?::([a-z][a-z0-9]*_[a-z0-9_]+))?(?::(roster|recruits|campfire):([0-9]{1,3})(?![0-9]))?/i.exec(String(value || '').trim());
    return m ? (m[1] + (m[2] ? ':' + m[2] : '') + (m[3] ? ':' + m[3] + ':' + m[4] : '')).slice(0, 64) : '';
  }
  function codeLikeRunText(msg) {
    var s = String(msg || '').trim();
    return s.length <= 160 && s.indexOf('_') >= 0 && /^[a-z][a-z0-9_]*(?::[a-z0-9_:.-]*)?$/i.test(s);
  }
  function runErrorCode(err) {
    var e = (err && typeof err === 'object') ? err : {};
    var msg = String(e.message || (typeof err === 'string' ? err : '') || '').trim();
    if (e.__hohFaultCode) return trimRunCode(e.__hohFaultCode);
    if ((e.__hohEngine || RUN_ENGINE_CODE_RE.test(msg)) && codeLikeRunText(msg.split(' ')[0])) return trimRunCode(msg);
    if (e.__hohEngine) return e.name === 'TypeError' ? 'engine_type_error' : 'engine_error';
    if (e.settleDetail && !e.__hocDetailRemembered && codeLikeRunText(e.settleDetail)) return trimRunCode(e.settleDetail);
    var named = String(e.settleCode || e.__hocRefused || '');
    if (named && named !== 'refused' && codeLikeRunText(named)) return trimRunCode(named);
    if (e.__hohRpcName && e.__hohRpcCode) return 'db' + String(e.__hohRpcCode).replace(/[^a-z0-9]/gi, '').slice(0, 12);
    if (e.name === 'AbortError' || e.__hohTimeout || /signal is aborted/i.test(msg)) return 'timeout';
    if (e.name === 'TypeError' && /fetch|network|load failed/i.test(msg)) return 'offline';
    if (e.httpStatus) return 'http' + (e.httpStatus | 0);
    return codeLikeRunText(msg) ? trimRunCode(msg) : '';
  }
  function runErrorKind(err) {
    var e = (err && typeof err === 'object') ? err : {};
    var msg = String(e.message || (typeof err === 'string' ? err : '') || '');
    var code = runErrorCode(err);
    var kind = 'transient';
    if (e.__hohAccountChanged || e.httpStatus === 401 || e.httpStatus === 403 || /^sign in first$/i.test(msg)) kind = 'account';
    else if (e.__hohReload || /^unsupported_engine_version:/.test(msg)) kind = 'reload';
    else if (e.__hohEngine || RUN_ENGINE_CODE_RE.test(msg)) kind = 'engine';
    else if (runServerDeterministic(e)) kind = 'server';
    return { kind: kind, code: code, deterministic: kind === 'engine' || kind === 'server', transport: settleTransportFailure(e) };
  }
  /* The code a player can read out from a screenshot: BG-<run id>-<code>.
     A namespace that is only an engine number ('upgrade114') is dropped, so
     Jan reads BG-4821-dice_work_budget and the fault table has the rest. */
  function runSupportCode(err, runId, prefix) {
    var code = runErrorCode(err);
    if (!code) return '';
    var parts = code.split(':');
    if (parts.length > 1 && /\d$/.test(parts[0])) parts.shift();
    code = parts.join('.').replace(/[^a-z0-9_.-]/gi, '').slice(0, 40);
    var id = String(runId == null ? '' : runId).replace(/[^a-z0-9_-]/gi, '').slice(0, 20);
    return (prefix || 'BG') + '-' + (id ? id + '-' : '') + code;
  }
  /* Copy this file or the UI wrote for a player is kept as it is. Anything
     that reads like a machine (PostgREST text, a DOMException, an engine code,
     'temporary settle failure (502)') is replaced by the caller's plain line,
     plus the support code when the failure is not just weather. */
  function plainRunCopy(msg, err) {
    if (!msg || msg.length > 280) return false;
    if (err && err.__hohEngine) return false;
    if (err && err.__hohRpcName && String(err.__hohRpcCode || '') !== 'P0001') return false;
    if (!/^[A-Z0-9"'“‘]/.test(msg) || msg.indexOf(' ') < 0 || !/[.!?)]$/.test(msg)) return false;
    return !/violates|constraint|relation "|column "|function |syntax error|permission denied|JWT|PGRST|duplicate key|\bnull\b|\bundefined\b|is not a function|Cannot read|Unexpected token|JSON|signal is aborted|Failed to fetch|Load failed|NetworkError|\(\d{3}\)|[a-z0-9]_[a-z0-9]|::|[{}<>]|https?:/i.test(msg);
  }
  var RUN_LINES = {
    end: 'The run could not be ended just now. Your banked rewards are safe. Try again.',
    start: 'That run could not start. Try again.',
    check: 'Your run could not be checked. Try again.',
    prepare: 'The next round could not start. Check your connection and try again.',
    shop: 'The shop could not be reached. Try again.',
    upgrade: 'The upgrade could not be saved. Your run is still saved.',
    claim: 'That upgrade could not be claimed. Nothing was spent.',
    quest: 'The quest could not be saved. Try again.',
    save: 'That could not be saved. Try again.',
    generic: 'Something went wrong. Try again.'
  };
  function playerLine(err, ctx) {
    var c = typeof ctx === 'string' ? { context: ctx } : (ctx || {});
    var context = String(c.context || '');
    var base = c.line || RUN_LINES[context] || RUN_LINES.generic;
    var msg = String((err && err.message) || (typeof err === 'string' ? err : '') || '').trim();
    var info = runErrorKind(err);
    if (plainRunCopy(msg, err)) return msg;
    if (info.kind === 'account') return 'Sign in again to continue.';
    if (info.transport) {
      return (context === 'end' || context === 'flee')
        ? 'The game server did not answer. Your banked rewards are safe. Try again.' : base;
    }
    var prefix = c.prefix || ((context === 'claim' || context === 'quest') ? 'HALL' : 'BG');
    var code = runSupportCode(err, c.runId, prefix);
    return code ? base + ' (Code ' + code + ')' : base;
  }
  /* The only durable trace of a run that broke on a player's device: one row
     per run and code in hoh_bg_run_faults (R791B). Asked once per run and
     code per page, silent in every failure, and a door that is not installed
     yet (PGRST202) is not asked again on this page. */
  var _faultNoted = {}, _faultNoteMissing = false;
  function noteRunFault(runId, code, where, engineVer) {
    try {
      if (_faultNoteMissing || localMode() || !signedIn()) return Promise.resolve(null);
      var id = String(runId == null ? '' : runId);
      var c = trimRunCode(code && typeof code === 'object' ? runErrorCode(code) : code).replace(/[^a-z0-9_:.-]/gi, '').slice(0, 64);
      if (!/^[0-9]{1,19}$/.test(id) || !c) return Promise.resolve(null);
      var key = id + '|' + c;
      if (_faultNoted[key]) return Promise.resolve(null);
      _faultNoted[key] = 1;
      var scope = captureCombatAccount();
      var eng = (engineVer | 0) || (((window.HOHContract || {}).ENGINE_VERSION) | 0) || null;
      var idNum = Number(id);
      return rpc('hoh_bg_run_fault_note', {
        p_email: scope.email, p_pin: scope.pin, p_run_id: Number.isSafeInteger(idNum) ? idNum : id, p_code: c,
        p_where: String(where || '').replace(/[^a-z0-9_-]/gi, '').slice(0, 40), p_engine: eng, p_build: THEME_BUILD
      }).catch(function (err) {
        if (rpcDefinitelyMissing(err, 'hoh_bg_run_fault_note')) _faultNoteMissing = true;
        return null;
      });
    } catch (_) { return Promise.resolve(null); }
  }

  /* ---------- the collection (the real cards you own) --------------------
     hohShopOwnedCards only knows the collection AFTER the Shop tab has
     fetched get_shop_state, so the Battlegrounds loads the collection itself.
     Items are enriched through the same hohGetAllCharacters lookup the Shop
     uses, so every card carries its true name, class, hearts, and portrait. */
  var collectionCache = null;
  var collectionCacheEmail = '';
  var collectionCachePin = '';
  var collectionCacheGeneration = 0;
  /* R318: remembers whether the LAST collection fetch failed, so the UI can
     tell "you own nothing yet" apart from "the Hall was unreachable". */
  var collectionLoadFailed = false;
  /* ⛔ R647: AND WHETHER ANYBODY EVER ASKED. collectionLoadFailed answers
     "the last attempt broke"; it says nothing about "no attempt was ever
     made", and those two are not the same thing to a screen that has to
     decide whether a player owns enough heroes to play.

     THAT GAP COST JAN THE WHOLE MODE. The Hall's Battlegrounds door asks
     HOHContractUI.unlimOwnedPlayableCount() how many heroes he has, and
     NOTHING on the Hall page loads the collection: loadCollection runs when
     the GAME boots (hoh-contract-ui.js, the enterBattlegrounds bootLoads),
     which is after that card has already painted and been tapped. So with a
     cold cache and no failure to report, the count read a real, honest,
     wrong ZERO, the door greyed Start behind "You need five heroes to play.
     You have 0.", and the tap handler's own copy of the gate refused it too.
     He owns more than a hundred cards.

     This flag is the missing third answer. It is true only once a load has
     actually produced a collection for THIS account, so "unknown" stops
     impersonating "empty". */
  var collectionLoadDone = false;
  function collectionAccount() { return String(email() || '').trim().toLowerCase(); }
  function useCollectionAccount() {
    var now = collectionAccount();
    var nowPin = String(pin() || '');
    if (collectionCacheEmail !== now || collectionCachePin !== nowPin) {
      /* R494: module globals survive an in-page sign-out/account switch. A
         collection without an account key can therefore show Account A's
         cards to Account B until reload. Reset before any cache read.
         R495: the PIN and generation are part of the boundary too. Comparing
         only the email admitted an old A response after A -> B -> A, and a
         rotated PIN could keep the prior credential's cache alive. */
      collectionCache = null;
      collectionCacheEmail = now;
      collectionCachePin = nowPin;
      collectionCacheGeneration++;
      collectionLoadFailed = false;
      /* R647: a new credential has never been looked up, whatever the old one
         answered. This reset is the whole reason the flag is safe. */
      collectionLoadDone = false;
    }
    return now;
  }
  function captureCollectionAccount() {
    return {
      account: useCollectionAccount(),
      generation: collectionCacheGeneration,
      email: email(),
      pin: pin()
    };
  }
  function collectionAccountIsCurrent(scope) {
    if (!scope) return false;
    useCollectionAccount();
    return collectionCacheEmail === scope.account &&
      collectionCacheGeneration === scope.generation &&
      collectionCachePin === String(scope.pin || '');
  }
  function heroLookup(id) {
    try {
      var all = (typeof window.hohGetAllCharacters === 'function') ? (window.hohGetAllCharacters() || []) : [];
      for (var i = 0; i < all.length; i++) { if (String(all[i].id) === String(id)) return all[i]; }
    } catch (e) {}
    return null;
  }
  function enrichCard(item) {
    var hero = heroLookup(item.character_id);
    return {
      id: item.character_id,
      name: (hero && hero.name) || item.name || 'Hero',
      character_class: (hero && hero.character_class) || item.character_class || 'Fighter',
      hearts: Math.max((hero && hero.hearts) || 0, item.hearts || 0),
      race: (hero && hero.race) || '',
      image_thumb_url: (hero && (hero.image_thumb_url || hero.image_url)) || item.img || item.image_thumb_url || '',
      /* R380: carry the real flags so ownedRoster can tell an own card from a
         portrait. The old hardcoded true predates own cards being playable. */
      is_ff_portrait: (item.ff != null ? !!item.ff : true),
      own: !!item.own,
      copies: item.copies, cashed_copies: (item.cashed_copies != null ? item.cashed_copies : item.cashed),
      spent_copies: (item.spent_copies != null ? item.spent_copies : item.spent),
      plays_left: item.plays_left
    };
  }
  function shopCards() {
    try { return (window.hohShopOwnedCards && window.hohShopOwnedCards()) || []; } catch (e) { return []; }
  }
  function ownedCards() {
    useCollectionAccount();
    /* The contract's forced refresh is newer than a warm Shop snapshot. Keep
       it authoritative; pack opens below merge into this same cache. */
    if (collectionCache) return collectionCache;
    var s = shopCards();
    if (s.length) return s;
    return [];
  }
  function loadCollection(force) {
    var scope = captureCollectionAccount();
    var warm = shopCards();
    if (warm.length && !force) {
      collectionCache = warm;
      collectionLoadDone = true;                 /* R647: a real answer */
      return Promise.resolve(collectionCache);   /* the Shop already knows */
    }
    if (collectionCache && !force) return Promise.resolve(collectionCache);
    if (!signedIn()) return Promise.resolve([]);
    return rpc('get_shop_state', { user_email: scope.email, user_pin: scope.pin }).then(function (d) {
      /* The account can change while the request is in flight. Never let the
         old response seed the new account's cache. */
      if (!collectionAccountIsCurrent(scope)) return [];
      /* A logical refusal is not an empty collection. Treating
         {ok:false,status:'unauthenticated'} as success erased the last good
         snapshot and sent an established player to the first-card gate. */
      if (!d || d.ok !== true || !Array.isArray(d.collection)) {
        throw new Error((d && (d.status || d.error)) || 'collection unavailable');
      }
      var coll = (d && Array.isArray(d.collection)) ? d.collection : [];
      /* R422 (Jan): EVERY collected card is playable — FF or not, yours or
         not. The old `(c.ff || c.own)` gate silently hid strangers' non-FF
         cards from the muster even though the server now fields them. */
      collectionCache = coll.filter(function (c) { return c && c.character_id != null; }).map(enrichCard);
      collectionCacheEmail = scope.account;
      collectionCachePin = String(scope.pin || '');
      collectionLoadFailed = false;
      /* R647: the server answered for this account. An empty answer is now a
         KNOWN empty, which is what lets the five-hero gate refuse honestly. */
      collectionLoadDone = true;
      return collectionCache;
    }).catch(function () {
      if (!collectionAccountIsCurrent(scope)) return [];
      collectionLoadFailed = true;
      /* Keep the last same-account view (or the warm Shop view) as a visibly
         stale fallback. A failed refresh must never impersonate zero cards. */
      if (!collectionCache && warm.length) collectionCache = warm;
      collectionCache = collectionCache || [];
      return collectionCache;
    });
  }

  /* ---------- card levels (per-account XP — the Battlegrounds power) ----
     Mirrors the profile hero level system exactly (1 ♥ = 10 XP, 200 XP × band
     multiplier per level, soft cap 100), but stored per account per card
     (card_xp), so a freshly pulled card starts at level 1. Level bands map
     one-to-one onto the engine's power tiers, so power comes from the CARD
     LEVEL while rarity (the gem) stays the hero's hall hearts. */
  var XP_PER_HEART = 10;
  function xpCost(level) {
    if (level >= 100) return Infinity;
    var m = level >= 90 ? 150 : level >= 75 ? 50 : level >= 60 ? 20 : level >= 40 ? 8 : level >= 20 ? 3 : 1;
    return 200 * m;
  }
  function levelPower(level) {   /* engine hearts-equivalent per band (R769 floors) */
    return level >= 91 ? 1200 : level >= 76 ? 1000 : level >= 61 ? 500 : level >= 41 ? 200 : level >= 21 ? 50 : 0;
  }
  function nextDieLevel(level) { /* the next level where the die grows */
    var STEPS = [21, 41, 61, 76, 91];
    for (var i = 0; i < STEPS.length; i++) { if (level < STEPS[i]) return STEPS[i]; }
    return null;
  }
  /* R494: collection state is not the only account-owned state this module
     keeps alive. XP and gear used to survive an in-page account switch, so
     Account B could inherit Account A's levels and abilities until some later
     write happened to invalidate the cache. Keep both combat caches behind
     one normalized account boundary. The generation also closes the A -> B ->
     A in-flight-response race once B has touched either cache. */
  var xpCache = null;
  /* Atomic battle settlement returns the exact saved XP rows. Keep those
     receipts as a narrow overlay so a settlement that lands before the full XP
     cache has loaded can still build the next roster at the server's level.
     This is copied server truth, never a client-side XP calculation. */
  var xpReceiptCache = {};
  /* A claim may settle while an older get_card_xp read is still in flight.
     Keep the exact saved mask over that stale reply until a later read has
     caught up. Claimed bits are permanent, so this overlay is monotonic. */
  var progressionReceiptCache = {};
  var ultimateQuestCache = {}, ultimateQuestsLoaded = false, ultimateQuestsFailed = false, ultimateQuestsPromise = null;
  /* Keep the last read's authority separate from its cached value. A failed
     request deliberately returns the last same-account snapshot so the card
     never flashes back to level 1, but callers that can spend Hearts must be
     able to tell that the snapshot was not verified. */
  var xpLoadFailed = false;
  var gearCache = null;
  var gearLoadFailed = false;
  /* Atomic rerolls return committed server truth before the follow-up gear
     read. Keep that exact row as a short receipt overlay and sequence reads,
     so a slower request started before the commit cannot repaint the old
     ability over the reel's landed result. */
  var gearReceiptCache = {};
  var gearLoadSeq = 0;
  var combatCacheAccount = '';
  var combatCachePin = '';
  var combatCacheGeneration = 0;
  function useCombatCacheAccount() {
    var now = collectionAccount();
    var nowPin = String(pin() || '');
    if (combatCacheAccount !== now || combatCachePin !== nowPin) {
      combatCacheAccount = now;
      combatCachePin = nowPin;
      combatCacheGeneration++;
      xpCache = null;
      xpReceiptCache = {};
      progressionReceiptCache = {};
      ultimateQuestCache = {}; ultimateQuestsLoaded = false; ultimateQuestsFailed = false; ultimateQuestsPromise = null;
      xpLoadFailed = false;
      /* Debug progression previews are session-only and account-shaped too. */
      progressionPreview = {};
      gearCache = null;
      gearLoadFailed = false;
      gearReceiptCache = {};
      gearLoadSeq++;
      /* A climb is account-owned too. The door reads this mirror
         synchronously, so leaving Account A's value alive until B's ladder
         request returns can paint — and, on a failed read, activate — A's
         Resume state for B. Invalidate the cache, request sequence, mirror,
         and one-shot door latch at the credential boundary. */
      _ladCache = null;
      _ladSeq++;
      try {
        window.__hohBgClimb = null;
        window.__hohBgClimbAsked = 0;
        document.dispatchEvent(new CustomEvent('hoh:bg-climb-changed'));
      } catch (_) {}
    }
    return now;
  }
  function captureCombatAccount() {
    return {
      account: useCombatCacheAccount(),
      generation: combatCacheGeneration,
      email: email(),
      /* Preserve the credential's original RPC type; normalize only when
         comparing snapshots. */
      pin: pin()
    };
  }
  function combatAccountIsCurrent(snap) {
    if (!snap) return false;
    useCombatCacheAccount();
    return combatCacheAccount === snap.account &&
      combatCacheGeneration === snap.generation &&
      combatCachePin === String(snap.pin || '');
  }
  var XP_LOCAL_KEY = 'hoh-card-xp-local-v1';
  function xpLocalRead() { try { return JSON.parse(localStorage.getItem(XP_LOCAL_KEY) || '{}') || {}; } catch (e) { return {}; } }
  function xpLocalWrite(m) { try { localStorage.setItem(XP_LOCAL_KEY, JSON.stringify(m)); } catch (e) {} }
  /* Engine 78: rewards are earned by level, but deliberately CLAIMED on the
     character card. The integer is compact enough to freeze into a run and
     each bit is a server-authoritative, permanent milestone. Keep the order
     stable: SQL, the engine, and the card back use these exact bit positions. */
  var CARD_PROGRESSION_MILESTONES = [
    { id:'card_20', bit:1, requires:0, kind:'craft', level:10, title:'Card Craft', summary:'Create 1 class card after 4 actions.' },
    { id:'class_move_20', bit:128, requires:0, kind:'move', level:20, title:'Second Class Move', summary:'Alternate your first and second class moves.' },
    { id:'card_30', bit:2, requires:1, kind:'craft', level:30, title:'Practiced Craft', summary:'Create 2 class cards after 3, then 4 actions.' },
    { id:'ability_35', bit:4, requires:3, kind:'crystal', level:40, title:'Second Crystal Ability', summary:'Unlock your second crystal ability slot.' },
    { id:'card_40', bit:8, requires:7, kind:'craft', level:50, title:'Mastered Craft', summary:'Create 3 class cards after 3, 4, then 5 actions.' },
    { id:'ultimate_60', bit:256, requires:128, kind:'ultimate', level:60, title:'Ultimate Class Move', summary:'Complete this hero’s solo quest to unlock move 3.' },
    { id:'card_50', bit:16, requires:15, kind:'living', level:70, title:'Living Cards', summary:'Generated cards improve while held.' },
    { id:'card_70', bit:32, requires:31, kind:'swift', level:80, title:'Swift Craft', summary:'Create cards after 2, 3, then 4 charges.' },
    { id:'card_100', bit:64, requires:63, kind:'endless', level:100, title:'Endless Craft', summary:'Start at 2 charges and keep crafting.' }
  ];
  var CARD_PROGRESSION_MASK = 511;
  var progressionPreview = {};
  function progressionMask(value) {
    var n = parseInt(value, 10);
    return isFinite(n) ? (Math.max(0, n) & CARD_PROGRESSION_MASK) : 0;
  }
  function progressionClaimsAtLevel(value, level) {
    /* R753: display order is not bit order. Previously claimed rewards are
       permanent even when their new unlock level is higher. The server checks
       new claims; the engine separately preserves old saved-run rules. */
    var mask = progressionMask(value), prefix = 0;
    for (var i = 0; i < 7; i++) {
      if (!(mask & (1 << i))) break;
      prefix |= 1 << i;
    }
    return prefix | (mask & 128) | ((mask & 128) ? (mask & 256) : 0);
  }
  function progressionPreviewFor(cardKey) {
    if (!godMode()) return null;
    return progressionPreview[String(cardKey || '')] || null;
  }
  function loadCardXp(force) {
    if (localMode()) { xpLoadFailed = false; xpCache = xpLocalRead(); return loadCardUltimateQuests(force).then(function () { return xpCache; }); }
    var scope = captureCombatAccount();
    /* ⛔ R638: A FAILED READ USED TO CACHE ITSELF FOREVER. The catch below
       sets `xpCache = xpCache || {}`, and `{}` is truthy, so this
       short-circuit answered every later unforced call with the empty object
       a failure produced. Nothing retried and nothing said so: every card
       read as level 1 for the life of the mount, which silently shuts the
       level-30 second-slot gate. A failure must stay retryable. */
    if (xpCache && !force && !xpLoadFailed) return loadCardUltimateQuests(false).then(function () { return xpCache; });
    if (!signedIn()) { xpLoadFailed = false; xpCache = {}; return Promise.resolve(xpCache); }
    return rpc('get_card_xp', { p_email: scope.email, p_pin: scope.pin }).then(function (rows) {
      var next = {};
      (rows || []).forEach(function (r) {
        next[r.card_key] = {
          level: r.level || 1,
          xp: r.xp || 0,
          progression_claims: progressionMask(r.progression_claims)
        };
      });
      /* A reply belongs to the credentials that sent it, never whichever
         account happens to be open when it lands. */
      if (!combatAccountIsCurrent(scope)) return xpCache || {};
      /* A full read may have started just before an atomic settlement. Do not
         let that older response overwrite the newer saved receipt. */
      for (var rk in xpReceiptCache) {
        if (!Object.prototype.hasOwnProperty.call(xpReceiptCache, rk)) continue;
        var rr = xpReceiptCache[rk], nr = next[rk];
        if (!nr || rr.level > nr.level || (rr.level === nr.level && rr.xp >= nr.xp)) {
          next[rk] = {
            level: rr.level,
            xp: rr.xp,
            progression_claims: progressionMask((nr && nr.progression_claims) | rr.progression_claims)
          };
        }
        else delete xpReceiptCache[rk]; /* a later server read (for example another device) wins */
      }
      for (var pk in progressionReceiptCache) {
        if (!Object.prototype.hasOwnProperty.call(progressionReceiptCache, pk)) continue;
        var pr = progressionMask(progressionReceiptCache[pk]);
        next[pk] = next[pk] || { level:1, xp:0, progression_claims:0 };
        var serverClaims = progressionMask(next[pk].progression_claims);
        next[pk].progression_claims = progressionMask(serverClaims | pr);
        if ((serverClaims & pr) === pr) delete progressionReceiptCache[pk];
      }
      xpLoadFailed = false;
      xpCache = next;
      try { document.dispatchEvent(new CustomEvent('hoh:card-progression-changed', { detail:{ loaded:true } })); } catch (_) {}
      return loadCardUltimateQuests(force).then(function () { return xpCache; });
    }).catch(function () {
      if (!combatAccountIsCurrent(scope)) return xpCache || {};
      xpLoadFailed = true;
      xpCache = xpCache || {};
      return xpCache;
    });
  }
  function cardXpFor(cardKey) {
    if (!localMode()) useCombatCacheAccount();
    var cached = (xpCache || {})[cardKey] || null;
    var receipt = (xpReceiptCache || {})[cardKey] || null;
    var e = receipt || cached;
    var preview = progressionPreviewFor(cardKey);
    return e ? {
      level: preview ? preview.level : (e.level || 1),
      xp: e.xp || 0,
      progression_claims: preview ? preview.claims : progressionMask(
        (cached && cached.progression_claims) | (receipt && receipt.progression_claims) |
        progressionReceiptCache[cardKey])
    } : {
      level: preview ? preview.level : 1,
      xp: 0,
      progression_claims: preview ? preview.claims : 0
    };
  }
  function applyBattleXpReceipt(res, scope) {
    scope = scope || captureCombatAccount();
    if (!combatAccountIsCurrent(scope)) return 0;
    applyUltimateQuestReceipt(res && res.ultimate_quests, scope);
    var awards = res && res.xp && Array.isArray(res.xp.awards) ? res.xp.awards : [];
    var applied = 0;
    for (var i = 0; i < awards.length; i++) {
      var row = awards[i] || {};
      var key = String(row.card_key || '');
      if (!key || row.level_after == null || row.xp_after == null) continue;
      var saved = {
        level: Math.max(1, parseInt(row.level_after, 10) || 1),
        xp: Math.max(0, parseInt(row.xp_after, 10) || 0),
        /* Settlement does not change claims. Preserve the newest verified
           mask instead of replacing the whole cache row with XP alone. */
        progression_claims: progressionMask((((xpCache || {})[key] || {}).progression_claims) |
          (((xpReceiptCache || {})[key] || {}).progression_claims) | progressionReceiptCache[key])
      };
      /* A receipt may arrive after a later fight (two tabs, a hidden tab, or a
         timeout replay). Permanent progression is monotonic: an older saved
         result may still be rendered, but it must never move the shared card
         cache backwards. */
      var current = (xpReceiptCache || {})[key] || (xpCache || {})[key] || { level: 1, xp: 0 };
      var newer = saved.level > (current.level | 0) ||
        (saved.level === (current.level | 0) && saved.xp >= (current.xp | 0));
      if (newer) {
        xpReceiptCache[key] = saved;
        if (xpCache) xpCache[key] = saved;
        applied++;
      }
    }
    return applied;
  }
  function emberLine(runId, trigger, characterId) {
    var scope = captureCombatAccount();
    if (!scope.email || localMode()) return Promise.resolve(null);
    return rpcDeadline(rpc('hoh_ember_line', {
      p_email:scope.email, p_pin:scope.pin, p_run_id:runId || null,
      p_trigger:trigger, p_character_id:characterId || null
    }), 3500, 'Ember dialogue unavailable').then(function (r) {
      return combatAccountIsCurrent(scope) ? r : null;
    }).catch(function () { return null; });
  }
  function prepareEmberPortrait(scope, party) {
    if (!(_unlimCache && _unlimCache.value && (_unlimCache.value.start_engine_ver | 0) >= 89) || party.length !== 1) return Promise.resolve();
    return rpcDeadline(rpc('hoh_ember_preview', {p_email:scope.email,p_pin:scope.pin,p_party:party}),
      6000, 'Ember could not finish preparing. Please try again. No ticket was spent.').then(function (r) {
      if (!combatAccountIsCurrent(scope)) throw accountChangedError('Ember was preparing', false);
      if (!r || !r.eligible) return;
      return new Promise(function (resolve,reject) {
        var img = new Image(), done = false;
        function finish(ok) {
          if (done) return; done = true; clearTimeout(timer); img.onload = img.onerror = null;
          if (ok) resolve(); else reject(new Error('Ember’s portrait could not load. Please try again. No ticket was spent.'));
        }
        var timer = setTimeout(function () { finish(false); }, 6000);
        img.onload = function () { finish(img.naturalWidth > 0); }; img.onerror = function () { finish(false); };
        img.src = String(r.hero && r.hero.img || '');
      });
    });
  }
  /* does the signed-in account OWN this character (submitted it)? */
  function ownsCharacter(hero) {
    try {
      var em = (email() || '').toLowerCase();
      return !!(em && hero && String(hero.auth_email || hero.email || '').toLowerCase() === em);
    } catch (e) { return false; }
  }
  /* the level that drives battle power. If you OWN this FF portrait, the card
     inherits your PROFILE hero level (hohCharLevel) — the same level you raise
     in Your Hall. Otherwise it is the per-account card level, from scratch.
     Mirrors the server's contract_start branch so the picker matches the
     battle the worker judges. */
  /* R791 (Cortez, Level 254): every ticket an owner spends on their own hero
     adds a profile level with no cap, and the card panel printed that raw
     number while the server's card tables only hold 1 to 100. The battle
     level stops at 100 here, the same clamp the server applies, so the panel,
     the picker and the judge all read one number. */
  var CARD_BATTLE_LEVEL_MAX = 100;
  function effectiveLevel(cardKey) {
    return Math.min(CARD_BATTLE_LEVEL_MAX, effectiveLevelRaw(cardKey));
  }
  function effectiveLevelRaw(cardKey) {
    var cardLvl = cardXpFor(cardKey).level;
    var preview = progressionPreviewFor(cardKey);
    if (preview) return preview.level;
    /* `own:` is a real battle-card key too. Leaving that prefix attached made
       authored non-FF cards show the profile level in the header and Level 1
       in the ability gate below it. */
    var hero = heroLookup(String(cardKey).replace(/^(ff|own):/, ''));
    if (hero && ownsCharacter(hero) &&
        (hero.is_ff_portrait || /^own:/.test(String(cardKey))) &&
        typeof window.hohCharLevel === 'function') {
      try { return Math.max((window.hohCharLevel(hero) | 0) || 1, cardLvl); } catch (e) {}
    }
    return cardLvl;
  }
  /* spend hearts as XP. Bank is deducted FIRST and refunded on failure, the
     same flow as the profile's hohGrantCharacterXp. */
  /* ⛔ R638: A LEVEL CHANGE HAD NO ANNOUNCEMENT, SO NOTHING COULD REACT TO IT.
     The wallet says `hoh:hearts-bumped`, gear says `hoh:gear-changed`, and the
     card's LEVEL, which is now a gate rather than a number (the second ability
     slot opens at 30), said nothing at all. The card profile paints its gear
     panel once on open and had no reason to paint it again, so a player could
     pour hearts on a level-29 hero, watch the XP bar reach Level 30, and see
     the Passive slot nine pixels below still reading "Opens at Level 30" with
     no roll button. The only cure was closing and reopening the card, which
     nothing on screen suggested. */
  function announceCardLevel(cardKey, from, to) {
    if ((to | 0) === (from | 0)) return;
    try {
      document.dispatchEvent(new CustomEvent('hoh:card-level-changed',
        { detail: { cardKey: cardKey, from: from | 0, to: to | 0 } }));
    } catch (_) {}
  }
  function giveCardXp(cardKey, hearts) {
    var h = parseInt(hearts, 10) || 0;
    if (h <= 0) return Promise.reject(new Error('no hearts'));
    if (!canAfford(h)) return Promise.reject(new Error('not enough hearts'));
    var scope = localMode() ? null : captureCombatAccount();
    var _lvBefore = (cardXpFor(cardKey) || {}).level | 0;
    bankApply(-h, 'spend_card_xp');
    var amount = h * XP_PER_HEART;
    if (localMode()) {
      var m = xpLocalRead();
      var e = m[cardKey] || { level: 1, xp: 0, progression_claims:0 };
      e.xp += amount;
      var up = false;
      while (e.level < 100 && e.xp >= xpCost(e.level)) { e.xp -= xpCost(e.level); e.level++; up = true; }
      m[cardKey] = e; xpLocalWrite(m); xpCache = m;
      announceCardLevel(cardKey, _lvBefore, e.level);
      return Promise.resolve({ ok: true, level: e.level, xp: e.xp, xp_to_next: xpCost(e.level), leveled_up: up, power: levelPower(e.level) });
    }
    return rpc('give_card_xp', { p_email: scope.email, p_pin: scope.pin, p_card_key: cardKey, p_xp: amount })
      .then(function (res) {
        if (!res || res.ok !== true) throw new Error((res && res.error) || 'xp failed');
        if (combatAccountIsCurrent(scope)) {
          xpCache = xpCache || {};
          var oldProgression = progressionMask(((xpCache[cardKey] || {}).progression_claims) |
            (((xpReceiptCache || {})[cardKey] || {}).progression_claims) | progressionReceiptCache[cardKey]);
          xpCache[cardKey] = { level: res.level, xp: res.xp, progression_claims:oldProgression };
          delete xpReceiptCache[cardKey];
          announceCardLevel(cardKey, _lvBefore, res.level);
        } else {
          /* The server saved A's XP, so the payment remains A's. Reject only
             the stale UI continuation; the catch below must not refund a
             successfully committed level-up. */
          throw accountChangedError('the level-up was saving', true);
        }
        return res;
      })
      .catch(function (e2) {
        if (!(e2 && e2.__hohCommitted)) accountHeart(scope, h, 'earn_card_xp_refund');
        throw e2;
      });
  }

  function mergeUltimateAttempt(previous, next) {
    if (!next || !Number.isSafeInteger(Number(next.run_family)) || Number(next.run_family) < 1 ||
        typeof next.eligible !== 'boolean' || !Number.isFinite(Date.parse(next.started_at)) ||
        !Number.isFinite(Date.parse(next.updated_at))) return previous || null;
    var clean = {run_family:Number(next.run_family), eligible:next.eligible,
      reason:next.eligible ? null : 'extra_hero', started_at:next.started_at, updated_at:next.updated_at};
    if (!previous) return clean;
    if (Number(previous.run_family) === clean.run_family) {
      if (Date.parse(previous.updated_at) > Date.parse(clean.updated_at)) return previous;
      if (previous.eligible === false) { clean.eligible = false; clean.reason = 'extra_hero'; }
      return clean;
    }
    var before = Date.parse(previous.started_at), after = Date.parse(clean.started_at);
    return after > before || (after === before && clean.run_family > Number(previous.run_family)) ? clean : previous;
  }
  function rememberUltimateQuest(row) {
    if (!row || !row.card_key || !/^(locked|available|active|complete|claimed)$/.test(String(row.status))) return;
    var isMove = row.quest_kind === 'class_move', key = (isMove ? 'class_move:' : '') + String(row.card_key), previous = ultimateQuestCache[key];
    var goal = isMove ? 10 : ultimateGoalOf(row.target_round ? row : previous);
    var rank = { locked:0, available:1, active:2, complete:3, claimed:4 };
    var soloPolicy=row.solo_policy || (previous && previous.solo_policy) || null;
    if (previous && rank[previous.status] > rank[row.status] && row.solo_policy !== 'one_main_lifetime') return;
    ultimateQuestCache[key] = {
      card_key:String(row.card_key), quest_kind:isMove ? 'class_move' : 'ultimate', status:row.status, level:Math.max(1, row.level | 0),
      accepted_at:row.accepted_at || (previous && previous.accepted_at) || null,
      solo_policy:soloPolicy,
      highest_solo_round:Math.max(0,row.highest_solo_round|0,(previous&&previous.highest_solo_round)|0),
      best_round:Math.min(goal, Math.max(0, row.best_round | 0, (previous && previous.best_round) | 0)),
      target_round:goal,
      run_family:row.run_family || (previous && previous.run_family) || null,
      latest_attempt:soloPolicy==='one_main_lifetime'?null:mergeUltimateAttempt(previous && previous.latest_attempt, row.latest_attempt)
    };
  }
  /* R784 (Engine 112): the Ultimate trial's goal is Round 20. The server's
     quest state names it (Round 30 until R784B runs), and every screen reads
     it from here; a row that does not name it means the new goal. The Class
     Move trial stays at Round 10. */
  function ultimateGoalOf(row) {
    var goal = row ? row.target_round | 0 : 0;
    return goal === 20 || goal === 30 ? goal : 20;
  }
  function applyUltimateQuestReceipt(rows, scope) {
    if (!Array.isArray(rows) || !combatAccountIsCurrent(scope)) return;
    rows.forEach(rememberUltimateQuest);
    if (rows.length) try { document.dispatchEvent(new CustomEvent('hoh:card-progression-changed', {detail:{quest:true}})); } catch (_) {}
  }
  function loadCardUltimateQuests(force) {
    if (!localMode()) useCombatCacheAccount();
    if (ultimateQuestsPromise) return ultimateQuestsPromise;
    if (ultimateQuestsLoaded && !force && !ultimateQuestsFailed) return Promise.resolve(ultimateQuestCache);
    if (localMode()) {
      try { ultimateQuestCache = JSON.parse(localStorage.getItem('hoh-ultimate-quests-local-v1') || '{}') || {}; } catch (_) { ultimateQuestCache = {}; }
      ultimateQuestsLoaded = true; ultimateQuestsFailed = false;
      return Promise.resolve(ultimateQuestCache);
    }
    var scope = captureCombatAccount();
    if (!signedIn()) return Promise.resolve({});
    var request = rpc('hoh_card_ultimate_quests', {p_email:scope.email,p_pin:scope.pin}).then(function (rows) {
      if (!combatAccountIsCurrent(scope)) return {};
      if (!Array.isArray(rows)) throw new Error('Quest status could not be verified.');
      rows.forEach(rememberUltimateQuest);
      ultimateQuestsLoaded = true; ultimateQuestsFailed = false;
      return ultimateQuestCache;
    }).catch(function () {
      if (combatAccountIsCurrent(scope)) ultimateQuestsFailed = true;
      return combatAccountIsCurrent(scope) ? ultimateQuestCache : {};
    }).then(function (rows) {
      if (ultimateQuestsPromise === request) ultimateQuestsPromise = null;
      if (combatAccountIsCurrent(scope)) try { document.dispatchEvent(new CustomEvent('hoh:card-progression-changed', {detail:{quest:true}})); } catch (_) {}
      return rows;
    });
    ultimateQuestsPromise = request;
    return request;
  }
  function cardUltimateQuestFor(cardKey, kind) {
    if (!localMode()) useCombatCacheAccount();
    var level = Math.max(1, effectiveLevel(cardKey) | 0), claims = cardProgressionClaims(cardKey);
    var isMove = kind === 'class_move', bit = isMove ? 128 : 256, required = isMove ? 20 : 60;
    var row = ultimateQuestCache[(isMove ? 'class_move:' : '') + String(cardKey)] || {};
    var target = isMove ? 10 : ultimateGoalOf(row);
    var lifetime=row.solo_policy==='one_main_lifetime', best=Math.max(0,row.highest_solo_round|0);
    return {
      status:(claims & bit) ? 'claimed' : (level<required?'locked':lifetime&&best>=target?'complete':/^(active|complete|claimed)$/.test(row.status) ? row.status : ultimateQuestsFailed ? 'error' : ultimateQuestsLoaded ? 'available' : 'loading'),
      accepted_at:row.accepted_at || null, best_round:(claims & bit) ? target : lifetime?Math.min(target,best):(row.best_round | 0),
      highest_solo_round:lifetime?best:ultimateQuestsLoaded&&!ultimateQuestsFailed?0:null,solo_policy:row.solo_policy||null,
      target_round:target, required_level:required, run_family:row.run_family || null,
      latest_attempt:row.latest_attempt ? Object.assign({}, row.latest_attempt) : null
    };
  }
  function acceptCardUltimateQuest(cardKey, kind) {
    cardKey = String(cardKey || '');
    var isMove = kind === 'class_move', required = isMove ? 20 : 60, cacheKey = (isMove ? 'class_move:' : '') + cardKey;
    var level = Math.max(1, effectiveLevel(cardKey) | 0);
    if (level < required) return Promise.reject(new Error('Reach Level ' + required + ' to start this quest.'));
    if (localMode()) {
      var old = ultimateQuestCache[cacheKey];
      rememberUltimateQuest({card_key:cardKey,quest_kind:isMove ? 'class_move' : 'ultimate',level:level,status:old && /^(active|complete|claimed)$/.test(old.status) ? old.status : 'active',accepted_at:old && old.accepted_at || new Date().toISOString(),best_round:old && old.best_round || 0});
      try { localStorage.setItem('hoh-ultimate-quests-local-v1', JSON.stringify(ultimateQuestCache)); } catch (_) {}
      ultimateQuestsLoaded = true;
      try { document.dispatchEvent(new CustomEvent('hoh:card-progression-changed', {detail:{cardKey:cardKey,quest:true}})); } catch (_) {}
      return Promise.resolve(cardProgressionFor(cardKey));
    }
    if (!signedIn()) return Promise.reject(new Error('Sign in to start this quest.'));
    var scope = captureCombatAccount();
    return rpc(isMove ? 'hoh_card_class_move_quest' : 'hoh_card_ultimate_quest', {p_email:scope.email,p_pin:scope.pin,p_card_key:cardKey,p_accept:true}).then(function (row) {
      if (!row || row.ok !== true) throw new Error((row && row.reason === 'second_move_required' ? 'Unlock your second class move first.' : row && (row.error || row.reason)) || 'Quest could not be started. Try again.');
      if (!combatAccountIsCurrent(scope)) throw accountChangedError('the quest was saving', true);
      if (String(row.card_key || '') !== cardKey || (isMove && row.quest_kind !== 'class_move') || !/^(active|complete|claimed)$/.test(row.status) || !row.accepted_at || !isFinite(Date.parse(row.accepted_at))) {
        throw new Error(row.status === 'locked' ? 'Reach Level ' + required + ' to start this quest.' : 'The saved quest could not be verified. Try again.');
      }
      rememberUltimateQuest(row);
      try { document.dispatchEvent(new CustomEvent('hoh:card-progression-changed', {detail:{cardKey:cardKey,quest:true}})); } catch (_) {}
      return cardProgressionFor(cardKey);
    });
  }
  function cardProgressionClaims(cardKey) {
    return progressionClaimsAtLevel(cardXpFor(cardKey).progression_claims, effectiveLevel(cardKey));
  }
  function cardProgressionFor(cardKey) {
    var level = Math.max(1, effectiveLevel(cardKey) | 0);
    var claims = cardProgressionClaims(cardKey);
    var quest = cardUltimateQuestFor(cardKey), moveQuest = cardUltimateQuestFor(cardKey, 'class_move');
    var milestones = CARD_PROGRESSION_MILESTONES.map(function (m) {
      var claimed = !!(claims & m.bit);
      var reached = level >= m.level;
      var prerequisite = (claims & m.requires) !== m.requires;
      var trial = m.kind === 'ultimate' ? quest : m.kind === 'move' ? moveQuest : null;
      var state = claimed ? 'claimed' : (reached && !prerequisite ? 'ready' : 'locked');
      if (!claimed && trial && reached && !prerequisite && trial.status !== 'complete') state = 'quest';
      var row = {
        id:m.id, bit:m.bit, kind:m.kind, level:m.level, title:m.title, summary:m.summary,
        state:state, remaining:Math.max(0, m.level - level),
        prerequisite:(!claimed && reached && prerequisite),
        earned:!claimed && reached && (!trial || trial.status === 'complete'),
        quest:trial
      };
      return row;
    });
    return {
      status: xpLoadFailed ? 'error' : ((xpCache === null && signedIn() && !localMode()) ? 'loading' : 'ready'),
      level:level, claims:claims, quest:quest,
      highest_solo_round:quest.highest_solo_round==null&&moveQuest.highest_solo_round==null?null:Math.max(quest.highest_solo_round||0,moveQuest.highest_solo_round||0), milestones:milestones
    };
  }
  var CARD_PROGRESSION_JOURNAL = 'hoh-card-progression-v1:';
  function progressionJournalKey(scope, cardKey, milestone) {
    return CARD_PROGRESSION_JOURNAL + encodeURIComponent(String(scope && scope.account || 'local')) + ':' +
      encodeURIComponent(String(cardKey || '')) + ':' + encodeURIComponent(String(milestone || ''));
  }
  function progressionRequest(scope, cardKey, milestone) {
    var key = progressionJournalKey(scope, cardKey, milestone), saved = null;
    try { saved = JSON.parse(localStorage.getItem(key) || 'null'); } catch (_) {}
    if (saved && saved.account === String(scope.account || '') && saved.cardKey === String(cardKey) &&
        saved.milestone === String(milestone) && unlimitedRequestIdValid(saved.requestId)) return saved;
    var row = {
      account:String(scope.account || ''), cardKey:String(cardKey), milestone:String(milestone),
      requestId:unlimitedMintRequestId()
    };
    var raw = JSON.stringify(row);
    try {
      localStorage.setItem(key, raw);
      if (localStorage.getItem(key) !== raw) throw new Error('write failed');
    } catch (_) {
      throw new Error('This device could not remember the upgrade before sending it. Storage access is required.');
    }
    return row;
  }
  function clearProgressionRequest(scope, row) {
    try {
      var key = progressionJournalKey(scope, row.cardKey, row.milestone);
      var saved = JSON.parse(localStorage.getItem(key) || 'null');
      if (saved && saved.requestId === row.requestId) localStorage.removeItem(key);
    } catch (_) {}
  }
  function claimCardProgression(cardKey, milestone) {
    cardKey = String(cardKey || ''); milestone = String(milestone || '');
    var index = -1;
    for (var i = 0; i < CARD_PROGRESSION_MILESTONES.length; i++) {
      if (CARD_PROGRESSION_MILESTONES[i].id === milestone) { index = i; break; }
    }
    if (index < 0) return Promise.reject(new Error('That upgrade does not exist.'));
    var state = cardProgressionFor(cardKey), row = state.milestones[index];
    if (row.state === 'claimed') return Promise.resolve(state);
    if (row.state !== 'ready') return Promise.reject(new Error(row.state === 'quest'
      ? 'Complete this hero’s solo trial before unlocking this move.' : row.prerequisite
      ? 'Claim the earlier upgrade first.' : 'Reach Level ' + row.level + ' first.'));
    if (localMode()) {
      var map = xpLocalRead(), current = map[cardKey] || { level:state.level, xp:0, progression_claims:0 };
      current.progression_claims = progressionMask(current.progression_claims | CARD_PROGRESSION_MILESTONES[index].bit);
      map[cardKey] = current; xpLocalWrite(map); xpCache = map;
      try { document.dispatchEvent(new CustomEvent('hoh:card-progression-changed', { detail:{ cardKey:cardKey, milestone:milestone } })); } catch (_) {}
      return Promise.resolve(cardProgressionFor(cardKey));
    }
    if (!signedIn()) return Promise.reject(new Error('Sign in to claim this upgrade.'));
    if (xpLoadFailed) return Promise.reject(new Error('Saved upgrades could not be verified. Reconnect and retry.'));
    var scope = captureCombatAccount(), journal;
    try { journal = progressionRequest(scope, cardKey, milestone); }
    catch (journalError) { return Promise.reject(journalError); }
    return rpc('claim_card_progression', {
      p_email:scope.email, p_pin:scope.pin, p_card_key:cardKey,
      p_milestone:milestone, p_request_id:journal.requestId
    }).then(function (res) {
      if (!res || res.ok !== true) {
        var reasons = {quest_required:'Accept this hero’s solo trial first.',quest_incomplete:'Reach Round '+(row.kind === 'move' ? 10 : ultimateGoalOf(row.quest))+' in a new solo run after accepting the trial, then return here.',level_required:'Reach Level '+row.level+' first.'};
        throw new Error(reasons[res && res.reason] || (res && (res.error || res.reason)) || 'Upgrade could not be claimed.');
      }
      if (!combatAccountIsCurrent(scope)) throw accountChangedError('the upgrade was saving', true);
      var savedClaims = progressionClaimsAtLevel(res.progression_claims,
        Math.max(state.level | 0, parseInt(res.level, 10) || 1));
      if (!(savedClaims & CARD_PROGRESSION_MILESTONES[index].bit)) throw new Error('The saved upgrade could not be verified.');
      xpCache = xpCache || {};
      var current = xpCache[cardKey] || cardXpFor(cardKey);
      savedClaims = progressionClaimsAtLevel(savedClaims | current.progression_claims |
        ((xpReceiptCache[cardKey] || {}).progression_claims) | progressionReceiptCache[cardKey]);
      xpCache[cardKey] = {
        level:Math.max(1, parseInt(res.level,10) || 1, current.level | 0, (xpReceiptCache[cardKey] || {}).level | 0),
        xp:Math.max(0, parseInt(current.xp,10) || 0), progression_claims:savedClaims
      };
      if (xpReceiptCache[cardKey]) xpReceiptCache[cardKey].progression_claims = savedClaims;
      progressionReceiptCache[cardKey] = savedClaims;
      clearProgressionRequest(scope, journal);
      try { document.dispatchEvent(new CustomEvent('hoh:card-progression-changed', { detail:{ cardKey:cardKey, milestone:milestone } })); } catch (_) {}
      return cardProgressionFor(cardKey);
    });
  }
  function debugSetCardProgression(cardKey, level, claims) {
    if (!godMode()) return false;
    progressionPreview[String(cardKey || '')] = {
      level:Math.max(1, Math.min(200, parseInt(level,10) || 1)),
      claims:progressionMask(claims)
    };
    try {
      document.dispatchEvent(new CustomEvent('hoh:card-level-changed', { detail:{ cardKey:String(cardKey || ''), debug:true } }));
      document.dispatchEvent(new CustomEvent('hoh:card-progression-changed', { detail:{ cardKey:String(cardKey || ''), debug:true } }));
    } catch (_) {}
    return cardProgressionFor(cardKey);
  }
  function debugClearCardProgression(cardKey) {
    if (!godMode()) return false;
    if (cardKey) delete progressionPreview[String(cardKey)]; else progressionPreview = {};
    try { document.dispatchEvent(new CustomEvent('hoh:card-progression-changed', { detail:{ cardKey:String(cardKey || ''), debug:true } })); } catch (_) {}
    return true;
  }

  /* ---------- card gear ---------- */
  function gearReceiptKey(cardKey, slot) { return String(cardKey || '') + '|' + String(slot || ''); }
  function mergeGearReceipts(rows) {
    var next = Array.isArray(rows) ? rows.slice() : [];
    var now = Date.now();
    for (var k in gearReceiptCache) {
      if (!Object.prototype.hasOwnProperty.call(gearReceiptCache, k)) continue;
      var receipt = gearReceiptCache[k];
      if (!receipt || now - receipt.at > 30000) { delete gearReceiptCache[k]; continue; }
      var found = -1, exact = false;
      for (var i = 0; i < next.length; i++) {
        var row = next[i];
        if (row && row.card_key === receipt.cardKey && row.slot === receipt.slot) {
          found = i;
          exact = String(row.skill_id || '') === receipt.id && Number(row.skill_rarity) === receipt.rarity;
          break;
        }
      }
      /* Once a sequenced read sees the exact id and rarity, ordinary server
         rows take over. Until then the committed receipt masks only its slot. */
      if (exact) { delete gearReceiptCache[k]; continue; }
      var overlay = {
        card_key: receipt.cardKey, slot: receipt.slot,
        skill_id: receipt.id, skill_rarity: receipt.rarity,
        price_paid: receipt.price, prev_skill_id: null, prev_rarity: null,
        pity_count: receipt.pity
      };
      if (found >= 0) next[found] = overlay; else next.push(overlay);
    }
    return next;
  }
  function applyGearReceipt(scope, cardKey, slot, res) {
    if (!combatAccountIsCurrent(scope) || !res) return false;
    var r = Number(res.rarity);
    if (!isFinite(r) || Math.floor(r) !== r || r < 0 || r > 5 || !String(res.skill_id || '')) return false;
    gearReceiptCache[gearReceiptKey(cardKey, slot)] = {
      cardKey:String(cardKey || ''), slot:String(slot || ''), id:String(res.skill_id), rarity:r,
      price:Math.max(0, Number(res.price) | 0), pity:Math.max(0, Number(res.pity) | 0), at:Date.now()
    };
    /* Invalidate every request that began before this committed receipt. */
    gearLoadSeq++;
    gearCache = mergeGearReceipts(gearCache || []);
    gearLoadFailed = false;
    return true;
  }
  function loadGear(force) {
    /* R324: gear is REAL account state everywhere — god mode included. The
       old localMode() bail returned [] here, so the sandbox hid the server
       rows and every god-mode roll lived (and died) in memory. That was the
       whole "it only caches" bug. Battles stay sandboxed; the gear on the
       card does not. */
    var scope = captureCombatAccount();
    if (gearCache && !force) return Promise.resolve(gearCache);
    if (!signedIn()) { gearLoadFailed = false; return Promise.resolve([]); }
    var requestSeq = ++gearLoadSeq;
    return rpc('get_card_skills', { p_email: scope.email, p_pin: scope.pin }).then(function (rows) {
      if (!Array.isArray(rows)) throw new Error('gear unavailable');
      if (!combatAccountIsCurrent(scope) || requestSeq !== gearLoadSeq) return gearCache || [];
      var next = mergeGearReceipts(rows);
      gearLoadFailed = false; gearCache = next; return gearCache;
    }).catch(function () {
      if (!combatAccountIsCurrent(scope) || requestSeq !== gearLoadSeq) return gearCache || [];
      /* Keep same-account cache as a fallback, but expose that it is stale so
         destructive/paid controls can fail closed instead of pretending a
         rolled slot is an undisclosed free first roll. */
      gearLoadFailed = true;
      return gearCache || [];
    });
  }
  function gearFor(cardKey) {
    var out = { active: null, passive: null };
    /* R324: server rows are the truth for ANY signed-in account. The
       in-memory localGear now only serves a god session with no account
       token at all. 'PENDING' means a roll was paid but its skill id never
       landed (the client refunded that price, R318) — read that slot as
       empty instead of showing a chip literally named PENDING. */
    if (!signedIn() && localMode()) { var lg = localGear[cardKey] || {}; out.active = lg.active || null; out.passive = lg.passive || null; return out; }
    useCombatCacheAccount();
    (gearCache || []).forEach(function (r) {
      if (r.card_key === cardKey && r.skill_id && r.skill_id !== 'PENDING') out[r.slot] = { id: r.skill_id, r: r.skill_rarity };
    });
    return out;
  }

  /* R321: CLASS AFFINITY. Every class prefers its signature gear, so a
     Barbarian tends to roll the big swings and a Cleric the mercies — cards
     FEEL like their class instead of a uniform grab bag. 65% of rolls take a
     signature skill when one exists at the rolled rarity; the rest roll the
     open pool, so off-flavor surprises still happen. This shapes only WHICH
     skill is picked after the server has rolled the rarity — odds, prices and
     battle simulation are untouched. */
  var CLS_SKILL_AFFINITY = {
    Barbarian: ['bigswing', 'rampage', 'firstblood', 'bloodroar'],
    Monk:      ['momentum', 'firstblood', 'stunner', 'pressurepoint'],
    Rogue:     ['mark', 'doubletap', 'erasure', 'poisonedge'],
    Ranger:    ['hunter', 'opport', 'doubletap', 'deadeye1', 'deadeye2', 'deadeye3', 'hawkcall'],
    /* v13: hexbolt is a caster's curse, rampart a soldier's wall, triage a
       healer's answer to pin-downs — each new id lands where its fantasy is */
    Wizard:    ['glassedge', 'shatter', 'godslayer', 'hexbolt', 'counterspell'],
    Sorcerer:  ['bigswing', 'rampage', 'godslayer', 'hexbolt', 'twinspark'],
    Warlock:   ['execute', 'mark', 'erasure', 'hexbolt', 'soultap', 'gravecall'],
    Artificer: ['steady', 'bulkblade', 'summoner', 'deployturret'],
    Fighter:   ['vanguard', 'towergrip', 'storedwrath', 'rampart', 'aegisbearer', 'counterhold', 'citadel', 'avatarstone'],
    Paladin:   ['phalanx', 'colossus', 'immortal', 'rampart', 'aegisbearer', 'banneret', 'judgment', 'retribution', 'avatarstone'],
    Cleric:    ['sanctify', 'guardian', 'miracle', 'armor', 'triage', 'springwell', 'circleward', 'sanctuary', 'springeternal'],
    Druid:     ['lifebloom', 'stonepalm', 'resur', 'ward', 'triage', 'springwell', 'packcall', 'lifewell', 'springeternal'],
    /* v36 R397: the Bard finally gets a signature shelf of their own */
    Bard:      ['warmup', 'tempo', 'crescendo', 'showstop', 'anthem', 'discordnote', 'muzzle', 'warchant', 'encore', 'maestro']
  };
  /* v19: the new blades land where their fantasy lives */
  CLS_SKILL_AFFINITY.Ranger = (CLS_SKILL_AFFINITY.Ranger || []).concat(['keeneye', 'songblade']);
  CLS_SKILL_AFFINITY.Monk = (CLS_SKILL_AFFINITY.Monk || []).concat(['keeneye', 'songblade']);
  CLS_SKILL_AFFINITY.Barbarian = (CLS_SKILL_AFFINITY.Barbarian || []).concat(['songblade']);
  CLS_SKILL_AFFINITY.Rogue = (CLS_SKILL_AFFINITY.Rogue || []).concat(['keeneye']);
  var ROLE_PASSIVE_AFFINITY = {
    tank:   ['ironresolve', 'stoneskin', 'warden', 'resurgence', 'standardbearer', 'autorepair', 'runicrebound', 'ironwill'],
    bless:  ['clarity', 'lastword', 'phoenix', 'resurgence', 'saprising', 'saintsledger', 'standingova', 'lastlight', 'overgrowth'],
    attack: ['adrenaline', 'vengeance', 'bloodthirst', 'focus', 'grimledger', 'painfuel', 'shadowbank', 'apex', 'entropy', 'fineprint']
  };
  /* v19: RACE leans on the roll too — a light thumb (35%), never a lock,
     so a Dwarf tends toward walls and an Eladrin toward the keen edge. */
  var RACE_SKILL_AFFINITY = {
    Dwarf:      ['aegisbearer', 'towergrip'],
    Elf:        ['keeneye', 'hunter'],
    Eladrin:    ['keeneye'],
    Drow:       ['keeneye', 'doubletap'],
    Tiefling:   ['grimledger', 'execute'],
    Halfling:   ['resurgence', 'lifebloom'],
    Dragonborn: ['songblade', 'bigswing'],
    Aasimar:    ['springwell', 'sanctify'],
    Warforged:  ['aegisbearer', 'bulkblade'],
    Draconis:   ['songblade', 'rampage']
  };
  function raceOf(heroLike) { return String(heroLike.race || '').split(' ')[0]; }
  /* v35 R394: THE RACE SIGNATURES. One signature active and one race wake
     per race, race-locked by construction: they live in the 'sig' pool (or
     outside the passive bands), which no open-pool branch ever draws from,
     so the ONLY road to them is this map. The 12% gate keeps them the rare
     sighting Jan asked for — a Dwarf who rolls rare has about one chance in
     eight of the Oathstone, and no other race can roll it at all. Eladrin
     rides the Elf line and Draconis the Dragonborn line, same as the v19
     affinity table treats them. */
  var RACE_SIG_ACTIVE = {
    Human: 'rallyline', Dwarf: 'oathstone', Elf: 'starvolley', Eladrin: 'starvolley',
    Drow: 'veilofnight', Tiefling: 'hellpact', Halfling: 'luckcharm',
    Dragonborn: 'ancestralbreath', Draconis: 'ancestralbreath',
    Aasimar: 'halosurge', Warforged: 'overclock', Gnome: 'gizmoheist'
  };
  var RACE_SIG_PASSIVE = {
    Human: 'undaunted', Dwarf: 'mountainpatience', Elf: 'feystep', Eladrin: 'feystep',
    Drow: 'webofshadows', Tiefling: 'infernalrebate', Halfling: 'braveheart',
    Dragonborn: 'dragonpride', Draconis: 'dragonpride',
    Aasimar: 'radiantguard', Warforged: 'failsafecore', Gnome: 'contingencygizmo'
  };
  var RACE_SIG_CHANCE = 0.12;
  /* the passive bands the race wakes live in — used ONLY by the race branch
     and by the odds display, never by the open band walk */
  var RACE_PASSIVE_BAND = {
    braveheart: 1,
    undaunted: 2, mountainpatience: 2, webofshadows: 2, dragonpride: 2, contingencygizmo: 2,
    feystep: 3, infernalrebate: 3, radiantguard: 3, failsafecore: 3
  };
  /* R392 (Jan): A REROLL MUST CHANGE SOMETHING. Every picker branch now
     excludes the skill the slot already holds, so Dead Calm six times in a
     row is impossible. Only a pool that would be EMPTY after the exclusion
     may repeat, and with the passive bands below that never happens.
     v35 R394: avoid may now be a LIST — the candidate pair must differ from
     the held skill AND from each other. */
  function _notCur(avoidId) {
    var list = (avoidId && avoidId.constructor === Array) ? avoidId : [avoidId];
    return function (id) {
      for (var i = 0; i < list.length; i++) {
        if (String(id) === String(list[i] || '')) return false;
      }
      return true;
    };
  }
  function pickActiveId(E, heroLike, rarity, rngHex, avoidId) {
    var role = E.roleOf(heroLike);
    var cls = String(heroLike.cls || heroLike.character_class || '').split(' ')[0];
    /* R450b: THE CLASS POOLS ARE OPEN. When the library is on, a roll
       draws the class-owned rows first (the verb matrix identity), the
       universal rows beside them, and only falls back to the legacy role
       pools when the band is empty. The race signature check below still
       outranks everything, at its own rarity, as before. */
    /* R616: the registry branch proposes ids the server does not accept yet;
       gated until the acquisition wave, see REGISTRY_ROLL_LIVE */
    var _libA = newSystemPool(E, heroLike, 'active', rarity, Array.isArray(avoidId) ? avoidId : [avoidId]);
    if (_libA.length) return pickFromNewPool(E, heroLike, _libA);
    var pool = role.act === 'bless' ? (cls === 'Bard' ? 'support' : 'heal')
             : role.act === 'tank' ? 'tank' : 'attack';
    /* v35 R394: the race signature, checked FIRST and only at its printed
       rarity — a signature never wears a tier color above its band, which is
       the R392 rarity-fraud rule applied to the new pool. */
    var sig = RACE_SIG_ACTIVE[raceOf(heroLike)];
    if (sig && E.content.SKILLS[sig] && E.content.SKILLS[sig].r === rarity
        && _notCur(avoidId)(sig) && Math.random() < RACE_SIG_CHANCE) {
      return sig;
    }
    var fav = CLS_SKILL_AFFINITY[cls] || [];
    var atRarity = fav.filter(function (id) {
      var sk = E.content.SKILLS[id];
      return sk && sk.p === pool && sk.r === rarity;
    }).filter(_notCur(avoidId));
    if (atRarity.length && Math.random() < 0.65) {
      return atRarity[Math.floor(Math.random() * atRarity.length)];
    }
    /* v19: the race's thumb on the scale, after the class has passed */
    var rfav = (RACE_SKILL_AFFINITY[raceOf(heroLike)] || []).filter(function (id) {
      var sk = E.content.SKILLS[id];
      return sk && sk.p === pool && sk.r === rarity;
    }).filter(_notCur(avoidId));
    if (rfav.length && Math.random() < 0.35) {
      return rfav[Math.floor(Math.random() * rfav.length)];
    }
    /* the open pool at this pool+rarity, minus the held id. Only a
       single-skill pool (nothing else printed at this rarity) may repeat. */
    var open = Object.keys(E.content.SKILLS).filter(function (id) {
      var sk = E.content.SKILLS[id];
      return sk && sk.p === pool && sk.r === rarity;
    });
    var openX = open.filter(_notCur(avoidId));
    if (!openX.length) openX = open;
    if (openX.length) return openX[Math.floor(Math.random() * openX.length)];
    return E.rollSkillId(pool, rarity, E.makeRng(rngHex));
  }
  /* R392 (Jan): PASSIVE RARITY BANDS. Every passive effect is identical no
     matter what rarity the roll landed, so a LEGENDARY chip could read the
     exact same text as the EPIC one it replaced — rarity fraud. Until the
     tiered ladder ships in the content wave, each passive now lives in ONE
     band, ordered by real impact, so a higher roll always lands a different,
     stronger passive and the same id can never wear two tier colors.
     A band that comes up empty (after the exclusion) walks DOWN the ladder,
     so a roll always lands something. */
  var PASSIVE_BANDS = [
    ['clarity', 'stoneskin'],                                          /* common */
    ['adrenaline', 'lastword', 'painfuel', 'innerflow'],               /* uncommon */
    ['vengeance', 'warden', 'ironresolve', 'standardbearer', 'shadowbank', 'saprising', 'runicrebound'],
    ['phoenix', 'bloodthirst', 'focus', 'apex', 'entropy', 'saintsledger', 'standingova', 'autorepair'],
    /* v36 R397: the legendary band doubles — three ids meant the no-repeat
       rule ran out of road after two rerolls */
    ['resurgence', 'grimledger', 'fineprint', 'lastlight', 'ironwill', 'overgrowth'],
    ['resurgence', 'grimledger', 'fineprint', 'lastlight', 'ironwill', 'overgrowth']
  ];
  function pickPassiveId(E, heroLike, rarity, avoidId) {
    var r = Math.max(0, Math.min(5, rarity | 0));
    /* R450b: the wake-slot library, same rule as the actives above */
    var _libP = newSystemPool(E, heroLike, 'passive', r, Array.isArray(avoidId) ? avoidId : [avoidId]);
    if (_libP.length) return pickFromNewPool(E, heroLike, _libP);
    /* v35 R394: the race wake, checked FIRST, at its own band only */
    var rp = RACE_SIG_PASSIVE[raceOf(heroLike)];
    if (rp && E.content.PASSIVES[rp] && RACE_PASSIVE_BAND[rp] === r
        && _notCur(avoidId)(rp) && Math.random() < RACE_SIG_CHANCE) {
      return rp;
    }
    var band = [];
    for (var b = r; b >= 0 && !band.length; b--) {
      band = (PASSIVE_BANDS[b] || []).filter(function (id) { return !!E.content.PASSIVES[id]; }).filter(_notCur(avoidId));
    }
    if (!band.length) band = (PASSIVE_BANDS[r] || []).slice();
    /* v19 flavor order survives INSIDE the band: race first, role second */
    var rfav = (RACE_SKILL_AFFINITY[raceOf(heroLike)] || []).filter(function (id) { return band.indexOf(id) >= 0; });
    if (rfav.length && Math.random() < 0.35) {
      return rfav[Math.floor(Math.random() * rfav.length)];
    }
    var role = E.roleOf(heroLike);
    var fav = (ROLE_PASSIVE_AFFINITY[role.act] || []).filter(function (id) { return band.indexOf(id) >= 0; });
    if (fav.length && Math.random() < 0.65) {
      return fav[Math.floor(Math.random() * fav.length)];
    }
    return band[Math.floor(Math.random() * band.length)];
  }

  /* v20 (Jan): SAVED PARTIES — up to four, named, kept client-side
     ('hoh-parties-v1'). A party stores hero KEYS in turn order; surfaces
     resolve them against the live collection so a traded-away card simply
     drops out instead of breaking the party. */
  var PARTY_KEY = 'hoh-parties-v1';
  function partiesGet() {
    try { var a = JSON.parse(localStorage.getItem(PARTY_KEY) || '[]'); return Array.isArray(a) ? a.slice(0, 4) : []; }
    catch (e) { return []; }
  }
  function partiesSet(a) { try { localStorage.setItem(PARTY_KEY, JSON.stringify((a || []).slice(0, 4))); } catch (e) {} }
  function partySave(idx, name, keys) {
    var a = partiesGet();
    while (a.length <= idx && a.length < 4) a.push(null);
    if (idx < 0 || idx > 3) return null;
    a[idx] = { name: String(name || 'Party ' + (idx + 1)).slice(0, 24), keys: (keys || []).slice(0, 5) };
    partiesSet(a); return a[idx];
  }
  function partyDelete(idx) { var a = partiesGet(); if (idx >= 0 && idx < a.length) { a[idx] = null; partiesSet(a); } }

  /* Contract RPCs mutate server-account state while hearts and ticket mirrors
     live on the device. A late A response must never charge or refund whichever
     account happens to be visible (B), nor write A's run marker into B's UI.
     Queue those small account-owned actions by normalized email and apply them
     only when that account is active again. PINs are intentionally not stored. */
  var ACCOUNT_ACTION_KEY = 'hoh-contract-roll-refunds-v1'; /* retain old key; migrate old rows below */
  var bgRefundInFlight = {};
  function plainObject(value) {
    return !!value && Object.prototype.toString.call(value) === '[object Object]';
  }
  function accountActionsRead() {
    try {
      var raw = localStorage.getItem(ACCOUNT_ACTION_KEY);
      if (raw == null || raw === '') return {};
      var m = JSON.parse(raw);
      return plainObject(m) ? m : null;
    } catch (e) { return null; }
  }
  function accountActionsWrite(m) {
    try {
      if (!plainObject(m)) return false;
      var has = Object.keys(m || {}).length > 0;
      var raw = has ? JSON.stringify(m) : '';
      if (has) localStorage.setItem(ACCOUNT_ACTION_KEY, raw);
      else localStorage.removeItem(ACCOUNT_ACTION_KEY);
      var check = localStorage.getItem(ACCOUNT_ACTION_KEY);
      return has ? check === raw : check == null;
    } catch (e) { return false; }
  }
  function refundTicketNow() {
    try {
      if (typeof window.hohRefundUnspentTicket === 'function') { window.hohRefundUnspentTicket(); return true; }
      if (typeof window.hohAddUnspentTicket === 'function') { window.hohAddUnspentTicket(1, 'refund'); return true; }
    } catch (e) {}
    return false;
  }
  function walletTicketGet() {
    try {
      if (typeof window.hohGetUnspentTickets !== 'function') return null;
      var value = Number(window.hohGetUnspentTickets());
      return isFinite(value) && value >= 0 ? (value | 0) : null;
    } catch (_) { return null; }
  }
  function accountActionRow(m, account) {
    var existing = m[account];
    if (existing != null && !plainObject(existing)) return null;
    var due = existing || {};
    if (due.hearts_due != null && !Array.isArray(due.hearts_due)) return null;
    if (due.tickets_due != null && !Array.isArray(due.tickets_due)) return null;
    if (due.bg_refunds_due != null && !Array.isArray(due.bg_refunds_due)) return null;
    if (!due.hearts_due) due.hearts_due = [];
    if (!due.tickets_due) due.tickets_due = [];
    if (!due.bg_refunds_due) due.bg_refunds_due = [];
    var ai;
    for (ai = 0; ai < due.hearts_due.length; ai++) {
      var ah = due.hearts_due[ai];
      if (!plainObject(ah) || !isFinite(Number(ah.delta)) || !(ah.delta | 0) ||
          (ah.state && ah.state !== 'due' && ah.state !== 'applying')) return null;
    }
    for (ai = 0; ai < due.tickets_due.length; ai++) {
      var at = due.tickets_due[ai];
      if (!plainObject(at) || (at.state !== 'due' && at.state !== 'applying')) return null;
    }
    for (ai = 0; ai < due.bg_refunds_due.length; ai++) {
      var ab = due.bg_refunds_due[ai];
      if (!plainObject(ab) || (ab.state !== 'due' && ab.state !== 'sending')) return null;
    }
    /* R495 shipped a positive reroll-refund aggregate under `hearts`. Preserve
       it across the generalized ledger rather than orphaning a real refund. */
    if ((due.hearts | 0) > 0) {
      due.hearts_due.push({ delta: due.hearts | 0, reason: 'earn_skill_roll_refund' });
      delete due.hearts;
    }
    /* Migrate the two old counters into individually durable work rows. A
       scalar cannot record the before/after boundary of a local ticket write
       or distinguish two server refunds when a response is lost. */
    var legacyTickets = Math.max(0, due.tickets | 0), ti;
    for (ti = 0; ti < legacyTickets; ti++) due.tickets_due.push({ id: rhex(24), state: 'due' });
    delete due.tickets;
    var legacyBg = Math.max(0, due.bg_refunds | 0);
    for (ti = 0; ti < legacyBg; ti++) due.bg_refunds_due.push({ id: rhex(24), state: 'due' });
    delete due.bg_refunds;
    return due;
  }
  function accountHeart(scope, delta, reason) {
    var account = String(scope && scope.account || '').trim().toLowerCase();
    delta = delta | 0;
    if (!account || !delta) return;
    if (collectionAccount() === account) {
      bankApply(delta, reason);
      return;
    }
    var m = accountActionsRead();
    if (!m) return;
    var due = accountActionRow(m, account);
    if (!due) return;
    due.hearts_due.push({ id: rhex(24), state: 'due', delta: delta,
      reason: reason || (delta > 0 ? 'earn_other' : 'spend_other') });
    m[account] = due;
    accountActionsWrite(m);
  }
  function queueWalletTicketRefund(scope, tickets) {
    var account = String(scope && scope.account || '').trim().toLowerCase();
    tickets = Math.max(0, tickets | 0);
    if (!account || !tickets) return;
    var m = accountActionsRead();
    if (!m) return;
    var due = accountActionRow(m, account);
    if (!due) return;
    while (tickets-- > 0) due.tickets_due.push({ id: rhex(24), state: 'due' });
    m[account] = due;
    if (accountActionsWrite(m) && collectionAccount() === account) flushAccountActions();
  }
  function queueBgRefund(scope) {
    var account = String(scope && scope.account || '').trim().toLowerCase();
    function queueError(message) {
      var e = new Error(message);
      e.__hohRefundPending = true;
      return e;
    }
    if (!account) throw queueError('The Battlegrounds entry could not be queued for a safe refund because its account is missing.');
    var m = accountActionsRead();
    if (!m) throw queueError('The saved Battlegrounds refund queue is damaged or unreadable. No second refund was attempted.');
    var due = accountActionRow(m, account);
    if (!due) throw queueError('The saved Battlegrounds refund row is invalid. No second refund was attempted.');
    /* This UUID identifies the device-side request, but is deliberately NOT
       sent to hoh_bg_refund: the installed RPC accepts only email + PIN and
       has no request-id ledger. A durable `sending` row is therefore an
       ambiguous terminal state, never an invitation to retry and mint twice. */
    var requestId = rhex(8) + '-' + rhex(4) + '-4' + rhex(3) + '-8' + rhex(3) + '-' + rhex(12);
    due.bg_refunds_due.push({ id: requestId, request_id: requestId, state: 'due' });
    m[account] = due;
    if (!accountActionsWrite(m)) {
      throw queueError('The Battlegrounds entry may need a refund, but this device could not save the request. Try again before starting another run.');
    }
    var check = accountActionsRead(), checked = check && accountActionRow(check, account), found = false;
    if (checked) {
      for (var qi = 0; qi < checked.bg_refunds_due.length; qi++) {
        if (String(checked.bg_refunds_due[qi] && checked.bg_refunds_due[qi].id || '') === requestId &&
            checked.bg_refunds_due[qi].state === 'due') { found = true; break; }
      }
    }
    if (!found) {
      throw queueError('The Battlegrounds refund request could not be verified on this device. No refund RPC was sent.');
    }
    flushAccountActions();
    return requestId;
  }
  function confirmedBattleTicketSpend(scope, id, origin) {
    if (!combatAccountIsCurrent(scope)) return;
    try { document.dispatchEvent(new CustomEvent('hoh:bg-ticket-spent', {detail:{confirmed:true,id:String(id),origin:origin}})); } catch (_) {}
  }
  function battleTicketSpendOrigin() {
    try { return typeof window.hohCaptureBattleTicketAnchor === 'function' ? window.hohCaptureBattleTicketAnchor() : null; } catch (_) { return null; }
  }
  function spendBgTicket(scope) {
    var spendId = unlimitedMintRequestId(), spendOrigin = battleTicketSpendOrigin();
    return rpc('hoh_bg_spend', { p_email: scope.email, p_pin: scope.pin }).then(function (data) {
      var row = Array.isArray(data) ? data[0] : data;
      if (row && typeof row.balance === 'number' && combatAccountIsCurrent(scope) && typeof window.hohSetBgTicketsLocal === 'function') {
        try { window.hohSetBgTicketsLocal(row.balance); } catch (e) {}
      }
      if (row && row.ok === true) confirmedBattleTicketSpend(scope, 'spend:' + spendId, spendOrigin);
      /* R519: the reason rides along. "no_tickets" is the server ANSWERING
         (nothing was spent), which the caller must tell apart from a network
         failure where a spend may have landed unseen. */
      return { ok: !!(row && row.ok), balance: row && row.balance,
               reason: (row && row.reason) || null, answered: !!row };
    }).catch(function () { return { ok: false, reason: null, answered: false }; });
  }
  function queueRunMarker(scope, runId, played) {
    var account = String(scope && scope.account || '').trim().toLowerCase();
    if (!account) return;
    if (collectionAccount() === account) {
      if (played) stampPlayed();
      rememberRun(runId);
      return;
    }
    var m = accountActionsRead();
    if (!m) return;
    var due = accountActionRow(m, account);
    if (!due) return;
    if (runId) due.run_id = String(runId);
    if (played) due.played = true;
    m[account] = due;
    accountActionsWrite(m);
  }
  function flushAccountActions() {
    var account = collectionAccount();
    if (!account) return;
    var m = accountActionsRead(), due;
    if (!m) return;
    due = m[account];
    if (!due) return;
    due = accountActionRow(m, account);
    if (!due) return;
    var runId = due.run_id || '';
    var played = !!due.played;
    /* Persist an applying marker BEFORE touching the scalar bank. If the bank
       write lands but removing this row does not, the next pass sees `after`
       and removes it without paying twice. If neither write landed it sees
       `before` and safely finishes. Any third balance is ambiguous and stops. */
    while (due.hearts_due.length) {
      var heartRow = due.hearts_due[0] || {};
      if (!plainObject(heartRow)) break;
      var current = bankGet();
      if (heartRow.state !== 'applying' || heartRow.before == null || heartRow.after == null) {
        heartRow.id = String(heartRow.id || rhex(24));
        heartRow.state = 'applying';
        heartRow.before = current;
        heartRow.after = current + (heartRow.delta | 0);
        due.hearts_due[0] = heartRow; m[account] = due;
        if (!accountActionsWrite(m)) break;
      }
      current = bankGet();
      if (current !== (heartRow.before | 0) && current !== (heartRow.after | 0)) break;
      try {
        if (current === (heartRow.before | 0)) {
          bankApplyVerified(heartRow.delta | 0, heartRow.reason, heartRow.before | 0);
        }
      } catch (_) { break; }
      due.hearts_due.shift();
      m[account] = due;
      if (!accountActionsWrite(m)) break;
    }
    while (due.tickets_due.length) {
      var ticketRow = due.tickets_due[0] || {};
      if (!plainObject(ticketRow)) break;
      var ticketNow = walletTicketGet();
      if (ticketNow == null) break;
      if (ticketRow.state !== 'applying' || ticketRow.before == null || ticketRow.after == null) {
        ticketRow.id = String(ticketRow.id || rhex(24));
        ticketRow.state = 'applying';
        ticketRow.before = ticketNow;
        ticketRow.after = ticketNow + 1;
        due.tickets_due[0] = ticketRow; m[account] = due;
        if (!accountActionsWrite(m)) break;
      }
      ticketNow = walletTicketGet();
      if (ticketNow !== (ticketRow.before | 0) && ticketNow !== (ticketRow.after | 0)) break;
      if (ticketNow === (ticketRow.before | 0)) {
        if (!refundTicketNow() || walletTicketGet() !== (ticketRow.after | 0)) break;
      }
      due.tickets_due.shift();
      m[account] = due;
      if (!accountActionsWrite(m)) break;
    }
    if (played) { stampPlayed(); delete due.played; }
    if (runId) { rememberRun(runId); delete due.run_id; }
    m[account] = due;
    accountActionsWrite(m);

    /* Battleground ticket refunds are server-account state. Call the RPC with
       credentials frozen while A is current, then update the local mirror only
       if that exact account generation is still visible when it returns. */
    var bgIndex = -1, bi;
    for (bi = 0; bi < due.bg_refunds_due.length; bi++) {
      if (!plainObject(due.bg_refunds_due[bi])) continue;
      if (due.bg_refunds_due[bi].state === 'due') { bgIndex = bi; break; }
    }
    if (bgIndex >= 0 && !bgRefundInFlight[account]) {
      var scope = captureCombatAccount();
      var bgRow = due.bg_refunds_due[bgIndex];
      bgRow.id = String(bgRow.id || rhex(24));
      /* Once `sending` is durable this exact request is never automatically
         repeated: hoh_bg_refund has no idempotency key, so a lost reply is
         unknowable and retrying could mint a second ticket. Later independent
         rows may still proceed. */
      bgRow.state = 'sending'; bgRow.sent_at = Date.now();
      due.bg_refunds_due[bgIndex] = bgRow; m[account] = due;
      if (!accountActionsWrite(m)) return;
      bgRefundInFlight[account] = true;
      rpc('hoh_bg_refund', { p_email: scope.email, p_pin: scope.pin }).then(function (payload) {
        /* Supabase returns scalar json functions as an object and SETOF/table
           functions as a one-row array. hoh_bg_spend already accepts both;
           the deferred refund boundary must do the same or a real successful
           refund is left forever in the ambiguous `sending` state. */
        var row = Array.isArray(payload) ? payload[0] : payload;
        var ok = !!(row && typeof row.balance === 'number');
        if (ok && combatAccountIsCurrent(scope) && typeof window.hohSetBgTicketsLocal === 'function') {
          try { window.hohSetBgTicketsLocal(row.balance); } catch (e) {}
        }
        if (ok) {
          var m2 = accountActionsRead(), d2 = m2 && m2[scope.account];
          if (d2) {
            d2 = accountActionRow(m2, scope.account);
            if (!d2) return;
            for (var ri = d2.bg_refunds_due.length - 1; ri >= 0; ri--) {
              if (String(d2.bg_refunds_due[ri] && d2.bg_refunds_due[ri].id || '') === bgRow.id) {
                d2.bg_refunds_due.splice(ri, 1); break;
              }
            }
            if (!d2.hearts_due.length && !d2.tickets_due.length && !d2.bg_refunds_due.length && !d2.run_id && !d2.played) delete m2[scope.account];
            else m2[scope.account] = d2;
            accountActionsWrite(m2);
          }
        }
      }).catch(function () {}).then(function () {
        bgRefundInFlight[scope.account] = false;
        if (collectionAccount() === scope.account) flushAccountActions();
      });
    } else if (!due.hearts_due.length && !due.tickets_due.length && !due.bg_refunds_due.length && !due.run_id && !due.played) {
      delete m[account]; accountActionsWrite(m);
    }
  }
  function accountChangedError(what, committed) {
    var e = new Error('Account changed while ' + what + '. Return to the account that started it to continue.');
    e.__hohAccountChanged = true;
    if (committed) e.__hohCommitted = true;
    return e;
  }

  /* R496: the server receipt is idempotent; the older device-local Heart bank
     is not a cross-device transaction. This account-scoped journal suppresses
     ordinary same-device duplicates and fails closed on ambiguous storage.
     Web Locks serializes tabs where available; the fallback elects a token.
     True cross-device exactly-once Hearts requires the future server wallet. */
  var BG_REWARD_CLAIMS_KEY = 'hoh-bg-reward-claims-v1';
  function claimHash(s) {
    var h = 2166136261, i;
    s = String(s || '');
    for (i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
    return (h >>> 0).toString(16);
  }
  function claimJournalRead() {
    try {
      var raw = localStorage.getItem(BG_REWARD_CLAIMS_KEY);
      if (raw == null || raw === '') return {};
      var m = JSON.parse(raw);
      return plainObject(m) ? m : null;
    } catch (e) { return null; }
  }
  function claimJournalWrite(m) {
    try {
      if (!plainObject(m)) return false;
      var raw = JSON.stringify(m || {});
      localStorage.setItem(BG_REWARD_CLAIMS_KEY, raw);
      return localStorage.getItem(BG_REWARD_CLAIMS_KEY) === raw;
    } catch (e) { return false; }
  }
  function claimAccount(scope) {
    return String((scope && scope.account) || (localMode() ? 'sandbox' : collectionAccount()) || 'anonymous').trim().toLowerCase();
  }
  function claimLegacyIdentity(res, runId) {
    if (res && res.receipt_id) return '';
    return 'legacy-v2:' + claimHash([String(runId || ''), String(res && res.status || '')].join('|'));
  }
  function claimIdentity(res, runId) {
    var rid = String((res && res.receipt_id) || '');
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(rid)) return 'receipt:' + rid.toLowerCase();
    /* Backward compatibility for already-open schema-0 runs and non-judged
       flee/expiry outcomes, which deliberately have no EXP receipt. */
    /* A schema-0 first judged reply carries `hash`; the idempotent retry does
       not. Optional response fields therefore cannot participate in the local
       wallet key. One run has one terminal status, while the category suffix
       below keeps payout/bonus/dust/refund distinct. */
    /* The old v2 identity compressed run + status to 32 bits. That is adequate
       for a lock name, but not for a permanent money tombstone: two distinct
       runs can share an FNV value. Keep the canonical components themselves,
       URI-escaped around fixed separators, so different run/status pairs can
       never alias merely because a non-cryptographic hash collided. */
    var run = String(runId == null ? '' : runId).trim();
    var status = String(res && res.status || '').trim().toLowerCase();
    return 'legacy-v3:run:' + encodeURIComponent(run) + '|status:' + encodeURIComponent(status);
  }
  function claimLocalReward(scope, identity, category, delta, reason, legacyIdentity) {
    delta = Math.max(0, Math.min(2000, delta | 0));
    if (!delta || !identity) return Promise.resolve({ first: false, amount: delta });
    var account = claimAccount(scope);
    var categoryKey = String(category || 'base');
    var key = identity + ':' + categoryKey;
    var legacyKey = legacyIdentity && legacyIdentity !== identity
      ? String(legacyIdentity) + ':' + categoryKey : '';
    /* Every category mutates one scalar Heart bank, so tabs must serialize by
       account/wallet, not by receipt/category. */
    var lockName = 'hoh-bg-wallet-' + claimHash(account);
    function pendingError(message) {
      var e = new Error(message);
      e.__hohRewardPending = true;
      return e;
    }
    function saveJournal(m) {
      if (!claimJournalWrite(m)) throw pendingError('Your reward is saved, but this device could not record it. Reopen Battlegrounds to retry.');
    }
    function manualReconcileError() {
      return pendingError('An older saved reward is missing the balance proof needed for automatic recovery. Nothing was changed. Support must reconcile this reward manually.');
    }
    function validateClaimRow(row) {
      if (row == null) return;
      if (!plainObject(row)) throw pendingError('The saved reward journal has an invalid claim row. Nothing was added.');
      if (row.state !== 'done' && row.state !== 'credited' && row.state !== 'claiming') {
        throw pendingError('The saved reward journal has an unknown claim state. Nothing was added.');
      }
      if ((row.delta | 0) !== delta) {
        throw pendingError('The saved reward amount does not match its first receipt. Nothing was added.');
      }
      /* Pre-R496 `credited` rows did not always record both sides of the bank
         write. Calling those rows done silently can erase an unpaid reward;
         replaying them can mint a duplicate. Neither outcome is provable, so
         leave the row byte-for-byte intact for manual reconciliation. */
      if (row.state === 'credited' && (row.before == null || row.after == null)) {
        throw manualReconcileError();
      }
    }
    function legacyTombstone(now) {
      return { state: 'done', delta: delta, at: now, migrated_to: key };
    }
    function copyClaimRow(row) {
      var out = {}, k;
      for (k in row) if (Object.prototype.hasOwnProperty.call(row, k) && k !== 'migrated_to') out[k] = row[k];
      out.claim_id = identity;
      return out;
    }
    function reconcileLegacyAlias(m, bucket, now) {
      var current = bucket[key], legacy = legacyKey ? bucket[legacyKey] : null;
      validateClaimRow(current);
      /* A v2 hash collision that has already been tombstoned to another
         canonical claim is unrelated to this run. Never let it suppress the
         new v3 claim, and never overwrite the old alias for an older client. */
      if (legacy && legacy.migrated_to && legacy.migrated_to !== key) return current;
      validateClaimRow(legacy);
      if (!current && legacy) {
        current = copyClaimRow(legacy);
        bucket[key] = current;
        bucket[legacyKey] = legacyTombstone(now);
        m[account] = bucket; saveJournal(m);
        return current;
      }
      if (current && legacy && !legacy.migrated_to) {
        /* Two independently active rows for one reward cannot be ordered from
           localStorage. Completed matching rows are safe to coalesce; any
           other combination needs human reconciliation. */
        if (current.state !== 'done' || legacy.state !== 'done') throw manualReconcileError();
        bucket[legacyKey] = legacyTombstone(now);
        m[account] = bucket; saveJournal(m);
      } else if (current && legacyKey && !legacy) {
        bucket[legacyKey] = legacyTombstone(now);
        m[account] = bucket; saveJournal(m);
      }
      return current;
    }
    function attempt(waitCount) {
      waitCount = waitCount | 0;
      if (scope && collectionAccount() !== account) {
        throw pendingError('Return to the account that won this reward, then reopen Battlegrounds to add it safely.');
      }
      var now = Date.now(), m = claimJournalRead();
      if (!m) throw pendingError('Your saved reward journal is damaged or unreadable. Nothing was added. Clear site storage only after support confirms the receipt.');
      var bucket = m[account];
      if (bucket == null) bucket = {};
      if (!plainObject(bucket)) throw pendingError('Your saved reward journal has an invalid account row. Nothing was added.');
      var old = reconcileLegacyAlias(m, bucket, now);
      if (old && old.state === 'done') {
        return Promise.resolve({ first: false, amount: Math.max(0, old.delta | 0) });
      }
      /* `credited` is the deliberate crash-recovery marker. If the bank still
         equals the recorded before value, the write never landed and is safe
         to finish. If it equals after, only the final journal write was lost.
         Any other balance is ambiguous, so fail closed instead of minting. */
      if (old && old.state === 'credited') {
        var current = bankGet();
        if (current !== (old.before | 0) && current !== (old.after | 0)) {
          throw pendingError('Your Heart balance changed while a saved reward was being added. Nothing was added twice; reopen Battlegrounds for recovery.');
        }
        var recovered = false;
        if (current === (old.before | 0)) {
          bankApplyVerified(delta, reason, old.before | 0);
          recovered = true;
        }
        old.state = 'done'; old.at = now; bucket[key] = old; m[account] = bucket; saveJournal(m);
        return Promise.resolve({ first: recovered, amount: delta, recovered: true });
      }
      if (old && old.state === 'claiming' && now - Number(old.at || 0) < 2000 && waitCount < 40) {
        return new Promise(function (resolve, reject) {
          setTimeout(function () { Promise.resolve().then(function () { return attempt(waitCount + 1); }).then(resolve, reject); }, 50);
        });
      }
      var before = bankGet();
      if (before + delta > HEART_BANK_MAX) {
        throw pendingError('Your reward is saved, but the Heart bank is full. Spend Hearts, then reopen Battlegrounds.');
      }
      var token = rhex(24);
      bucket[key] = { state: 'claiming', token: token, delta: delta, at: now, claim_id: identity };
      /* New schema-0 claims also leave a v2 tombstone. An older already-open
         tab therefore sees `done` instead of paying the same run under the old
         hash key. If that hash already names another canonical claim, retain
         its alias and let the collision-free v3 key proceed independently. */
      if (legacyKey && !bucket[legacyKey]) bucket[legacyKey] = legacyTombstone(now);
      m[account] = bucket; saveJournal(m);
      return new Promise(function (resolve, reject) {
        setTimeout(function () {
          try {
            var m2 = claimJournalRead();
            if (!m2) throw pendingError('Your saved reward journal became unreadable. Nothing was added.');
            var b2 = m2[account];
            if (b2 == null) b2 = {};
            if (!plainObject(b2)) throw pendingError('Your saved reward journal has an invalid account row. Nothing was added.');
            var mine = b2[key];
            if (!mine || mine.token !== token || mine.state !== 'claiming') {
              Promise.resolve().then(function () { return attempt(waitCount + 1); }).then(resolve, reject); return;
            }
            var exactBefore = bankGet(), exactAfter = exactBefore + delta;
            if (exactAfter > HEART_BANK_MAX) throw pendingError('Your reward is saved, but the Heart bank is full. Spend Hearts, then reopen Battlegrounds.');
            mine.state = 'credited'; mine.before = exactBefore; mine.after = exactAfter; mine.at = Date.now();
            b2[key] = mine; m2[account] = b2; saveJournal(m2);
            bankApplyVerified(delta, reason, exactBefore);
            var m3 = claimJournalRead();
            if (!m3) throw pendingError('Your saved reward journal became unreadable after the Heart balance changed. Reopen Battlegrounds for recovery.');
            var b3 = m3[account];
            if (b3 == null) b3 = {};
            if (!plainObject(b3)) throw pendingError('Your saved reward journal has an invalid account row. Reopen Battlegrounds for recovery.');
            var done = b3[key] || mine;
            if (!done || done.token !== token || done.state !== 'credited') {
              throw pendingError('Your reward was saved, but its device receipt needs recovery. Reopen Battlegrounds.');
            }
            done.state = 'done'; done.at = Date.now(); b3[key] = done; m3[account] = b3; saveJournal(m3);
            resolve({ first: true, amount: delta });
          } catch (e) { reject(e); }
        }, 0);
      });
    }
    try {
      if (navigator.locks && typeof navigator.locks.request === 'function') {
        return navigator.locks.request(lockName, function () { return attempt(0); });
      }
    } catch (e) {}
    return Promise.resolve().then(function () { return attempt(0); });
  }

  /* Paid path choices are stored on the server before the device-local Heart
     wallet moves. Keep that unavoidable split as a two-phase journal: prepare
     the exact run/stage/choice price before pathSet, then debit only after the
     server accepts (or reports the same stored choice). A reload can finish a
     known before/after window; any third balance fails closed. */
  var BG_EVENT_DEBITS_KEY = 'hoh-bg-event-debits-v1';
  function eventDebitRead() {
    try {
      var raw = localStorage.getItem(BG_EVENT_DEBITS_KEY);
      if (raw == null || raw === '') return {};
      var m = JSON.parse(raw);
      return plainObject(m) ? m : null;
    } catch (e) { return null; }
  }
  function eventDebitWrite(m) {
    try {
      if (!plainObject(m)) return false;
      var raw = JSON.stringify(m);
      localStorage.setItem(BG_EVENT_DEBITS_KEY, raw);
      return localStorage.getItem(BG_EVENT_DEBITS_KEY) === raw;
    } catch (e) { return false; }
  }
  function eventDebitError(message, unverified) {
    var e = new Error(message);
    e.__hohEventDebitPending = true;
    if (unverified) e.__hohEventDebitUnverified = true;
    return e;
  }
  function eventDebitParts(runId, stage, choice, amount) {
    var rid = String(runId == null ? '' : runId).trim();
    stage = stage | 0; choice = choice | 0; amount = amount | 0;
    if (!rid || rid.length > 96 || stage < 1 || stage > 999 || choice < -1 || choice > 5 || amount < 0 || amount > HEART_BANK_MAX) {
      throw eventDebitError('That saved event payment has an invalid identity. No Hearts were charged.');
    }
    var identity = [rid, stage, choice].join('|');
    return { identity: identity, key: 'event:' + identity, run_id: rid,
      stage: stage, choice: choice, amount: amount };
  }
  function eventDebitScope() {
    return localMode() ? null : captureCombatAccount();
  }
  function eventDebitAccount(scope) {
    return claimAccount(scope);
  }
  function eventDebitBucket(m, account) {
    var bucket = m[account];
    if (bucket == null) bucket = {};
    return plainObject(bucket) ? bucket : null;
  }
  function validateEventDebitRow(row, parts) {
    if (!plainObject(row)) return false;
    if (row.identity !== parts.identity || (row.amount | 0) !== parts.amount) return false;
    return row.state === 'prepared' || row.state === 'claiming' || row.state === 'debiting' ||
      row.state === 'done' || row.state === 'unverified';
  }
  function prepareEventDebitFor(scope, runId, stage, choice, amount) {
    var parts = eventDebitParts(runId, stage, choice, amount);
    if (!parts.amount) return { ok: true, free: true, identity: parts.identity };
    var account = eventDebitAccount(scope);
    if (!localMode() && collectionAccount() !== account) {
      throw eventDebitError('Return to the account that made this event choice before paying it.');
    }
    var m = eventDebitRead();
    if (!m) throw eventDebitError('The saved event-payment journal is damaged or unreadable. No Hearts were charged.');
    var bucket = eventDebitBucket(m, account);
    if (!bucket) throw eventDebitError('The saved event-payment account row is invalid. No Hearts were charged.');
    var old = bucket[parts.key];
    if (old != null && !validateEventDebitRow(old, parts)) {
      throw eventDebitError('The saved event payment does not match this choice. No Hearts were charged.');
    }
    if (old) return { ok: true, prepared: true, existing: true, state: old.state, identity: parts.identity };
    if (bankGet() < parts.amount) throw eventDebitError('You no longer have enough Hearts for the choice that was saved.');
    bucket[parts.key] = { identity: parts.identity, run_id: parts.run_id,
      stage: parts.stage, choice: parts.choice, amount: parts.amount,
      state: 'prepared', at: Date.now() };
    m[account] = bucket;
    if (!eventDebitWrite(m)) throw eventDebitError('This device could not save the event payment, so the choice was not submitted. Try again.');
    return { ok: true, prepared: true, existing: false, state: 'prepared', identity: parts.identity };
  }
  function prepareEventDebit(runId, stage, choice, amount) {
    return prepareEventDebitFor(eventDebitScope(), runId, stage, choice, amount);
  }
  function quarantineEventDebit(runId, stage, choice, amount) {
    var scope = eventDebitScope(), parts = eventDebitParts(runId, stage, choice, amount);
    if (!parts.amount) return true;
    var account = eventDebitAccount(scope), m = eventDebitRead();
    if (!m) throw eventDebitError('The saved event-payment journal is damaged or unreadable. No Hearts were charged.', true);
    var bucket = eventDebitBucket(m, account), row = bucket && bucket[parts.key];
    if (!validateEventDebitRow(row, parts)) {
      throw eventDebitError('This saved paid choice has no verified device payment record. No Hearts were charged again.', true);
    }
    if (row.state === 'done' || row.state === 'debiting') {
      throw eventDebitError('This event payment has already crossed its debit boundary and needs support review.', true);
    }
    row.state = 'unverified'; row.at = Date.now(); bucket[parts.key] = row; m[account] = bucket;
    if (!eventDebitWrite(m)) throw eventDebitError('This device could not preserve the ambiguous payment boundary. No Hearts were charged again.', true);
    return true;
  }
  function commitEventDebit(runId, stage, choice, amount) {
    var scope = eventDebitScope();
    var parts = eventDebitParts(runId, stage, choice, amount);
    if (!parts.amount) return Promise.resolve({ first: false, amount: 0, free: true });
    var account = eventDebitAccount(scope);
    var lockName = 'hoh-bg-wallet-' + claimHash(account);
    function save(m) {
      if (!eventDebitWrite(m)) throw eventDebitError('Your choice is saved, but this device could not record its Heart payment. Tap retry.');
    }
    function attempt(waitCount) {
      waitCount = waitCount | 0;
      if (!localMode() && collectionAccount() !== account) {
        throw eventDebitError('Return to the account that made this event choice, then tap retry.');
      }
      var now = Date.now(), m = eventDebitRead();
      if (!m) throw eventDebitError('The saved event-payment journal is damaged or unreadable. No Hearts were charged.');
      var bucket = eventDebitBucket(m, account);
      if (!bucket) throw eventDebitError('The saved event-payment account row is invalid. No Hearts were charged.');
      var row = bucket[parts.key];
      /* Never invent a debit record after observing only the server choice.
         Older builds charged before this journal existed, while a lost new
         response might not have charged at all. With no prepared boundary the
         truth is unknowable, so block instead of double-charging or granting
         the paid branch for free. New choices always prepare synchronously
         before pathSet, so their reload path is recoverable. */
      if (!validateEventDebitRow(row, parts)) throw eventDebitError('This saved paid choice has no verified device payment record. No Hearts were charged again.', true);
      if (row.state === 'unverified') throw eventDebitError('This older paid choice cannot be verified safely on this device. No Hearts were charged again.', true);
      if (row.state === 'done') return Promise.resolve({ first: false, amount: parts.amount });
      if (row.state === 'debiting') {
        if (row.before == null || row.after == null || (row.after | 0) !== (row.before | 0) - parts.amount) {
          throw eventDebitError('The saved event payment has an invalid recovery boundary. No Hearts were charged again.');
        }
        var current = bankGet();
        if (current !== (row.before | 0) && current !== (row.after | 0)) {
          throw eventDebitError('Your Heart balance changed while this saved choice was being paid. Nothing was charged twice; tap retry after restoring the balance.');
        }
        var recovered = false;
        if (current === (row.before | 0)) {
          bankApplyVerified(-parts.amount, 'spend_event', row.before | 0);
          recovered = true;
        }
        row.state = 'done'; row.at = now; bucket[parts.key] = row; m[account] = bucket; save(m);
        return Promise.resolve({ first: recovered, amount: parts.amount, recovered: true });
      }
      if (row.state === 'claiming' && now - Number(row.at || 0) < 2000 && waitCount < 40) {
        return new Promise(function (resolve, reject) {
          setTimeout(function () { Promise.resolve().then(function () { return attempt(waitCount + 1); }).then(resolve, reject); }, 50);
        });
      }
      if (bankGet() < parts.amount) throw eventDebitError('Your saved choice still needs ' + parts.amount + ' Hearts. Add Hearts, then tap retry.');
      var token = rhex(24);
      row.state = 'claiming'; row.token = token; row.at = now;
      bucket[parts.key] = row; m[account] = bucket; save(m);
      return new Promise(function (resolve, reject) {
        setTimeout(function () {
          try {
            var m2 = eventDebitRead();
            if (!m2) throw eventDebitError('The saved event-payment journal became unreadable. No Hearts were charged.');
            var b2 = eventDebitBucket(m2, account);
            if (!b2) throw eventDebitError('The saved event-payment account row became invalid. No Hearts were charged.');
            var mine = b2[parts.key];
            if (!mine || mine.identity !== parts.identity || mine.token !== token || mine.state !== 'claiming') {
              Promise.resolve().then(function () { return attempt(waitCount + 1); }).then(resolve, reject); return;
            }
            var before = bankGet(), after = before - parts.amount;
            if (after < 0) throw eventDebitError('Your saved choice still needs ' + parts.amount + ' Hearts. Add Hearts, then tap retry.');
            mine.state = 'debiting'; mine.before = before; mine.after = after; mine.at = Date.now();
            b2[parts.key] = mine; m2[account] = b2; save(m2);
            bankApplyVerified(-parts.amount, 'spend_event', before);
            var m3 = eventDebitRead();
            if (!m3) throw eventDebitError('The Hearts were charged, but the device receipt needs recovery. Tap retry; it will not charge twice.');
            var b3 = eventDebitBucket(m3, account);
            if (!b3) throw eventDebitError('The Hearts were charged, but the device receipt is invalid. Tap retry; it will not charge twice.');
            var done = b3[parts.key];
            if (!done || done.identity !== parts.identity || done.token !== token || done.state !== 'debiting') {
              throw eventDebitError('The Hearts were charged, but the device receipt needs recovery. Tap retry; it will not charge twice.');
            }
            done.state = 'done'; done.at = Date.now(); b3[parts.key] = done; m3[account] = b3; save(m3);
            resolve({ first: true, amount: parts.amount });
          } catch (e) { reject(e && e.__hohEventDebitPending ? e : eventDebitError((e && e.message) || 'The saved event payment could not finish. Tap retry.')); }
        }, 0);
      });
    }
    try {
      if (navigator.locks && typeof navigator.locks.request === 'function') {
        return navigator.locks.request(lockName, function () { return attempt(0); });
      }
    } catch (e) {}
    return Promise.resolve().then(function () { return attempt(0); });
  }

  /* Local acknowledgement latches cover two replay-safe UI decisions that
     have no installed server RPC: reading an event outcome, and declining the
     Court. They are account + run scoped and write-verified. A malformed latch
     fails closed as already handled, avoiding a duplicate debit/recruit offer.
     Court skips still cannot follow the player to another device until the
     server grows an idempotent skip endpoint. */
  var BG_LOCAL_LATCH_KEY = 'hoh-bg-local-latches-v1';
  function localLatchRead() {
    try {
      var raw = localStorage.getItem(BG_LOCAL_LATCH_KEY);
      if (raw == null || raw === '') return {};
      var m = JSON.parse(raw);
      return plainObject(m) ? m : null;
    } catch (e) { return null; }
  }
  function localLatchWrite(m) {
    try {
      if (!plainObject(m)) return false;
      var raw = JSON.stringify(m);
      localStorage.setItem(BG_LOCAL_LATCH_KEY, raw);
      return localStorage.getItem(BG_LOCAL_LATCH_KEY) === raw;
    } catch (e) { return false; }
  }
  function localLatchIdentity(kind, runId, stage, choice) {
    var rid = String(runId == null ? '' : runId).trim();
    if (!rid || rid.length > 96) return null;
    var identity = [String(kind || ''), rid, stage == null ? '' : (stage | 0), choice == null ? '' : (choice | 0)].join('|');
    return { identity: identity, key: 'latch:' + identity };
  }
  function localLatchHas(kind, runId, stage, choice) {
    var parts = localLatchIdentity(kind, runId, stage, choice);
    if (!parts) return true;
    var m = localLatchRead();
    if (!m) return true;
    var account = claimAccount(localMode() ? null : captureCombatAccount());
    var bucket = m[account];
    if (bucket == null) return false;
    if (!plainObject(bucket)) return true;
    var row = bucket[parts.key];
    if (row == null) return false;
    /* A present but malformed tombstone is intentionally treated as handled:
       replaying a possibly paid choice or declined recruit is the unsafe side. */
    if (!plainObject(row) || row.identity !== parts.identity || row.state !== 'done') {
      try { console.warn('[hoc] malformed local Battlegrounds latch; replay blocked', kind, runId); } catch (_) {}
    }
    return true;
  }
  function localLatchSet(kind, runId, stage, choice) {
    var parts = localLatchIdentity(kind, runId, stage, choice);
    if (!parts) throw new Error('That saved Battlegrounds decision has an invalid run identity.');
    var m = localLatchRead();
    if (!m) throw new Error('The saved Battlegrounds decision journal is damaged or unreadable.');
    var account = claimAccount(localMode() ? null : captureCombatAccount());
    var bucket = m[account];
    if (bucket == null) bucket = {};
    if (!plainObject(bucket)) throw new Error('The saved Battlegrounds decision account row is invalid.');
    var old = bucket[parts.key];
    if (old != null && (!plainObject(old) || old.identity !== parts.identity || old.state !== 'done')) {
      throw new Error('The saved Battlegrounds decision does not match this run.');
    }
    if (old) return true;
    bucket[parts.key] = { identity: parts.identity, state: 'done', at: Date.now() };
    m[account] = bucket;
    if (!localLatchWrite(m)) throw new Error('This device could not save that decision. Tap again.');
    return true;
  }
  function eventResolutionDone(runId, stage, choice) { return localLatchHas('event', runId, stage, choice); }
  function markEventResolutionDone(runId, stage, choice) { return localLatchSet('event', runId, stage, choice); }
  function courtSkipped(runId) { return localLatchHas('court-skip', runId, null, null); }
  function markCourtSkipped(runId) { return localLatchSet('court-skip', runId, null, null); }

  function claimSettlementExtra(res, runId, category, legacyAmount) {
    res = res || {};
    /* ============ R622 · THE SECOND DOOR INTO THE DEVICE BANK ==============
       settle()'s mode branch closes the main road; this is the side road, and
       it takes the same reply object. An Unlimited receipt names owner_bonus
       and recruit_dust as literal ZERO (R611B:1999-2000) and its Hearts were
       paid server-side, so there is nothing here to claim and the legacy
       amount must never stand in for the missing number. Keyed on res.mode,
       the server's field, exactly like the branch in settle(). */
    if (String(res.mode || '') === 'unlimited') {
      return Promise.resolve({ first: false, amount: 0, state: 'skipped-unlimited' });
    }
    var scope = null;
    if (!(localMode() || /^local-/.test(String(runId || '')))) {
      scope = res.local_reward_account
        ? { account: String(res.local_reward_account).trim().toLowerCase() }
        : captureCombatAccount();
    }
    var amount = category === 'recruit_dust' ? (res.recruit_dust | 0) : 0;
    if (!res.receipt_id && !(amount > 0)) amount = Math.max(0, legacyAmount | 0);
    return claimLocalReward(scope, claimIdentity(res, runId), category, amount,
      category === 'recruit_dust' ? 'earn_contract_recruit_dust' : 'earn_contract_reward',
      claimLegacyIdentity(res, runId));
  }

  /* R603 · one durable client envelope for one atomic crystal reroll.
     The database receipt is the economy authority; this tiny journal only
     remembers which immutable request to replay when a connection drops after
     commit but before the browser sees the reply. It never stores the PIN and
     it is written BEFORE the RPC, so a reload cannot turn an ambiguous first
     request into a second charge. */
  var ATOMIC_SKILL_ROLL_KEY = 'hoh-atomic-skill-reroll-v1:';
  /* ============ R616 · THE ROLL HOTFIX ============
     Jan ran the read-only census on 2026-09-02
     (docs/claude/r615a-live-capture-20260902.md): 1,624 of 1,692 held
     abilities were the browser's own fallback ids, and every roll since the
     week of 24 August (592 of 594, then 489 of 489) landed one. The cause is
     two pickers, not one:
       1. pickActiveId / pickPassiveId propose a 472-row REGISTRY id first
          whenever the engine's PATHF.classPools flag is on. The server's
          hoh_valid_skill_id accepts only the 123 legacy ids, refuses it with
          'bad skill', and trySet quietly retries with safeFallbackId().
       2. atomicPickSkill, the Mythic Crystal path's OWN picker, was written
          to draw actives only from ATOMIC_SAFE_ACTIVE (44 of 86), so a
          crystal reroll could never land the other 42 or a class favourite.
     REGISTRY_ROLL_LIVE gates the registry branch and stays false until the
     R618/R620 acquisition wave installs the ids server-side and moves the
     stamp (NEXT-WAVE-ABILITY-SYSTEM.md 16.10). The engine's own flag is left
     alone on purpose: editing the engine breaks Worker parity, and this is a
     client bug. _fallbackHits counts every trip through safeFallbackId so a
     recurrence is one number, and the crystal picker now draws from the ids
     the server PROVABLY accepts (the allowlist body captured at
     docs/claude/r504a-live-capture-20260825.md:54-95) with the same class
     affinity the ordinary roll uses. The 55 safe ids stay as the last resort. */
  /* ============================================================
     R646 · THE SWITCH. THE NEW ABILITY SYSTEM IS THE ROLL POOL.
     Jan: "when you roll the ability, it'll roll into the new system and
     they'll start getting new system abilities instead of the old abilities
     that we used to have ... the characters with the abilities already rolled,
     they'll still have their current one, it just is useless."

     The comment above says this flag "stays false until the R618/R620
     acquisition wave installs the ids server-side and moves the stamp". Both
     have happened: R628B installed all 792 ids in hoh_skill_catalog and R629B
     moved the stamp to 66, so hoh_valid_skill_id now accepts a compiled id
     (it tests the catalog and engine_ver_min, and 66 <= 66).

     ⛔ FLIPPING THE FLAG ALONE WOULD STILL HAVE ROLLED OLD ABILITIES. The
     branch it gates draws from E.abPool, which returns a MIX: measured 28 new
     and 9 legacy for a Fighter Common. Jan's rule is that a roll lands in the
     new system, so the pool is filtered to v66 rows and the legacy ids are
     retired from ROLLING while staying valid forever on the cards that hold
     them. That is the R450b retirement rule, finally enforced.

     ⛔ AND `rollable` IN THE DATABASE IS NOT THE SWITCH, WHICH IS WORTH
     WRITING DOWN BECAUSE IT LOOKS LIKE IT IS. hoh_valid_skill_id does not read
     it (deliberately: a legacy id must stay writable or a live player loses
     their ability). It gates only hoh_roll_candidates, which NOTHING calls
     yet. So the SQL flag is the catalog telling the truth about itself and the
     door a future server-picks wave will use, and this flag is what actually
     changes what a player rolls today.

     ⛔ COVERAGE WAS CHECKED BEFORE FLIPPING IT: every one of the 13 classes has
     v66 rows at every one of the 6 rarities, the thinnest cell being 2
     (Fighter Mythic). No cell can silently fall through to legacy. */
  var REGISTRY_ROLL_LIVE = true;
  function abilityRollError(code, message) {
    var error = new Error(message);
    error.code = code;
    error.userMessage = message;
    return error;
  }
  function abilityRollErrorMessage(error) {
    return error && error.userMessage ? String(error.userMessage) : '';
  }
  function heldAbilityIds(cardKey) {
    var gear = gearFor(cardKey) || {}, meta = gearMetaFor(cardKey) || {};
    return ['active', 'passive'].map(function (slot) {
      var row = gear[slot], pending = meta[slot];
      return row && row.id || (pending && pending.prevId) || '';
    }).filter(function (id) { return id && id !== 'PENDING'; });
  }
  function checkNewRollPools(E, heroLike, slot, cap, held, avoid) {
    if (!REGISTRY_ROLL_LIVE) return;
    var floor = held ? Math.max(0, Math.min(cap, held.r | 0)) : 0;
    var top = held ? Math.min(cap, floor + 1) : cap;
    for (var rarity = floor; rarity <= top; rarity++) newSystemPool(E, heroLike, slot, rarity, avoid);
  }
  /* the ONE door. Three pickers used to each have their own idea of the pool,
     which is how the crystal path ended up drawing from a hard-coded list of
     44 legacy ids while the ordinary roll drew from the registry. */
  function newSystemPool(E, heroLike, slot, rarity, avoid) {
    if (!REGISTRY_ROLL_LIVE) return [];
    if (!E || typeof E.abPool !== 'function' || !E.ABILITIES || !(E.PATHF && E.PATHF.classPools)) {
      throw abilityRollError('ability_catalog_unavailable', 'Abilities are still loading. Reopen this card to try again. Nothing was spent.');
    }
    var pool;
    try {
      var cls = String((heroLike && (heroLike.cls || heroLike.character_class)) || '').split(' ')[0];
      var blocked = {};
      (avoid || []).forEach(function (id) { if (id) blocked[String(id)] = true; });
      pool = (E.abPool(cls, slot === 'passive' ? 'pas' : 'act', rarity) || [])
        .filter(function (id) {
          var a = E.ABILITIES[id];
          return a && a.v66 === 1 && !blocked[String(id)];
        });
    } catch (_) {
      throw abilityRollError('ability_catalog_unavailable', 'The new abilities could not load. Reopen this card to try again. Nothing was spent.');
    }
    if (!pool.length) {
      throw abilityRollError('ability_pool_exhausted', 'No different ability is available at this rarity for this hero. Nothing was spent.');
    }
    return pool;
  }
  /* a hero's OWN class rows are preferred over the all-class ones, the same
     70/30 the ordinary roll has always used */
  function pickFromNewPool(E, heroLike, lib) {
    var cls = String((heroLike && (heroLike.cls || heroLike.character_class)) || '').split(' ')[0];
    var own = lib.filter(function (id) {
      var a = E.ABILITIES[id];
      return a && a.owners && a.owners.indexOf(cls) >= 0;
    });
    var from = (own.length && Math.random() < 0.7) ? own : lib;
    return from[Math.floor(Math.random() * from.length)];
  }
  var _fallbackHits = 0;
  var SERVER_ACCEPTS_ACTIVE = {
    steady:1,opport:1,bigswing:1,hunter:1,firstblood:1,momentum:1,execute:1,
    bulkblade:1,glassedge:1,doubletap:1,shatter:1,rampage:1,mark:1,erasure:1,
    godslayer:1,stunner:1,summoner:1,towergrip:1,vanguard:1,spikes:1,phalanx:1,
    colossus:1,storedwrath:1,immortal:1,mender:1,stonepalm:1,armor:1,ward:1,
    lifebloom:1,barrier:1,sanctify:1,dawnwall:1,miracle:1,guardian:1,resur:1,
    warmup:1,tempo:1,crescendo:1,showstop:1,anthem:1,finalact:1,
    hexbolt:1,rampart:1,triage:1,
    songblade:1,keeneye:1,aegisbearer:1,springwell:1,
    deadeye1:1,deadeye2:1,deadeye3:1,bloodroar:1,pressurepoint:1,poisonedge:1,
    judgment:1,twinspark:1,soultap:1,counterspell:1,deployturret:1,hawkcall:1,
    gravecall:1,counterhold:1,banneret:1,circleward:1,packcall:1,
    discordnote:1,muzzle:1,
    rallyline:1,oathstone:1,starvolley:1,veilofnight:1,hellpact:1,luckcharm:1,
    ancestralbreath:1,halosurge:1,overclock:1,gizmoheist:1,
    citadel:1,retribution:1,avatarstone:1,sanctuary:1,lifewell:1,
    springeternal:1,warchant:1,encore:1,maestro:1
  };
  var ATOMIC_SAFE_ACTIVE = {
    steady:1,opport:1,bigswing:1,hunter:1,firstblood:1,momentum:1,execute:1,
    bulkblade:1,glassedge:1,doubletap:1,shatter:1,rampage:1,mark:1,erasure:1,
    godslayer:1,stunner:1,summoner:1,towergrip:1,vanguard:1,spikes:1,phalanx:1,
    colossus:1,storedwrath:1,immortal:1,mender:1,stonepalm:1,armor:1,ward:1,
    lifebloom:1,barrier:1,sanctify:1,dawnwall:1,miracle:1,guardian:1,resur:1,
    warmup:1,tempo:1,crescendo:1,showstop:1,anthem:1,finalact:1,hexbolt:1,
    rampart:1,triage:1
  };
  function atomicSkillJournalKey(scope, cardKey, slot) {
    var who = String(scope && scope.account || '').trim().toLowerCase();
    return ATOMIC_SKILL_ROLL_KEY + encodeURIComponent(who) + ':' +
      encodeURIComponent(String(cardKey || '')) + ':' + String(slot || '');
  }
  function atomicSkillJournalPending(cardKey, slot) {
    if (!signedIn()) return false;
    try {
      return localStorage.getItem(atomicSkillJournalKey(captureCombatAccount(), cardKey, slot)) !== null;
    } catch (_) { return false; }
  }
  function atomicInt(value, min, max) {
    if (value === null || value === '' || typeof value === 'boolean') return null;
    var n = Number(value);
    return isFinite(n) && Math.floor(n) === n && n >= min && n <= max ? n : null;
  }
  function atomicReadSkillJournal(scope, cardKey, slot) {
    var key = atomicSkillJournalKey(scope, cardKey, slot), raw;
    try { raw = localStorage.getItem(key); }
    catch (_) { throw new Error('This device could not verify a saved crystal reroll. Storage access is required before trying again.'); }
    if (raw == null) return null;
    var row = null;
    try { row = JSON.parse(raw); } catch (_) {}
    var cap = String(cardKey || '').indexOf('ff:') === 0 ? 5 : 4;
    var expectedR = row && atomicInt(row.expectedR, 0, cap);
    var expectedPity = row && atomicInt(row.expectedPity, 0, 1000000000);
    var requestId = String(row && row.requestId || '');
    var upRequired = expectedR !== null && expectedR < cap;
    var valid = !!row && row.v === 1 && row.account === String(scope.account || '') &&
      row.cardKey === String(cardKey || '') && row.slot === String(slot || '') &&
      String(row.expectedId || '') && String(row.expectedId) !== 'PENDING' &&
      expectedR !== null && expectedPity !== null &&
      String(row.sameId || '') && String(row.sameId) !== 'PENDING' &&
      (!upRequired || (String(row.upId || '') && String(row.upId) !== 'PENDING')) &&
      (upRequired || !String(row.upId || '')) && requestId.length >= 16 &&
      requestId.length <= 128 && !/[\x00-\x1f\x7f]/.test(requestId);
    if (!valid) {
      throw new Error('A saved crystal reroll could not be verified. No new reroll was started; reopen the Hall or contact support before spending another crystal.');
    }
    row.expectedR = expectedR;
    row.expectedPity = expectedPity;
    row.__key = key;
    return row;
  }
  function atomicWriteSkillJournal(scope, row) {
    var key = atomicSkillJournalKey(scope, row.cardKey, row.slot);
    var raw = JSON.stringify(row);
    try {
      localStorage.setItem(key, raw);
      if (localStorage.getItem(key) !== raw) throw new Error('journal did not persist');
    } catch (_) {
      throw new Error('This device could not safely remember the crystal reroll. Storage access is required before spending.');
    }
    row.__key = key;
    return row;
  }
  function atomicClearSkillJournal(row) {
    if (!row || !row.__key) return;
    try {
      var saved = JSON.parse(localStorage.getItem(row.__key) || 'null');
      if (saved && saved.requestId === row.requestId) localStorage.removeItem(row.__key);
    } catch (_) {}
  }
  function atomicPickSkill(E, heroLike, slot, rarity, avoid) {
    rarity = Math.max(0, Math.min(5, rarity | 0));
    var blocked = {};
    (avoid || []).forEach(function (id) { if (id) blocked[String(id)] = true; });
    /* ⛔ R646: THE MYTHIC CRYSTAL PATH HAD ITS OWN IDEA OF THE POOL, AND IT WAS
       THE OLDEST ONE. Everything below draws from SERVER_ACCEPTS_ACTIVE and
       PASSIVE_BANDS, which are hard-coded lists of LEGACY ids captured from the
       live allowlist in R504. So the ordinary roll could reach the new system
       and the crystal reroll, the thing Jan actually charges for, could not.
       Jan: "when you roll the ability, it'll roll into the new system".
       Same door as the other two pickers, ahead of everything, and the legacy
       lists stay below as the fail-safe for a pool that comes back empty. */
    var _libX = newSystemPool(E, heroLike, slot, rarity, avoid || []);
    if (_libX.length) return pickFromNewPool(E, heroLike, _libX);
    var candidates = [];
    if (slot === 'passive') {
      /* The live authority has no band-5 passives. R603 deliberately uses a
         different band-4 passive for a rarity-5 result, matching the existing
         persisted-rarity law without ever lowering that persisted rarity. */
      var passiveBand = rarity === 5 ? 4 : rarity;
      candidates = (PASSIVE_BANDS[passiveBand] || []).filter(function (id) {
        return E && E.content && E.content.PASSIVES && E.content.PASSIVES[id];
      });
    } else {
      var role = E.roleOf(heroLike || {});
      var cls = String(heroLike && (heroLike.cls || heroLike.character_class) || '').split(' ')[0];
      var pool = role.act === 'bless' ? (cls === 'Bard' ? 'support' : 'heal')
               : role.act === 'tank' ? 'tank' : 'attack';
      /* R616: draw from every active the server provably accepts at this pool
         and rarity, preferring the class's own favourites the way the
         ordinary roll does (CLS_SKILL_AFFINITY, 65%). The 44-id safe list is
         only the last resort for a pool the allowlist leaves empty. */
      var skills = (E.content && E.content.SKILLS) || {};
      var accepted = Object.keys(skills).filter(function (id) {
        var skill = skills[id];
        return SERVER_ACCEPTS_ACTIVE[id] && skill && skill.p === pool && skill.r === rarity;
      });
      if (!accepted.length) {
        accepted = Object.keys(skills).filter(function (id) {
          var skill = skills[id];
          return ATOMIC_SAFE_ACTIVE[id] && skill && skill.p === pool && skill.r === rarity;
        });
      }
      var favs = (CLS_SKILL_AFFINITY[cls] || []).filter(function (id) { return accepted.indexOf(id) >= 0; });
      candidates = (favs.length && Math.random() < 0.65) ? favs : accepted;
    }
    var fresh = candidates.filter(function (id) { return !blocked[String(id)]; });
    if (fresh.length) candidates = fresh;
    if (!candidates.length) throw new Error('No verified ability is available for that rarity. No crystal was spent.');
    return candidates[Math.floor(Math.random() * candidates.length)];
  }

  function atomicCrystalSkillRoll(scope, cardKey, slot, heroLike, held, journal) {
    var cap = String(cardKey || '').indexOf('ff:') === 0 ? 5 : 4;
    if (!journal) {
      if (!held || !held.id || held.id === 'PENDING') {
        return Promise.reject(new Error('That held ability could not be verified. No crystal was spent.'));
      }
      var E = window.HOHContract;
      var heldR = Math.max(0, Math.min(cap, held.r | 0));
      var sameId, upId = '';
      try {
        var avoid = heldAbilityIds(cardKey).concat([held.id]);
        sameId = atomicPickSkill(E, heroLike, slot, heldR, avoid);
        if (heldR < cap) upId = atomicPickSkill(E, heroLike, slot, heldR + 1, avoid.concat([sameId]));
        journal = atomicWriteSkillJournal(scope, {
          v:1, account:String(scope.account || ''), cardKey:String(cardKey || ''), slot:String(slot || ''),
          expectedId:String(held.id), expectedR:heldR, expectedPity:Math.max(0, held.pity | 0),
          sameId:String(sameId), upId:String(upId || ''),
          requestId:'skill:' + Date.now().toString(36) + ':' + rhex(24), createdAt:Date.now()
        });
      } catch (candidateError) { return Promise.reject(candidateError); }
    }
    var args = {
      p_email: scope.email, p_pin: scope.pin, p_card_key: journal.cardKey, p_slot: journal.slot,
      p_expected_skill_id: journal.expectedId, p_expected_rarity: journal.expectedR,
      p_expected_pity: journal.expectedPity, p_same_skill_id: journal.sameId,
      p_up_skill_id: journal.upId || null, p_request_id: journal.requestId,
      p_device: String((typeof navigator !== 'undefined' && navigator.userAgent) || '').slice(0, 120)
    };
    function send(attempt) {
      return rpc('hoh_crystal_reroll_card_skill', args).catch(function (error) {
        if (rpcDefinitelyMissing(error, 'hoh_crystal_reroll_card_skill')) {
          atomicClearSkillJournal(journal);
          throw new Error('Crystal rerolls are being upgraded. No crystal was spent; try again after the Hall update is installed.');
        }
        /* A lost reply may follow a committed transaction. Retry the exact
           immutable request once; the server receipt returns the first result
           without charging or rolling again. Keep the journal if both replies
           are ambiguous so the next deliberate tap can perform that replay. */
        if (attempt < 1 && combatAccountIsCurrent(scope)) {
          return new Promise(function (resolve) { setTimeout(resolve, 180); }).then(function () { return send(attempt + 1); });
        }
        throw error;
      });
    }
    return send(0).then(function (res) {
      if (!res || res.ok !== true) {
        var status = String(res && res.status || 'unverified');
        var definitive = res && res.ok === false && [
          'insufficient_crystals', 'stale_ability', 'bad_same_candidate', 'bad_up_candidate',
          'unexpected_up_candidate', 'request_conflict', 'bad_slot', 'bad_card',
          'bad_expected_state', 'transaction_refused'
        ].indexOf(status) >= 0;
        if (!definitive) {
          throw abilityRollError('ability_receipt_unverified', 'This Crystal roll may already be saved. Choose Verify on this card before starting another roll.');
        }
        /* These answers are definitive and made no change. A conflict is the
           exception: it says this request id already belongs to different
           facts, so retaining the journal is safer than inventing a new roll. */
        if (status !== 'request_conflict') atomicClearSkillJournal(journal);
        if (status === 'insufficient_crystals') throw new Error('no crystals');
        if (status === 'stale_ability') throw abilityRollError(status, 'This ability changed in another window. The latest version is shown; choose it again to reroll. No Crystal was spent.');
        if (status === 'bad_same_candidate' || status === 'bad_up_candidate' || status === 'unexpected_up_candidate') {
          throw abilityRollError(status, 'The Hall could not accept this new ability. Your abilities and Crystal were kept. Please try again after the Hall update.');
        }
        if (status === 'request_conflict') throw new Error('A saved crystal reroll conflicts with its receipt. No new roll was started; contact support before trying again.');
        if (status === 'transaction_refused') throw abilityRollError(status, 'The Hall could not complete this reroll. Your ability and Crystal were kept. Please try again later.');
        throw abilityRollError(status, 'The Hall could not start this reroll. No Crystal was spent. Reopen the card to try again.');
      }
      var resultR = atomicInt(res.rarity, journal.expectedR, Math.min(cap, journal.expectedR + 1));
      var floor = atomicInt(res.floor, 0, cap);
      var spent = atomicInt(res.crystals_spent, 0, 1);
      var price = atomicInt(res.price, 0, 150);
      var pity = atomicInt(res.pity, 0, 1000000000);
      var pityCap = atomicInt(res.pity_cap, 0, 1000000000);
      var crystalBalance = atomicInt(res.crystals, 0, 1000000000);
      var expectedId = resultR === journal.expectedR ? journal.sameId : journal.upId;
      var paid = res.paid === true;
      var valid = res.status === 'ok' && res.atomic_model === 'r603-crystal-skill-v1' &&
        String(res.request_id || '') === journal.requestId && resultR !== null &&
        floor === journal.expectedR && String(res.skill_id || '') === String(expectedId || '') &&
        String(res.id || '') === String(expectedId || '') && typeof res.tiered_up === 'boolean' &&
        (res.tiered_up === (resultR > journal.expectedR)) && typeof res.paid === 'boolean' &&
        typeof res.bonus === 'boolean' && res.free === false && typeof res.pity_forced === 'boolean' &&
        spent === (paid ? 1 : 0) && price === (paid ? 150 : 0) && pity !== null &&
        pityCap !== null && crystalBalance !== null &&
        ((res.bonus === true && !paid) || (res.bonus === false && paid));
      if (!valid) {
        throw new Error('The crystal reroll receipt could not be verified. No new reroll will start until this saved request is reconciled.');
      }
      if (!combatAccountIsCurrent(scope)) {
        throw new Error('Account changed while the crystal reroll was saving. Reopen this card on the original account to verify its committed result.');
      }
      atomicClearSkillJournal(journal);
      applyGearReceipt(scope, cardKey, slot, res);
      /* Wallet reconciliation starts now but is not on the animation's
         critical path. Waiting for a second network request here would keep
         the reel spinning after the atomic result was already committed. */
      try {
        if (window.HOHWallet && window.HOHWallet.applyCrystalReceipt) {
          window.HOHWallet.applyCrystalReceipt(crystalBalance, scope.email);
        }
        if (window.HOHWallet && window.HOHWallet.sync) {
          var walletSync = window.HOHWallet.sync(true);
          if (walletSync && walletSync.catch) walletSync.catch(function () {});
        }
      } catch (_) {}
      try { document.dispatchEvent(new CustomEvent('hoh:gear-changed', { detail:{ cardKey:cardKey, slot:slot } })); } catch (_) {}
      return {
        id:String(res.skill_id), rarity:resultR, price:price,
        free:false, bonus:!!res.bonus, floor:floor, tiered_up:!!res.tiered_up,
        pity:pity, pity_cap:pityCap, pity_forced:!!res.pity_forced,
        paid:paid, crystals_spent:spent, request_id:journal.requestId, replayed:!!res.replayed,
        crystals:crystalBalance, atomic_model:'r603-crystal-skill-v1'
      };
    });
  }

  /* roll a slot: server rolls the rarity (and the price), the engine's content
     picks the concrete skill for that rarity, and we write the id back. */
  function rollSlot(cardKey, slot, heroLike, payWith, lockHeld) {
    var freeOnly = (payWith === 'free-only');
    /* R643: refuse BEFORE the navigator lock is taken, so a doomed roll never
       holds a lock another tab is waiting on. The error is named so the shop
       can say which mode is holding it. */
    var _runLock = runLocksAbilities();
    var _savedCrystal = payWith === 'crystal' && atomicSkillJournalPending(cardKey, slot);
    if (_runLock && !_savedCrystal) {
      var _lockErr = abilityRollError('ability_run_locked', _runLock === 'unlimited'
        ? 'Abilities stay locked for this Battlegrounds run. Resume your run to finish it, then reroll.'
        : 'Abilities stay locked for this climb. Resume your climb to finish it, then reroll.');
      _lockErr.__hohRunLock = _runLock;
      return Promise.reject(_lockErr);
    }
    /* Every tab uses the same per-account/card lock and verifies the
       authoritative row while holding it. This closes the stale-profile race
       where Quick review could see an empty slot, another tab could fill it,
       and the original click could silently become a paid reroll. */
    if (!lockHeld) {
      var lockScope = captureCombatAccount();
      /* Both slots share this lock: otherwise simultaneous rolls can each
         select the same new id before either sees the other's saved row. */
      var lockName = 'hoh-card-roll-v2:' + String(lockScope.account || 'local') + ':' + String(cardKey || '');
      var lockedRoll = function () {
        return loadGear(true).then(function () {
          if (!combatAccountIsCurrent(lockScope)) throw new Error('Account changed before the roll began.');
          if (gearLoadFailed) throw new Error('Abilities could not be verified. Try again before rolling.');
          var verifiedMeta = gearMetaFor(cardKey) || {};
          var verifiedRow = verifiedMeta[slot];
          var resumableFresh = !!(freeOnly && verifiedRow && verifiedRow.id === 'PENDING' &&
            verifiedRow.prevId == null && verifiedRow.prevR == null && (verifiedRow.price | 0) === 0);
          if (verifiedRow && verifiedRow.id === 'PENDING' && !resumableFresh) throw new Error('That ability is still being recovered. Try again in a moment.');
          if (freeOnly) {
            var verifiedGear = gearFor(cardKey) || {};
            if (verifiedGear[slot]) throw new Error('That ability was rolled in another tab. No Hearts were spent.');
          }
          return rollSlot(cardKey, slot, heroLike, payWith, true);
        });
      };
      try {
        if (navigator.locks && typeof navigator.locks.request === 'function') {
          return navigator.locks.request(lockName, lockedRoll);
        }
      } catch (_) {}
      return lockedRoll();
    }
    var payTicket = (payWith === 'ticket');
    /* ⛔ R595 (Jan): a reroll costs ONE MYTHIC CRYSTAL, not 150 hearts. The
       server still prices the roll in hearts and still decides the rarity;
       only what the player hands over changes. The heart price is read and
       then deliberately not charged, because the rarity ladder, the
       fresh-versus-adjacent rule and the Mythic cap all live on the server and
       must not move.
       The FIRST roll stays free and never reaches here: freeOnly takes its own
       path above and price comes back 0. */
    var payCrystal = (payWith === 'crystal');
    var _rollAvoid = heldAbilityIds(cardKey);
    if (!payCrystal || !_savedCrystal) {
      try {
        var _checkGear = gearFor(cardKey) || {};
        checkNewRollPools(window.HOHContract, heroLike, slot, String(cardKey).indexOf('ff:') === 0 ? 5 : 4, _checkGear[slot], _rollAvoid);
      } catch (poolError) { return Promise.reject(poolError); }
    }
    function chargeAlt() {
      if (!payTicket) return true;
      var t = (typeof window.hohGetUnspentTickets === 'function') ? window.hohGetUnspentTickets() : 0;
      if (t < 1 || typeof window.hohSpendUnspentTicket !== 'function') return false;
      return !!window.hohSpendUnspentTicket();
    }
    function refundAlt() { try { if (typeof window.hohRefundUnspentTicket === 'function') window.hohRefundUnspentTicket();
      else if (typeof window.hohAddUnspentTicket === 'function') window.hohAddUnspentTicket(1, 'refund'); } catch (e) {} }
    /* R560: rolls write the REAL card_skills rows for any signed-in
       account, god mode included — a roll that vanished on refresh was the
       "it only caches" bug. The sandbox branch below now serves ONLY a god
       session with no account token. Fresh rolls and rerolls intentionally
       use different laws: a fresh slot draws once from the six-tier table;
       a reroll can only stay put or move ONE adjacent tier. This mirrors the
       R560 database migration and prevents a paid reroll from jumping several
       bands. Only ff: cards can ever reach Mythic. */
    if (!signedIn() && localMode()) {
      var Eng = window.HOHContract;
      var cds = []; try { cds = (window.hohShopOwnedCards && window.hohShopOwnedCards()) || []; } catch (e) {}
      /* R422: match by bare id so own: card keys resolve their tier too. */
      var _rid = String(cardKey).replace(/^(ff|own):/, '');
      var card = cds.filter(function (x) { return String(x.id || x.card_id || x.character_id) === _rid; })[0] || {};
      var isFFCard = String(cardKey).indexOf('ff:') === 0;
      /* R708: the sandbox mirrors the server's submitted-owner rule.
         `own:` means non-FF card identity, not authorship; the collection's
         `own` bit or the real Hall owner decides whether this hero is mine.
         A held submitted ability may only reroll through the crystal path. */
      var _mine = !!(card && (card.own === true || ownsCharacter(card)));
      var cap = isFFCard ? 5 : 4;   /* non-FF cards clamp at Legendary, FF can reach Mythic */
      var rk = 'hoh-contract-localroll-' + cardKey + '-' + slot, done = false;
      try { done = localStorage.getItem(rk) === '1'; } catch (e) {}
      if (freeOnly && done) return Promise.reject(new Error('That ability is already rolled. No Hearts were spent.'));
      if (done && _mine && !payCrystal) return Promise.reject(new Error('Submitted hero rerolls require one Mythic Crystal.'));
      var price = done ? 150 : 0;               /* R395 (Jan): first roll free, rerolls a FLAT 150 */
      if (price > 0) {
        /* ⛔ R600 · THE SANDBOX MUST CHARGE WHAT THE BUTTON SAYS.
           This branch is the signed-out / god-mode path, and it had no crystal
           case at all: it fell through to the hearts one. So a button reading
           "×1 crystal" quietly took 150 HEARTS instead, which is the surface
           Jan tests on. A price on a button that is not the price charged is
           the worst kind of small bug, because everything looks like it worked. */
        if (payCrystal) {
          if (!window.HOHWallet || window.HOHWallet.crystals() < 1) return Promise.reject(new Error('no crystals'));
          window.HOHWallet.spendCrystalForRoll();
        }
        else if (payTicket) { if (!chargeAlt()) return Promise.reject(new Error('no tickets')); }
        else { if (!canAfford(price)) return Promise.reject(new Error('not enough hearts')); bankApply(-price, 'spend_skill_roll'); }
      }
      try { localStorage.setItem(rk, '1'); } catch (e) {}
      var _cur = (localGear[cardKey] && localGear[cardKey][slot] && localGear[cardKey][slot].r) || 0;
      var _curId = (localGear[cardKey] && localGear[cardKey][slot] && localGear[cardKey][slot].id) || null;
      var _fresh = !_curId;
      /* R735: the server's roll_card_skill reads tickets + guest_tickets, so the sandbox mirror does too. */
      var _tickets = Math.max(0, ((card && (card.tickets == null ? card.new_tickets : card.tickets)) | 0) + ((card && card.guest_tickets) | 0));
      /* The engine exposes its seeded stream as `.f`; accept the historical
         callable shape too so sandbox rolls mirror either saved build. */
      var _rngState = Eng.makeRng(rhex());
      var _rng = (typeof _rngState === 'function') ? _rngState : _rngState.f;
      var rarity;
      if (_fresh && _mine && slot === 'active') {
        /* R708: the submitted owner's first Active starts at exact Epic. */
        rarity = 3;
      } else if (_fresh) {
        /* 0.001% direct Mythic = one in 100,000 per FF ability slot. The
           final sliver folds into Legendary for a non-FF card. */
        var _x = _rng() * 100;
        rarity = _x < 60 ? 0 : _x < 85 ? 1 : _x < 96 ? 2 : _x < 99.5 ? 3 : _x < 99.999 ? 4 : 5;
        if (!isFFCard && rarity > 4) rarity = 4;
      } else {
        rarity = _cur;
        var _chance = abilityTierChancePct(_cur, _tickets, isFFCard) / 100;
        if (_cur < cap && _rng() < _chance) rarity = _cur + 1;
      }
      var sid = (slot === 'passive') ? pickPassiveId(Eng, heroLike, rarity, _rollAvoid) : pickActiveId(Eng, heroLike, rarity, rhex(), _rollAvoid);
      localGear[cardKey] = localGear[cardKey] || {}; localGear[cardKey][slot] = { id: sid, r: rarity };
      /* R527: the roll ceremony reads floor and tiered_up off every reply.
         The sandbox has no pity state, so pity fields answer honestly: zero
         progress, never forced. FOR FUTURE AGENTS: keep this shape in step
         with the server okResult below or god mode plays a different
         ceremony than a paying player sees. */
      try { document.dispatchEvent(new CustomEvent('hoh:gear-changed', { detail:{ cardKey:cardKey, slot:slot } })); } catch (e2) {}
      return Promise.resolve({ id: sid, rarity: rarity, price: price, free: price === 0, bonus: false,
        once: _mine,
        floor: _cur, tiered_up: !_fresh && rarity > _cur, pity: 0, pity_cap: pityCapFor(rarity), pity_forced: false,
        chance_base: _fresh ? null : abilityTierBasePct(_cur, isFFCard),
        chance_effective: _fresh ? null : abilityTierChancePct(_cur, _tickets, isFFCard) });
    }
    if (!signedIn()) return Promise.reject(new Error('sign in first'));
    /* Freeze the account that began the roll. Every RPC in this transaction
       must finish against that same account even if the Hall switches profile
       while Supabase is answering; only the matching account may receive the
       confirm-read cache. */
    var _rollScope = captureCombatAccount();
    flushAccountActions();
    var _chargedHearts = 0;
    var _chargedTickets = 0;
    var _paymentRefunded = false;
    var _offerOpened = false;
    var _offerFinalized = false;
    var _restoreOfferPromise = null;
    function refundRollPayment() {
      if (_paymentRefunded) return;
      _paymentRefunded = true;
      if (_chargedHearts || _chargedTickets) {
        if (_chargedHearts) accountHeart(_rollScope, _chargedHearts, 'earn_skill_roll_refund');
        if (_chargedTickets) queueWalletTicketRefund(_rollScope, _chargedTickets);
        _chargedHearts = 0; _chargedTickets = 0;
      }
    }
    /* R392 ROOT CAUSE, HALF ONE OF JAN'S RESET BUG. roll_card_skill OVERWRITES
       the row to skill_id 'PENDING' the moment it runs — the held skill is
       destroyed server-side BEFORE this client ever checked the price. So a
       tap with an empty purse rolled the server, threw 'not enough hearts'
       here, and left the slot stranded at PENDING, which paints as "Empty ·
       first roll free". Twenty rolls of history, gone to a display default.
       The gate now runs BEFORE the server is allowed to touch the row: a slot
       that already holds a skill is a paid reroll (flat 300, or one ticket),
       so an empty purse refuses HERE, with the held skill untouched.
       Known trade: the one-time tier-3+ bonus reroll is server-side state this
       client cannot see, so a broke player sits out their free bonus until
       they hold 300. Stranding twenty rolls of history was worse. */
    var _held = null;
    try {
      var _heldView = (gearFor(cardKey) || {})[slot] || null;
      var _heldMeta = (gearMetaFor(cardKey) || {})[slot] || null;
      if (_heldView) {
        _held = { id:_heldView.id, r:_heldView.r | 0, pity:0 };
        if (_heldMeta && String(_heldMeta.id || '') === String(_held.id || '') &&
            (_heldMeta.r | 0) === (_held.r | 0)) _held.pity = Math.max(0, _heldMeta.pity | 0);
      }
    } catch (e) {}
    var _heldId = (_held && _held.id) || null;
    var _heldR = (_held && (_held.r | 0)) || 0;
    var _atomicJournal = null;
    if (payCrystal) {
      try { _atomicJournal = atomicReadSkillJournal(_rollScope, cardKey, slot); }
      catch (journalError) { return Promise.reject(journalError); }
    }
    if (_held) {
      if (payCrystal) {
        /* A Mythic Crystal is its own tender. Heart balance must never veto a
           crystal button that the wallet says is affordable. A saved request
           is a receipt replay, not a new purchase, so it never needs a second
           crystal in the mirror. */
        if (!_atomicJournal && (!window.HOHWallet || window.HOHWallet.crystals() < 1)) return Promise.reject(new Error('no crystals'));
      } else if (payTicket) {
        var _t0 = (typeof window.hohGetUnspentTickets === 'function') ? window.hohGetUnspentTickets() : 0;
        if (_t0 < 1) return Promise.reject(new Error('no tickets'));
      } else if (!canAfford(150)) {   /* R395: flat 150 */
        return Promise.reject(new Error('not enough hearts'));
      }
    }
    /* Held crystal rerolls never enter the legacy offer -> browser debit ->
       finalizer chain below. R603 commits rarity, pity/bonus, one conditional
       crystal debit, the concrete ability, and an immutable receipt in one
       database transaction. A missing R603 endpoint therefore fails closed;
       falling back would recreate the split-payment race this path removes. */
    if (payCrystal) {
      return atomicCrystalSkillRoll(_rollScope, cardKey, slot, heroLike, _held, _atomicJournal);
    }
    function restoreHeldOffer() {
      if (!_held || !_offerOpened || _offerFinalized) return Promise.resolve(false);
      if (_restoreOfferPromise) return _restoreOfferPromise;
      _restoreOfferPromise = rpc('hoh_keep_card_skill', {
        p_email: _rollScope.email, p_pin: _rollScope.pin, p_card_key: cardKey, p_slot: slot
      }).then(function (reply) {
        var restoredR = reply && reply.rarity !== null && reply.rarity !== '' ? Number(reply.rarity) : NaN;
        if (!reply || reply.ok !== true || reply.kept !== true ||
            String(reply.id || '') !== String(_heldId || '') || !isFinite(restoredR) ||
            Math.floor(restoredR) !== restoredR || restoredR !== _heldR) {
          throw new Error('The original ability could not be verified after recovery.');
        }
        _offerOpened = false;
        if (combatAccountIsCurrent(_rollScope)) gearCache = null;
        return true;
      }).catch(function (restoreError) {
        /* A PENDING predecessor is recoverable by gearHealPending on reopen.
           Never hide the original failure or pretend this second RPC landed. */
        if (combatAccountIsCurrent(_rollScope)) gearCache = null;
        throw restoreError;
      });
      return _restoreOfferPromise;
    }
    /* Quick review must never be allowed to "degrade" into any kind of
       reroll after its verified-empty read. The dedicated R560 endpoint
       takes the same server-side account/card lock, requires the slot row to
       still be absent, and only then delegates to the normal rarity roll.
       Failing closed when that endpoint is not installed is intentional: an
       older database may keep the ordinary card reroll UI, but it cannot
       safely power the one-tap first-roll queue. */
    var _rollRpc = freeOnly ? 'hoh_roll_first_card_skill' : 'roll_card_skill';
    return rpc(_rollRpc, { p_email: _rollScope.email, p_pin: _rollScope.pin, p_card_key: cardKey, p_slot: slot })
      .then(function (res) {
        var price = (res && res.price) || 0;
        _offerOpened = true;
        /* The database migration already makes held rarity an immutable
           floor. Validate the reply anyway so a mixed-version, corrupt, or
           replayed authority can never turn a crystal into a downgrade. Do
           this before any wallet is touched or concrete ability is chosen. */
        var _hasOfferedR = !!(res && Object.prototype.hasOwnProperty.call(res, 'rarity') &&
          res.rarity !== null && res.rarity !== '');
        var _offeredR = _hasOfferedR ? Number(res.rarity) : NaN;
        var _rarityCap = String(cardKey || '').indexOf('ff:') === 0 ? 5 : 4;
        var _badRarity = !_hasOfferedR || !isFinite(_offeredR) || Math.floor(_offeredR) !== _offeredR || _offeredR < 0 || _offeredR > _rarityCap;
        if (_held) {
          _badRarity = _badRarity || _offeredR < _heldR || _offeredR > Math.min(_rarityCap, _heldR + 1);
          if (res && res.floor != null) {
            var _floorN = Number(res.floor);
            _badRarity = _badRarity || !isFinite(_floorN) || Math.floor(_floorN) !== _floorN || _floorN !== _heldR;
          }
          if (res && res.tiered_up != null) _badRarity = _badRarity || (!!res.tiered_up !== (_offeredR > _heldR));
        }
        if (_badRarity) {
          return restoreHeldOffer().then(function () {
            throw new Error('Rarity downgrade protection refused an unverified ability result.');
          });
        }
        /* `roll_card_skill` has already placed PENDING on Account A. If A is
           no longer visible, never charge the current account. Restore A's
           held skill through the server's existing keep path and stop. A
           failed restore remains recoverable by gearHealPending on A's next
           visit; no client wallet is touched. */
        if (!combatAccountIsCurrent(_rollScope)) {
          /* A fresh offer has no predecessor for hoh_keep_card_skill to
             restore. Leave that exact rarity PENDING: the first-only RPC can
             replay it and the CAS finalizer can complete it when the player
             returns to the original account. */
          if (freeOnly) {
            gearCache = null;
            throw new Error('Account changed while the ability was saving. Its rarity is waiting safely on the original account; reopen Quick review there to finish it.');
          }
          return restoreHeldOffer().then(function () {
            throw new Error('Account changed while the ability was saving. No hearts or tickets were taken from the other account.');
          });
        }
        /* Defense in depth for mixed-version deployments: Quick review only
           accepts a newly issued, genuinely free first roll. A replayed
           PENDING offer, a consumed tier-3 bonus, or a paid offer is restored
           before the browser chooses an ability id. This also prevents a
           second device from changing a just-rolled slot at zero Heart cost. */
        var _freshReplay = !!(res && res.first_only && res.resumed && res.replayed);
        var _newFresh = !!(res && res.first_only && !res.resumed && !res.replayed);
        if (freeOnly && (!res || !res.free || !!res.bonus || price !== 0 || (!_freshReplay && !_newFresh))) {
          /* Only a reroll offer has a predecessor to keep. A malformed fresh
             response must remain PENDING and recoverable, never be mistaken
             for something the legacy Keep helper can undo. */
          var _undo = (res && res.prev_id) ? rpc('hoh_keep_card_skill', {
            p_email: _rollScope.email, p_pin: _rollScope.pin, p_card_key: cardKey, p_slot: slot
          }).catch(function () {}) : Promise.resolve();
          return _undo.then(function () {
            if (combatAccountIsCurrent(_rollScope)) gearCache = null;
            throw new Error('That first roll could not be verified. No Hearts were spent; reopen Quick review to recover it safely.');
          });
        }
        var paymentReady = Promise.resolve();
        if (price > 0) {
          if (payTicket) {
            if (!chargeAlt()) throw new Error('no tickets');
            _chargedTickets = 1;
          } else {
            if (!canAfford(price)) throw new Error('not enough hearts');
            bankApply(-price, 'spend_skill_roll');
            _chargedHearts = price;
          }
        }
        return paymentReady.then(function () {
        var E = window.HOHContract;
        var skillId = (slot === 'passive') ? pickPassiveId(E, heroLike, res.rarity, _rollAvoid) : pickActiveId(E, heroLike, res.rarity, randHex(), _rollAvoid);
        /* R527: forward the ceremony's facts. The installed roll_card_skill
           already answers floor, tiered_up, pity, pity_cap and pity_forced;
           the fallbacks below only cover a server that predates them, and
           they derive from the held rarity this client just read, so the
           ceremony can never invent a tier-up the server did not grant. */
        var okResult = { id: skillId, rarity: res.rarity, price: price, free: !!res.free, bonus: !!res.bonus,
          floor: (res.floor == null ? _heldR : (res.floor | 0)),
          tiered_up: (res.tiered_up == null ? ((res.rarity | 0) > _heldR) : !!res.tiered_up),
          pity: res.pity | 0,
          pity_cap: (res.pity_cap == null ? pityCapFor(res.rarity | 0) : (res.pity_cap | 0)),
          pity_forced: !!res.pity_forced };
        /* R392 ROOT CAUSE, HALF TWO. The server whitelist (R347) was cut at
           v13 and never learned the six v19 ids — keeneye, songblade,
           aegisbearer, springwell, resurgence, grimledger. Every roll that
           landed one died server-side with 'bad skill', stranding PENDING and
           resetting the slot, exactly like the v13 cut R347's own header
           documents. R392's SQL extends the list; until Jan runs it, a refused
           id RETRIES ONCE with a fallback from the same pool and rarity drawn
           from the v13-era list the server provably accepts, so a paid roll
           always lands a real skill either way. */
        var SAFE_ACTIVE = ATOMIC_SAFE_ACTIVE;
        var SAFE_PASSIVE = { bloodthirst:1,adrenaline:1,vengeance:1,ironresolve:1,stoneskin:1,
          clarity:1,lastword:1,phoenix:1,warden:1,focus:1 };
        function safeFallbackId() {
          /* R616: this path is the census's 100%. It must never be taken
             again in normal play; if it is, one flat line names it. */
          _fallbackHits++;
          try { console.warn('[battlegrounds] R616 FALLBACK REACHED', slot, res && res.rarity, skillId); } catch (eFb) {}
          var safe = (slot === 'passive') ? SAFE_PASSIVE : SAFE_ACTIVE;
          if (slot === 'passive') {
            var band = Math.max(0, Math.min(5, res.rarity | 0));
            var cand = (PASSIVE_BANDS[band] || []).filter(function (id) { return safe[id] && String(id) !== String(_heldId || ''); });
            return cand.length ? cand[Math.floor(Math.random() * cand.length)] : null;
          }
          var sk0 = E.content.SKILLS[skillId] || {};
          var pool = sk0.p || 'attack';
          var rr = res.rarity | 0;
          var cands = Object.keys(E.content.SKILLS).filter(function (id) {
            var sk = E.content.SKILLS[id];
            return safe[id] && sk && sk.p === pool && sk.r === rr && String(id) !== String(_heldId || '');
          });
          return cands.length ? cands[Math.floor(Math.random() * cands.length)] : null;
        }
        function trySet(idToSet, attempt) {
          var _setRpc = freeOnly ? 'hoh_set_first_card_skill' : 'set_card_skill_id';
          return rpc(_setRpc, {
            p_email: _rollScope.email, p_pin: _rollScope.pin, p_card_key: cardKey, p_slot: slot, p_skill_id: idToSet
          }).then(function (setReply) {
            /* The queue finalizer is compare-and-set. If another device
               finished this exact fresh offer first, its stable winner comes
               back here and is what the ceremony and confirm-read must show. */
            if (freeOnly) {
              if (!setReply || setReply.ok !== true || !(setReply.skill_id || setReply.id)) throw new Error('first roll did not finalize');
              okResult.id = String(setReply.skill_id || setReply.id);
              if (setReply.skill_rarity != null || setReply.rarity != null) okResult.rarity = (setReply.skill_rarity == null ? setReply.rarity : setReply.skill_rarity) | 0;
            }
            return setReply;
          }).catch(function (eSet) {
            if (!REGISTRY_ROLL_LIVE && attempt === 0 && !safeSame(idToSet)) {
              var fb = safeFallbackId();
              if (!fb) throw eSet;
              try { console.warn('[battlegrounds] roll id refused, retrying with', fb, eSet && eSet.message); } catch (e3) {}
              okResult.id = fb;
              return trySet(fb, 1);
            }
            throw eSet;
          });
        }
        function safeSame(id) {
          var safe = (slot === 'passive') ? SAFE_PASSIVE : SAFE_ACTIVE;
          return !!safe[id];
        }
        return trySet(skillId, 0).then(function () {
          _offerFinalized = true;
          if (combatAccountIsCurrent(_rollScope)) gearCache = null;
          /* R338 (Jan): rpc() only throws when the DB itself returns an error, so
             a write that comes back "ok" but never actually persisted would keep
             the hearts and silently drop the reroll — exactly the reported bug.
             Read the gear straight back and confirm the new skill is really on
             the slot (a stuck 'PENDING' row counts as "did not land"). If it is
             not there, refund and surface it: a reroll that did not save must
             never cost hearts. */
          return rpc('get_card_skills', { p_email: _rollScope.email, p_pin: _rollScope.pin }).then(function (rows) {
            var confirmedRows = rows || [];
            if (combatAccountIsCurrent(_rollScope)) gearCache = confirmedRows;
            /* R392: compare against the id that was actually WRITTEN — the
               safe-retry may have swapped skillId for its fallback. */
            var landed = confirmedRows.some(function (r) {
              var confirmedR = r && r.skill_rarity !== null && r.skill_rarity !== '' ? Number(r.skill_rarity) : NaN;
              return r && r.card_key === cardKey && r.slot === slot &&
                String(r.skill_id) === String(okResult.id) && isFinite(confirmedR) &&
                Math.floor(confirmedR) === confirmedR && confirmedR === Number(okResult.rarity);
            });
            if (!landed) {
              refundRollPayment();
              throw new Error(price > 0
                ? 'That reroll did not save. Your payment is being reconciled; refresh the card before trying again.'
                : 'That reroll did not save. Please try again.');
            }
            try { document.dispatchEvent(new CustomEvent('hoh:gear-changed', { detail:{ cardKey:cardKey, slot:slot } })); } catch (e4) {}
            return okResult;
          }, function () {
            /* The confirm-read itself failed (a network blip). The write returned
               without error, so assume it landed rather than claw back a good
               reroll — the UI force-refetches gear right after and will show the
               real state on the next paint. */
            return okResult;
          });
        }).catch(function (e2) {
          /* R318 + R338: the roll was paid but the skill never landed — give the
             price back. Idempotent, so an already-refunded "did not save" above
             won't double-refund here. */
          refundRollPayment();
          throw e2;
        });
        });
      })
      .catch(function (e3) {
        /* Includes an initial roll RPC failure, before the inner save/confirm
           catch exists. Any payment already taken still belongs to the
           initiating account and is either returned now or held for it. */
        return restoreHeldOffer().then(function () {
          refundRollPayment();
          throw e3;
        });
      });
  }

  /* ---------------- R395 (Jan): THE SIMPLE REROLL ----------------
     The offer sheet is gone. A paid reroll rolls, the reel lands, the result
     is the result — flat 150 hearts, first roll still free, pity and the
     band law still live underneath (server side, R394 SQL). What survives
     from R394 here: the meter readers the panel paints, and a healer that
     quietly resolves any offer stranded open by the brief sheet era — the
     held skill comes back through hoh_keep_card_skill, nothing lost. */
  function gearRowFor(cardKey, slot) {
    if (signedIn() || !localMode()) useCombatCacheAccount();
    var row = null;
    (gearCache || []).forEach(function (r) {
      if (r && r.card_key === cardKey && r.slot === slot) row = r;
    });
    return row;
  }
  function gearMetaFor(cardKey) {
    var out = { active: null, passive: null };
    ['active', 'passive'].forEach(function (sl) {
      var r = gearRowFor(cardKey, sl);
      if (r) out[sl] = {
        id: r.skill_id, r: r.skill_rarity,
        prevId: r.prev_skill_id || null,
        prevR: (r.prev_rarity == null ? null : r.prev_rarity),
        price: Math.max(0, r.price_paid | 0),
        pity: r.pity_count | 0
      };
    });
    return out;
  }
  /* R687: adjacent upgrade chances. Normal cards use 45/35/25/3/0.05%;
     FramedFantasy cards use 55/45/35/5/0.5%. Tickets remain the existing
     relative multiplier, never flat percentage points. */
  function abilityTierBasePct(rarity, isFF) {
    rarity = Math.max(0, Math.min(5, rarity | 0));
    if (rarity === 0) return isFF ? 55 : 45;
    if (rarity === 1) return isFF ? 45 : 35;
    if (rarity === 2) return isFF ? 35 : 25;
    if (rarity === 3) return isFF ? 5 : 3;
    if (rarity === 4) return isFF ? 0.5 : 0.05;
    return 0;
  }
  function abilityTierChancePct(rarity, tickets, isFF) {
    var base = abilityTierBasePct(rarity, isFF);
    var boost = 1 + Math.min(50, Math.max(0, tickets | 0)) * 0.02;
    return Math.round(base * boost * 1000) / 1000;
  }
  /* Hard guarantees keep the published Red Cube 25/83 cadence for the
     corresponding Rare→Epic and Epic→Legendary steps. Mythic has no hard pity. */
  function pityCapFor(rarity) {
    return rarity === 0 ? 10 : rarity === 1 ? 16 : rarity === 2 ? 25
         : rarity === 3 ? 83 : 0;
  }
  /* R395: restore every offer the retired sheet left open. A PENDING row
     with a prev behind it means the player paid, saw the sheet era end, and
     chose nothing — keep gives them back exactly what they had. Rows heal
     one by one; a network miss just leaves them for the next visit. Returns
     a promise resolving true when anything was restored. */
  function gearHealPending(cardKey) {
    try { localStorage.removeItem('hoh-gear-choice-v1'); } catch (e) {}
    if (!signedIn()) return Promise.resolve(false);
    var healScope = captureCombatAccount();
    var rows = (gearCache || []).filter(function (r) {
      return r && r.skill_id === 'PENDING' && r.prev_skill_id
        && (!cardKey || r.card_key === cardKey);
    });
    if (!rows.length) return Promise.resolve(false);
    var chain = Promise.resolve();
    rows.forEach(function (r) {
      chain = chain.then(function () {
        return rpc('hoh_keep_card_skill', {
          p_email: healScope.email, p_pin: healScope.pin, p_card_key: r.card_key, p_slot: r.slot
        }).then(function (reply) {
          var restoredR = reply && reply.rarity !== null && reply.rarity !== '' ? Number(reply.rarity) : NaN;
          var expectedR = r.prev_rarity == null ? 0 : Number(r.prev_rarity);
          if (!reply || reply.ok !== true || reply.kept !== true ||
              String(reply.id || '') !== String(r.prev_skill_id || '') || !isFinite(restoredR) ||
              Math.floor(restoredR) !== restoredR || restoredR !== expectedR) {
            throw new Error('A pending ability could not be restored exactly.');
          }
        });
      });
    });
    return chain.then(function () {
      if (!combatAccountIsCurrent(healScope)) return true;
      gearCache = null;
      return loadGear(true).then(function () { return true; });
    });
  }

  /* R322: the Muster is a loadout. Re-freeze the run's gear and turn
     order server-side (contract_update_loadout re-reads card_skills by key
     and permutes the frozen roster), so the playback, the tape and the
     settle worker all judge the same party. keys = the card behind each
     CURRENT slot; order = 1-based permutation of the current slots.
     Local (god sandbox) mode rebuilds the snapshot from the same keys in
     the new order -- localSnapshot reads live gear, so both paths agree. */
  function updateLoadout(run, keys, order) {
    if (localMode()) {
      /* refresh the gear cache FIRST: rollSlot nulls it after every roll, and
         localSnapshot reads it synchronously — without this a reroll followed
         by a reorder would rebuild the party with empty gear. */
      var pr = window.__hohLocalRun || run || {};
      var newKeys = order.map(function (o) { return keys[o - 1]; });
      return loadGear().then(function () {
        pr.roster = localSnapshot(newKeys);
        window.__hohLocalRun = pr;
        return pr;
      });
    }
    if (!signedIn()) return Promise.reject(new Error('sign in first'));
    var loadoutScope = captureCombatAccount();
    var loadoutRun = String(run && run.id || '');
    if (!loadoutRun) return Promise.reject(new Error('no such run'));
    return rpcDeadline(rpc('contract_update_loadout', {
      p_email: loadoutScope.email, p_pin: loadoutScope.pin,
      p_run_id: run.id, p_keys: keys, p_order: order
    }), 12000, 'That party change took too long. Check your connection and try again.')
      .then(function (saved) {
        if (!combatAccountIsCurrent(loadoutScope)) {
          throw accountChangedError('the party change was saving', true);
        }
        if (!saved || !Array.isArray(saved.roster)) {
          throw new Error('The party change was not confirmed. Try again.');
        }
        if (saved.id != null && String(saved.id) !== loadoutRun) {
          throw new Error('The Hall answered for a different run. Reopen Battlegrounds.');
        }
        return saved;
      });
  }

  /* R325: stamp the carried health onto a continued run. The parent's final
     health was written by the settle worker; this copies it forward under the
     account's own credentials. Local runs already carry it in continueRun. */
  function applyCarry(run, scope, strict) {
    if (!run || !run.id) return Promise.resolve(run);
    if (localMode()) return Promise.resolve(window.__hohLocalRun || run);
    scope = scope || captureCombatAccount();
    if (!scope.email || scope.pin == null || String(scope.pin).length < 1) {
      return strict ? Promise.reject(new Error('Sign in again before carrying your health forward.')) : Promise.resolve(run);
    }
    /* R363: carry_a is stamped on the ROW and the worker opens the fight on
       it, so a failed write is not a missed kindness, it is the client and the
       judge starting from different health. contract_apply_carry is idempotent
       (it stamps the same number), so one retry is free. If both attempts
       fail the client keeps the row it already has, which is the same row the
       worker will read, so the two still agree. */
    function _carry() {
      return rpc('contract_apply_carry', {
        p_email: scope.email, p_pin: scope.pin, p_run_id: run.id
      });
    }
    return _carry()
      .catch(function () { return _carry(); })
      .then(function (r) {
        if (!combatAccountIsCurrent(scope)) {
          throw accountChangedError('your health was carrying forward', true);
        }
        return r || run;
      })
      .catch(function (err) { if (strict) throw err; return run; });
  }

  /* ---------- runs ---------- */
  function startContract(stake, rosterKeys, opts) {
    opts = opts || {};
    if (localMode() && !opts._accountScope) {
      if (stake > 0 && !canAfford(stake)) return Promise.reject(new Error('not enough hearts'));
      var lrun = { id: 'local-' + Date.now(), day: today(), seed: localSeed(), stake: stake, pot: localPot(stake),
        foe: { scale: localScale(stake) }, engine_ver: (window.HOHContract && window.HOHContract.ENGINE_VERSION) || 1,
        status: 'open', depth: 0, roster: localSnapshot(rosterKeys) };
      window.__hohLocalRun = lrun;
      if (stake > 0) bankApply(-stake, 'spend_contract_stake');   /* no stampPlayed in the sandbox: replay freely */
      return Promise.resolve(lrun);
    }
    /* Freeze account + PIN before the asynchronous Battleground Ticket spend.
       Every fallback and retry in this one start transaction keeps the same
       credentials; a late A callback can neither open a B run nor touch B's
       wallet/local run markers. */
    var startScope = opts._accountScope || captureCombatAccount();
    if (!startScope.email || !startScope.pin) return Promise.reject(new Error('sign in first'));
    if (stake > 0 && !canAfford(stake)) return Promise.reject(new Error('not enough hearts'));
    var rememberedBeforeStart = (opts._lastRunId != null) ? opts._lastRunId : lastRunId();
    function giveBackTicket(err) {
      try { queueBgRefund(startScope); }
      catch (refundErr) {
        refundErr.original_error = err && err.message ? String(err.message) : '';
        throw refundErr;
      }
      throw err;
    }
    function finishStart(run, reason, played) {
      queueRunMarker(startScope, run && run.id, played);
      if (stake > 0) accountHeart(startScope, -stake, reason);
      if (!combatAccountIsCurrent(startScope)) {
        var changed = accountChangedError('the Battlegrounds run was opening', true);
        changed.run = run;
        throw changed;
      }
      return run;
    }
    /* R376: one server Battleground Ticket opens every run. `hohBgSpend`
       captures the current credentials when invoked; prepaid retries never
       invoke it again. */
    /* Verify the live judge before the ticket transaction. A stale tab must
       learn that it needs a reload before it spends or opens anything. */
    return workerGate(true).then(function () {
      return opts._prepaid ? Promise.resolve({ ok: true }) : spendBgTicket(startScope);
    }).then(function (_sp) {
      if (!_sp || !_sp.ok) {
        /* ============ R519 · THE REFUND HEALER STOPS MINTING ============
           R380's reading was "the refund is ledger guarded server side (only
           an unmatched spend inside 15 minutes pays), so firing it here is a
           free heal when the network ate a ticket and a no op otherwise."
           The live hoh_bg_refund body disproved the no-op half: it only
           counts spends minus refunds in the window, and the ticket that
           OPENED the run the player just lost is a countable spend. So a
           broke player's failed retry refunded the previous run's entry, the
           label flipped to "1 entry", and the game restarted for free,
           forever (Jan: "you can technically play on forever, even if you
           dont have enough hearts").
           The two failures are now told apart. "no_tickets" is the server
           ANSWERING that nothing was spent, so there is nothing to heal and
           no refund is queued. Only an UNANSWERED spend (network death,
           where a decrement may have landed unseen) keeps the safety net. */
        if (_sp && _sp.answered && _sp.reason === 'no_tickets') {
          throw new Error('no battle ticket');
        }
        giveBackTicket(new Error('no battle ticket'));
      }
      if (opts.rematch) {
        return rpc('contract_rematch', {
          p_email: startScope.email, p_pin: startScope.pin, p_stake: stake, p_roster: rosterKeys
        }).then(function (run) {
          return finishStart(run, 'spend_contract_stake', false);
        }).catch(function (err) {
          if (err && err.__hohCommitted) throw err;
          /* A stale device stamp can route a genuine first run through the
             rematch door. Retry prepaid against the SAME frozen account. */
          if (/no battle today/i.test(String(err && err.message))) {
            if (combatAccountIsCurrent(startScope)) { try { localStorage.removeItem(DAY_KEY); } catch (_) {} }
            return startContract(stake, rosterKeys, {
              _prepaid: true, _accountScope: startScope, _lastRunId: rememberedBeforeStart
            });
          }
          return giveBackTicket(err);
        });
      }
      return rpc('contract_start', {
        p_email: startScope.email, p_pin: startScope.pin, p_stake: stake, p_roster: rosterKeys
      }).then(function (run) {
        return finishStart(run, 'spend_contract_stake', true);
      }).catch(function (err) {
        if (err && err.__hohCommitted) throw err;
        /* R301 REPLAY: the day's first contract is server-unique, so a paid
           rematch lands here with an "already fought" rejection.
           R320: route it to contract_rematch — a FRESH run built from the
           party just picked at the gate. The old contract_continue fallback
           only accepted a WON run (the diagnostics proved it throws
           "no winnable run" after a loss) and silently fought the OLD
           snapshot instead of the new picks. It stays only as a safety net
           for a database that has not run R320 yet. */
        var msg = String((err && err.message) || '');
        if (!/already|duplicate|unique|daily/i.test(msg)){
          return giveBackTicket(err);
        }
        /* R376: the server calls this a rematch (second device, cleared cache).
           The Battlegrounds Ticket is already spent for THIS run, so the
           reroute rides prepaid. One ticket, one run, either door. */
        return rpc('contract_rematch', {
          p_email: startScope.email, p_pin: startScope.pin, p_stake: stake, p_roster: rosterKeys
        })
          .then(function (run) {
            return finishStart(run, 'spend_contract_rematch', false);
          })
          /* the ticket comes back on every exit that does NOT open a run.
             Refunding earlier would also refund the legacy fallback below,
             which DOES open one — that would be a free extra run. */
          .catch(function (err2) {
            /* the ticket comes back on every exit that does NOT open a run.
               Refunding before this point would also refund the legacy
               fallback below, which DOES open one — a free extra run. */
            function giveBack(e) {
              if (e && e.__hohCommitted) throw e;
              return giveBackTicket(e);
            }
            /* only fall through when the RPC itself is missing (pre-R320 DB);
               a real rejection (wrong price, no battle today) surfaces. */
            if (!rpcDefinitelyMissing(err2, 'contract_rematch')) giveBack(err2);
            var last = rememberedBeforeStart;
            if (!last) giveBack(err2);
            return rpc('contract_continue', { p_email: startScope.email, p_pin: startScope.pin, p_run_id: last })
              .then(function (run) {
                return finishStart(run, 'spend_contract_rematch', false);
              })
              .catch(giveBack);
          });
      });
    });
  }
  /* v20 THE RECRUIT RUN: continuing is FREE — the price is the wall.
     The engine ramps difficulty from run.depth (LAWS.RUN_RAMP) so the old
     +0.08 scale nudge is retired; the base scale rides unchanged and both
     judge and client add the same ramp. Recruits live ON THE RUN ROW so
     the worker re-simulates the identical fight. */
  /* v38 THE BUY-BACK. The hearts are charged by the caller through bankApply,
     exactly like the stake, the gear reroll and the card XP grant before it —
     this wallet has always been client side. What this RPC buys is PERMISSION:
     it increments the count on the run row, and the worker refuses any Second
     Wind past the number that row has paid for. So the mechanical effect is
     bounded server side even though the price is not.
     In the sandbox there is no row to write, so it resolves and god mode plays
     on; the run bar already says nothing there is real. */
  function refreshCard(runId) {
    if (localMode()) {
      var lr = window.__hohLocalRun || {};
      lr.cards_refreshed = (lr.cards_refreshed | 0) + 1;
      return Promise.resolve({ ok: true, cards_refreshed: lr.cards_refreshed, local: true });
    }
    if (!signedIn()) return Promise.reject(new Error('sign in'));
    if (!runId) return Promise.reject(new Error('no such run'));
    return rpc('hoh_contract_refresh_card', { p_email: email(), p_pin: pin(), p_run_id: runId });
  }
  /* ================================================================== */
  /* v39: THE LADDER                                                     */
  /* ================================================================== */
  /* Four calls, and between them they are the entire climb. Everything that
     pays or advances happens in the worker; these only read the state and ask
     to stand on a rung. Deliberately thin: the temptation with a feature this
     size is to keep a mirror of the climb in the browser and reconcile it, and
     a mirror is exactly what made the road's hand, day stamp and loss lock all
     per-device and all subtly wrong. There is one copy of the climb and it is
     not here. */
  /* The ladder cache is credential-scoped. A single module-global value let a
     late Account A read repaint Account B's climb after an A -> B switch. The
     sequence also prevents an older forced refresh from winning over a newer
     one for the same account. */
  var _ladCache = null;
  var _ladSeq = 0;
  function ladderCacheKey(scope) {
    return String(scope && scope.account || '') + '|' +
      String(scope && scope.generation || 0) + '|' + claimHash(scope && scope.pin || '');
  }

  /* the climb, as the server sees it. Cached only for the length of one
     screen paint — anything that changes the climb clears it. */
  /* R450b: every sandbox mirror walks through this one accessor. The old
     `window.__hohLocalLadder || {}` pattern wrote onto a DETACHED object
     whenever the ladder had not been read yet — an admin jump before the
     first ladderState landed on a ghost and vanished. */
  function localLad() {
    return window.__hohLocalLadder || (window.__hohLocalLadder = {
      ok: true, stage: 1, best_stage: 0, hp: 0, max_hp: 0, live: false,
      party: [], items: { mend: 1, bolt: 1, ally: 1 }, kind: 'fight',
      climbs: 0, retries: 0, wins: 0, retry_hearts: 0, recruits: []
    });
  }
  function ladderState(force) {
    if (localMode()) {
      var L = localLad();
      L.kind = 'fight';
      try {
        window.__hohBgClimb = { account: collectionAccount(), live: !!L.live, run_id: L.run_id || null, run_status: L.run_status || null, stage: L.stage | 0, best_stage: L.best_stage | 0 };
        document.dispatchEvent(new CustomEvent('hoh:bg-climb-changed'));
      } catch (_) {}
      try {
        var E9 = _E(), _savedLocalRun = window.__hohLocalRun || {};
        var _savedLocalEngine = (_savedLocalRun.engine_ver | 0) || ((E9 && E9.ENGINE_VERSION) | 0);
        if (E9 && E9.ladderKind) L.kind = E9.ladderKind(L.stage | 0 || 1, _savedLocalEngine);
      } catch (e) {}
      return Promise.resolve(L);
    }
    if (!signedIn()) return Promise.reject(new Error('sign in first'));
    var ladderScope = captureCombatAccount();
    var cacheKey = ladderCacheKey(ladderScope);
    if (!force && _ladCache && _ladCache.key === cacheKey) return Promise.resolve(_ladCache.value);
    var requestSeq = ++_ladSeq;
    return rpc('hoh_bg_ladder_state', { p_email: ladderScope.email, p_pin: ladderScope.pin })
      .then(function (r) {
        if (requestSeq !== _ladSeq || !combatAccountIsCurrent(ladderScope)) {
          throw accountChangedError('the climb was loading', false);
        }
        _ladCache = { key: cacheKey, value: r || null };
        /* ============ R466 · THE HALL HAS TO KNOW A CLIMB IS WAITING ========
           The Hall's Battlegrounds door decided everything from the ENTRY
           WALLET alone, so a player who paused at stage 24 with an empty
           wallet was locked out of a run he had already paid for. Resuming
           costs nothing (ladderFight passes _prepaid on a live row), but no
           button in the Hall would take him back.

           The answer is already here and nowhere else, so it is published
           here: one synchronous mirror plus one event, no new RPC, no SQL, no
           worker paste. Everything downstream reads the mirror. */
        try {
          window.__hohBgClimb = {
            account: ladderScope.account,
            live: !!(r && r.ok === true && r.live),
            run_id: (r && r.run_id != null) ? r.run_id : null,
            run_status: (r && r.run_status) || null,
            stage: (r && r.stage) | 0,
            best_stage: (r && r.best_stage) | 0
          };
          document.dispatchEvent(new CustomEvent('hoh:bg-climb-changed'));
        } catch (_) {}
        return r;
      });
  }
  /* ============ R468 · FORGETTING THE LADDER FORGETS THE DOOR ============
     Jan: "when you give up and take the 20 or whatever hearts, when i go back
     to battlegrounds, it still says no entry needed and resume your climb,
     which is not correct, it should take 1 entry to retry stage 69."

     He is right, and it was one line short. R466 published a synchronous
     mirror (window.__hohBgClimb) so the Hall could tell a saved climb from an
     empty wallet, and it is written in exactly one place: a fresh ladderState
     read. ladderForget cleared the RPC cache and left the mirror alone, so
     after a give-up (or a loss, or any other ending) the Hall kept painting
     "Resume your climb, no entry needed" for a climb the server had already
     closed. The one-shot __hohBgClimbAsked latch then made sure it never
     asked again.

     Clearing all three together means any ending invalidates the door, and the
     door re-asks the next time it paints. */
  function ladderForget() {
    _ladCache = null;
    _ladSeq++;
    try {
      window.__hohBgClimb = null;
      window.__hohBgClimbAsked = 0;
      document.dispatchEvent(new CustomEvent('hoh:bg-climb-changed'));
    } catch (_) {}
  }

  /* ADOPT a run the ordinary opener just created. contract_start is what
     checks ownership, burns the own: copies, folds in gear and level and
     freezes the five — months of debugging this file has no business
     reimplementing — so a climb opens a run exactly the way the road always
     did and then hands the row to the ladder, which stamps the rung, swaps in
     the rung's SAVED seed so a retry meets the enemies it lost to, and writes
     the carried health.
     The server decides whether standing up costs anything. R418 (Jan): it
     answers retry_hearts 0 now — the ticket is the whole price of a retry —
     so the debit below is dormant. It stays because the wiring is correct
     (charge only what the server names, only when the stand-up was real) and
     a future priced mode reuses it without a client change. */
  function ladderAdopt(runId) {
    if (localMode()) {
      var L = localLad();
      /* R456 sandbox parity with sql/R456 (Jan: "forget letting 2 party
         members rejoin after a loss, just add X amount of party members to
         your party based on the stage ... stage 32 then 3, stage 20 then 2,
         stage 12 then its 1").
         THE HALL ANSWERS: floor(stage / 10) capped at three, drawn from real
         Hall heroes so they arrive with real names and real portraits, and
         NAMED on the stand-up sheet before the fight — which is how this
         honours game-design law 9 rather than breaking it. The R419 starters
         it replaces were synthetic 'THE HALL'S <CLASS>' rows that arrived
         unannounced with no face; that is what Jan called a bug in R454, and
         none of it is true here.
         SEEDED, NEVER Math.random(): the sandbox has to be able to replay a
         stand-up and mint the same bodies, exactly like the SQL's
         md5(seed || climbs || id) ordering. */
      var chg = !L.live;
      var _joined = [];
      /* R456: PIN THE SEED BEFORE ANYTHING READS IT. This assignment used to
         sit BELOW the mint (it is the `L.stage_seed = L.stage_seed || ...`
         line further down), so the salt read a stage_seed that was still
         null after an adminJump and fell through to localSeed() — a fresh
         Math.random() value that was then THROWN AWAY, because the line
         below minted a second, unrelated one to store. Two consequences:
         the same jump-and-stand-up dealt different heroes every time, and
         the deal could not be recomputed from the run state afterwards,
         which is the exact parity with the SQL's md5(v_seed || ...) that
         the comment below claims. The fallback chain now mirrors the SQL's
         own `coalesce(v_row.stage_seed, v_run.seed)`. */
      L.stage_seed = L.stage_seed || ((window.__hohLocalRun || {}).seed) || localSeed();
      if (chg) {
        var _kept = (L.recruits || []).filter(function (r) { return r && r.kept === true; });
        L.recruits = _kept;
        var _want = Math.min(3, Math.max(0, Math.floor((L.stage | 0) / 10)));
        if (_want > 0) {
          var _all = [];
          try { _all = (typeof window.hohGetAllCharacters === 'function' ? window.hohGetAllCharacters() : []) || []; } catch (_) {}
          /* R456 BUG CAUGHT IN THE HARNESS: the Hall sent Aldous while Aldous
             was standing in the party — the player's own crowned champion
             arriving as a stranger on their own bench. A body may be on the
             board or on the bench, never both. Filtered by id AND by name,
             because a stored bench row carries `cid` but a party snapshot
             may only carry a name. */
          var _busy = {};
          try {
            /* THE ROSTER, NOT L.party. ladderAdopt runs BEFORE the local
               run's snapshot is copied onto L.party (that happens in
               ladderStart, data.js ~1294), so reading L.party here found an
               empty array and the exclusion did nothing — it passed the
               harness only by luck of the draw. __hohLocalRun.roster is
               written by startContract, which the adopt path always calls
               first, and it is the same snapshot the engine fights with.
               Both fall back on each other so neither ordering can break it.
               Name is the key: localSnapshot rows carry no id, and stored
               bench rows carry cid but not always a name match for it. */
            ((((window.__hohLocalRun || {}).roster) || L.party || [])
              .concat(L.recruits || [])).forEach(function (h) {
              if (!h) return;
              if (h.cid) _busy['i:' + String(h.cid)] = 1;
              if (h.name) _busy['n:' + String(h.name).toLowerCase()] = 1;
            });
          } catch (_) {}
          _all = _all.filter(function (c) {
            if (!c || !(c.image_thumb_url || c.image_url)) return false;
            if (_busy['i:' + String(c.id || '')]) return false;
            if (_busy['n:' + String(c.name || '').toLowerCase()]) return false;
            return true;
          });
          if (_all.length) {
            /* the same murmur-style mix the ally picker uses, salted with the
               seed and the climb count so a replay lands on the same heroes */
            var _salt = String(L.stage_seed) + ':hall:' + (L.climbs | 0);
            var _hh = 1779033703;
            for (var _si = 0; _si < _salt.length; _si++) {
              _hh = Math.imul(_hh ^ _salt.charCodeAt(_si), 3432918353);
              _hh = (_hh << 13) | (_hh >>> 19);
            }
            _hh = Math.imul(_hh ^ (_hh >>> 16), 2246822507);
            _hh = Math.imul(_hh ^ (_hh >>> 13), 3266489909);
            _hh = (_hh ^= _hh >>> 16) >>> 0;
            var _seen = {}, _take = Math.min(_want, _all.length);
            for (var _k = 0; _k < _take; _k++) {
              var _at = (_hh + _k * 7919) % _all.length;
              /* walk to the next free index rather than dealing the same hero
                 twice; bounded by the pool size so it can never spin */
              var _guard = 0;
              while (_seen[_at] && _guard++ < _all.length) _at = (_at + 1) % _all.length;
              if (_seen[_at]) break;
              _seen[_at] = 1;
              var _c = _all[_at];
              _joined.push({
                name: String(_c.name || 'A hero'),
                cls: String(_c.character_class || _c.cls || 'Fighter').split(' ')[0],
                rar: Math.min(4, Math.max(1, Math.floor((L.stage | 0) / 8))),
                lvl: Math.min(100, 1 + (Math.max(1, L.stage | 0) - 1) * 2),
                hearts: _c.hearts | 0,
                img: _c.image_thumb_url || _c.image_url || '',
                cid: String(_c.id || ''),
                hall: true
              });
            }
            var _adopted = addPersistentBench(L.recruits, _joined,
              (window.__hohLocalRun || {}).engine_ver);
            L.recruits = _adopted.list;
            _joined = _joined.slice(0, _adopted.added);
          }
        }
        /* the stash is spent by the stand-up now, exactly as the SQL does it */
        L.fallen = [];
      }
      /* R420 parity: the ticket refills the bag in the sandbox too — each
         stack to at least one, bigger saved stacks kept. */
      if (chg) {
        L.items = L.items || {};
        L.items.mend = Math.max(1, L.items.mend | 0);
        L.items.bolt = Math.max(1, L.items.bolt | 0);
        L.items.ally = Math.max(1, L.items.ally | 0);
      }
      /* R491: a promoted recruit owns one run-only seat. A player may change
         the owned cards around it between stages, but neither a reload nor a
         new selection may replace that fixed body. The live SQL applies the
         same merge to contract_runs.roster before the next fight is judged. */
      var _lr491 = window.__hohLocalRun || {};
      var _in491 = _lr491.roster || [];
      L.party = chg ? slotParty(_in491) : mergePromotedParty(L.party || [], _in491);
      _lr491.roster = L.party.slice();
      L.live = true; L.run_id = runId;   /* stage_seed was pinned above, before the mint read it */
      if (chg) L.climbs = (L.climbs | 0) + 1;
      return Promise.resolve({ ok: true, resumed: false, stage: L.stage | 0 || 1, run_id: runId,
                               seed: L.stage_seed, hp: L.hp | 0, charged: chg, retry: false,
                               recruits: (L.recruits || []).slice(),
                               party: L.party.slice(),
                               joined: _joined.slice() });
    }
    var adoptScope = captureCombatAccount();
    return rpc('hoh_bg_ladder_adopt', { p_email: adoptScope.email, p_pin: adoptScope.pin, p_run_id: runId })
      .then(function (r) {
        if (combatAccountIsCurrent(adoptScope)) ladderForget();
        if (!r || r.ok !== true) {
          /* The UI can recover from a party_too_big refusal by trimming to
             the server's exact `allowed` count. Preserve those structured
             fields on the human-readable Error instead of flattening the
             reply into copy and forcing the client to guess again. */
          var gateErr = new Error(ladderGateMessage(r));
          gateErr.reason = (r && r.reason) || '';
          if (r && r.allowed != null) gateErr.allowed = r.allowed | 0;
          throw gateErr;
        }
        if (r.retry && r.retry_hearts > 0) accountHeart(adoptScope, -(r.retry_hearts | 0), 'spend_contract_retry');
        if (!combatAccountIsCurrent(adoptScope)) throw accountChangedError('the climb was resuming', true);
        return r;
      });
  }

  /* R419/R450b: court one fallen foe after an ordinary rung win. The server
     rolls the same d20 the screen animates and seats or refuses. Engine 82's
     server door mirrors addPersistentBench and replaces a recruit at 100;
     `replaceIdx` remains wire-compatible with historical servers, while the
     deterministic server policy owns the actual outgoing seat.
     One courtship per rung — the server latches it, win or walk. */
  function ladderRecruit(runId, idx, recruit, replaceIdx) {
    if (localMode()) {
      var L = localLad();
      var E2 = window.HOHContract, run = window.__hohLocalRun || {};
      var chk = { pass: true, roll: 20, dc: 0 };
      try { chk = E2.recruitCheck(String(run.seed || ''), run.depth | 0, idx | 0, (recruit && recruit.rar) | 0); } catch (_) {}
      L.recruits = L.recruits || [];
      var _courtSeat = chk.pass ? addPersistentBench(L.recruits, recruit, L.engine_ver) : null;
      if (chk.pass && !_courtSeat.added) {
        return Promise.resolve({ ok: false, reason: 'bench_limit',
          limit:persistentBenchLimitAt(L.engine_ver), roll:chk.roll, dc:chk.dc,
          recruits:L.recruits.slice() });
      }
      if (_courtSeat) L.recruits = _courtSeat.list;
      return Promise.resolve({ ok:true, joined:!!chk.pass, roll:chk.roll, dc:chk.dc,
        replaced:_courtSeat ? _courtSeat.replaced : [], recruits:L.recruits.slice() });
    }
    return rpc('hoh_bg_ladder_recruit', {
      p_email: email(), p_pin: pin(), p_run_id: runId, p_idx: idx | 0,
      p_recruit: recruit, p_replace: (replaceIdx == null ? null : replaceIdx | 0)
    }).then(function (r) { ladderForget(); return r; });
  }

  /* take the shop. Free, once, and the server refuses a second helping. */
  function ladderShop() {
    if (localMode()) {
      var L = localLad();
      L.stage = (L.stage | 0) + 1; L.stage_seed = null;
      return Promise.resolve({ ok: true, items: L.items, gained: 'mend', healed: 50, stage: L.stage });
    }
    return rpc('hoh_bg_ladder_shop', { p_email: email(), p_pin: pin() })
      .then(function (r) { ladderForget(); return r; });
  }

  /* swap the five. Refused while a rung is live, because the run row froze the
     roster the judge is about to score and the two must not disagree. */
  function ladderParty(rosterKeys) {
    if (localMode()) {
      var L = localLad();
      var incoming = localSnapshot(rosterKeys || []);
      L.party = L.live ? mergePromotedParty(L.party || [], incoming) : slotParty(incoming);
      return Promise.resolve({ ok: true, party: L.party });
    }
    return rpc('hoh_bg_ladder_party', { p_email: email(), p_pin: pin(), p_roster: rosterKeys })
      .then(function (r) { ladderForget(); return r; });
  }

  /* ============ R450 THE PATH: stored choices and the champion ============
     Every event pick, draft pick, opt-in accept and the champion slot is a
     STORED INPUT the worker re-derives effects from. One door in
     (hoh_bg_path_set, one-shot per key), one door out (hoh_bg_path_get).
     The sandbox mirrors the same shape on window.__hohLocalLadder.path so a
     signed-out climb rehearses the real one. */
  function pathGet() {
    if (localMode()) {
      var L = localLad();
      /* R466: the sandbox vouches the origin with the SAME four conditions the
         SQL uses, so god mode and live play answer alike. A sandbox that
         silently lacks a feature is a sandbox that cannot test it, and this is
         the mode the browser harness runs the whole climb in.
           live      a dead climb has no origin to hand out
           c         the stamp names THIS ticket, not the last one
           s >= 1    a real rung
           s <= stage an origin can never be ahead of where you stand */
      var _p = L.path || {};
      var _from = null;
      if (L.live && _p.from && (_p.from.c | 0) === (L.climbs | 0)) {
        var _s = _p.from.s | 0;
        if (_s >= 1 && _s <= (L.stage | 0)) _from = _s;
      }
      var _out = {};
      for (var k in _p) { if (Object.prototype.hasOwnProperty.call(_p, k)) _out[k] = _p[k]; }
      _out.climb_from = _from;
      /* R471: same four conditions the SQL overlay uses */
      _out.vigil_used = !!(L.live && _p.vigil && (_p.vigil.c | 0) === (L.climbs | 0));
      return Promise.resolve({ ok: true, path: _out });
    }
    return rpc('hoh_bg_path_get', { p_email: email(), p_pin: pin() });
  }
  /* kind: 'ev' | 'draft' | 'draft2' | 'optin' with a stage and an integer
     choice (-1 is the draft skip), or 'champ' with {i, key}. The server
     refuses a rewrite, so the caller treats already_chosen as success and
     re-reads. */
  function pathSet(kind, stage, val) {
    if (localMode()) {
      var L = localLad();
      L.path = L.path || {};
      /* R466: the sandbox mirrors the SQL exactly, including the part that
         matters most: the ORIGIN IS READ OFF THE ROW, never off the caller.
         `stage` and `val` are ignored here for the same reason the server
         ignores p_stage and p_val, so the one number this feature pays on
         cannot be named by a client in either mode. */
      /* R471: the sandbox mirrors the server's one-free-revive latch, keyed on
         `climbs` exactly as the SQL is, so god mode and live play agree. */
      if (kind === 'vigilused') {
        if (!L.live) return Promise.resolve({ ok: false, reason: 'not_live' });
        var _vc = L.climbs | 0;
        if (L.path.vigil && (L.path.vigil.c | 0) === _vc) {
          return Promise.resolve({ ok: true, already: true, climb: _vc });
        }
        L.path.vigil = { s: Math.max(1, L.stage | 0), c: _vc };
        return Promise.resolve({ ok: true, already: false, climb: _vc });
      }
      if (kind === 'climbfrom') {
        if (!L.live) return Promise.resolve({ ok: false, reason: 'not_live' });
        var _c0 = L.climbs | 0;
        if (L.path.from && (L.path.from.c | 0) === _c0) {
          return Promise.resolve({ ok: true, already: true, from: L.path.from.s | 0, climb: _c0 });
        }
        L.path.from = { s: Math.max(1, L.stage | 0), c: _c0 };
        return Promise.resolve({ ok: true, already: false, from: L.path.from.s, climb: _c0 });
      }
      if (kind === 'champ') {
        /* R450b: the SQL's own guard is `run_id is not null` — a rung in
           flight — not the whole climb. Between rungs (a win settled,
           nothing bound) the champion may change, and the crowning at
           stage 11 lands exactly there. The old L.live check refused the
           entire climb and starved the sandbox of the crowning moment. */
        if (L.live && L.run_id != null) return Promise.resolve({ ok: false, reason: 'mid_run' });
        var slotI = (val && val.i != null) ? (val.i | 0) : -1;
        if (slotI < 0 || slotI > 7) return Promise.resolve({ ok: false, reason: 'bad_slot' });
        L.path.champ = slotI;
        L.path.champ_key = String((val && val.key) || '').slice(0, 64);
        var C = window.__hohLocalChamp || {};
        L.path.stars = ((C[L.path.champ_key] || {}).stars | 0);
        return Promise.resolve({ ok: true, champ: L.path.champ, stars: L.path.stars });
      }
      var bag = kind === 'optin' ? 'optin' : kind === 'ev' ? 'ev' : 'draft';
      var key = kind === 'draft2' ? (String(stage | 0) + 'b') : String(stage | 0);
      /* the SQL's own guards, mirrored so the sandbox rehearses refusals too */
      if (!(stage >= 1 && stage <= 999)) return Promise.resolve({ ok: false, reason: 'bad_stage' });
      var vNum = val | 0;
      if (vNum < -1 || vNum > 5) return Promise.resolve({ ok: false, reason: 'bad_choice' });
      if (kind === 'optin' && vNum !== 0 && vNum !== 1) return Promise.resolve({ ok: false, reason: 'bad_choice' });
      L.path[bag] = L.path[bag] || {};
      if (L.path[bag][key] != null) return Promise.resolve({ ok: false, reason: 'already_chosen' });
      L.path[bag][key] = vNum;
      return Promise.resolve({ ok: true, kind: kind, key: key, choice: vNum });
    }
    return rpc('hoh_bg_path_set', {
      p_email: email(), p_pin: pin(), p_kind: String(kind), p_stage: (stage == null ? null : stage | 0),
      p_val: (kind === 'champ' ? val : (val | 0))
    }).then(function (r) { ladderForget(); return r; });
  }
  function championState() {
    if (localMode()) {
      var C = window.__hohLocalChamp || {};
      var out = [];
      for (var k in C) out.push({ card_key: k, xp: C[k].xp | 0, stars: C[k].stars | 0 });
      return Promise.resolve({ ok: true, champions: out });
    }
    return rpc('hoh_champion_state', { p_email: email(), p_pin: pin() });
  }
  /* the star purchase: hearts leave the client wallet (the accepted trust
     model, HANDOFF §1) and the SERVER enforces the XP threshold and tier
     order, which is what actually bounds the effect. The caller charges
     hearts only after an ok. */
  function championStar(cardKey, tier) {
    if (localMode()) {
      var C = window.__hohLocalChamp = window.__hohLocalChamp || {};
      var row = C[cardKey] = C[cardKey] || { xp: 0, stars: 0 };
      if (!(tier >= 1 && tier <= 3)) return Promise.resolve({ ok: false, reason: 'bad_tier' });
      var need = tier === 1 ? 25 : tier === 2 ? 75 : 150;
      if (row.stars !== tier - 1) return Promise.resolve({ ok: false, reason: 'wrong_order', stars: row.stars });
      if (row.xp < need) return Promise.resolve({ ok: false, reason: 'needs_xp', xp: row.xp, need: need });
      row.stars = tier;
      return Promise.resolve({ ok: true, card_key: cardKey, stars: tier, xp: row.xp });
    }
    return rpc('hoh_champion_star', { p_email: email(), p_pin: pin(), p_card_key: String(cardKey || ''), p_tier: tier | 0 });
  }

  /* ============ R461: THE VIGIL ============
     Jan: "there can be special events between rounds that could let you spend
     hearts to revive them for the run again ... this only for your main bench
     characters, and it should happen, like, occasionally as an unlock when one
     of your main characters are dead."

     A hero who falls is gone for the rest of the climb, and this is the ONE
     door back. Deliberately not an engine event: the fight stream must stay a
     pure function of (stage, seed, inputs), and an event that only exists
     because of what happened in an earlier fight is run STATE. So it lives
     where the shop lives — a server-held stop between rungs, priced in hearts,
     latched server side, with the client wallet paying exactly the way
     hoh_bg_shop_buy is already paid.

     THE PRICE IS THE RARITY. A common body comes back for 250 and a mythic for
     800, because what you are buying back is a die, a card and a seat, and
     those are worth what the hero is worth. Two to six stages of purse either
     way, so it is always a real decision and never a formality. */
  /* R462 (Jan): "it should be 300 to revive a dead character (not benched)."
     Flat, and flat is better than the rarity ladder R461 shipped for one
     concrete reason beyond the ask: a price a player can hold in their head
     is a price they can plan around, and the whole point of the Vigil is that
     it is a decision you make three stages before you need it. */
  /* R471 (Jan): "make it free instead, you can res a hero for free but just
     one instead of paying 300 heart." The scarcity moved from the price to the
     count: one per climb, latched server side through hoh_bg_path_set so it
     survives a reload. Kept as a named constant rather than deleted, because
     vigilCost() is exported and a cached older asset may still call it. */
  var VIGIL_COST = 0;
  function vigilCost() { return VIGIL_COST; }
  /* ============ R491 · THE OPEN SEAT BELONGS TO THE RUN ============
     A promoted recruit is not a sixth kind of owned card. It is a run-only
     occupant of one exact party seat. These helpers are deliberately plain
     ES5 data transforms so the sandbox, the SQL migration and the regression
     harness can all enforce the same three rules:

       1. rarity outranks level; level breaks a rarity tie;
       2. a stable bench order breaks a complete tie;
       3. the promoted body keeps its party_slot until it falls or the run ends.

     The tiny class term only resolves equal rarity + equal level bodies. It
     mirrors statsOf's base-stat total (four or five) and can never jump a
     rarity or level band. */
  function recruitStrength(r) {
    r = r || {};
    var rar = Math.max(0, Math.min(5, r.rar | 0));
    var lvl = Math.max(1, Math.min(100, r.lvl | 0 || 1));
    var cls = String(r.cls || r.character_class || '').split(' ')[0];
    var five = /^(Rogue|Ranger|Bard|Wizard|Sorcerer|Warlock|Artificer)$/.test(cls);
    return rar * 100000 + lvl * 100 + (five ? 5 : 4);
  }
  function partySlot(h, fallback) {
    var n = h && h.party_slot;
    n = (n == null || n === '') ? (fallback | 0) : (n | 0);
    return n >= 0 && n < 5 ? n : (fallback | 0);
  }
  function slotParty(list) {
    var used = {}, ranked = [];
    (list || []).slice(0, 5).forEach(function (src, i) {
      var h = src || {}, s = partySlot(h, i);
      if (used[s]) {
        for (s = 0; s < 5 && used[s]; s++) {}
      }
      if (s > 4) return;
      used[s] = 1;
      var out = {}, k;
      for (k in h) if (Object.prototype.hasOwnProperty.call(h, k)) out[k] = h[k];
      out.party_slot = s;
      ranked.push({ h: out, s: s, i: i });
    });
    ranked.sort(function (a, b) { return a.s - b.s || a.i - b.i; });
    return ranked.map(function (x) { return x.h; });
  }
  function mergePromotedParty(saved, incoming) {
    var fixed = {}, out = [], at = 0;
    slotParty(saved || []).forEach(function (h, i) {
      if (h && h.promoted === true) fixed[partySlot(h, i)] = h;
    });
    /* R495: adopt is also the resume door. On a bound resume the incoming
       contract roster already contains the promoted body that an earlier
       adopt merged. Treating that snapshot as five owned bodies inserted the
       same recruit again beside its fixed copy. Filter every run-only trace
       before filling mutable seats; promoted_stage survives the historical
       SQL's flag stripping, so it also heals a roster already duplicated by
       that version. */
    incoming = (incoming || []).filter(function (h) {
      if (!h) return false;
      if (h.promoted === true) return false;
      if (h.promoted_stage != null) return false;
      if (/^run:/.test(String(h.key || ''))) return false;
      /* R496: temporary is not synonymous with fixed. A verified Hall loaner
         is a mutable main-party body for this run, even though it is correctly
         ineligible for permanent EXP. Only promoted/run-only traces are fixed. */
      if (/^hall:/.test(String(h.key || '')) && h.hire === true && h.owned !== true) return true;
      return h.run_only !== true && h.permanent_xp !== false;
    });
    for (var s = 0; s < 5; s++) {
      if (fixed[s]) out.push(fixed[s]);
      else if (at < incoming.length) {
        var src = incoming[at++] || {}, own = {}, k;
        for (k in src) if (Object.prototype.hasOwnProperty.call(src, k)) own[k] = src[k];
        own.party_slot = s;
        out.push(own);
      }
    }
    return slotParty(out);
  }
  function promoteStrongestRecruit(L, stage) {
    L = L || {};
    var path = L.path || {}, vig = path.vigil || {};
    var used = !!(L.live && (vig.c | 0) === (L.climbs | 0));
    var fallen = L.fallen_party || [], rec = L.recruits || [], party = slotParty(L.party || []);
    if (!used || !fallen.length || !rec.length || party.length >= 5) {
      L.party = party;
      return null;
    }
    var best = 0, bestScore = recruitStrength(rec[0]);
    for (var i = 1; i < rec.length; i++) {
      var score = recruitStrength(rec[i]);
      if (score > bestScore) { best = i; bestScore = score; }
    }
    var usedSlots = {};
    party.forEach(function (h, i) { usedSlots[partySlot(h, i)] = 1; });
    var open = 0;
    while (open < 5 && usedSlots[open]) open++;
    if (open > 4) { L.party = party; return null; }
    var src = rec.splice(best, 1)[0] || {}, promoted = {}, k;
    for (k in src) if (Object.prototype.hasOwnProperty.call(src, k)) promoted[k] = src[k];
    promoted.promoted = true;
    promoted.run_only = true;
    promoted.permanent_xp = false;
    promoted.promoted_stage = Math.max(1, stage | 0);
    promoted.party_slot = open;
    party.push(promoted);
    L.party = slotParty(party);
    L.recruits = rec;
    return promoted;
  }
  function applyLocalFalls(L, fellBench, fellHero, stage) {
    L.recruits = L.recruits || [];
    L.party = slotParty(L.party || []);
    L.fallen_party = L.fallen_party || [];
    (fellBench || []).slice().sort(function (a, b) { return b - a; })
      .forEach(function (i) { if (L.recruits[i]) L.recruits.splice(i, 1); });
    (fellHero || []).slice().sort(function (a, b) { return b - a; })
      .forEach(function (i) {
        if (!L.party[i]) return;
        var down = L.party.splice(i, 1)[0];
        /* A run-only body is gone when it falls. It never enters the owned
           grave and therefore can never be offered by the free revive. */
        if (!(down && down.promoted === true)) L.fallen_party.push(down);
      });
    var promoted = promoteStrongestRecruit(L, stage);
    return { party: L.party.slice(), fallen_party: L.fallen_party.slice(),
             recruits: L.recruits.slice(), promoted: promoted };
  }
  /* peek the fallen line (idx null) or bring ONE of them back */
  function vigilRevive(idx) {
    if (localMode()) {
      var L = localLad();
      L.fallen_party = L.fallen_party || [];
      L.party = slotParty(L.party || []);
      var _vu = !!(L.live && L.path && L.path.vigil &&
                   (L.path.vigil.c | 0) === (L.climbs | 0));
      if (idx == null) return Promise.resolve({ ok: true, used: _vu,
        party: L.party.slice(), fallen_party: L.fallen_party.slice() });
      if (_vu) return Promise.resolve({ ok: false, reason: 'already_used' });
      if (L.run_id != null) return Promise.resolve({ ok: false, reason: 'mid_run' });
      var i = idx | 0;
      if (!L.fallen_party[i]) return Promise.resolve({ ok: false, reason: 'not_fallen' });
      var back = JSON.parse(JSON.stringify(L.fallen_party[i]));
      L.fallen_party.splice(i, 1);
      L.party = slotParty(L.party.concat([back]));
      L.path = L.path || {};
      L.path.vigil = { s: Math.max(1, L.stage | 0), c: L.climbs | 0 };
      return Promise.resolve({ ok: true, used: true, hero: back,
        party: L.party.slice(), fallen_party: L.fallen_party.slice() });
    }
    return rpc('hoh_bg_vigil_revive', { p_email: email(), p_pin: pin(), p_idx: idx == null ? null : (idx | 0) })
      .then(function (r) { ladderForget(); return r; });
  }
  /* ============ R463 · THE CLIMB'S OWN LINE ============
     Jan: "if you go back to the hall and the run is saved but 2 of your
     characters die, then resuming they are no longer dead."

     He is right, and it was the herald rather than the ledger. The SERVER had
     it correct the whole time — settle shrinks `party` and fills
     `fallen_party` — but leaving the Hall throws away S.picked, and walking
     back in put the player at a picker that happily offered the two heroes who
     had just died. Picking them then sent a roster of five at a climb whose
     party is three, and hoh_bg_ladder_adopt refused it with `party_too_big`,
     which is the "press continue and nothing happens" half of the same report.

     One read, cached for the session, and the picker can finally tell the
     truth. It is the vigil's own peek: p_idx null writes nothing. */
  function climbLine(force) {
    if (localMode()) {
      var L = localLad();
      return Promise.resolve({ ok: true, party: (L.party || []).slice(), fallen_party: (L.fallen_party || []).slice() });
    }
    return rpc('hoh_bg_vigil_revive', { p_email: email(), p_pin: pin(), p_idx: null })
      .catch(function () { return { ok: false, party: [], fallen_party: [] }; });
  }
  /* ============ R463 · THE BATTLEGROUNDS TICKET RALLIES YOUR LINE ============
     Jan: "at any point you are allowed to actually use your battlegrounds
     tickets during the battle setup to gain 3 recruits, revive all your dead
     heroes, and restore to full health."

     This is what finally makes the ticket a THING rather than a turnstile. It
     is also the honest answer to "my heroes came back on their own": they do
     not, and this is the door that brings them back, and it costs something
     the player understands. The ticket is spent through the same wallet the
     entry uses, and it is refunded if the server refuses. */
  /* ============ R466 · THE DOOR THAT ENDS A CLIMB ============
     Jan: "give up and gain hearts equal to how long that run was ... if they
     got from stage 21 to 24 then they flee and still earn 30 hearts instead of
     just losing at 24 and getting nothing."

     TWO HALVES AND THE ORDER IS THE SAFETY. The RUN is closed first, because
     hoh_bg_ladder_retire refuses while the run can still be judged, and a
     climb given up underneath an open verdict is a climb the judge and the
     row disagree about. Only then does the ladder row let go, and only then
     do the hearts land. Paying before the ending is the one ordering a
     dropped connection could farm, so it is the one ordering never used.

     stake 0 is what stops the road's own flee credit paying a second time on
     the way past: purseFlee is capped at the stake and a rung stakes nothing. */
  function ladderRetire(runId, stage) {
    var _st = stage | 0;
    if (localMode()) {
      var L = localLad();
      if (!L.live) return Promise.resolve({ ok: false, reason: 'no_climb' });
      var _lf = (L.path && L.path.from && L.path.from.s) | 0;
      if (!_lf) return Promise.resolve({ ok: false, reason: 'no_origin' });
      var _ls = Math.max(0, (L.stage | 0) - _lf);
      L.live = false; L.run_id = null; L.hp = 0;
      L.fallen = (L.recruits || []).slice(); L.recruits = []; L.fallen_party = []; L.hired = 0;
      try {                                   /* R468: same truth in god mode */
        window.__hohBgClimb = { account: collectionAccount(), live: false, stage: L.stage | 0, best_stage: L.best_stage | 0 };
        window.__hohBgClimbAsked = 1;
        document.dispatchEvent(new CustomEvent('hoh:bg-climb-changed'));
      } catch (_) {}
      return Promise.resolve({ ok: true, retired: true, from: _lf, stage: L.stage | 0,
                               stages: _ls, rate: 10, hearts: _ls * 10 });
    }
    if (!signedIn()) return Promise.reject(new Error('sign in first'));
    var retireScope = captureCombatAccount();
    function _endClimb() {
      return rpc('hoh_bg_ladder_retire', { p_email: retireScope.email, p_pin: retireScope.pin })
        .then(function (r) {
          ladderForget();
          /* R468: and say what we now KNOW, rather than leaving the Hall to
             discover it. ladderForget cleared the mirror, so without this the
             door would flash its wallet state for one paint before the re-read
             lands. The climb is over and we are the ones who ended it. */
          if (r && r.ok === true && combatAccountIsCurrent(retireScope)) {
            try {
              window.__hohBgClimb = { account: retireScope.account, live: false, stage: (r.stage | 0), best_stage: (r.best_stage | 0) };
              window.__hohBgClimbAsked = 1;
              document.dispatchEvent(new CustomEvent('hoh:bg-climb-changed'));
            } catch (_) {}
          }
          return r || { ok: false, reason: 'no_answer' };
        });
    }
    if (!runId) return _endClimb();
    /* close the run on the worker first. A refusal here is not fatal: the run
       may already be closed, and the ladder door below is the thing that
       actually ends the climb. */
    return settle(runId, 'fled_half', { stake: 0, planId: '', depth: Math.max(0, _st - 1) })
      .catch(function () { return null; })
      .then(_endClimb);
  }

  /* R497: release a ladder that still points at a contract the judge already
     expired. V72 proved why a browser-only dead-run marker is not a lifecycle
     boundary: another device cannot see it, storage may reject it, and the
     server row keeps advertising Resume. This RPC authenticates the owner,
     requires the exact run to be terminal-expired, and moves the ladder into
     its ordinary loss shape without rewards. Open and successfully settled
     runs are never eligible. */
  function releaseExpiredLadder(runId) {
    if (localMode()) {
      var L = localLad();
      if (L.run_id != null && String(L.run_id) === String(runId)) {
        L.live = false; L.run_id = null; L.hp = 0;
        L.fallen = (L.recruits || []).slice(); L.recruits = [];
        L.fallen_party = []; L.hired = 0;
      }
      ladderForget(); forgetRunIf(runId);
      return Promise.resolve({ ok: true, released: true, idempotent: true, run_id: runId });
    }
    if (!signedIn()) return Promise.reject(new Error('Sign in again to clear the old run.'));
    var releaseScope = captureCombatAccount();
    return rpc('hoh_bg_ladder_release_expired', {
      p_email: releaseScope.email,
      p_pin: releaseScope.pin,
      p_run_id: runId
    }).then(function (r) {
      if (!combatAccountIsCurrent(releaseScope)) {
        throw accountChangedError('the expired run was clearing', true);
      }
      if (!r || r.ok !== true) {
        var err = new Error('The old run could not be cleared yet. Try again.');
        err.reason = (r && r.reason) || 'no_answer';
        throw err;
      }
      ladderForget();
      forgetRunIf(runId);
      return r;
    });
  }

  function ticketRally() {
    if (localMode()) {
      var L = localLad();
      L.party = (L.party || []).concat(L.fallen_party || []);
      L.fallen_party = [];
      L.recruits = L.recruits || [];
      var _cls = ['Fighter', 'Cleric', 'Ranger'];
      var _rallied = _cls.map(function (cls) {
        return { name:'THE RALLIED ' + cls.toUpperCase(), cls:cls, rar:2,
          lvl:Math.max(1, (L.stage | 0) * 2), hearts:200, rally:true };
      });
      var _rallySeats = addPersistentBench(L.recruits, _rallied, L.engine_ver);
      L.recruits = _rallySeats.list;
      L.hp = L.max_hp || 150;
      return Promise.resolve({ ok: true, party: L.party.slice(), fallen_party: [],
                               recruits:L.recruits.slice(), replaced:_rallySeats.replaced,
                               hp: L.hp, revived: 0 });
    }
    if (!signedIn()) return Promise.reject(new Error('sign in first'));
    var rallyScope = captureCombatAccount();
    var rallyRefundQueued = false;
    function queueRallyRefund() {
      if (rallyRefundQueued) return;
      queueBgRefund(rallyScope);
      rallyRefundQueued = true;
    }
    var spend = spendBgTicket(rallyScope);
    return spend.then(function (sp) {
      if (!sp || !sp.ok) {
        queueRallyRefund();
        throw new Error('no battle ticket');
      }
      return rpc('hoh_bg_ticket_rally', { p_email: rallyScope.email, p_pin: rallyScope.pin })
        .then(function (r) {
          if (!r || r.ok !== true) {
            /* the ledger only pays an unmatched spend, so this cannot mint */
            queueRallyRefund();
            return r;
          }
          if (combatAccountIsCurrent(rallyScope)) ladderForget();
          else throw accountChangedError('the ticket rally was saving', true);
          return r;
        })
        .catch(function (e) {
          if (!(e && (e.__hohCommitted || e.__hohRefundPending))) queueRallyRefund();
          throw e;
        });
    });
  }

  /* R462: ONE OF YOUR LINE STEPS DOWN. The event outcomes that cost a party
     member land here. They are not dead, they are OUT, and they go into the
     same fallen_party the Vigil reads — so a hero traded to a cleric and a
     hero who fell in a fight are bought back through one door. */
  function partyOut(idx) {
    if (localMode()) {
      var L = localLad();
      L.party = L.party || []; L.fallen_party = L.fallen_party || [];
      var i = idx | 0;
      if (!L.party[i]) return Promise.resolve({ ok: false, reason: 'no_such_hero' });
      if (L.party.length <= 1) return Promise.resolve({ ok: false, reason: 'last_hero' });
      L.fallen_party.push(L.party.splice(i, 1)[0]);
      return Promise.resolve({ ok: true, party: L.party.slice(), fallen_party: L.fallen_party.slice() });
    }
    return rpc('hoh_bg_party_out', { p_email: email(), p_pin: pin(), p_idx: idx | 0 })
      .then(function (r) { ladderForget(); return r; });
  }
  /* R499 THE LIGHTER HIRING BOARD. The first recruit is free, then the ladder
     rises gently (10, 20, 30, 40, 50, then 100 forever). The SERVER owns both,
     because a price the browser computes is a price the browser can change:
     hoh_bg_board_hire counts the hires on the ladder row and charges from its
     own copy of the ladder. The wallet is the client's, exactly as the shop's
     hearts branch is, and it only pays after the server says yes. */
  var HIRE_LADDER = [0, 10, 20, 30, 40, 50, 100];
  function hireCost(n) { return HIRE_LADDER[Math.min(HIRE_LADDER.length - 1, Math.max(0, n | 0))]; }
  /* R463 (Jan): "hired people also need to be directly from the hall with a
     character that is not a ff portrait from the hall that actually joins your
     team with the reframe of the image saved from the hall so it looks proper
     on the battle."
     So the CLIENT proposes the body, exactly the way an event companion is
     proposed, and the server clamps and charges it. That is the only way the
     hire can be a real Hall hero: the ladder tables hold no copy of the Hall,
     and hoh_ladder_clamp_recruit already exists to bound whatever arrives. */
  function boardHire(hero) {
    if (localMode()) {
      var L = localLad();
      L.recruits = L.recruits || []; L.hired = L.hired | 0;
      if (L.hired >= 10) return Promise.resolve({ ok: false, reason: 'board_closed' });
      var _cls = ['Fighter', 'Cleric', 'Rogue', 'Ranger', 'Barbarian', 'Monk', 'Bard', 'Druid'];
      var c = (hero && hero.cls) || _cls[L.hired % _cls.length];
      var body = { name: (hero && hero.name) || ('THE HIRED ' + c.toUpperCase()), cls: c,
                   id: (hero && hero.id) || '', img: (hero && hero.img) || '',
                   race: (hero && hero.race) || '',
                   rar: Math.min(3, 1 + ((L.hired / 3) | 0)),
                   lvl: Math.max(1, Math.min(100, (L.stage | 0) * 2)), hearts: 200, hired: true };
      var cost = hireCost(L.hired);
      var _hireSeat = addPersistentBench(L.recruits, body, L.engine_ver);
      if (!_hireSeat.added) return Promise.resolve({ ok:false, reason:'bench_limit',
        limit:persistentBenchLimitAt(L.engine_ver), hired:L.hired, recruits:L.recruits.slice() });
      L.recruits = _hireSeat.list; L.hired++;
      return Promise.resolve({ ok: true, hero: body, cost: cost,
                               replaced:_hireSeat.replaced, hired: L.hired,
                               recruits: L.recruits.slice() });
    }
    return rpc('hoh_bg_board_hire', { p_email: email(), p_pin: pin(), p_hero: hero || null })
      .then(function (r) { ladderForget(); return r; });
  }

  /* R461: the climb records who did not get up. The worker writes this inside
     hoh_bg_ladder_settle for every real fight, in the same transaction that
     advances the climb. This door exists ONLY for god mode, whose fights never
     reach the worker at all (the sandbox is its own judge), so without it law
     4 breaks: "god mode should have the same experience as normal people". */
  function localFall(fellBench, fellHero) {
    if (!localMode()) return Promise.resolve({ ok: true, skipped: true });
    var L = localLad();
    var moved = applyLocalFalls(L, fellBench, fellHero, L.stage | 0);
    moved.ok = true;
    return Promise.resolve(moved);
  }

  /* ============ R450b: the bench, the shop and the jump ============ */
  /* peek the fallen bench (p null) or carry up to two back (array of
     indices). Between runs only, the same window a stand-up uses. */
  function reclaimFallen(idx) {
    if (localMode()) {
      var L = localLad();
      L.fallen = L.fallen || [];
      if (idx == null) return Promise.resolve({ ok: true, fallen: L.fallen.slice() });
      if (L.live) return Promise.resolve({ ok: false, reason: 'mid_run' });
      var take = [];
      L.recruits = L.recruits || [];
      (idx || []).slice(0, 2).forEach(function (i) {
        if (L.fallen[i]) { var r = JSON.parse(JSON.stringify(L.fallen[i])); r.kept = true; take.push(r); }
      });
      var _reclaimed = addPersistentBench(L.recruits, take, L.engine_ver);
      L.recruits = _reclaimed.list;
      L.fallen = [];
      return Promise.resolve({ ok:true, kept:_reclaimed.added,
        replaced:_reclaimed.replaced, recruits:L.recruits.slice() });
    }
    return rpc('hoh_bg_reclaim', { p_email: email(), p_pin: pin(), p_idx: idx == null ? null : idx })
      .then(function (r) { ladderForget(); return r; });
  }
  /* an event seats a companion onto the SERVER bench, where it lives
     until the run is lost. Idempotent per event stage. */
  function companionJoin(stage, recruit) {
    if (localMode()) {
      var L = localLad();
      L.recruits = L.recruits || [];
      for (var i = 0; i < L.recruits.length; i++) if ((L.recruits[i].ev | 0) === (stage | 0)) return Promise.resolve({ ok: true, idempotent: true, recruits: L.recruits.slice() });
      var r2 = JSON.parse(JSON.stringify(recruit || {})); r2.companion = true; r2.ev = stage | 0;
      r2.rar = Math.max(0, Math.min(3, r2.rar | 0));
      var _companionSeat = addPersistentBench(L.recruits, r2, L.engine_ver);
      if (!_companionSeat.added) return Promise.resolve({ ok:false, reason:'bench_limit',
        limit:persistentBenchLimitAt(L.engine_ver), recruits:L.recruits.slice() });
      L.recruits = _companionSeat.list;
      return Promise.resolve({ ok:true, replaced:_companionSeat.replaced,
        recruits:L.recruits.slice() });
    }
    return rpc('hoh_bg_companion_join', { p_email: email(), p_pin: pin(), p_stage: stage | 0, p_recruit: recruit })
      .then(function (r) { ladderForget(); return r; });
  }
  /* the grave toll takes one back. The pick is the stage folded over the
     bench, so every device removes the same body. One-shot per stage. */
  /* R462: `nth` lets a chain of losses be four DIFFERENT losses. The server
     latch is per stage, which is right for one event granting one loss and
     wrong for a crew event that takes four, so the nth is part of the key. */
  function companionLose(stage, nth) {
    if (localMode()) {
      var L = localLad();
      L.recruits = L.recruits || [];
      L._lost = L._lost || {};
      var _k = (stage | 0) + ':' + (nth | 0);
      if (L._lost[_k]) return Promise.resolve({ ok: true, idempotent: true, recruits: L.recruits.slice() });
      if (!L.recruits.length) return Promise.resolve({ ok: false, reason: 'empty_bench' });
      var pick = ((stage | 0) + (nth | 0)) % L.recruits.length;
      var gone = L.recruits.splice(pick, 1)[0];
      L._lost[_k] = 1;
      return Promise.resolve({ ok: true, gone: gone, recruits: L.recruits.slice() });
    }
    return rpc('hoh_bg_companion_lose', { p_email: email(), p_pin: pin(), p_stage: stage | 0, p_nth: nth | 0 })
      .then(function (r) { ladderForget(); return r; });
  }
  /* R476: ONE FREE, THEN BUY UP TO TWO MORE, EVERY STACK CAPPED AT THREE.
     Jan: "the item restocker will give you 1 of everything unless you already
     have 3 of it for free, then you can buy 1 to 2 more, the items limits are
     capped at 3."

     The free one comes from hoh_bg_ladder_shop, which tops every stack to 1 on
     the way out. This door validates the second and third card; the readable
     UI charges the stable per-card Heart prices (10/20/25), or 10 health. The
     30 heal is retired from the client and left accepted here only
     for a browser still being served yesterday's JS by the CDN. Hearts are
     charged by the CALLER on ok (the accepted wallet model), blood is taken
     here and never below 35. */
  function shopBuy(kind, pay) {
    if (localMode()) {
      var L = localLad();
      L.items = L.items || {}; L._shopBuys = L._shopBuys || {};
      if (public_kindBad(kind)) return Promise.resolve({ ok: false, reason: 'bad_kind' });
      if (pay !== 'hearts' && pay !== 'health') return Promise.resolve({ ok: false, reason: 'bad_pay' });
      /* The sandbox is the god-mode judge (Jan's law 4: god mode plays the real
         game), so it mirrors the LIVE hoh_bg_shop_buy exactly. That includes
         reading the OLD latch shape, because a sandbox ladder saved by an
         earlier build still holds `_shopBuys[stage] = '<kind>'` as a bare
         string, and reading that as a map would silently unlock all three. */
      /* R457: the SANDBOX WRITES THE SAME PLACE THE SERVER DOES. The latch
         used to live on a private `_shopBuys` map, which was fine while the
         only reader was this function — but the shop screen now reads
         `path.shop[<stage>]` to know which rows are already bought, and a
         god-mode player reading a map nobody wrote would be offered three
         live buttons the sandbox then refuses. Law 4: god mode plays the real
         game. `_shopBuys` is still written beside it so a ladder saved by an
         earlier build keeps its latch. */
      L.path = L.path || {}; L.path.shop = L.path.shop || {};
      var _sKey = String(L.stage | 0);
      var _slot = L.path.shop[_sKey];
      if (_slot == null) _slot = L._shopBuys[L.stage | 0];
      if (typeof _slot === 'string') _slot = (function (k) { var o = {}; o[k] = 1; return o; })(_slot);
      if (!_slot || typeof _slot !== 'object') _slot = {};
      /* R476: THE LATCH COUNTS, IT NO LONGER FLAGS. Found by review AFTER the
         SQL had already gone live, which is the bad order: sql/R476 taught the
         server to sell a 2nd and 3rd copy, and this sandbox was left judging by
         sql/R457's one-per-kind rule. The shop screen reads the same numbers as
         the server, so god mode offered a live "buy" button, said "room for 1
         more", and then refused the tap. Jan's law 4 is that god mode plays the
         real game, so the three reads below now mirror the patched
         hoh_bg_shop_buy exactly: count the latch, refuse at 2, and floor the
         stack at the free one.

         The boolean is what an older sandbox ladder saved, and it means ONE
         purchase. A number means that many. Absent means none. */
      var _bought = (function (v) {
        if (v == null || v === false) return 0;
        if (typeof v === 'number') return Math.max(0, v | 0);
        return 1;
      })(_slot[kind]);
      /* R478: the count gate is for the STACKING items. heal30 never walks the
         stack, so the cap of 3 cannot stop it, and it stays one a visit the way
         it always was. sql/R478 puts the same line back on the server. */
      if (_bought >= 2 || (kind === 'heal30' && _bought >= 1)) {
        return Promise.resolve({ ok: false, reason: 'already_bought', bought: _bought });
      }
      if (kind === 'heal30') {
        if (pay !== 'hearts') return Promise.resolve({ ok: false, reason: 'bad_pay' });
        /* R457: mirrors sql/R457's safety fix. Adding 30 to a raw hp of 0
           WROTE 30 into a pool that was open at full. The heal is retired
           from the client; this door stays open only for a browser still
           being served yesterday's JS, and it has to be safe. */
        var _hmax = (L.max_hp | 0) > 0 ? (L.max_hp | 0) : 150;
        var _heff = (L.hp | 0) > 0 ? (L.hp | 0) : (L.max_hp | 0);
        if (_heff <= 0 || _heff >= _hmax) return Promise.resolve({ ok: false, reason: 'already_full' });
        L.hp = Math.min(_hmax, _heff + 30);
      } else {
        /* R476: START FROM THE FREE ONE, the same greatest(1, ...) the server
           now uses. hoh_bg_ladder_shop tops every stack to 1 on the way out, so
           a player holding none of a kind already owns one. Reading the raw
           number made them pay 100 hearts to reach 1 and then handed them 1 for
           nothing: a hundred hearts for air. */
        var _walk = Math.max(1, L.items[kind] | 0);
        if (_walk >= 3) return Promise.resolve({ ok: false, reason: 'stack_full' });
        if (pay === 'health') {
          /* R457: hp 0 means OPEN AT FULL (the R408 column convention), so the
             gate reads the effective pool the way ladHpHtml does. Same read as
             sql/R457 and as renderShopStop, so all three agree. */
          var _eff = (L.hp | 0) > 0 ? (L.hp | 0) : (L.max_hp | 0);
          if (_eff < 50) return Promise.resolve({ ok: false, reason: 'too_hurt' });
          L.hp = Math.max(35, _eff - 10);
        }
        L.items[kind] = _walk + 1;
      }
      _slot[kind] = _bought + 1;   /* R476: a count, matching to_jsonb(v_bought + 1) */
      L._shopBuys[L.stage | 0] = _slot;
      L.path.shop[_sKey] = _slot;
      return Promise.resolve({ ok: true, kind: kind, pay: pay, items: L.items, hp: L.hp | 0, bought: _slot });
    }
    return rpc('hoh_bg_shop_buy', { p_email: email(), p_pin: pin(), p_kind: String(kind), p_pay: String(pay) })
      .then(function (r) { ladderForget(); return r; });
  }
  function public_kindBad(k) { return ['mend', 'bolt', 'ally', 'heal30'].indexOf(String(k)) < 0; }
  /* Jan's playtest door. The server refuses every account but the owner's. */
  function adminJump(stage) {
    if (localMode()) {
      var L = localLad();
      L.stage = Math.max(1, stage | 0); L.live = false; L.run_id = null; L.hp = 0; L.stage_seed = null;
      return Promise.resolve({ ok: true, stage: L.stage });
    }
    return rpc('hoh_bg_admin_jump', { p_email: email(), p_pin: pin(), p_stage: stage | 0 })
      .then(function (r) { ladderForget(); return r; });
  }

  /* the refusals, in the player's words rather than the database's */
  function ladderGateMessage(r) {
    var why = (r && r.reason) || '';
    if (why === 'no_tickets') return 'You need a Battlegrounds entry.';
    if (why === 'no_party')   return 'Pick five heroes first.';
    if (why === 'shop_rung')  return 'There is a shop here, not a fight.';
    return 'Could not start that stage.';
  }

  function continueRun(runId, opts) {
    opts = opts || {};
    if (localMode()) {
      var pr = window.__hohLocalRun || {};
      var nDepth = (pr.depth || 0) + 1;
      var nrun = { id: 'local-' + Date.now(), seed: localSeed(), stake: pr.stake || 0,
        pot: pr.pot || localPot(pr.stake || 0),
        foe: { scale: (pr.foe && pr.foe.scale) || 1 }, engine_ver: pr.engine_ver || 1,
        status: 'open', depth: nDepth, roster: pr.roster || [],
        recruits: (pr.recruits || []).slice(),
        plans: Array.isArray(opts.plans) ? opts.plans.slice() : (pr.plans || []).slice(),
        /* R325: the run remembers — a won battle's final health opens the next */
        carry_a: (pr._finalTa > 0) ? Math.max(1, pr._finalTa | 0) : null };
      if (plainObject(opts.recruit)) {
        var _nextBench = addPersistentBench(nrun.recruits, opts.recruit, nrun.engine_ver);
        if (!_nextBench.added) return Promise.reject(persistentBenchLimitError(nrun.engine_ver));
        nrun.recruits = _nextBench.list;
      }
      window.__hohLocalRun = nrun;
      return Promise.resolve(nrun);
    }
    /* R496: continuing a road is one server transaction, not a child INSERT
       followed by four best-effort mutations. The database locks the settled
       parent, creates/returns its one unique child, and commits recruit,
       carried plans/health and rival setup together. A timeout or a second
       device therefore returns the same ready child instead of forking a new
       payable run. During theme-first rollout only a positively missing new
       RPC may use the legacy chain, and that chain keeps one frozen account
       scope from first request through last. */
    var continueScope = opts._accountScope || captureCombatAccount();
    var recruit = plainObject(opts.recruit) ? opts.recruit : null;
    var plans = Array.isArray(opts.plans) ? opts.plans.slice(0, 12) : [];
    if (!continueScope.email || continueScope.pin == null || String(continueScope.pin).length < 1) {
      return Promise.reject(new Error('Sign in again before continuing this run.'));
    }
    function current() {
      if (!combatAccountIsCurrent(continueScope)) {
        throw accountChangedError('the next fight was saving', true);
      }
    }
    function validChild(run, requireParent) {
      current();
      if (!plainObject(run) || run.id == null || String(run.status || '') !== 'open') {
        throw new Error('The next fight was not confirmed. Try again.');
      }
      if (requireParent && String(run.continued_from_run_id || '') !== String(runId)) {
        throw new Error('The Hall answered with a different run. Reopen Battlegrounds.');
      }
      return run;
    }
    return rpcDeadline(rpc('hoh_bg_continue_road', {
      p_email: continueScope.email,
      p_pin: continueScope.pin,
      p_parent_run_id: runId,
      p_recruit: recruit,
      p_plans: plans
    }), 15000, 'The next fight took too long to save. Check your connection and try again.')
      .then(function (run) { return validChild(run, true); })
      .catch(function (err) {
        if (!rpcDefinitelyMissing(err, 'hoh_bg_continue_road')) throw err;
        current();
        return rpcDeadline(rpc('contract_continue', {
          p_email: continueScope.email, p_pin: continueScope.pin, p_run_id: runId
        }), 12000, 'The next fight took too long to save. Check your connection and try again.')
          .then(function (run) { return validChild(run, false); })
          .then(function (run) {
            if (!recruit) return run;
            return setRecruit(run.id, recruit, continueScope).then(function (all) {
              run.recruits = all; return run;
            });
          })
          .then(function (run) {
            return setPlans(run.id, plans, continueScope).then(function (saved) {
              run.plans = saved; return run;
            });
          })
          .then(function (run) { return applyCarry(run, continueScope, true); })
          .then(function (run) {
            if ((run.depth | 0) < 4) return run;
            return fetchRival(run.id, continueScope, true).then(function (rv) {
              if (rv) run.rival = rv; return run;
            });
          })
          .then(function (run) { current(); return run; });
      });
  }

  /* ============ R622 · THE SIX UNLIMITED DOORS ============================
     Battlegrounds Unlimited Phase 4b, chunk 1: the DATA LAYER ONLY. Six
     wrappers over the doors R611B/R614B/R621B actually installed, each in the
     shape of the ladder wrappers just above (captureCombatAccount before the
     first await, combatAccountIsCurrent before anything is written to this
     device, the door's own jsonb returned verbatim), plus a localMode() twin
     for every one so a signed-out god can play the mode end to end (law 4:
     god plays the real game). No screen work and no UI symbol lives here.

     EVERY KEY BELOW WAS READ OUT OF THE INSTALLED SQL, never assumed. The
     four places the SQL disagreed with the brief are named at their door.

     ⛔ THE STANDING R611B WARNING BINDS THIS FILE: an Unlimited settle reply
     carries `pot` because the receipt identity check forces it, and the RPC
     ALREADY CREDITED THE SERVER WALLET. It must never reach the device hearts
     applier. That branch is in settle() below, keyed on res.mode. */

  /* The request-id journal. The start door spends a Ticket, and its only
     replay key is p_request_id (16..128 chars, trimmed, no control bytes:
     R621B:330-336). So the id is minted and WRITTEN BEFORE the call, exactly
     the R559/R603 mold, and a retry re-reads it: the door then answers the
     saved receipt forever and the second Ticket is never spent. Account
     scoped, because a switched account must never inherit a stranded id. */
  var UNLIMITED_START_KEY = 'hoh-bg-unlimited-start-v1:';
  function unlimitedStartJournalKey(scope) {
    return UNLIMITED_START_KEY + encodeURIComponent(String(scope && scope.account || '').trim().toLowerCase());
  }
  /* The door's own grammar, stated once. 36 chars of uuid-shaped hex clears
     the 16 floor with room, carries no control byte, and needs no trim. */
  function unlimitedRequestIdValid(id) {
    var s = String(id == null ? '' : id);
    return s === s.replace(/^\s+|\s+$/g, '') && s.length >= 16 && s.length <= 128 &&
      !/[\x00-\x1f\x7f]/.test(s);
  }
  function unlimitedMintRequestId() {
    return rhex(8) + '-' + rhex(4) + '-4' + rhex(3) + '-8' + rhex(3) + '-' + rhex(12);
  }
  /* One stable id per Start tap. The UI calls this BEFORE it calls the door,
     hands the answer straight back to unlimitedStart, and clears it only once
     a receipt has landed and been consumed. A reload mid-flight re-reads the
     same id, so the ambiguous first request can never become a second spend. */
  function unlimitedStartRequestId(scope) {
    scope = scope || captureCombatAccount();
    var key = unlimitedStartJournalKey(scope), raw = null;
    try { raw = localStorage.getItem(key); }
    catch (_) {
      /* R647 (Jan): "just call it battlegrounds" — and this message already
         said "reopen Battlegrounds" two lines down, so the mode was wearing
         two names inside one error. */
      throw new Error('This device could not check for a saved Battlegrounds start. Storage access is required before spending an entry.');
    }
    if (raw != null) {
      var row = null;
      try { row = JSON.parse(raw); } catch (_) {}
      /* A row that exists but cannot be trusted is NOT quietly replaced: a
         fresh id here would spend a second Ticket for the same tap. */
      if (!plainObject(row) || row.v !== 1 ||
          String(row.account || '') !== String(scope.account || '') ||
          !unlimitedRequestIdValid(row.requestId)) {
        throw new Error('A saved Battlegrounds start could not be verified. No entry was spent. Reopen Battlegrounds before starting another run.');
      }
      return String(row.requestId);
    }
    var fresh = { v: 1, account: String(scope.account || ''), requestId: unlimitedMintRequestId(), at: Date.now() };
    var write = JSON.stringify(fresh);
    try {
      localStorage.setItem(key, write);
      if (localStorage.getItem(key) !== write) throw new Error('journal did not persist');
    } catch (_) {
      throw new Error('This device could not remember the Battlegrounds start before sending it. Storage access is required before spending an entry.');
    }
    return fresh.requestId;
  }
  function unlimitedClearStartRequest(scope, requestId) {
    scope = scope || captureCombatAccount();
    var key = unlimitedStartJournalKey(scope);
    try {
      var saved = JSON.parse(localStorage.getItem(key) || 'null');
      if (saved && String(saved.requestId) === String(requestId)) localStorage.removeItem(key);
    } catch (_) {}
  }

  /* ---- the sandbox mirror -------------------------------------------------
     R450b's lesson, carried: every sandbox read walks through this accessor,
     so nothing can ever write onto a detached object. The key set mirrors the
     state door's own reply (R621B:842-873 and :955-989) field for field, so
     the browser never branches on whether it is talking to a server. */
  function localUnlim() {
    return window.__hohLocalUnlimited || (window.__hohLocalUnlimited = {
      ok: true, live: false, run_id: null, run_family: null, seed: null,
      round: null, best_round: 0, best_round_at_start: null,
      aelric_revisit: false, best_at: null, run_status: null,
      run_created_at: null, wave_start: null, wave_count: null,
      engine_ver: null, at_stop: false, hp: null, max_hp: null, armor: null,
      ward: null, lock_a: null, items: null, buff_ids: null, fallen_hero: null,
      party: null, recruits: null, banked: null, shop_latched: null,
      receipt: null, last_receipt: null, packs_unopened: 0,
      expires_in_seconds: null, mode_open: true,
      round10_heroes: 0, round20_heroes: 0, round30_heroes: 0, allowed_party: 1,
      /* sandbox-only bookkeeping, never part of the door's reply */
      _shop: {}, _partyHeroBest: {}, _rootPartyKeys: [], _sandbox: true
    });
  }
  function unlimitedProgressCount(value) {
    return Math.max(0, Math.min(3, value | 0));
  }
  function unlimitedAllowedParty(value) {
    return Math.max(1, Math.min(5, value | 0));
  }
  function unlimitedCanonicalHeroId(key) {
    var match = String(key || '').toLowerCase().match(/^(?:ff|own):([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/);
    return match ? match[1] : null;
  }
  function unlimitedLocalProgress(U, engineVer) {
    U = U || localUnlim();
    var ledger = plainObject(U._partyHeroBest) ? U._partyHeroBest : {};
    var c10 = 0, c20 = 0, c30 = 0;
    Object.keys(ledger).forEach(function (id) {
      var best = ledger[id] | 0;
      if (best >= 10) c10++;
      if (best >= 20) c20++;
      if (best >= 30) c30++;
    });
    c10 = unlimitedProgressCount(c10);
    c20 = unlimitedProgressCount(c20);
    c30 = unlimitedProgressCount(c30);
    var e = _E(), ver = (engineVer | 0) || (U.engine_ver | 0) || ((e && e.ENGINE_VERSION) | 0);
    /* R728 is a setup policy, not a replay rule: keep the frozen engine bytes.
       R778 (Jan): "best 40 to unlock the 4th slot", the sandbox twin of the
       R778B slot helper, so the sandbox perk path agrees with live. */
    var slots = ver >= 88
      ? ((U.best_round | 0) >= 80 ? 5 : (U.best_round | 0) >= 40 ? 4 :
         (U.best_round | 0) >= 30 ? 3 : (U.best_round | 0) >= 20 ? 2 : 1)
      : e && typeof e.unlimitedPartySlots === 'function'
      ? e.unlimitedPartySlots(U.best_round | 0, ver,
          { round10Heroes: c10, round20Heroes: c20, round30Heroes: c30 })
      : (ver >= 75
          ? ((U.best_round | 0) >= 80 ? 5 : (U.best_round | 0) >= 50 ? 4 :
             (U.best_round | 0) >= 30 ? 3 : c10 >= 3 ? 2 : 1)
          : ver >= 74
            ? (c20 >= 3 && c30 >= 3 ? ((U.best_round | 0) >= 80 ? 5 :
               (U.best_round | 0) >= 50 ? 4 : 3) : c20 >= 3 ? 2 : 1)
            : 1);
    U._partyHeroBest = ledger;
    U.round10_heroes = c10;
    U.round20_heroes = c20;
    U.round30_heroes = c30;
    U.allowed_party = unlimitedAllowedParty(slots);
    if (ver >= 75) {
      return { round10Heroes: c10, round20Heroes: c20,
        round30Heroes: c30, allowedParty: U.allowed_party };
    }
    return { round20Heroes: c20, round30Heroes: c30,
      allowedParty: U.allowed_party };
  }
  function unlimitedLocalCreditStarters(U, reachedRound) {
    if (!(reachedRound >= 10)) return unlimitedLocalProgress(U);
    var ledger = plainObject(U._partyHeroBest) ? U._partyHeroBest : {};
    (Array.isArray(U._rootPartyKeys) ? U._rootPartyKeys : []).forEach(function (key) {
      var id = unlimitedCanonicalHeroId(key);
      if (id) ledger[id] = Math.max(ledger[id] | 0, reachedRound | 0);
    });
    U._partyHeroBest = ledger;
    return unlimitedLocalProgress(U);
  }
  /* The segment window, derived from the engine's own twin exactly the way
     both SQL doors derive it (R621B:685-688, R611B continue :1030-1035):
     one Round at a time until the Round just added is a stop, capped at the
     engine's absoluteSegmentMax. Never a literal. */
  function unlimitedSegmentCount(roundStart) {
    var e = _E(), start = Math.max(1, roundStart | 0);
    var cap = Math.max(1, (e && e.UNLIMITED_SEGMENT_MAX_V65 | 0) || 5);
    var n = 1;
    while (n < cap) {
      var plan = null;
      try { plan = e && e.wavePlan ? e.wavePlan(start + n - 1, 65) : null; } catch (_) { plan = null; }
      if (plan && plan.stopAfter) break;
      n++;
    }
    return n;
  }
  function unlimitedShopRule(engineVer) {
    var e = _E();
    var rule = e && typeof e.unlimitedShopRule === 'function'
      ? e.unlimitedShopRule(engineVer) : (e && e.UNLIMITED_SHOP_RULE_V65);
    /* fail closed on a late engine asset rather than invent a shop */
    if (!rule || !(rule.heal > 0) || !(rule.stackCap > 0)) return null;
    return rule;
  }

  /* ---- the state door ----------------------------------------------------
     hoh_bg_unlimited_state(p_email, p_pin). Browser granted, WRITES NOTHING
     (R621B:787-791), so it is safe on every gate paint. It is the ladderState
     pattern exactly: one cache for the length of a screen paint, one
     synchronous mirror, one event, and every downstream reader takes the
     mirror rather than a second RPC.
     ⛔ THE SQL CARRIES ONE KEY THE BRIEF'S FIELD LIST DOES NOT: `mode_open`,
     computed in BOTH branches, which is how the gate degrades to the legacy
     climb without spending a Ticket to discover the mode is shut.
     ⛔ AND IT OMITS `ledger` ENTIRELY (R621B:793-795), so the end ceremony's
     fresh-device contribution chart has no source here and must come from
     the frozen settlement receipt. */
  var _unlimCache = null;
  var _unlimSeq = 0;
  function unlimitedPublish(account, row) {
    try {
      var round10 = unlimitedProgressCount(row && (row.round10_heroes != null ? row.round10_heroes : row.round10Heroes));
      var round20 = unlimitedProgressCount(row && (row.round20_heroes != null ? row.round20_heroes : row.round20Heroes));
      var round30 = unlimitedProgressCount(row && (row.round30_heroes != null ? row.round30_heroes : row.round30Heroes));
      var best = Math.max(0, (row && row.best_round) | 0);
      var bestAtStart = row && row.best_round_at_start;
      bestAtStart = typeof bestAtStart === 'number' && isFinite(bestAtStart) &&
        bestAtStart >= 0 && Math.floor(bestAtStart) === bestAtStart ? bestAtStart : null;
      var startVer = row && row.start_engine_ver != null ? (row.start_engine_ver | 0) :
        (row && row._sandbox && _E() ? _E().ENGINE_VERSION : 67);
      var allowed = unlimitedAllowedParty(row && (row.allowed_party != null ? row.allowed_party : row.allowedParty));
      /* Engine 74–87's counts and best Round are the persisted quest facts. A
         stale/malformed allowance must not make setup promise seats the start
         door will reject—or hide a seat the player really earned. Re-derive
         the display value with the same versioned rule the SQL helper uses. */
      /* R728: Engine88+ setup uses the server allowance directly. Replaying
         the engine's historical hero-count helper here would undo the new
         Best20 rule. Invalid/missing allowances remain closed at one seat. */
      if (startVer >= 88) {
        var serverAllowed = Number(row && (row.allowed_party != null ? row.allowed_party : row.allowedParty));
        allowed = isFinite(serverAllowed) && Math.floor(serverAllowed) === serverAllowed &&
          serverAllowed >= 1 && serverAllowed <= 5 ? serverAllowed : 1;
      }
      if (startVer >= 74 && startVer < 88) {
        var progressEngine = _E();
        allowed = progressEngine && typeof progressEngine.unlimitedPartySlots === 'function'
          ? unlimitedAllowedParty(progressEngine.unlimitedPartySlots(best, startVer,
              { round10Heroes: round10, round20Heroes: round20, round30Heroes: round30 }))
          : (startVer >= 75
              ? (best >= 80 ? 5 : best >= 50 ? 4 : best >= 30 ? 3 : round10 >= 3 ? 2 : 1)
              : round20 >= 3 && round30 >= 3
                ? (best >= 80 ? 5 : best >= 50 ? 4 : 3) : round20 >= 3 ? 2 : 1);
      }
      var published = {
        account: account,
        live: !!(row && row.ok === true && row.live),
        run_id: (row && row.run_id != null) ? row.run_id : null,
        run_status: (row && row.run_status) || null,
        round: (row && row.round) | 0,
        best_round: best,
        bestRoundAtStart: bestAtStart,
        cleared20AtStart: row && typeof row.cleared20_at_start === 'boolean' ? row.cleared20_at_start : undefined,
        aelricRevisit: bestAtStart !== null && bestAtStart >= 21,
        start_engine_ver: startVer,
        round20Heroes: round20,
        round30Heroes: round30,
        allowedParty: allowed,
        _sandbox: !!(row && row._sandbox),
        at_stop: !!(row && row.at_stop),
        mode_open: !!(row && row.mode_open)
      };
      if (startVer >= 75) published.round10Heroes = round10;
      /* R778: the away block rides on the mirror too, but it is a SECOND door
         and it never holds this publish up. The last block for the same
         account is carried across so the gate line does not flicker while
         the fresh one is in flight; the fetch below replaces it when it
         lands and dispatches again. */
      var priorMirror = window.__hohBgUnlimited;
      if (priorMirror && priorMirror.account === account && priorMirror.away) published.away = priorMirror.away;
      window.__hohBgUnlimited = published;
      document.dispatchEvent(new CustomEvent('hoh:bg-unlimited-changed'));
      if (!published._sandbox && (published.live || best >= AWAY_EARNED_AT)) awayMirrorFetch(published);
    } catch (_) {}
  }
  /* ⛔ R643 · JAN'S RULE: "abilities lock for a run". Nothing enforced it.
     WHAT IS AND IS NOT ALREADY TRUE, measured against the installed SQL rather
     than assumed. The FIGHT is already safe: the Unlimited start door snapshots
     each hero's skill and passive into the run roster (R621B:514-523), so the
     judge simulates what the player STARTED with and a mid-run reroll cannot
     change a fight in flight. What is NOT safe is the RECORD: R627B's
     best_party reads abilities live from card_skills at settle time
     (R627B:586-592), so a reroll mid-run rewrites the permanent leaderboard
     row for a run it never took part in. That half needs SQL and is reported
     rather than silently added to an owner sitting.
     What this closes is the player-facing half, which is Jan's actual ruling
     and the part that costs a person something: today they can spend a Mythic
     Crystal mid-climb on a reroll that provably cannot help the run they are
     in, and nothing on the screen says so.
     ⛔ IT FAILS OPEN, DELIBERATELY. The published mirrors can be absent or
     belong to another account, and refusing a roll on missing state would
     block a legitimate one, which is worse than the gap it closes given the
     fight is already frozen. Only a mirror that positively says LIVE for the
     CURRENT account locks anything. */
  function runLocksAbilities() {
    /* god mode plays the real game while signed in (R410), so localMode() is
       false for Jan mid-run and would not exempt him. godMode() is the test. */
    if (godMode()) return null;
    var acct = collectionAccount();
    if (!acct) return null;
    try {
      var U = window.__hohBgUnlimited;
      if (U && U.live && U.account === acct) return 'unlimited';
      var L = window.__hohBgClimb;
      if (L && L.live && L.account === acct) return 'climb';
    } catch (_) {}
    return null;
  }
  function unlimitedForget() {
    _unlimCache = null;
    _unlimSeq++;
    try {
      window.__hohBgUnlimited = null;
      window.__hohBgUnlimitedAsked = 0;
      document.dispatchEvent(new CustomEvent('hoh:bg-unlimited-changed'));
    } catch (_) {}
  }
  function unlimitedState(force) {
    if (localMode()) {
      var U = localUnlim();
      unlimitedPublish(collectionAccount(), U);
      return Promise.resolve(U);
    }
    if (!signedIn()) return Promise.reject(new Error('sign in first'));
    var stateScope = captureCombatAccount();
    var cacheKey = ladderCacheKey(stateScope);
    if (!force && _unlimCache && _unlimCache.key === cacheKey) { rememberLiveRunEngineR797(_unlimCache.value); return Promise.resolve(_unlimCache.value); }
    var requestSeq = ++_unlimSeq;
    return rpc('hoh_bg_unlimited_state', { p_email: stateScope.email, p_pin: stateScope.pin })
      .then(function (data) {
        var r = Array.isArray(data) ? data[0] : data;
        if (requestSeq !== _unlimSeq || !combatAccountIsCurrent(stateScope)) {
          throw accountChangedError('the run was loading', false);
        }
        rememberLiveRunEngineR797(r);
        _unlimCache = { key: cacheKey, value: r || null };
        unlimitedPublish(stateScope.account, r);
        return r;
      });
  }

  /* ---- the start door ----------------------------------------------------
     hoh_bg_unlimited_start(p_email, p_pin, p_party, p_recruits, p_request_id):
     the R621 FIVE-argument signature (R621B:266-274 drops the old four-arg
     form first, engineering rule 7, so there is no ambiguous overload left to
     call). It SPENDS THE TICKET ITSELF through hoh_bg_spend (R621B:673), so
     this wrapper must never call spendBgTicket: that would charge twice.
     ⛔ THE REFUSALS ARRIVE BEFORE ANY SPEND: mode_not_open (the dormancy
     gate, stamp below 65 or no startable runtime), already_live{run_id,round},
     and the spend's own reason (no_tickets{balance}). None of them throws.
     ⛔ THE REPLY NAMES BOTH LANES (R621B:744-772): party_kept/party_dropped/
     party_hires and recruits_kept/recruits_dropped/recruit_hires, with
     allowed_picks/picks_kept/picks_dropped/hires kept ONE WAVE as aliases.
     Read the lane keys; the aliases are the union and will go. */
  function unlimitedStart(partyKeys, recruitKeys, requestId) {
    var party = (partyKeys || []).map(function (k) { return String(k == null ? '' : k); });
    var recruits = (recruitKeys || []).map(function (k) { return String(k == null ? '' : k); });
    /* The door raises 22023 on a list longer than 64 (R621B:399-404), and a
       raise is not a refusal a screen can read. Refuse it here by name
       instead. NOT by truncating: a silently dropped key is a hero the player
       chose and nothing ever names, which is the opposite of the door's own
       drop-and-name contract. A party is five and a bench is at most ten, so
       this can only ever fire on a bug. */
    if (party.length > 64 || recruits.length > 64) {
      return Promise.reject(new Error('That party list is too long to send. Reopen Battlegrounds and pick again.'));
    }
    if (localMode()) return unlimitedStartLocal(party, recruits, requestId);
    if (!signedIn()) return Promise.reject(new Error('sign in first'));
    var startScope = captureCombatAccount(), ticketOrigin = battleTicketSpendOrigin();
    if (!startScope.email || !startScope.pin) return Promise.reject(new Error('sign in first'));
    /* The id is the caller's, so one Start tap keeps one id across every
       retry. A missing or malformed one is refused here rather than sent: the
       door raises 22023 on it, and a raise is not a refusal a screen can
       read. */
    if (!unlimitedRequestIdValid(requestId)) {
      return Promise.reject(new Error('This run had no saved start request. Reopen Battlegrounds and start again.'));
    }
    /* R749: verify the live judge before the ticket transaction, exactly as
       startContract does. A stale tab must learn it needs a reload before it
       spends an entry and binds a run its engine cannot play. */
    return workerGate(true).then(function () {
      return prepareEmberPortrait(startScope, party);
    }).then(function () {
      if (!combatAccountIsCurrent(startScope)) throw accountChangedError('the run was opening', false);
      return rpc('hoh_bg_unlimited_start', {
      p_email: startScope.email, p_pin: startScope.pin,
      p_party: party, p_recruits: recruits, p_request_id: String(requestId)
      });
    }).then(function (data) {
      var r = Array.isArray(data) ? data[0] : data;
      if (!combatAccountIsCurrent(startScope)) {
        throw accountChangedError('the run was opening', true);
      }
      /* The Ticket counter is the door's, never this device's arithmetic.
         ticket_balance is present only on the success receipt; the
         no_tickets refusal carries `balance` instead (R621B:678). */
      var bal = (r && r.ok === true) ? r.ticket_balance : (r && r.balance);
      if (typeof bal === 'number' && typeof window.hohSetBgTicketsLocal === 'function') {
        try { window.hohSetBgTicketsLocal(bal); } catch (_) {}
      }
      if (r && r.reason === 'run_reset') unlimitedClearStartRequest(startScope, requestId);
      if (r && r.ok === true) {
        unlimitedForget();
        if (r.ticket_spent === true && r.run_id) confirmedBattleTicketSpend(startScope, 'start:' + r.run_id, ticketOrigin);
      }
      return r || { ok: false, reason: 'no_answer' };
    });
  }

  /* ---- the continue door -------------------------------------------------
     hoh_bg_unlimited_continue(p_email, p_pin, p_parent_run_id) →
     {ok, replay, run_id, wave_start, wave_count, round}. Replay-safe by
     parent id: a successful continue rebinds the row to the child, and a
     retry carrying the parent answers with that same child (R611B:1006-1023).
     ⛔ THIS DOOR HAS NO {ok:false, reason} VOCABULARY AT ALL. Every refusal
     is a RAISE ('no live Unlimited run', 'parent is not a settled win',
     'parent is not the bound Unlimited segment', 'continuation lineage
     crossed', 'engine moved between Unlimited segments'). So the rejection is
     let through with the RPC's own metadata attached rather than dressed up
     as a reason this file invented. */
  function unlimitedContinue(parentRunId) {
    if (localMode()) return unlimitedContinueLocal(parentRunId);
    if (!signedIn()) return Promise.reject(new Error('sign in first'));
    var contScope = captureCombatAccount();
    return rpcDeadline(rpc('hoh_bg_unlimited_continue', {
      p_email: contScope.email, p_pin: contScope.pin,
      p_parent_run_id: Number(parentRunId) || 0
    }), 15000, 'The next round did not answer. Your round is saved. Try again.').then(function (data) {
      var r = Array.isArray(data) ? data[0] : data;
      if (!combatAccountIsCurrent(contScope)) {
        throw accountChangedError('the next round was opening', true);
      }
      if (r && r.ok === true) unlimitedForget();
      return r || { ok: false, reason: 'no_answer' };
    });
  }

  /* ---- the stop door -----------------------------------------------------
     hoh_bg_unlimited_stop(p_email, p_pin, p_buy) → {ok, round, stop_round,
     hp, max_hp, items, shop, bought, latched{shop,buy}}. The FIRST call heals
     and restocks; a later call replays the latched receipt. p_buy is
     mend|bolt|ally exactly once for 20 Hearts, charged INSIDE the door
     through the wallet (R611B:1206-1212), so this wrapper must never
     pre-debit the device bank.
     Refusals, all named and none thrown: not_live, not_at_stop{round},
     stop_passed, item_at_cap{item,items}, not_enough_hearts.
     ⛔ `latched` IS THE PRE-CALL SNAPSHOT (R611B built it from the row it
     read for update, before its own update). So on a genuine first visit
     latched.shop is FALSE even though this very call just latched it: the key
     means "this was a replay", never "the shop is now latched". Read the
     state door's shop_latched for the settled truth. */
  function unlimitedStop(buyKind) {
    var buy = (buyKind === 'mend' || buyKind === 'bolt' || buyKind === 'ally') ? buyKind : null;
    if (localMode()) return unlimitedStopLocal(buy);
    if (!signedIn()) return Promise.reject(new Error('sign in first'));
    var stopScope = captureCombatAccount();
    return rpc('hoh_bg_unlimited_stop', {
      p_email: stopScope.email, p_pin: stopScope.pin, p_buy: buy
    }).then(function (data) {
      var r = Array.isArray(data) ? data[0] : data;
      if (!combatAccountIsCurrent(stopScope)) {
        throw accountChangedError('the shop was answering', true);
      }
      if (r && r.ok === true) unlimitedForget();
      return r || { ok: false, reason: 'no_answer' };
    });
  }

  /* ---- the two release doors --------------------------------------------
     hoh_bg_unlimited_expire(p_email, p_pin) is the gate's SELF-RELEASE and is
     safe to call on any gate paint: {ok:true, released:true} when a run older
     than 48 hours (or a live row with no run at all) is freed,
     {ok:true, released:false} with nothing live, and {ok:false,
     reason:'not_expired', run_id} inside the window (R611B:2128-2146).
     hoh_bg_unlimited_forfeit(p_email, p_pin) is that same body with the
     48-hour test removed (R621B:1008-1059), so a PARKED refusal can free the
     account now instead of waiting out two days. It keeps best_round,
     best_at and all three banked totals by deliberate omission, and its reply
     adds run_id, best_round and banked{hearts,packs,crystals}. */
  function unlimitedExpire() {
    if (localMode()) return unlimitedReleaseLocal(true);
    if (!signedIn()) return Promise.reject(new Error('sign in first'));
    var expScope = captureCombatAccount();
    return rpc('hoh_bg_unlimited_expire', { p_email: expScope.email, p_pin: expScope.pin })
      .then(function (data) {
        var r = Array.isArray(data) ? data[0] : data;
        if (r && r.ok === true && r.released === true) unlimitedForget();
        return r || { ok: false, reason: 'no_answer' };
      });
  }
  function unlimitedForfeit(opts) {
    if (localMode()) return unlimitedReleaseLocal(false);
    if (!signedIn()) return Promise.reject(new Error('sign in first'));
    var ffScope = captureCombatAccount();
    /* R791: the release that never replays. It ends THIS run (the id is
       checked on the server) and pays nothing new, because nothing from the
       open part of the run is owed. The judged path below replays the
       segment, which is exactly what a run the engine cannot play refuses. */
    if(opts&&opts.runId&&opts.release===true)return unlimitedReleaseRun(opts.runId,ffScope);
    if(opts&&opts.runId)return settleOnce(opts.runId,'solo_forfeit',{
      engineVer:opts.engineVer,mode:'unlimited',plays:opts.plays,lastCompletedRound:opts.lastCompletedRound
    },ffScope).then(function(r){
      if(!combatAccountIsCurrent(ffScope))throw accountChangedError(opts.solo?'the solo result was saving':'the run was ending',true);
      if(!r||r.ok!==true||r.solo_saved!==true)throw forfeitRefusal(r,opts.solo);
      applyUltimateQuestReceipt(r.ultimate_quests,ffScope);unlimitedForget();return r;
    });
    return rpc('hoh_bg_unlimited_forfeit', { p_email: ffScope.email, p_pin: ffScope.pin })
      .then(function (data) {
        var r = Array.isArray(data) ? data[0] : data;
        if (r && r.ok === true && r.released === true) unlimitedForget();
        return r || { ok: false, reason: 'no_answer' };
      });
  }
  /* R791B adds hoh_bg_unlimited_forfeit(p_email, p_pin, p_run_id): the same
     body as the two-argument door, but it lets go only when the bound run is
     this run. Until R791B is installed the call answers PGRST202, and the
     two-argument door stands in ONLY after the state door confirms the bound
     run is still this one, so an End run tapped on an old screen can never
     end a newer run another device started. */
  function unlimitedReleaseRun(runId, scope) {
    function reply(data) {
      var r = Array.isArray(data) ? data[0] : data;
      if (!combatAccountIsCurrent(scope)) throw accountChangedError('the run was ending', true);
      if (r && r.ok === true && r.released === true) unlimitedForget();
      return r || { ok: false, reason: 'no_answer' };
    }
    return rpc('hoh_bg_unlimited_forfeit', { p_email: scope.email, p_pin: scope.pin, p_run_id: runId })
      .then(reply, function (err) {
        if (!rpcDefinitelyMissing(err, 'hoh_bg_unlimited_forfeit')) throw err;
        return unlimitedState(true).then(function (st) {
          if (!combatAccountIsCurrent(scope)) throw accountChangedError('the run was ending', false);
          if (!st || st.ok !== true) throw new Error('Your run could not be checked. Try again.');
          if (!st.live) return { ok: true, released: false, run_id: runId };
          if (String(st.run_id) !== String(runId)) return { ok: false, reason: 'run_changed', run_id: st.run_id };
          return rpc('hoh_bg_unlimited_forfeit', { p_email: scope.email, p_pin: scope.pin }).then(reply);
        });
      });
  }
  /* A judged End run the Worker answered but did not save. The reply's own
     code rides on the error so the screen can tell a refusal that repeats
     (offer the release) from weather (try again). Solo wording only for a
     solo trial: a normal run has no "solo result". */
  function forfeitRefusal(r, solo) {
    var e = new Error(solo ? 'Your solo result could not be saved. The run is still open. Try again.'
                           : 'The run could not be ended. It is still open. Try again.');
    var code = String((r && (r.error || r.reason)) || 'no_answer');
    e.__hocRefused = code;
    e.settleCode = code;
    e.settleDetail = String((r && r.detail) || '').slice(0, 200);
    e.__hocDeterministic = !!(r && r.deterministic === true);
    e.canEndBankedOnly = !!(r && r.can_end_banked_only === true);
    e.httpStatus = r && r.httpStatus;
    return e;
  }

  /* ---- R778 · Away mode and one driver per run ---------------------------
     Jan (R778): the run lives on the server and the device you last opened
     Battlegrounds on drives it. A phone that picks the run up pauses the
     desktop, and a party with Away mode keeps fighting once its driver is
     gone. Every door below is (p_email, p_pin, ...) -> jsonb, refuses as
     {ok:false, reason}, and is wrapped the way the other browser doors are:
     the account scope is captured before the call and checked after it,
     and a deadline bounds the wait.
     ⛔ THE RELEASE ORDER IS R778A, R778B, THEN THE THEME PUSH, THEN R778C.
     A page that still meets a missing door (the schema reload moment, or
     the order not followed) gets PGRST202, and every wrapper turns exactly
     that (rpcDefinitelyMissing, never a timeout or a permission error) into
     {ok:false, reason:'not_installed'} so the Hall behaves as it did the
     day before. The sandbox has no server to drive, so it answers
     reason:'local'. */
  var AWAY_EARNED_AT = 50;
  var DEVICE_KEY = 'hoh-bg-device-v1';
  var _deviceMemo = '';
  /* the door's own grammar: btrim-equal, 16..128 chars, no control byte,
     phone: or desktop: first */
  function deviceIdValid(id) {
    var s = String(id == null ? '' : id);
    return /^(phone|desktop):/.test(s) && unlimitedRequestIdValid(s);
  }
  /* R778 (Jan): "Your party is on your phone". A touch screen with no hover
     anywhere is a phone; everything else, a laptop with a touch screen
     included, is a desktop. Decided once, when the id is minted. */
  function deviceKind() {
    try {
      var coarse = window.matchMedia('(pointer: coarse)').matches;
      var hover = window.matchMedia('(any-hover: hover)').matches;
      return coarse && !hover ? 'phone' : 'desktop';
    } catch (_) { return 'desktop'; }
  }
  /* One id per browser, kept for good. When storage is refused the id still
     holds for this page's life, so the heartbeat never names two drivers.
     The 119 character ceiling leaves room for the tab part below inside the
     door's 128. */
  function browserDeviceId() {
    var saved = '';
    try { saved = String(localStorage.getItem(DEVICE_KEY) || ''); } catch (_) { saved = ''; }
    if (deviceIdValid(saved) && saved.length <= 119) return saved;
    if (deviceIdValid(_deviceMemo)) return _deviceMemo;
    _deviceMemo = deviceKind() + ':' + unlimitedMintRequestId();
    try { localStorage.setItem(DEVICE_KEY, _deviceMemo); } catch (_) {}
    return _deviceMemo;
  }
  /* R778 review: one driver per TAB. Two tabs of one browser shared the
     browser id, so the heartbeat told both "you drive" and both settled the
     same segment, the very desync one driver exists to stop. The tab part
     lives in sessionStorage, so a reload keeps driving, and a new tab mints
     its own. A duplicated tab copies sessionStorage, so a new page asks the
     other pages once over a BroadcastChannel whether its tab id is already
     in use, and mints a fresh one if it is. */
  var TAB_KEY = 'hoh-bg-tab-v1';
  var _tabMemo = '', _tabNonce = '', _tabChannel = null, _tabAsking = false;
  function tabIdValid(t) { return /^[0-9a-f]{8}$/.test(String(t == null ? '' : t)); }
  function tabIdMint() {
    _tabMemo = rhex(8);
    try { sessionStorage.setItem(TAB_KEY, _tabMemo); } catch (_) {}
  }
  function tabIdWatch() {
    if (_tabChannel) return;
    try {
      if (typeof BroadcastChannel !== 'function') return;
      _tabNonce = rhex(8);
      _tabChannel = new BroadcastChannel('hoh-bg-tab-v1');
      _tabChannel.onmessage = function (ev) {
        var m = ev && ev.data;
        if (!m || m.nonce === _tabNonce || m.tab !== _tabMemo) return;
        if (m.kind === 'hello') _tabChannel.postMessage({ kind: 'mine', tab: _tabMemo, nonce: _tabNonce });
        else if (m.kind === 'mine' && _tabAsking) { _tabAsking = false; tabIdMint(); }
      };
      /* only a page's own question is answered: a page that has been open
         for a while never gives its id up because two newer tabs asked */
      _tabAsking = true;
      setTimeout(function () { _tabAsking = false; }, 3000);
      _tabChannel.postMessage({ kind: 'hello', tab: _tabMemo, nonce: _tabNonce });
    } catch (_) {}
  }
  function tabId() {
    if (tabIdValid(_tabMemo)) return _tabMemo;
    var saved = '';
    try { saved = String(sessionStorage.getItem(TAB_KEY) || ''); } catch (_) { saved = ''; }
    if (tabIdValid(saved)) _tabMemo = saved;
    else tabIdMint();
    tabIdWatch();
    return _tabMemo;
  }
  /* asked when this file loads, so a duplicated tab has its own id long
     before its first claim */
  try { tabId(); } catch (_) {}
  /* '<phone|desktop>:<browser id>.<tab id>': still starts with the kind, so
     the doors' started_by and the other-device card read it as before */
  function deviceId() {
    return browserDeviceId() + '.' + tabId();
  }
  function awayCall(fn, args, ms, message, what) {
    if (localMode()) return Promise.resolve({ ok: false, reason: 'local' });
    if (!signedIn()) return Promise.reject(new Error('sign in first'));
    var scope = captureCombatAccount();
    if (!scope.email || !scope.pin) return Promise.reject(new Error('sign in first'));
    var body = { p_email: scope.email, p_pin: scope.pin }, k;
    for (k in args) if (Object.prototype.hasOwnProperty.call(args, k)) body[k] = args[k];
    return rpcDeadline(rpc(fn, body), ms, message).then(function (data) {
      var r = Array.isArray(data) ? data[0] : data;
      if (!combatAccountIsCurrent(scope)) throw accountChangedError(what, true);
      return r || { ok: false, reason: 'no_answer' };
    }, function (err) {
      if (rpcDefinitelyMissing(err, fn)) return { ok: false, reason: 'not_installed' };
      throw err;
    });
  }
  var AWAY_SLOW = 'Your run took too long to answer. Try again.';
  /* the heartbeat: 15 s while a live run is on screen, and the leaving call */
  function awaySeen(leaving) {
    return awayCall('hoh_bg_unlimited_seen', { p_device: deviceId(), p_leaving: !!leaving },
      8000, AWAY_SLOW, 'your run was checking in');
  }
  /* The same request as awaySeen(true), sent with fetch keepalive so it
     survives pagehide or a phone locking. supabase-js cannot ask for
     keepalive, and its own fetch waits on the auth session before sending,
     which a closing page may never give it. So the request is rebuilt from
     the SAME client, synchronously: its REST url, its own headers, its
     schema profile and its key, exactly what rpc() puts on the wire (the
     client has no Supabase session here; the key is the bearer it sends).
     Returns true when the request left, false when it could not be built. */
  function awaySeenBeacon() {
    try {
      if (localMode() || !signedIn() || typeof fetch !== 'function') return false;
      var c = window.hohSupabaseClient, rest = c && c.rest;
      var base = rest && typeof rest.url === 'string' ? rest.url.replace(/\/+$/, '') : '';
      var key = c && typeof c.supabaseKey === 'string' ? c.supabaseKey : '';
      if (!base || !key) return false;
      var headers = {}, h = rest.headers;
      if (h && typeof h.forEach === 'function') h.forEach(function (v, name) { headers[String(name).toLowerCase()] = v; });
      else if (h && typeof h === 'object') Object.keys(h).forEach(function (name) { headers[name.toLowerCase()] = h[name]; });
      headers['content-type'] = 'application/json';
      if (typeof rest.schemaName === 'string' && rest.schemaName) headers['content-profile'] = rest.schemaName;
      if (!headers.apikey) headers.apikey = key;
      if (!headers.authorization) headers.authorization = 'Bearer ' + key;
      var scope = captureCombatAccount();
      if (!scope.email || !scope.pin) return false;
      fetch(base + '/rpc/hoh_bg_unlimited_seen', {
        method: 'POST', headers: headers, keepalive: true,
        body: JSON.stringify({ p_email: scope.email, p_pin: scope.pin, p_device: deviceId(), p_leaving: true })
      })['catch'](function () {});
      return true;
    } catch (_) { return false; }
  }
  /* "I am playing here now". Always takes over, and materialises the trip
     the server saw start while nobody was driving. */
  function awayClaim() {
    return awayCall('hoh_bg_unlimited_away_claim', { p_device: deviceId() }, 12000, AWAY_SLOW, 'your run was opening')
      .then(awayAfterWrite);
  }
  /* read only: the mirror's away block comes from here */
  function awayState() {
    return awayCall('hoh_bg_unlimited_away_state', { p_device: deviceId() }, 8000, AWAY_SLOW, 'your run was loading');
  }
  /* orders go as the door's wire shape ({stop_before, bench_left, shop},
     HOHAway.toWire); null keeps the stored orders */
  function awaySet(on, orders) {
    var wire = (orders && typeof orders === 'object' && !Array.isArray(orders)) ? orders : null;
    return awayCall('hoh_bg_unlimited_away_set', { p_on: !!on, p_orders: wire }, 8000, AWAY_SLOW, 'Away mode was saving')
      .then(awayAfterWrite);
  }
  /* "Stop watching" */
  function awayStart() {
    return awayCall('hoh_bg_unlimited_away_start', { p_device: deviceId() }, 8000, AWAY_SLOW, 'Away mode was starting')
      .then(awayAfterWrite);
  }
  function awayLog(entry) {
    entry = entry || {};
    return awayCall('hoh_bg_unlimited_away_log', { p_device: deviceId(), p_entry: {
      round: entry.round | 0, kind: String(entry.kind || ''), text: String(entry.text || '').slice(0, 140)
    } }, 8000, AWAY_SLOW, 'your run was writing its log');
  }
  function awayEnd(why, toRound) {
    return awayCall('hoh_bg_unlimited_away_end', { p_device: deviceId(), p_why: String(why || ''),
      p_to_round: toRound == null ? null : (toRound | 0) }, 8000, AWAY_SLOW, 'your run was coming back')
      .then(awayAfterWrite);
  }
  /* the mirror's shape of an away_state reply. fetched_at is this device's
     clock, so a reader can add the time since the answer. */
  function awayBlock(r) {
    return {
      earned: r.earned === true, on: r.on === true,
      orders: (r.orders && typeof r.orders === 'object') ? r.orders : {},
      trip: (r.trip && typeof r.trip === 'object') ? r.trip : { open: false },
      away_seconds: Math.max(0, r.away_seconds | 0),
      driver_kind: r.driver_kind || null,
      you_drive: r.you_drive === true, driving_elsewhere: r.driving_elsewhere === true,
      server_now: r.server_now || null, fetched_at: Date.now()
    };
  }
  /* Fire and forget. Lands on the mirror only if that mirror is still the
     current one for the same account; otherwise it is dropped. A door that
     is not installed yet is asked once per page, not once per publish. */
  var _awayMirrorMissing = false;
  function awayMirrorFetch(published) {
    if (_awayMirrorMissing || !published) return Promise.resolve(null);
    try {
      return awayState().then(function (r) {
        if (r && r.reason === 'not_installed') _awayMirrorMissing = true;
        if (!r || r.ok !== true || window.__hohBgUnlimited !== published) return null;
        if (String(published.account || '') !== collectionAccount()) return null;
        published.away = awayBlock(r);
        document.dispatchEvent(new CustomEvent('hoh:bg-unlimited-changed'));
        return published.away;
      })['catch'](function () { return null; });
    } catch (_) { return Promise.resolve(null); }
  }
  /* after a write, the mirror catches up in the background */
  function awayAfterWrite(r) {
    if (r && r.ok === true) {
      try { var m = window.__hohBgUnlimited; if (m && !m._sandbox) awayMirrorFetch(m); } catch (_) {}
    }
    return r;
  }
  /* R778: the continue door with the child trimmed to wave_count rounds, so
     "Stop before Round X" and "stop when the bench is empty" land between
     rounds instead of at the next stop. Same checks, deadline and error
     shape as unlimitedContinue; a replay answers the child it already made. */
  function unlimitedContinueN(parentRunId, waveCount) {
    var n = Number(waveCount);
    if (!(Math.floor(n) === n && n >= 1 && n <= 5)) {
      return Promise.reject(new Error('The next round could not be sized. Reopen Battlegrounds to pick the run back up.'));
    }
    if (localMode()) return Promise.resolve({ ok: false, reason: 'local' });
    if (!signedIn()) return Promise.reject(new Error('sign in first'));
    var contScope = captureCombatAccount();
    return rpcDeadline(rpc('hoh_bg_unlimited_continue_n', {
      p_email: contScope.email, p_pin: contScope.pin,
      p_parent_run_id: Number(parentRunId) || 0, p_wave_count: n
    }), 15000, 'The next round did not answer. Your round is saved. Try again.').then(function (data) {
      var r = Array.isArray(data) ? data[0] : data;
      if (!combatAccountIsCurrent(contScope)) {
        throw accountChangedError('the next round was opening', true);
      }
      if (r && r.ok === true) unlimitedForget();
      return r || { ok: false, reason: 'no_answer' };
    }, function (err) {
      if (rpcDefinitelyMissing(err, 'hoh_bg_unlimited_continue_n')) return { ok: false, reason: 'not_installed' };
      throw err;
    });
  }

  /* ---- the sandbox twins ------------------------------------------------
     Live Unlimited answers mode_not_open until the stamp is 65, so the
     signed-out sandbox is the only pre-activation playable path and it has to
     be honest: the same reply shapes, the same refusal names, the same
     derived numbers. Every number below comes from the engine's exports. */
  function unlimitedRunBestAtStart(run) {
    var foe = run && run.foe;
    var value = foe && foe.best_round_at_start;
    return typeof value === 'number' && isFinite(value) && value >= 0 && Math.floor(value) === value
      ? value : null;
  }
  function unlimitedStartLocal(party, recruits, requestId) {
    var U = localUnlim(), e = _E();
    if (U.live) {
      return Promise.resolve({ ok: false, reason: 'already_live', run_id: U.run_id, round: U.round });
    }
    if (!e || !e.wavePlan) return Promise.resolve({ ok: false, reason: 'mode_not_open' });
    /* the allowance rule is the door's own arithmetic (R621B:387), stated
       once here so the sandbox screen shows the same number of slots */
    var progress = typeof unlimitedLocalProgress === 'function'
      ? unlimitedLocalProgress(U, e.ENGINE_VERSION | 0)
      : { allowedParty: e.unlimitedPartySlots ? e.unlimitedPartySlots(U.best_round, e.ENGINE_VERSION | 0) : 5,
          round10Heroes: 0, round20Heroes: 0, round30Heroes: 0 };
    var partySlots = progress.allowedParty;
    var allowed = e.ENGINE_VERSION >= 68 ? 0 : Math.min(10, Math.floor((U.best_round | 0) / 10));
    /* ⛔ THE SANDBOX NEVER ANSWERS not_owned, and that is deliberate rather
       than missing. There is no ownership authority signed out, so inventing
       one would teach the screen a verdict the real door might not share.
       over_allowance and duplicate ARE modelled, because both are decidable
       from the lists themselves. */
    var partyKeep = [], recKeep = [], partyDrop = [], recDrop = [], seen = {};
    party.forEach(function (k) {
      if (!k) return;
      if (partyKeep.length >= partySlots) partyDrop.push({ key: k, why: 'over_allowance' });
      else if (seen[k]) partyDrop.push({ key: k, why: 'duplicate' });
      else { seen[k] = 1; partyKeep.push(k); }
    });
    recruits.forEach(function (k) {
      if (!k) return;
      if (recKeep.length >= allowed) recDrop.push({ key: k, why: 'over_allowance' });
      else if (seen[k]) recDrop.push({ key: k, why: 'duplicate' });
      else { seen[k] = 1; recKeep.push(k); }
    });
    var count = unlimitedSegmentCount(1);
    var seed = localSeed();
    /* The server freezes each body with its own key and seat, and the UI
       chunk reads both (key for identity across segments, _unlimSlot for the
       R614 casualty seat map). localSnapshot answers the battle fields only,
       so the identity is stamped on here rather than left for a screen to
       guess. `owned` mirrors the door: only an owned key contributes to
       owned_roster_keys, so a sandbox hall: body is not owned. */
    function stamp(keys, isParty) {
      return localSnapshot(keys).map(function (b, i) {
        b = b || {};
        b.key = keys[i];
        b.owned = String(keys[i]).indexOf('hall:') !== 0;
        b._unlimSlot = i;
        if (isParty) b.party_slot = i;
        return b;
      });
    }
    if (!partyKeep.length) return Promise.resolve({ ok: false, reason: 'no_party' });
    var bodies = stamp(partyKeep, true);
    var bench = stamp(recKeep, false);
    U._rootPartyKeys = partyKeep.map(function (key) {
      return unlimitedCanonicalHeroId(key) ? String(key) : null;
    }).filter(Boolean);
    var bestAtStart = Math.max(0, U.best_round | 0);
    var run = { id: 'local-' + Date.now(), seed: seed, mode: 'unlimited',
      status: 'open', depth: 0, ladder_stage: null, stake: 0, pot: 0,
      engine_ver: (e.ENGINE_VERSION | 0) || 65, wave_start: 1, wave_count: count,
      roster: bodies, recruits: bench,
      foe: { mode: 'unlimited', best_round_at_start: bestAtStart, cleared20_at_start: U.cleared20 === true || bestAtStart >= 21 } };
    window.__hohLocalRun = run;
    U.live = true; U.run_id = run.id; U.run_family = run.id; U.seed = seed;
    U.best_round_at_start = bestAtStart; U.cleared20_at_start = run.foe.cleared20_at_start; U.aelric_revisit = bestAtStart >= 21;
    U.round = 1; U.run_status = 'open'; U.run_created_at = new Date().toISOString();
    U.wave_start = 1; U.wave_count = count; U.engine_ver = run.engine_ver;
    U.at_stop = false; U.hp = 0; U.max_hp = 0; U.armor = 0; U.ward = 0;
    U.lock_a = null; U.items = { mend: 1, bolt: 1, ally: 1 }; U.buff_ids = [];
    U.fallen_hero = []; U.party = bodies; U.recruits = bench;
    U.banked = { hearts: 0, packs: 0, crystals: 0 };
    U.shop_latched = { shop: false, buy: false }; U.receipt = null;
    U.packs_unopened = 0; U.expires_in_seconds = 172800; U._shop = {};
    unlimitedPublish(collectionAccount(), U);
    var startReply = {
      ok: true, kind: 'unlimited_start', request_id: String(requestId || 'sandbox'),
      run_id: run.id, run_family: run.id, engine_ver: run.engine_ver, seed: seed,
      round: 1, wave_start: 1, wave_count: count, best_round: U.best_round | 0,
      best_round_at_start: bestAtStart, cleared20_at_start: run.foe.cleared20_at_start, aelric_revisit: bestAtStart >= 21,
      round20_heroes: progress.round20Heroes, round30_heroes: progress.round30Heroes,
      allowed_party: partySlots, allowed_recruits: allowed, party_kept: partyKeep, party_dropped: partyDrop,
      party_hires: [], recruits_kept: recKeep, recruits_dropped: recDrop,
      recruit_hires: [],
      allowed_picks: allowed, picks_kept: partyKeep.concat(recKeep),
      picks_dropped: partyDrop.concat(recDrop), hires: [],
      party: bodies, recruits: bench, items: { mend: 1, bolt: 1, ally: 1 },
      ticket_balance: null, started_at: U.run_created_at, sandbox: true
    };
    if ((e.ENGINE_VERSION | 0) >= 75) startReply.round10_heroes = progress.round10Heroes;
    return Promise.resolve(startReply);
  }
  function unlimitedContinueLocal(parentRunId) {
    var U = localUnlim();
    if (!U.live) return Promise.reject(new Error('no live Unlimited run'));
    if (String(U.run_id) !== String(parentRunId)) {
      return Promise.reject(new Error('parent is not the bound Unlimited segment'));
    }
    if (U.run_status !== 'settled_win') {
      return Promise.reject(new Error('parent segment is not a settled win'));
    }
    var start = U.round | 0, count = unlimitedSegmentCount(start);
    var run = window.__hohLocalRun || {};
    var bestAtStart = unlimitedRunBestAtStart(run);
    if ((U.engine_ver | 0) >= 80 && bestAtStart === null) {
      return Promise.reject(new Error('Unlimited run is missing its frozen best round'));
    }
    var child = { id: 'local-' + Date.now(), seed: U.seed, mode: 'unlimited',
      status: 'open', depth: 0, ladder_stage: null, stake: 0, pot: 0,
      engine_ver: U.engine_ver, wave_start: start, wave_count: count,
      roster: U.party || [], recruits: U.recruits || [],
      foe: { mode: 'unlimited', best_round_at_start: bestAtStart, cleared20_at_start: run.foe.cleared20_at_start },
      continued_from_run_id: run.id || null };
    window.__hohLocalRun = child;
    U.run_id = child.id; U.run_status = 'open'; U.at_stop = false;
    U.wave_start = start; U.wave_count = count; U.receipt = null;
    unlimitedPublish(collectionAccount(), U);
    return Promise.resolve({ ok: true, replay: false, run_id: child.id,
      wave_start: start, wave_count: count, round: start, sandbox: true });
  }
  /* ⛔ R626 · A REPLY IS A SNAPSHOT, NEVER A LIVE HANDLE, and the sandbox was
     handing out a live one. `entry.items`, `U.items` and the reply's `items`
     were ONE OBJECT with four owners, so buying an item at a stop reached back
     and rewrote what the FREE restock's reply had already said: a screen that
     kept the first reply (unlimShopAdopt does exactly that) would find its
     "before" had silently become its "after". The installed door returns fresh
     jsonb on every call and cannot do this, so the sandbox was behaving
     differently from the server, which is the one thing law 4 forbids. Found
     by playing a whole run rather than one door (harness/r626-sandbox-run). */
  function copyItems(it, engineVer) {
    it = it || {};
    var out = { mend: it.mend | 0, bolt: it.bolt | 0, ally: it.ally | 0 };
    if ((engineVer | 0) >= 72) out.horn = it.horn | 0;
    if ((engineVer | 0) >= 101) out.goblin = Math.min(1,it.goblin | 0);
    return out;
  }
  var UNLIMITED_SHOP_CHOICE_MS_LOCAL = 20000;
  var UNLIMITED_SHOP_CHOICE_MS_V74_LOCAL = 30000;
  function unlimitedStopLocal(buy) {
    var U = localUnlim(), engineVer = U.engine_ver | 0;
    var rule = unlimitedShopRule(engineVer), timed = engineVer >= 72;
    if (!U.live) return Promise.resolve({ ok: false, reason: 'not_live' });
    if (!rule) return Promise.resolve({ ok: false, reason: 'not_live' });
    var r = (U.round | 0) - 1, e = _E();
    var plan = null;
    try { plan = (e && e.wavePlan && r >= 1) ? e.wavePlan(r, 65) : null; } catch (_) { plan = null; }
    if (r < 1 || !(plan && plan.stopAfter)) {
      return Promise.resolve({ ok: false, reason: 'not_at_stop', round: U.round });
    }
    var key = String(U.run_family || 0) + ':' + r, buyKey = key + ':buy';
    U._shop = U._shop || {};
    var entry = U._shop[key], bought = U._shop[buyKey];
    var latched = { shop: !!entry, buy: !!bought };
    var items = U.items || { mend: 0, bolt: 0, ally: 0 };
    var hp = U.hp | 0;
    if (!entry) {
      if ((U.max_hp | 0) > 0) hp = Math.min(U.max_hp | 0, (U.hp | 0) + (rule.heal | 0));
      var heldHorn = items.horn | 0;
      items = {
        mend: Math.min(rule.stackCap | 0, (items.mend | 0) + (rule.freeEach.mend | 0)),
        bolt: Math.min(rule.stackCap | 0, (items.bolt | 0) + (rule.freeEach.bolt | 0)),
        ally: Math.min(rule.stackCap | 0, (items.ally | 0) + (rule.freeEach.ally | 0))
      };
      if (timed) items.horn = heldHorn;
      /* the latched entry keeps its OWN copy: it records what this visit
         granted, and a later purchase must not be able to rewrite it */
      entry = { stop_round: r, healed: (U.max_hp | 0) > 0, hp: hp,
                items: copyItems(items, engineVer), at: Date.now() };
      if (timed) entry.choice_until_ms = entry.at + ((engineVer | 0) >= 74
        ? UNLIMITED_SHOP_CHOICE_MS_V74_LOCAL : UNLIMITED_SHOP_CHOICE_MS_LOCAL);
      U._shop[key] = entry;
    } else {
      hp = entry.hp | 0;
      /* A sandbox stop created by an older cached asset gets one deadline on
         its first replay, matching the SQL migration's legacy backfill. */
      if (timed && !(entry.choice_until_ms > 0)) {
        entry = Object.assign({}, entry, { choice_until_ms: Date.now() + ((engineVer | 0) >= 74
          ? UNLIMITED_SHOP_CHOICE_MS_V74_LOCAL : UNLIMITED_SHOP_CHOICE_MS_LOCAL) });
        U._shop[key] = entry;
      }
    }
    if (buy) {
      if ((window.__hohLocalRun || {}).status === 'open' && String((window.__hohLocalRun || {}).id) === String(U.run_id) && U.run_status === 'open') {
        return Promise.resolve({ ok: false, reason: 'stop_passed' });
      }
      if (bought) { /* the purchase latch replays, exactly like the door */ }
      else if (timed && entry.choice_until_ms > 0 && Date.now() >= Number(entry.choice_until_ms)) {
        return Promise.resolve({ ok: false, reason: 'shop_expired', round: U.round,
          stop_round: r, hp: hp, max_hp: U.max_hp, items: copyItems(items, engineVer),
          shop: entry, bought: null,
          choice_until: new Date(Number(entry.choice_until_ms)).toISOString(),
          choice_remaining_ms: 0, sandbox: true });
      }
      else if ((items[buy] | 0) >= (rule.stackCap | 0)) {
        return Promise.resolve({ ok: false, reason: 'item_at_cap', item: buy, items: copyItems(items, engineVer) });
      } else if (bankGet() < (rule.optionalPurchase.priceHearts | 0)) {
        return Promise.resolve({ ok: false, reason: 'not_enough_hearts' });
      } else {
        bankApply(-(rule.optionalPurchase.priceHearts | 0), 'spend_unlimited_shop');
        items[buy] = Math.min(rule.stackCap | 0, (items[buy] | 0) + (rule.optionalPurchase.units | 0));
        bought = { item: buy, hearts: rule.optionalPurchase.priceHearts | 0, at: Date.now() };
        U._shop[buyKey] = bought;
      }
    }
    U.hp = hp; U.items = copyItems(items, engineVer);
    unlimitedPublish(collectionAccount(), U);
    var reply = { ok: true, round: U.round, stop_round: r, hp: hp,
      max_hp: U.max_hp, items: copyItems(items, engineVer),
      shop: { stop_round: entry.stop_round, healed: entry.healed, hp: entry.hp,
              items: copyItems(entry.items, engineVer), at: entry.at },
      bought: bought || null, latched: latched,
      sandbox: true };
    if (timed && entry.choice_until_ms > 0) {
      reply.shop.choice_until_ms = entry.choice_until_ms;
      reply.shop.choice_until = new Date(Number(entry.choice_until_ms)).toISOString();
      reply.choice_until = reply.shop.choice_until;
      reply.choice_remaining_ms = Math.max(0, Number(entry.choice_until_ms) - Date.now());
    }
    return Promise.resolve(reply);
  }
  function unlimitedReleaseLocal(honourTheClock) {
    var U = localUnlim();
    if (!U.live) return Promise.resolve({ ok: true, released: false, sandbox: true });
    /* the expire door refuses inside 48 hours; the forfeit door never does */
    if (honourTheClock && (U.expires_in_seconds | 0) > 0) {
      return Promise.resolve({ ok: false, reason: 'not_expired', run_id: U.run_id, sandbox: true });
    }
    var id = U.run_id;
    U.live = false; U.run_id = null; U.run_family = null; U.seed = null;
    U.best_round_at_start = null; U.cleared20_at_start = null; U.aelric_revisit = false;
    U.run_status = null; U.at_stop = false; U.receipt = null;
    try { if (window.__hohLocalRun && String(window.__hohLocalRun.id) === String(id)) window.__hohLocalRun.status = 'expired'; } catch (_) {}
    unlimitedPublish(collectionAccount(), U);
    return Promise.resolve({ ok: true, released: true, run_id: id,
      best_round: U.best_round | 0, banked: U.banked, sandbox: true });
  }

  /* R496: bind the visible battle to one owner-authenticated server snapshot.
     The UI compares and adopts this snapshot before it simulates, then sends
     the opaque token back with the eventual result. A token without the
     snapshot would still allow another tab's newer loadout to be judged while
     this tab animated stale heroes. */
  function runRequiresAtomicSettlement(run) {
    /* R498 keeps protocol admission separate from permanent-card identity.
       A Continue child of an old keyless run is generation 1/schema 0: it earns
       no permanent EXP, but it still MUST use the atomic settlement boundary.
       Never use roster_schema_version as a proxy for this decision. */
    return !!run && (run.settlement_generation | 0) >= 1;
  }
  function prepareCompatibilitySnapshot(snapshot, runId, savedPath) {
    if (!plainObject(snapshot) || !plainObject(snapshot.run)) return null;
    var run = snapshot.run;
    var rung = (run.ladder_stage | 0) > 0;
    var ladder = plainObject(snapshot.ladder) ? snapshot.ladder : null;
    if (String(run.id) !== String(runId) || runRequiresAtomicSettlement(run) ||
        (run.roster_schema_version | 0) >= 1 ||
        (run.status && String(run.status) !== 'open') || !Array.isArray(run.roster) || !run.roster.length) return null;
    /* hoh_bg_ladder_state does not expose the later Path column. A schema-0
       rung is compatible only after hoh_bg_path_get has authenticated that
       separately stored input. Never manufacture an empty Path here: the
       Worker reads the full ladder row and would judge different mechanics. */
    if (rung && (!ladder || !plainObject(savedPath))) return null;
    try {
      var raw = JSON.stringify({ run: run, ladder: ladder,
        saved_path: rung ? savedPath : null });
      /* A real run/ladder snapshot is small. Refuse an accidental UI graph or a
         tampered multi-megabyte object instead of turning localStorage/network
         pressure into the rollout path. */
      if (!raw || raw.length > 180000) return null;
      var copy = JSON.parse(raw);
      if (rung) copy.ladder.path = copy.saved_path;
      delete copy.saved_path;
      return { ok: true, run_id: runId,
        state_token: 'c0c0c0c0c0c0c0c0c0c0c0c0c0c0c0c0',
        run: copy.run, ladder: copy.ladder, compatibility_schema0: true };
    } catch (_) { return null; }
  }
  function prepareSettle(runId, compatibilitySnapshot) {
    if (localMode() || /^local-/.test(String(runId || ''))) {
      var localRun = window.__hohLocalRun || null;
      var localLadder = window.__hohLocalLadder || null;
      /* The sandbox stores Path on the ladder, but a brand-new local ladder
         can still predate its first pathGet. Return a complete snapshot without
         mutating the shared sandbox row. */
      if (localRun && (localRun.ladder_stage | 0) > 0 && localLadder) {
        try {
          localLadder = JSON.parse(JSON.stringify(localLadder));
          localLadder.path = plainObject(compatibilitySnapshot && compatibilitySnapshot.path)
            ? JSON.parse(JSON.stringify(compatibilitySnapshot.path))
            : (plainObject(localLadder.path) ? localLadder.path : {});
        } catch (_) { return Promise.reject(new Error('The saved battle rules could not be verified. Try again.')); }
      }
      return Promise.resolve({ ok: true, run_id: runId, state_token: 'local',
        run: localRun, ladder: localLadder });
    }
    var scope = captureCombatAccount();
    if (!scope.email || scope.pin == null || String(scope.pin).length < 1) {
      return Promise.reject(new Error('Sign in again before starting this battle.'));
    }
    return rpcDeadline(rpc('hoh_bg_contract_prepare', {
      p_email: scope.email,
      p_pin: scope.pin,
      p_run_id: runId
    }), 12000, 'Battle verification took too long. Check your connection and try again.').then(function (res) {
      if (!combatAccountIsCurrent(scope)) {
        throw accountChangedError('the battle was preparing', false);
      }
      if (!res || res.ok !== true || String(res.run_id) !== String(runId) ||
          !/^[0-9a-f]{32}$/.test(String(res.state_token || '')) || !res.run) {
        throw new Error('The battle state could not be verified. Try again.');
      }
      res.account = scope.account;
      res.generation = scope.generation;
      rememberRunEngineR796(res.run);
      return res;
    }).catch(function (err) {
      /* Zero-downtime rollout bridge: the theme may arrive before the R496 RPC.
         Only PostgREST positively saying THIS function is absent may enter it.
         Auth, permission, timeout and transport failures stay failures. */
      if (!rpcDefinitelyMissing(err, 'hoh_bg_contract_prepare')) throw err;
      if (!combatAccountIsCurrent(scope)) throw accountChangedError('the battle was preparing', false);
      function finishCompatibility(savedPath) {
        if (!combatAccountIsCurrent(scope)) throw accountChangedError('the battle was preparing', false);
        var compat = prepareCompatibilitySnapshot(compatibilitySnapshot, runId, savedPath);
        if (!compat) throw new Error('This saved run needs the Battlegrounds update before it can be verified. Try again after deployment finishes.');
        compat.account = scope.account;
        compat.generation = scope.generation;
        return compat;
      }
      var snapRun = compatibilitySnapshot && compatibilitySnapshot.run;
      if (!snapRun || (snapRun.ladder_stage | 0) <= 0) return finishCompatibility(null);
      /* Schema 1 is never allowed through the browser compatibility bridge,
         and it does not need an extra Path read before receiving that refusal. */
      if (runRequiresAtomicSettlement(snapRun) ||
          (snapRun.roster_schema_version | 0) >= 1) return finishCompatibility(null);
      /* The temporary schema-0 bridge cannot use the old ladder-state reply as
         Path authority because that RPC predates the column. Re-read the
         owner-authenticated Path now. A real cross-tab change will therefore
         still mismatch and require confirmation; matching state starts on the
         first tap. */
      return rpcDeadline(rpc('hoh_bg_path_get', {
        p_email: scope.email,
        p_pin: scope.pin
      }), 12000, 'Battle rules took too long to verify. Check your connection and try again.')
        .then(function (saved) {
          if (!saved || saved.ok !== true || !plainObject(saved.path)) {
            throw new Error('The saved battle rules could not be verified. Try again.');
          }
          return finishCompatibility(saved.path);
        });
    });
  }
  /* v20: seat the chosen recruit on the OPEN run row (server-validated), so
     the settle worker heals with the same hands the player picked. */
  function setRecruit(runId, recruit, scope) {
    if (localMode() || /^local-/.test(String(runId || ''))) {
      var pr = window.__hohLocalRun || {};
      var _seated = addPersistentBench(pr.recruits || [], recruit, pr.engine_ver);
      if (!_seated.added) return Promise.reject(persistentBenchLimitError(pr.engine_ver));
      pr.recruits = _seated.list;
      return Promise.resolve(pr.recruits);
    }
    /* R363: NEVER FABRICATE THE BENCH. The old fallback answered `[recruit]`
       whenever the reply could not be read, which is right for the FIRST
       recruit and wrong for every one after it: the row keeps three, the
       client sims one, and the two judges fight different battles. Measured,
       a bench of two against a bench of one changes the event stream 100% of
       the time and flips the verdict about a quarter of the time. One retry
       (the RPC appends, so a retry after a LOST REPLY would double-seat — the
       retry only fires when the call itself rejected before the append), then
       an honest failure that marchOn's own catch turns back into the picker. */
    scope = scope || captureCombatAccount();
    if (!scope.email || scope.pin == null || String(scope.pin).length < 1) {
      return Promise.reject(new Error('Sign in again before choosing a recruit.'));
    }
    return rpcDeadline(rpc('hoh_contract_set_recruit', {
      p_email: scope.email, p_pin: scope.pin, p_run_id: runId, p_recruit: recruit
    }), 12000, 'That recruit took too long to save. Try again.')
      .then(function (res) {
        if (!combatAccountIsCurrent(scope)) {
          throw accountChangedError('the recruit was saving', true);
        }
        if (!res || !Array.isArray(res.recruits)) throw new Error('recruit not seated');
        return res.recruits;
      });
  }
  /* v23: the run's PLANS live on the row — the judge reads them there.
     Full-list replace, server-validated (whitelist, one per fight). */
  function setPlans(runId, plans, scope) {
    /* R399: THE WRITE DOOR DEDUPES. Every plans write walks through here
       (the pick, the carry on continue), so the row can never store the
       same enchantment twice again — the engine reads plans by membership,
       so a duplicate was always a dead slot wearing a name. */
    var _seen = {}, _u = [];
    (plans || []).forEach(function (id) { if (id && !_seen[id]) { _seen[id] = 1; _u.push(id); } });
    plans = _u;
    if (localMode() || /^local-/.test(String(runId || ''))) {
      var pr = window.__hohLocalRun || {};
      pr.plans = (plans || []).slice(0, 5);
      return Promise.resolve(pr.plans);
    }
    var planScope = scope || captureCombatAccount();
    return rpcDeadline(rpc('hoh_contract_set_plans', {
      p_email: planScope.email, p_pin: planScope.pin,
      p_run_id: runId, p_plans: plans || []
    }), 12000, 'That plan took too long to save. Tap the same plan to try again.')
      .then(function (res) {
        if (!combatAccountIsCurrent(planScope)) {
          throw accountChangedError('the battle plan was saving', true);
        }
        if (!res || !Array.isArray(res.plans)) throw new Error('The plan was not confirmed. Tap it again.');
        return res.plans;
      });
  }
  /* v20: the fifth fight fields a REAL rival — a stranger's frozen roster,
     gear and all, written onto the run row by the server so both judges
     fight the same table. Local sandbox improvises one from the collection. */
  function fetchRival(runId, scope, strict) {
    if (localMode() || /^local-/.test(String(runId || ''))) {
      var cds = ownedCards().slice();
      var pick = [];
      while (pick.length < 5 && cds.length) pick.push(cds.splice(Math.floor(Math.random() * cds.length), 1)[0]);
      var roster = pick.map(function (c) {
        return { name: c.name || 'Rival', cls: c.character_class || c.cls || 'Fighter',
                 hearts: c.hearts || 100, rar: (window.HOHContract ? HOHContract.tierOf(c.hearts || 100) : 1),
                 lvl: 1, skill: null, passive: null, house: 1 };
      });
      var pr = window.__hohLocalRun || {};
      pr.rival = { roster: roster, title: 'The Rival Hall' };
      return Promise.resolve(pr.rival);
    }
    /* R363: hoh_contract_rival WRITES the rival onto the row and is idempotent
       (a second call returns the same one), so a retry is free. That matters:
       if the write lands and the REPLY is lost, the worker judges a geared
       rival while the client sims a generated house team, which is a different
       battle end to end. An empty roster is treated as no rival, because the
       engine reads a supplied `foe` of [] as "a side with nobody in it" and
       hands out the grand prize. A genuine null (a hall too young to have a
       rival) still falls back to the seeded house on BOTH sides, which is the
       one case where client and judge agree by construction. */
    scope = scope || captureCombatAccount();
    function _ask() {
      return rpc('hoh_contract_rival', {
        p_email: scope.email, p_pin: scope.pin, p_run_id: runId
      });
    }
    return _ask()
      .catch(function () { return _ask(); })
      .then(function (res) {
        if (!combatAccountIsCurrent(scope)) {
          throw accountChangedError('the rival was saving', true);
        }
        return (res && res.roster && res.roster.length) ? res : null;
      })
      .catch(function (err) { if (strict) throw err; return null; });
  }
  /* settle through the worker: it re-simulates and is the only judge.
     outcome: 'battle' | 'fled_half' | 'fled_quarter'. In god-mode local
     test the same re-simulation runs client-side against the local run. */
  /* R333: THE SETTLE SURVIVES A BAD NETWORK.
     The old body was one bare fetch with no timeout, no retry, and a raw
     r.json(). On a phone that is three separate ways to lose a won fight:
       1. a cellular handover stalls the request and the promise NEVER
          settles, so the result screen hangs forever;
       2. Cloudflare answers 502/522/1101 with an HTML error page and
          r.json() throws SyntaxError;
       3. a single dropped packet is fatal because nothing tries again.
     Retrying is SAFE because the worker is idempotent by design: a run whose
     status is no longer 'open' returns { ok:true, status, idempotent:true }
     without re-settling. So the only real risk is doing nothing.
     GET-like backoff: 3 attempts, 12s ceiling each, 600ms then 1800ms apart. */
  var SETTLE_TRIES = 3;
  var SETTLE_TIMEOUT_MS = 12000;
  var BG_PENDING_SETTLEMENT_KEY = 'hoh-bg-pending-settlement-v1';
  var BG_PARKED_SETTLEMENT_KEY = 'hoh-bg-pending-settlement-parked-v1';
  /* R565: move one dead record out of the live key so it stops gating the
     game, without destroying it. The parked key is diagnostics, read by
     nobody automatically and safe to inspect or clear by hand. */
  function parkSettlementRecord(label, value) {
    try {
      var box = [];
      /* a corrupt parked box must never block parking: it is diagnostics,
         and the record being rescued matters more than the ones before it */
      try {
        var raw = localStorage.getItem(BG_PARKED_SETTLEMENT_KEY);
        box = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(box)) box = [];
      } catch (_) { box = []; }
      box.push({ label: String(label || 'parked'), at: Date.now(), value: value });
      var out = JSON.stringify(box);
      /* bounded by bytes as well as count: one oversized record must not make
         every later park fail on quota */
      while (box.length > 1 && (box.length > 5 || out.length > 60000)) {
        box.shift(); out = JSON.stringify(box);
      }
      localStorage.setItem(BG_PARKED_SETTLEMENT_KEY, out);
      return true;
    } catch (_) { return false; }
  }
  function pendingSettlementRead() {
    try {
      var raw = localStorage.getItem(BG_PENDING_SETTLEMENT_KEY);
      if (raw == null || raw === '') return {};
      var rows = null;
      try { rows = JSON.parse(raw); } catch (_) { rows = null; }
      if (plainObject(rows)) return rows;
      /* ⛔ R565: this used to return null, and null reads as "unfinished work"
         to hasPendingSettlement, so ONE corrupt record locked the whole
         Battlegrounds behind an unwinnable recovery screen forever, with no
         network involved. A record we can read but not parse is dead: park
         its bytes for diagnosis and free the door. A storage layer that
         cannot be read at all (the outer catch) stays conservative. */
      if (parkSettlementRecord('unparseable', raw)) {
        try { localStorage.removeItem(BG_PENDING_SETTLEMENT_KEY); } catch (_) {}
        try { console.error('R565 SETTLE QUARANTINE PARKED reason=unparseable bytes=' + raw.length); } catch (_) {}
        return {};
      }
      return null;
    } catch (_) { return null; }
  }
  function pendingSettlementWrite(rows) {
    try {
      if (!plainObject(rows)) return false;
      var raw = JSON.stringify(rows || {});
      localStorage.setItem(BG_PENDING_SETTLEMENT_KEY, raw);
      return localStorage.getItem(BG_PENDING_SETTLEMENT_KEY) === raw;
    } catch (_) { return false; }
  }
  function pendingResumeSnapshot(value, runId) {
    if (!plainObject(value) || (value.version | 0) !== 1 || !plainObject(value.run) ||
        String(value.run.id) !== String(runId)) return null;
    try {
      var raw = JSON.stringify(value);
      if (!raw || raw.length > 180000) return null;
      var copy = JSON.parse(raw);
      return plainObject(copy) && plainObject(copy.run) ? copy : null;
    } catch (_) { return null; }
  }
  /* ⛔ ============ R560 · ASK THE ENGINE HOW MANY PLAYS IT READS ========
     Jan, after losing a fight he had watched himself win, twice: "NEVER have
     the referee re-ran the fight and score it as a loss because the player has
     agency and plays the cards."
     He named the cause without seeing the code. THE PLAYER'S CARDS WERE NOT
     ALL REACHING THE JUDGE. `readPlays` reads up to 16 under the card ramp,
     and this file sent 7 — the number R407 set when a fight could hold at most
     that many, before R523's ramp began dealing a fresh card to every hero
     every few actions. So on a long fight the browser simulated the whole
     hand, the worker re-simulated the first seven cards only, and the two
     verdicts came apart. Short fights never reach seven, which is why it
     looked intermittent.
     ⛔ THE COMMENT IN settleOnce ALREADY SAID "this number must match
     readPlays." It had not for a whole wave. That is what a literal copied
     between two files is worth. Ask the engine. */
  function playSendCap(engineVer, mode) {
    var v = Math.max(0, engineVer | 0);
    var unlimited = String(mode || '') === 'unlimited' && v >= 65;
    /* A version newer than the legacy ramp only earns the wide segment cap
       when its saved run also says Unlimited. This keeps an old/unknown-mode
       recovery row on the single-fight rail instead of widening it merely
       because the newest browser asset happens to be loaded. */
    var readVer = unlimited ? v : (v >= 65 ? 64 : v);
    try {
      var E = window.HOHContract;
      /* R608b: playReadCap is the LEGACY cap for every version now (the
         engine's 65-wide first cut was the R560 seam, third edition). Only a
         named Unlimited segment asks for the wide rail, and it asks the
         export, never a re-typed number. */
      if (E && unlimited && (E.UNLIMITED_PLAY_CAP_V65 | 0) > 0)
        return Math.max(1, E.UNLIMITED_PLAY_CAP_V65 | 0);
      if (E && typeof E.playReadCap === 'function' && readVer > 0)
        return Math.max(1, E.playReadCap(readVer) | 0);
    } catch (_) {}
    /* The saved inputs still choose the fail-closed fallback when the engine
       asset is late: 62/63 read ten, 64 reads sixteen, and only a named
       Engine-65 Unlimited segment reads the measured 128-play envelope.
       R608b: an UNKNOWN saved version falls to sixteen, not ten — sending
       too few plays is the R560 bug class, sending extra is harmless because
       every judge truncates at its own cap. */
    return unlimited ? 128 : (readVer === 62 || readVer === 63 ? 10 : 16);
  }
  function playWire(p) {
    p = p || {};
    var out = {
      id: String(p.id || '').slice(0, 32),
      atA: Math.max(0, p.atA | 0),
      name: String(p.name || '').slice(0, 40),
      cls: String(p.cls || '').slice(0, 40)
    };
    if (p.auto === 'mobile' || p.auto === 'overflow') out.auto = p.auto;
    if (Array.isArray(p.names)) {
      out.names = p.names.slice(0, 3).map(function (name) { return String(name || '').slice(0, 40); });
    }
    /* R781 · Engine 110: the window a play lands before, and its fight. The
       engine's own reader validates both at the judge; a play without them
       keeps the atA rule. */
    if ((p.w | 0) > 0) { out.w = p.w | 0; out.fight = Math.max(0, p.fight | 0); }
    if(/^bolt(?:[2-9]|10)?$/.test(out.id)&&typeof p.ownerId==='string'&&/^main:[0-9]+$/.test(p.ownerId))out.ownerId=p.ownerId;
    return out;
  }
  /* ⛔ R811 · ENGINE 120 · A ROUND'S MOVE RECORD IS NOT A CARD. A manual chapter keeps one record a round in the
     plays ({id:'f<fight>.m<round>', fight, w, mv}). The engine's move reader refuses any other field and needs mv, so
     playWire's card shape (atA, name, cls) would refuse the whole ledger; and the records have their own cap (256,
     the engine's MOVE_RECORD_CAP_V120), so the 128-play cap never counts or trims them. A choice still open (?) goes
     to the judge as x, the auto pick, which is what the Worker reads it as anyway. */
  var MOVE_ID_RE_R811 = /^f\d{1,3}\.m\d{1,2}$/, MOVE_RECORD_CAP_R811 = 256;
  function isMoveRecord(p) { return !!(p && MOVE_ID_RE_R811.test(String(p.id || ''))); }
  function moveWire(p) { return { id: String(p.id), fight: Math.max(0, p.fight | 0), w: Math.max(0, p.w | 0), mv: String(p.mv || '').replace(/\?/g, 'x').slice(0, 5) }; }
  function wireOne(p) { return isMoveRecord(p) ? moveWire(p) : playWire(p); }
  function wirePlays(list, engineVer, mode) {
    var cards = [], moves = [];
    (list || []).forEach(function (p) { (isMoveRecord(p) ? moves : cards).push(p); });
    return cards.slice(0, playSendCap(engineVer, mode)).map(playWire).concat(moves.slice(0, MOVE_RECORD_CAP_R811).map(moveWire));
  }
  function unlimitedFaceSnapshot(runId, opts, engineVer) {
    var engine = _E(), faces = opts && opts.recruitFaces;
    if (!engine || !engine.unlimitedRecruitFaces) return {};
    var faceVer = (engineVer | 0) || ((opts && opts.engineVer) | 0) || (engine.ENGINE_VERSION | 0) || 68;
    faceVer = Math.max(68, faceVer);
    if (!faces) {
      try { faces = window.HOHContractUI && window.HOHContractUI.unlimRecruitFaces(runId); } catch (_) {}
    }
    if (!faces || typeof faces !== 'object' || Array.isArray(faces)) return {};
    var bodies = Object.keys(faces).filter(function (key) {
      return /^run:join:[0-9]+:[0-9]+$/.test(key) ||
        (faceVer >= 71 && /^run:aid:[1-9][0-9]{0,6}:ally[23]?:[0-2]$/.test(key)) ||
        /* Engine 84: a hero minted from three merged tokens wears a chosen face */
        (faceVer >= 84 && /^run:merge:[1-9][0-9]{0,6}:[1-9][0-9]{0,2}$/.test(key)) ||
        /* Engine 85: and a piece left behind by a fallen bench body */
        (faceVer >= 85 && /^run:split:[1-9][0-9]{0,6}:[1-9][0-9]{0,3}$/.test(key));
    }).slice(0, MAX_PERSISTENT_BENCH).map(function (key) {
      var body = { key:key, run_only:true };
      if (faceVer >= 71 && /^run:aid:[1-9][0-9]{0,6}:ally[23]?:[0-2]$/.test(key)) body.aid = 1;
      if (faceVer >= 84 && /^run:merge:/.test(key)) body.merged = 1;
      if (faceVer >= 85 && /^run:split:/.test(key)) body.split = 1;
      return body;
    });
    var out = {};
    engine.unlimitedRecruitFaces(bodies, faces, faceVer).forEach(function (body) {
      if (body.joinFace) out[body.key] = body.joinFace;
    });
    return out;
  }
  function localAidRows(sim, engine, engineVer, faceMap) {
    var rows = [], fell = {}, seen = {}, ver = engineVer | 0;
    (sim && sim.fellBench || []).forEach(function (i) { fell[i | 0] = 1; });
    (sim && sim.events || []).forEach(function (event) {
      if (!event || (event.e !== 'play' && event.e !== 'autocast') || event.blocked ||
          !/^ally[23]?$/.test(String(event.id || ''))) return;
      var crew = Array.isArray(event.crew) && event.crew.length ? event.crew : [event];
      crew.forEach(function (member) {
        member = member || {};
        var index = member.i != null ? (member.i | 0) : (event.i | 0);
        if (fell[index]) return;
        var key = String(member.key || '');
        var validAidKey = /^run:aid:[1-9][0-9]{0,6}:ally[23]?:[0-2]$/.test(key);
        if (ver >= 71 && !validAidKey) return;
        if (key && seen[key]) return;
        if (key) seen[key] = 1;
        var row = {
          name: String(member.name || event.name || '').slice(0, 40),
          cls: String(member.cls || event.cls || 'Fighter').slice(0, 40),
          hearts: 150,
          rar: Math.max(0, Math.min(5, member.rar != null ? (member.rar | 0) : (event.rar | 0))),
          lvl: 1,
          aid: 1
        };
        if (ver >= 71 && validAidKey) {
          row.key = key; row.run_only = true;
        }
        rows.push(row);
      });
    });
    if (ver >= 71 && engine && engine.unlimitedRecruitFaces) {
      try { rows = engine.unlimitedRecruitFaces(rows, faceMap || {}, ver); } catch (_) {}
    }
    rows.forEach(function (row) {
      if (!row.name) row.name = String(row.joinFace && row.joinFace.name || 'An ally').slice(0, 40);
    });
    return rows;
  }
  function pendingSettlementOpts(opts, account, runId) {
    opts = opts || {};
    var plays = [];
    var resume = pendingResumeSnapshot(opts.resumeSnapshot || opts.resume, runId);
    var engineVer = Math.max(0, Math.min(9999,
      (opts.engineVer | 0) || ((resume && resume.run && resume.run.engine_ver) | 0)));
    var mode = String(opts.mode || (resume && resume.run && resume.run.mode) || '');
    mode = mode === 'unlimited' ? mode : '';
    try {
      plays = wirePlays(opts.plays, engineVer, mode);
    } catch (_) {}
    return {
      stateToken: /^[0-9a-f]{32}$/.test(String(opts.stateToken || '')) ? String(opts.stateToken) : '',
      doubled: !!opts.doubled,
      planId: String(opts.planId || '').slice(0, 80),
      planIds: Array.isArray(opts.planIds) ? opts.planIds.slice(0, 5).map(function (v) { return String(v || '').slice(0, 80); }) : null,
      plays: plays,
      stake: Math.max(0, Math.min(600, opts.stake | 0)),
      expectPot: Math.max(0, Math.min(POT_CAP, opts.expectPot | 0)),
      depth: Math.max(0, Math.min(999, opts.depth | 0)),
      engineVer: engineVer,
      mode: mode,
      recruitFaces: mode === 'unlimited' && engineVer >= 68 ? unlimitedFaceSnapshot(runId, opts, engineVer) : null,
      ownerBonus: Math.max(0, Math.min(250, opts.ownerBonus | 0)),
      recruitDust: Math.max(0, Math.min(120, opts.recruitDust | 0)),
      claimRecruitDust: !!opts.claimRecruitDust,
      preparedAccount: account,
      resume: resume
    };
  }
  function rememberPendingSettlement(scope, runId, outcome, opts) {
    if (!scope || !scope.account || !runId ||
        ['battle', 'fled_half', 'fled_quarter'].indexOf(String(outcome || '')) < 0) return null;
    var rows = pendingSettlementRead();
    if (!rows) {
      var corrupt = new Error('This device has an unreadable saved-result record. Nothing was sent. Restore or clear that record before starting another result.');
      corrupt.__hohRewardPending = true;
      throw corrupt;
    }
    var old = rows[scope.account];
    if (old != null && !plainObject(old)) {
      var badRow = new Error('This account has an invalid saved-result record. Nothing was sent.');
      badRow.__hohRewardPending = true;
      throw badRow;
    }
    if (old && ((old.version !== 1 && old.version !== 2) ||
        (old.account && String(old.account) !== String(scope.account)))) {
      var badVersion = new Error('This account has an incompatible saved-result record. Nothing was sent.');
      badVersion.__hohRewardPending = true;
      throw badVersion;
    }
    if (old && (String(old.run_id) !== String(runId) || String(old.outcome) !== String(outcome))) {
      var occupied = new Error('A previous Battlegrounds result is still waiting on this device. Reopen it before saving another run.');
      occupied.__hohRewardPending = true;
      throw occupied;
    }
    if (old && !plainObject(old.opts)) {
      var badOpts = new Error('This account has an invalid saved-result request. Nothing was sent.');
      badOpts.__hohRewardPending = true;
      throw badOpts;
    }
    var nextOpts = pendingSettlementOpts(opts, scope.account, runId);
    var record = old || { version: 2, account: scope.account, run_id: String(runId),
      outcome: String(outcome), phase: 'request', at: Date.now() };
    record.version = 2;
    record.account = scope.account;
    record.run_id = String(runId);
    record.outcome = String(outcome);
    record.phase = record.phase === 'committed' && plainObject(record.response) ? 'committed' : 'request';
    record.opts = plainObject(record.opts) ? record.opts : nextOpts;
    /* The first request is the immutable replay body. A later in-memory replay
       may add the UI snapshot if the pre-R496 row did not have one, but it may
       not change the judged token or combat inputs. */
    if (!record.opts.resume && nextOpts.resume) record.opts.resume = nextOpts.resume;
    if (record.opts.stateToken && nextOpts.stateToken && record.opts.stateToken !== nextOpts.stateToken) {
      var changed = new Error('The saved battle token changed before its result was consumed. Nothing was sent.');
      changed.__hohRewardPending = true;
      throw changed;
    }
    rows[scope.account] = record;
    if (!pendingSettlementWrite(rows)) {
      var e = new Error('This device could not save a recovery copy before the battle result was sent. Reopen Battlegrounds and try again.');
      e.__hohRewardPending = true;
      throw e;
    }
    var checkRows = pendingSettlementRead();
    var check = checkRows && checkRows[scope.account];
    if (!plainObject(check) || check.version !== 2 || check.run_id !== record.run_id || check.outcome !== record.outcome ||
        !plainObject(check.opts) || check.opts.stateToken !== record.opts.stateToken) {
      throw new Error('The battle recovery copy did not verify. Nothing was sent.');
    }
    return record;
  }
  /* R565: the player-chosen exit from a deterministic settle refusal. The
     row moves to the parked key whole, so nothing is destroyed and the door
     stops being gated, then the still-open run resumes at the same stage.
     Only the UI calls this, only from the recovery screen, only after the
     worker has flagged the same refusal deterministic on three or more
     recovery attempts. */
  function parkPendingSettlement(runId) {
    var scope = captureCombatAccount();
    var account = String(scope && scope.account || '').trim().toLowerCase();
    if (!account) return false;
    var rows = pendingSettlementRead();
    if (!rows) return false;
    var row = rows[account];
    if (!plainObject(row) || String(row.run_id) !== String(runId)) return false;
    if (!parkSettlementRecord('player-set-aside', row)) return false;
    delete rows[account];
    if (!pendingSettlementWrite(rows)) return false;
    try { console.error('R565 SETTLE QUARANTINE PARKED reason=player-set-aside run=' + runId +
      ' attempts=' + (row.attempts | 0) + ' detail=' + String(row.last_detail || '')); } catch (_) {}
    return true;
  }
  function clearPendingSettlement(scope, runId) {
    var account = String(scope && scope.account || '').trim().toLowerCase();
    if (!account) return true;
    var rows = pendingSettlementRead();
    if (!rows) return false;
    var old = rows[account];
    if (!old || String(old.run_id) !== String(runId)) return true;
    delete rows[account];
    if (!pendingSettlementWrite(rows)) return false;
    var checkRows = pendingSettlementRead();
    return !!(checkRows && !checkRows[account]);
  }
  function pendingResponse(scope, runId, outcome) {
    var rows = pendingSettlementRead();
    if (!rows) return null;
    var row = rows[String(scope && scope.account || '').trim().toLowerCase()];
    if (!plainObject(row) || String(row.run_id) !== String(runId) ||
        String(row.outcome) !== String(outcome) || row.phase !== 'committed' ||
        !plainObject(row.response) || row.response.ok !== true ||
        typeof row.response.status !== 'string' || !row.response.status || row.response.status.length > 64) return null;
    try {
      var res = JSON.parse(JSON.stringify(row.response));
      if (row.opts && row.opts.resume) res.__hocResume = JSON.parse(JSON.stringify(row.opts.resume));
      res.__hocPendingRun = String(row.run_id);
      res.__hocPendingOutcome = String(row.outcome);
      res.__hocStateToken = String(row.opts && row.opts.stateToken || '');
      res.__hocRecoveryAccount = String(scope && scope.account || '');
      res.__hocRecoveryGeneration = scope && scope.generation == null ? null : (scope.generation | 0);
      return res;
    } catch (_) { return null; }
  }
  /* A committed response is more than result-screen copy. Its immutable EXP
     receipt is also the newest safe overlay for the card cache on this
     device. Every door that returns a saved response must pass through this
     helper, including a warm second tab that never reaches the normal remote
     settle continuation. Keeping the overlay at the recovery boundary stops
     a correct receipt being rendered beside stale card levels. */
  function committedResponse(scope, runId, outcome) {
    var res = pendingResponse(scope, runId, outcome);
    if (res) applyBattleXpReceipt(res, scope);
    return res;
  }
  function savePendingResponse(scope, runId, outcome, response) {
    var rows = pendingSettlementRead();
    if (!rows) return false;
    var account = String(scope && scope.account || '').trim().toLowerCase();
    var row = rows[account];
    if (!plainObject(row) || String(row.run_id) !== String(runId) || String(row.outcome) !== String(outcome)) return false;
    try {
      var raw = JSON.stringify(response || {});
      if (!raw || raw.length > 240000) return false;
      var copy = JSON.parse(raw);
      if (!plainObject(copy) || copy.ok !== true || !copy.status) return false;
      /* `first` describes a device mutation that happened while processing the
         live response. A recovered screen may show the saved amounts again,
         but it must not replay the "just credited" wallet animation. */
      copy.local_reward_first = false;
      copy.local_reward_amount = 0;
      if (plainObject(copy.local_reward_claims)) {
        Object.keys(copy.local_reward_claims).forEach(function (category) {
          if (plainObject(copy.local_reward_claims[category])) copy.local_reward_claims[category].first = false;
        });
      }
      row.version = 2; row.phase = 'committed'; row.response = copy; row.committed_at = Date.now();
      rows[account] = row;
      if (!pendingSettlementWrite(rows)) return false;
      var checkRows = pendingSettlementRead();
      var check = checkRows && checkRows[account];
      return !!(plainObject(check) && check.phase === 'committed' &&
        String(check.run_id) === String(runId) && plainObject(check.response) &&
        String(check.response.status) === String(copy.status));
    } catch (_) { return false; }
  }
  function consumePendingSettlement(runId) {
    if (localMode() || !signedIn()) return true;
    var scope = captureCombatAccount(), rows = pendingSettlementRead();
    if (!rows) return false;
    var row = rows[scope.account];
    if (!row) return true;
    if (!plainObject(row) || String(row.run_id) !== String(runId) || row.phase !== 'committed') return false;
    return clearPendingSettlement(scope, runId);
  }
  /* ⛔ Jan (2026-09-17): a player locked out for a day and a half, on two
     computers, behind "Your result is safe". Every engine change closes the
     open runs (R681B clean slate: status expired, no receipt), so a fight that
     was running at that moment finishes into a run that no longer exists. Its
     retry answers receipt_missing with result_safe, and R565 only counted the
     Worker's deterministic 502, so a named result_safe refusal kept the streak
     at zero and the set-aside door never opened: Retry refused, Return to the
     Hall came straight back here. A named refusal that cannot change on retry
     now counts too, as does a judge fault that is not weather (the replay
     throwing the same way every time). A run the server closed with no receipt
     opens the door at once, because nothing is left to save for it. */
  var SETTLE_TERMINAL_REFUSALS = {
    receipt_missing: 'A game update closed this fight before the server saved it, so it can\'t be saved now. Everything else on your account is kept. Set it aside to keep playing.',
    unlimited_binding_mismatch: '',
    client_run_engine_mismatch: '',
    unsupported_saved_engine_version: '',
    unlimited_engine_unsupported: '',
    unlimited_run_authority_missing: ''
  };
  function settleRefusalKind(err) {
    if (!err) return '';
    if (err.deterministic || err.__hocDeterministic || err.settleCode === 'engine_refused') return 'refused';
    var code = String(err.__hocRefused || '');
    if (err.resultSafe && Object.prototype.hasOwnProperty.call(SETTLE_TERMINAL_REFUSALS, code)) return code;
    var detail = String(err.settleDetail || '');
    /* R791: an engine code in the detail is the replay throwing, which is
       never weather, even when the code's own words say "budget" or "limit" */
    if (err.settleCode === 'settlement_temporarily_unavailable' && detail &&
        (RUN_ENGINE_CODE_RE.test(detail) ||
         !/supabase|timeout|timed out|network|fetch|abort|socket|econn|reset|exceeded|limit|unavailable|rate/i.test(detail))) {
      return 'judge';
    }
    if (err.settleCode === 'upgrade_checkpoint_temporarily_unavailable' && detail) return 'judge';
    return '';
  }
  function repairUpgradeSettlement114(scope, row) {
    var saved=row && row.opts;
    if(!saved || row.phase==='committed' || row.outcome!=='battle' || (saved.engineVer|0)<114 || saved.mode!=='unlimited' ||
       row.last_detail!=='upgrade_committed_plays_changed' || row.upgradeReplayRepair114)return Promise.resolve(row);
    var E=window.HOHContract,U=window.HohUpgradeRun114,resume=pendingResumeSnapshot(saved.resume,row.run_id);
    function blocked(message){var error=new Error(message);error.__hohRewardPending=true;return error;}
    if(!E || !U || !U.replayPlays || !resume) return Promise.reject(blocked('Your original result is still saved. Its battle replay could not be checked yet. Reload and try again.'));
    var original=JSON.stringify(row);
    return upgradeReconcile114(row.run_id).then(function(authority){
      if(!combatAccountIsCurrent(scope))throw accountChangedError('the saved battle was being checked',false);
      if(!authority || authority.ok!==true || !authority.run || String(authority.run.id)!==String(row.run_id))throw blocked('Your saved result could not be checked with the game server. Try again.');
      // A committed run uses the existing receipt recovery path unchanged.
      if(authority.run.status!=='open')return row;
      var run=resume.run;
      if((run.engine_ver|0)<114 || run.mode!=='unlimited' || String(run.seed)!==String(authority.run.seed) ||
         run.wave_start!==authority.run.wave_start || run.wave_count!==authority.run.wave_count)throw blocked('The saved battle and server replay do not match. Your original result has been kept.');
      var candidate=U.replayPlays(E,saved.plays || [],authority).map(wireOne);
      var read=function(plays){return E.readUnlimitedPlays(plays,run.wave_start,run.wave_count,run.engine_ver|0);};
      if(U.stable(read(candidate))===U.stable(read(saved.plays || [])))return row;
      var base={mode:'unlimited',engineVer:run.engine_ver|0,seed:run.seed,roundStart:run.wave_start,roundCount:run.wave_count,plays:candidate,
        aelricRevisit:authority.run.foe && authority.run.foe.best_round_at_start>=21};
      var options=U.options(base,authority,E),result=E.run(options);
      if(result.pendingUpgrade || typeof result.canonical!=='string' || !result.canonical)throw blocked('This run still has a choice to finish. Your original result has been kept.');
      var originalPlays=resume.hand && resume.hand.plays && resume.hand.plays[String(run.depth|0)];
      var exactResume=false,carry=run._unlim,foe=run.foe || {};
      if(Array.isArray(originalPlays) && Array.isArray(run.roster) && Array.isArray(run.recruits) && plainObject(carry)){
        try {
          var oldResult=E.run(Object.assign({},options,{plays:originalPlays,roster:run.roster,recruits:run.recruits,
            carryA:carry.hp,maxA:carry.max,armorA:carry.armor,wardA:carry.ward,items:carry.items || {},lockA:carry.lock || null,fallenHero:carry.fallen || [],
            bestRoundAtStart:foe.best_round_at_start,cleared20AtStart:foe.cleared20_at_start,soloUltimate:foe.solo_ultimate,
            upgradeStart:foe.upgrade_start || authority.upgrade.start}));
          exactResume=!oldResult.pendingUpgrade && oldResult.canonical===result.canonical;
        } catch (_) {}
      }
      // If the old wire projection also damaged its resume copy, the final
      // accepted reward can prove the complete played segment independently.
      // Its pre-choice canonical hash was recorded by the server at acceptance.
      var proof=Promise.resolve(exactResume?'saved-replay':null),offer=authority.offer;
      if(!exactResume && offer && offer.selection && offer.context && offer.context.afterRound===run.wave_start+run.wave_count-1 && /^[a-f0-9]{64}$/.test(offer.prefixHash || '')){
        var choices=authority.upgrade.choices,at=choices.findIndex(function(choice){return choice.checkpointKey===offer.checkpointKey;});
        if(at===choices.length-1 && at>=0){
          var prefix=E.run(Object.assign({},options,{upgradeChoices:choices.slice(0,at)}));
          if(prefix.pendingUpgrade && prefix.pendingUpgrade.checkpointKey===offer.checkpointKey){
            proof=E.canonicalHash(prefix.pendingUpgrade.prefixCanonical).then(function(hash){return hash===offer.prefixHash?'accepted-checkpoint':null;});
          }
        }
      }
      return proof.then(function(source){
        if(!source)throw blocked('The original result could not be matched exactly. It is still saved, and nothing was changed.');
        return E.canonicalHash(result.canonical).then(function(hash){
          if(!combatAccountIsCurrent(scope))throw accountChangedError('the saved battle was being checked',false);
          var latest=pendingSettlementRead(),current=latest && latest[scope.account];
          if(!current || JSON.stringify(current)!==original)throw blocked('The saved result changed on this device. Try again to load its latest copy.');
          var repaired=JSON.parse(original),nextOpts=Object.assign({},repaired.opts,{plays:candidate});
          // One atomic journal write retains the exact old request as evidence.
          // The run, outcome, state token and original resume are unchanged.
          repaired.upgradeReplayRepair114={version:1,proof:source,canonicalHash:hash,prefixHash:authority.upgrade.prefixHash || null,originalOpts:repaired.opts};
          repaired.opts=nextOpts;latest[scope.account]=repaired;
          if(!pendingSettlementWrite(latest))throw blocked('This device could not save the checked recovery copy. Nothing was sent.');
          return repaired;
        });
      });
    });
  }
  function recoverPendingSettlement() {
    if (localMode() || !signedIn()) return Promise.resolve(null);
    var scope = captureCombatAccount();
    var rows = pendingSettlementRead();
    if (!rows) {
      var corrupt = new Error('The saved Battlegrounds result on this device is unreadable. Nothing was replayed.');
      corrupt.__hohRewardPending = true;
      return Promise.reject(corrupt);
    }
    var row = rows[scope.account];
    if (!row) return Promise.resolve(null);
    if (!plainObject(row) || (row.version !== 1 && row.version !== 2) ||
        ['battle', 'fled_half', 'fled_quarter'].indexOf(String(row.outcome || '')) < 0 || !row.run_id || !plainObject(row.opts) ||
        (row.outcome === 'battle' && !/^[0-9a-f]{32}$/.test(String(row.opts.stateToken || '')))) {
      /* R565 (review catch): a row the strict parser refuses is exactly as
         dead as an unparseable one, and it used to gate the game exactly as
         permanently. Park it whole, free the door, and say so flat. */
      if (parkSettlementRecord('invalid-shape', row)) {
        delete rows[scope.account];
        if (pendingSettlementWrite(rows)) {
          try { console.error('R565 SETTLE QUARANTINE PARKED reason=invalid-shape run=' +
            String(row && row.run_id || '')); } catch (_) {}
          return Promise.resolve(null);
        }
      }
      var invalid = new Error('The saved Battlegrounds result has an invalid shape. Nothing was replayed.');
      invalid.__hohRewardPending = true;
      return Promise.reject(invalid);
    }
    var committed = committedResponse(scope, row.run_id, row.outcome);
    if (committed) {
      committed.__hocRecovered = true;
      return Promise.resolve(committed);
    }
    if (row.version === 1) {
      row.version = 2; row.phase = 'request'; rows[scope.account] = row;
      if (!pendingSettlementWrite(rows)) {
        var upgrade = new Error('The saved result could not be upgraded safely. Nothing was replayed.');
        upgrade.__hohRewardPending = true;
        return Promise.reject(upgrade);
      }
    }
    return repairUpgradeSettlement114(scope,row).then(function(repaired){
      var opts = pendingSettlementOpts(repaired.opts, scope.account, repaired.run_id);
      opts.__recovery = true;
      return settle(repaired.run_id, repaired.outcome, opts);
    }).then(function (res) {
      if (res) res.__hocRecovered = true;
      return res;
    }, function (err) {
      /* R565: settleRemote already retries 3 times per call, but nothing
         counted ACROSS calls, so the recovery screen could never distinguish
         attempt one from attempt forty. The count lives on the row itself so
         it survives reloads, and it rides out on the error so the screen can
         earn its honesty. Counting is best-effort: a failed write must never
         eat the real error. */
      try {
        var rows2 = pendingSettlementRead();
        var row2 = rows2 && rows2[scope.account];
        if (plainObject(row2) && String(row2.run_id) === String(row.run_id)) {
          row2.attempts = Math.min(999, (row2.attempts | 0) + 1);
          /* Review catch (R565): a one-way latch here let a single flagged
             refusal paint every later NETWORK failure as "the server refuses
             this every time", which is the screen lying in the other
             direction. The streak counts CONSECUTIVE deterministic refusals
             and any other failure resets it, so the set-aside door only opens
             on three server refusals in a row, with nothing else between. */
          var kind = settleRefusalKind(err);
          if (kind) {
            /* the SAME refusal in a row, as R565 meant: a different one
               starts the count again */
            if (row2.last_kind !== kind) row2.det_streak = 0;
            row2.last_kind = kind;
            row2.det_streak = Math.min(999, (row2.det_streak | 0) + 1);
            if (kind === 'receipt_missing') row2.det_streak = Math.max(3, row2.det_streak);
            var note = err.settleDetail || SETTLE_TERMINAL_REFUSALS[kind] || '';
            if (note) row2.last_detail = String(note).slice(0, 200);
            err.deterministic = true;
          } else {
            row2.det_streak = 0;
            row2.last_kind = '';
          }
          pendingSettlementWrite(rows2);
          if (err) {
            err.__hocPendingRun = String(row2.run_id);
            /* R791: the recovery screen offers End run only for an Unlimited
               result, so it needs to know which game this record belongs to */
            err.__hocPendingMode = String((row2.opts && row2.opts.mode) || '');
            err.recoveryAttempts = row2.attempts;
            err.deterministicStreak = row2.det_streak | 0;
            /* R791: a remembered detail explains an earlier refusal. It is
               shown, but it never makes this failure count as one (the R565
               rule: a network blip is not a server refusal) */
            if (!err.settleDetail && row2.last_detail) { err.settleDetail = row2.last_detail; err.__hocDetailRemembered = true; }
          }
        }
      } catch (_) {}
      throw err;
    });
  }
  function hasPendingSettlement() {
    try {
      if (localMode() || !signedIn()) return false;
      var scope = captureCombatAccount();
      var rows = pendingSettlementRead();
      if (!rows) return true;
      var row = rows[scope.account];
      /* Any account row is unfinished work until the strict recovery parser
         accepts it. Treating a malformed row as "nothing pending" lets a new
         result collide with storage the UI never surfaced. */
      return row != null;
    } catch (_) { return true; }
  }
  /* Engine 114: checkpoint input uses the same authenticated Worker door as
     settlement. Only logical choices leave the client; the server owns offers,
     recipients, clocks, carried builds and replay evidence. */
  /* R796: the engine a run was minted at (a level run is 115), learned from its prepare reply.
     V143 refuses an upgrade request whose engine_ver is not the run's (W16). */
  var RUN_ENGINE_R796 = {};
  function rememberRunEngineR796(run) {
    try {
      if (run && run.id != null && (run.engine_ver | 0) > 0) RUN_ENGINE_R796[String(run.id)] = run.engine_ver | 0;
      if (run && run.id != null && (run.wave_start | 0) > 0) RUN_DEPTH_DEEP[String(run.id)] = run.wave_start | 0;
    } catch (_) {}
  }
  function runEngineR796(runId) { return RUN_ENGINE_R796[String(runId)] || 114; }
  /* ============ DEEP ENDLESS · THE SERVER GETS TIME TO REPLAY (Noam, 2026-10-20) ============
     "at high levels can get super bogged down and 'break' at the boon-granting stage. I cleared Round 154 twice
     and then got reset to 150." The upgrade offer and the settle each make the Worker replay the whole five-round
     segment. Measured with the live engine on an Apple M4 Max (strong 5-hero party): 0.15 s at round 50, 0.8 s at
     round 100, 5.1 s at rounds 150 to 169, before the database calls; Cloudflare's cores and a heavier party are
     slower still. The page gave the offer 15 s and each settle try 12 s, and a request the page abandons is
     cancelled on the Worker too, so every retry started the replay over and the segment never saved: the run fell
     back to its first round. Past round 60 the page now waits in step with the depth (about 57 s at round 150,
     capped at 90 s). Shallower runs keep the old quick retry. Waiting changes nothing the server decides. */
  var RUN_DEPTH_DEEP = {};
  function serverWaitMs(runId, base) {
    var start = RUN_DEPTH_DEEP[String(runId)] | 0;
    return start > 60 ? Math.min(90000, base + (start - 60) * 500) : base;
  }
  function upgradeRequest114(action, runId, fields) {
    var scope = captureCombatAccount(), id = Number(runId);
    if (localMode() || !Number.isSafeInteger(id) || id < 1) return Promise.reject(new Error('This upgrade needs an authenticated Battlegrounds run.'));
    if (!scope.email || scope.pin == null || !String(scope.pin)) return Promise.reject(new Error('Sign in again to choose your upgrade.'));
    var body = Object.assign({}, fields || {}, {action:action,run_id:id,engine_ver:runEngineR796(id),email:scope.email,pin:scope.pin});
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    /* only the offer replays the segment on the Worker; choose, reserve, activate and reconcile are database
       calls and keep the quick 15 s give-up (self-review, 2026-10-21) */
    var timeout = controller ? setTimeout(function () { controller.abort(); }, action === 'offer' ? serverWaitMs(id, 15000) : 15000) : null;
    return fetch(WORKER_URL.replace(/\/+$/, '') + '/contract-settle', {
      method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body),signal:controller ? controller.signal : undefined
    }).then(function (response) {
      return response.json().then(function (result) {
        if (!combatAccountIsCurrent(scope)) throw accountChangedError('an upgrade was being chosen', false);
        if (!result || typeof result !== 'object' || result.run && String(result.run.id) !== String(id)) throw new Error('The upgrade response could not be verified. Reconnect to try again.');
        result.httpStatus = response.status;
        result.account = scope.account;
        result.generation = scope.generation;
        return result;
      });
    }).finally(function () { if (timeout) clearTimeout(timeout); });
  }
  /* R797 fix (live 2026-09-24): a level run resumed after a reload asked the upgrade door
     with 114, because only the prepare reply taught the map above and a resume reconciles
     before it prepares. V144 answered upgrade_saved_engine_mismatch and the run showed
     "This run can't continue" (run 2330, Engine 116). The live-run state names the run's
     engine, so every state read teaches the map, and a request that had to guess learns the
     engine once and asks again. The Worker checks the engine before any action, so the
     refused request changed nothing. */
  function rememberLiveRunEngineR797(st) {
    try { if (st && st.run_id != null && (st.engine_ver | 0) > 0) rememberRunEngineR796({ id: st.run_id, engine_ver: st.engine_ver, wave_start: st.wave_start }); } catch (_) {}
  }
  function upgradeRequestR797(action, runId, fields) {
    var guessed = !RUN_ENGINE_R796[String(runId)], sent = runEngineR796(runId);
    return upgradeRequest114(action, runId, fields).then(function (result) {
      if (!guessed || !result || result.httpStatus !== 409 || result.error !== 'upgrade_saved_engine_mismatch') return result;
      return unlimitedState(true).then(function (st) {
        rememberLiveRunEngineR797(st);
        var learned = RUN_ENGINE_R796[String(runId)];
        return learned && learned !== sent ? upgradeRequest114(action, runId, fields) : result;
      }, function () { return result; });
    });
  }
  function upgradeReconcile114(runId, offerId) { return upgradeRequestR797('reconcile', runId, offerId ? {offer_id:offerId} : {}); }
  function upgradeOffer114(runId, plays, checkpointKey) { var fields=plays == null ? {} : {plays:wirePlays(plays,runEngineR796(runId),'unlimited')}; if(checkpointKey)fields.checkpoint_key=checkpointKey;return upgradeRequestR797('offer',runId,fields); }
  function upgradeActivate114(runId, offerId, revision) { return upgradeRequestR797('activate', runId, {offer_id:offerId,revision:revision}); }
  function upgradeReserve114(runId, offerId, revision, abilityId) { return upgradeRequestR797('reserve', runId, {offer_id:offerId,revision:revision,ability_id:abilityId}); }
  function upgradeChoose114(runId, offerId, revision, abilityId, ownerId) { return upgradeRequestR797('choose', runId, {offer_id:offerId,revision:revision,ability_id:abilityId,owner_id:ownerId}); }
  function upgradeExpire114(runId, offerId, revision) { return upgradeRequestR797('choose', runId, {offer_id:offerId,revision:revision,timeout:true}); }
  function settleOnce(runId, outcome, opts, settleScope) {
    var url = WORKER_URL.replace(/\/+$/, '') + '/contract-settle';
    /* V39: SAY WHICH ENGINE DREW THE FIGHT THE PLAYER JUST WATCHED.
       The run row carries the version the DATABASE stamped and the worker
       checks its own against that, so a stale worker is caught. Nothing ever
       checked THIS file, and this file is the one that draws the battle on
       screen. A theme one version behind would show a fight from its own
       engine and be paid for a different fight judged in the worker, with no
       error anywhere and no way to notice. Sending it makes that loud.
       Null when the engine has not loaded, and the worker skips the check on
       null, so a missing engine cannot turn into a refused settle. */
    var _ev = (opts.engineVer | 0) || null;
    if (!_ev) {
      try { _ev = (window.HOHContract && window.HOHContract.ENGINE_VERSION) || null; } catch (_) {}
    }
    var _mode = String(opts.mode || '') === 'unlimited' ? 'unlimited' : '';
    /* v37: THE HAND. The worker rebuilds the fight from the row and the row
       knows nothing about a card, so a battle that Arcane Volley decided would
       settle as the battle without it — the screen would show a win and the
       payout would be a loss. The engine clamps every number on the far side,
       so the only thing this sentence trusts the client with is WHEN the cards
       were played and which face answered the call. Null when nothing was
       played, which is the exact v36 body byte for byte.

       R407 FIX, LIVE DIVERGENCE. This read slice(0, 3) — correct for v37,
       where a road held exactly three cards. v38 added the Second Wind
       buy-back, so one fight can legitimately hold mend, mend2, mend3, bolt
       and ally. readPlays caps at SEVEN on both the engine and the judge, and
       the client simulated all of them, but only the first three ever left the
       browser. A player who bought back a heal and played five cards watched a
       fight the judge never saw and got paid for a different one, with no error
       anywhere. Exactly the failure v38's rail lock was built to prevent,
       arriving through a different door. This number must match readPlays.
       ⛔ R560: AND IT STOPPED MATCHING, exactly as this comment feared. R523's
       ramp raised readPlays to 16 and this stayed at 7 for a whole wave, so
       every long fight was judged without the player's eighth card onward.
       It asks the engine now (playSendCap) instead of carrying a copy of a
       number that lives in another file. */
    var _plays = null;
    try {
      if (Array.isArray(opts.plays) && opts.plays.length) {
        _plays = wirePlays(opts.plays, _ev, _mode);
      }
    } catch (_) {}
    /* ⛔ R791 (Oran's frozen boss stop): Engine 114's upgrade authority reads
       an ABSENT plays field as "use the saved card history" and refuses null
       as upgrade_invalid_plays on every retry, so an Unlimited segment with no
       card played could never settle. Leave the key out (JSON.stringify drops
       undefined) for exactly that case. Every engine reads null, undefined and
       [] as the same empty hand, so no replay changes; every other mode and
       engine keeps the null body byte for byte. */
    if (_plays === null && _mode === 'unlimited' && (_ev | 0) >= 114) _plays = undefined;
    var body = JSON.stringify({ run_id: runId, outcome: outcome,
      email: settleScope && settleScope.email,
      pin: settleScope && settleScope.pin,
      state_token: opts.stateToken || null,
      doubled: !!opts.doubled, plan_id: opts.planId || '',
      plan_ids: opts.planIds || null, plays: _plays, engine_ver: _ev,
      recruit_faces: _mode === 'unlimited' && _ev >= 68 ? unlimitedFaceSnapshot(runId, opts, _ev) : null,
      last_completed_round:outcome==='solo_forfeit'?opts.lastCompletedRound:undefined });
    var ctl = null, timer = null, init = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body };
    /* AbortController is everywhere we ship, but never assume: without it the
       call simply keeps the old no-timeout behaviour instead of throwing. */
    try {
      if (typeof AbortController === 'function') {
        ctl = new AbortController();
        init.signal = ctl.signal;
        timer = setTimeout(function () { try { ctl.abort(); } catch (_) {} }, _mode === 'unlimited' ? serverWaitMs(runId, SETTLE_TIMEOUT_MS) : SETTLE_TIMEOUT_MS);
      }
    } catch (_) {}
    return fetch(url, init)
      .then(function (r) { return r.text().then(function (t) { return { status: r.status, text: t }; }); })
      .then(function (raw) {
        if (timer) { clearTimeout(timer); timer = null; }
        var json = null;
        try { json = raw.text ? JSON.parse(raw.text) : null; } catch (_) { json = null; }
        /* v39: KEEP WHAT THE JUDGE SAID. The pack screen asks the vault what it
           is owed and, when the answer is nothing, has had no way to say why.
           The reason is always in this reply and it was being thrown away the
           moment the result screen finished with it. One compact line, so a
           failure explains itself on screen instead of costing a round trip.
           HTTP status included, because a 502 here and a settled_loss here are
           the same blank screen to a player and opposite problems to fix. */
        try {
          window.__hohLastSettle = {
            http: raw.status,
            run: runId,
            status: json && json.status,
            ok: json && json.ok,
            packs: (json && json.packs != null) ? json.packs : null,
            pot: (json && json.pot != null) ? json.pot : null,
            grant: (json && json.grant) || null,
            wv: (json && json.worker_ver) || null,
            cv: (json && json.client_ver != null) ? json.client_ver : null,
            rv: (json && json.run_ver != null) ? json.run_ver : null,
            reason: (json && json.reason) || null,
            err: (json && json.error) || null,
            at: Date.now()
          };
        } catch (_) {}
        /* A parsed edge response is still ambiguous when the HTTP status says
           the Worker/upstream failed. In particular, a database timeout may
           happen after COMMIT; retrying the identical request is how the saved
           receipt gets back to the player. Account and state 4xx responses stay
           terminal and are shown as actionable recovery instead of retried. */
        if (raw.status === 408 || raw.status === 425 || raw.status === 429 || raw.status >= 500) {
          var eHttp = new Error('temporary settle failure (' + raw.status + ')');
          eHttp.retryable = true;
          eHttp.httpStatus = raw.status;
          eHttp.resultSafe = !!(json && json.result_safe);
          eHttp.settleCode = String(json && json.error || '');
          /* R565: the worker's 502 body now names its reason. `deterministic`
             means a present server function REFUSED the request (a supabase
             4xx), which will refuse the identical retry identically, so the
             screen can stop pretending it is weather. Logged FLAT (the R559
             rule: Jan screenshots consoles, and an Object collapses). */
          eHttp.settleDetail = String(json && json.detail || '').slice(0, 200);
          eHttp.deterministic = !!(json && json.deterministic);
          /* R791: the Worker's own word, kept apart from `deterministic`,
             which the recovery streak also sets for its own counting */
          eHttp.__hocDeterministic = eHttp.deterministic;
          if (eHttp.settleDetail) {
            try { console.error('R565 SETTLE REFUSED run=' + runId + ' status=' + raw.status +
              ' code=' + eHttp.settleCode + ' deterministic=' + eHttp.deterministic +
              ' detail=' + eHttp.settleDetail); } catch (_) {}
          }
          throw eHttp;
        }
        if (json) return json;
        /* a body we cannot parse is an edge error page, not a verdict */
        var e = new Error('bad settle body (' + raw.status + ')');
        e.retryable = true;
        throw e;
      })
      .catch(function (e) {
        if (timer) { clearTimeout(timer); timer = null; }
        /* every network-shaped failure is worth another go; a parsed
           { ok:false } from the worker is a real answer and is not */
        if (e && e.retryable === undefined) e.retryable = true;
        throw e;
      });
  }
  function settleRemote(runId, outcome, opts, settleScope) {
    var attempt = 0;
    function go() {
      attempt++;
      return settleOnce(runId, outcome, opts, settleScope).catch(function (e) {
        /* R791: a refusal that repeats (the Worker says so, or its replay
           threw an engine code) costs the Worker seconds per try and answers
           the same way, so it goes straight to the recovery screen */
        if (attempt >= SETTLE_TRIES || !(e && e.retryable) || runErrorKind(e).kind === 'server') throw e;
        return new Promise(function (res) { setTimeout(res, attempt === 1 ? 600 : 1800); }).then(go);
      });
    }
    return go();
  }
  /* ============ R622 · THE SANDBOX JUDGES A SEGMENT =======================
     The one arm of settleLocal that answers an Unlimited segment, in the
     R611B reply shape (:1985-2029) so the whole result path downstream reads
     one shape whichever judge answered. It advances the sandbox carry row the
     way hoh_bg_unlimited_settle_atomic advances the real one, and it is the
     ONLY reason a signed-out god can play this mode at all.
     There is NO champ and NO stars input here and there must never be: the
     engine throws unlimited_champion_rule_owner_pending_Q7 on either, which
     Jan's R-1 ruling adopts as the shipped design (the crown is out of this
     mode). Nothing in this arm reads pathInputs() either. */
  function settleUnlimitedLocal(runId, opts) {
    var U = localUnlim(), run = window.__hohLocalRun || {}, E = window.HOHContract;
    if (!E || !E.run) return Promise.reject(new Error('the engine is still loading'));
    var simVer = (opts.engineVer | 0) || (run.engine_ver | 0) || (E.ENGINE_VERSION | 0);
    var bestAtStart = unlimitedRunBestAtStart(run);
    if (simVer >= 80 && bestAtStart === null) {
      return Promise.reject(new Error('Unlimited run is missing its frozen best round'));
    }
    var sim = E.run({
      mode: 'unlimited', engineVer: simVer, seed: run.seed,
      aelricRevisit: simVer >= 80 ? bestAtStart >= 21 : undefined,
      bestRoundAtStart: simVer >= 88 ? bestAtStart : undefined,
      /* R745: Engine 92 refuses a run without its frozen Round 20 fact, and the
         signed-out judge never passed it, so a sandbox settle would throw. */
      cleared20AtStart: simVer >= 92 ? !!(run.foe && run.foe.cleared20_at_start === true) : undefined,
      roster: (run.roster || []), recruits: (run.recruits || []),
      roundStart: run.wave_start | 0, roundCount: run.wave_count | 0,
      plays: opts.plays || [],
      items: simVer >= 69 ? copyItems(U.items, simVer) : undefined,
      carryA: U.hp | 0, maxA: U.max_hp | 0, armorA: U.armor | 0,
      wardA: U.ward | 0, lockA: U.lock_a || null, fallenHero: U.fallen_hero || []
    });
    var hornGrants = 0, itemRule = null;
    if (simVer >= 80) {
      var itemGrants = sim && sim.itemGrants;
      if (!itemGrants || typeof itemGrants !== 'object' || Array.isArray(itemGrants) ||
          Object.keys(itemGrants).some(function (key) { return key !== 'horn' && !(simVer >= 101 && key === 'goblin'); })) {
        return Promise.reject(new Error('Engine80 returned malformed item grants'));
      }
      var hasHornGrant = Object.prototype.hasOwnProperty.call(itemGrants, 'horn');
      hornGrants = hasHornGrant ? itemGrants.horn : 0;
      if (!Number.isSafeInteger(hornGrants) || hornGrants < 0 ||
          hornGrants > (sim.roundsCompleted | 0)) {
        return Promise.reject(new Error('Engine80 returned impossible Horn grants'));
      }
      if (simVer >= 101 && (!Number.isSafeInteger(itemGrants.goblin || 0) || (itemGrants.goblin || 0) < 0 || (itemGrants.goblin || 0) > 1)) return Promise.reject(new Error('Invalid Goblin Horn grant'));
      itemRule = unlimitedShopRule(simVer);
      if (!itemRule) return Promise.reject(new Error('Engine80 item stack rule is unavailable'));
    }
    /* R671: the local judge owns the same saved-stock ledger as the Worker.
       Only resolved plays spend stock; request inventories are never trusted. */
    if (simVer >= 69) {
      U.items = copyItems(U.items, simVer);
      (sim.spentItems || []).forEach(function (id) {
        var kind = E.itemKind(id);
        if (Object.prototype.hasOwnProperty.call(U.items, kind)) U.items[kind] = Math.max(0, U.items[kind] - 1);
      });
    }
    if (simVer >= 80) {
      U.items.horn = Math.min(itemRule.stackCap | 0, (U.items.horn | 0) + hornGrants);
      if (simVer >= 101) U.items.goblin = Math.min(1,(U.items.goblin | 0) + (sim.itemGrants.goblin | 0));
    }
    var won = sim.winner === 'A';
    var lastRound = sim.lastRound | 0;
    var hearts = 0, packs = 0, crystals = 0;
    /* the payout table is the engine's, never a literal here */
    for (var rr = sim.roundStart | 0; rr < (sim.roundStart | 0) + (sim.roundsCompleted | 0); rr++) {
      try {
        var plan = E.wavePlan(rr, simVer);
        hearts += (plan.reward.hearts | 0);
        packs += (plan.reward.packs | 0);
        crystals += (plan.reward.crystals | 0);
      } catch (_) {}
    }
    var progressRounds = simVer >= 68 ? (sim.roundsReached | 0) : (sim.roundsCompleted | 0);
    var reachedRound = progressRounds ? (sim.roundStart | 0) + progressRounds - 1 : 0;
    var newBest = Math.max(U.best_round | 0, reachedRound);
    var family = U.run_family;
    U.best_round = newBest;
    if ((sim.roundsCompleted | 0) > 0 && (sim.roundStart | 0) + (sim.roundsCompleted | 0) - 1 >= 20) U.cleared20 = true;
    if (typeof unlimitedLocalCreditStarters === 'function') unlimitedLocalCreditStarters(U, reachedRound);
    if (newBest > 0) U.best_at = new Date().toISOString();
    U.banked = { hearts: ((U.banked && U.banked.hearts) | 0) + hearts,
                 packs: ((U.banked && U.banked.packs) | 0) + packs,
                 crystals: ((U.banked && U.banked.crystals) | 0) + crystals };
    U.packs_unopened = (U.packs_unopened | 0) + packs;
    if (won) {
      U.hp = sim.ta | 0; U.max_hp = sim.maxA | 0; U.armor = sim.armorA | 0;
      U.ward = sim.wardA | 0; U.lock_a = sim.lockA || null;
      U.party = sim.roster || [];
      U.recruits = E.unlimitedRecruitFaces ? E.unlimitedRecruitFaces(sim.recruits || [],
        unlimitedFaceSnapshot(runId, opts, simVer), simVer) : (sim.recruits || []);
      U.fallen_hero = sim.fallenHero || []; U.buff_ids = sim.buffIds || [];
      U.round = (sim.roundStart | 0) + (sim.roundsCompleted | 0);
      U.run_status = 'settled_win';
      var nextPlan = null;
      try { nextPlan = E.wavePlan(U.round - 1, simVer); } catch (_) {}
      U.at_stop = !!(nextPlan && nextPlan.stopAfter);
    } else {
      /* a defeat unbinds the row, exactly as R611B:1956-1959 does */
      U.live = false; U.run_id = null; U.run_family = null; U.seed = null;
      U.run_status = 'settled_loss'; U.at_stop = false;
    }
    try { if (window.__hohLocalRun) window.__hohLocalRun.status = won ? 'settled_win' : 'settled_loss'; } catch (_) {}
    var response = {
      ok: true, receipt_id: rhex(8) + '-' + rhex(4) + '-4' + rhex(3) + '-8' + rhex(3) + '-' + rhex(12),
      run_id: runId, status: won ? 'settled_win' : 'settled_loss',
      winner: won ? 'A' : 'B', mode: 'unlimited',
      ta: sim.ta | 0, tb: 0, rounds: sim.roundsReached | 0, hash: 'local',
      stage: null, encounter_kind: 'unlimited_segment',
      pot: hearts, packs: packs, crystals: crystals,
      owner_bonus: 0, recruit_dust: 0, grant: packs > 0 ? 'ok' : 'skipped',
      wave_start: sim.roundStart | 0, wave_count: sim.roundCount | 0,
      rounds_reached: sim.roundsReached | 0, rounds_completed: sim.roundsCompleted | 0,
      last_round: lastRound, next_round: sim.nextRound | 0, best_round: newBest,
      live: won, run_family: won ? family : null,
      items: U.items, fallen_hero: sim.fallenHero || [],
      banked: U.banked,
      wallet: { hearts_entitled: hearts, hearts_granted: hearts, capped: false,
                hearts_balance: null, crystals_granted: crystals, crystals_balance: null },
      xp: { pool: 0, awarded: 0, outcome_multiplier_bps: won ? 10000 : 3500, awards: [] },
      committed_at: new Date().toISOString(),
      sandbox: true
    };
    U.receipt = won ? response : null;
    U.last_receipt = response;
    unlimitedPublish(collectionAccount(), U);
    try {
      window.__hohLastSettle = { local: true, unlimited: true, http: 0, run: runId,
        ok: true, status: response.status, packs: packs, pot: hearts,
        grant: 'sandbox, never sent', at: Date.now() };
    } catch (_) {}
    return Promise.resolve(response);
  }
  function settleLocal(runId, outcome, opts) {
    var run = window.__hohLocalRun || {};
    if (outcome === 'fled_half' || outcome === 'fled_quarter') return Promise.resolve({ ok: true, status: outcome, packs: 0, sandbox: true });
    /* R622: THE RUN ROW'S MODE IS THE ONLY AUTHORITY (Part 2 rule 6). The
       sandbox run row says what it is; opts is a hint and never the decision.
       There is no flee in this mode, which is why the arm sits below the flee
       branch and not above it. */
    if (String(run.mode || '') === 'unlimited') return settleUnlimitedLocal(runId, opts || {});
    var E = window.HOHContract;
    /* v37: the sandbox judges the same fight the sandbox drew, hand included.
       Without this line god mode would settle every carded battle against a
       simulation the screen never played. */
    /* R450b (review fix 1j): THE PATH RIDES INTO THE SANDBOX JUDGE. The UI's
       one opts builder (_simOpts) folds the stored path into every screen sim
       — champion, stars, event picks, draft picks, opt-ins, and the rung
       itself — and the settle call now hands the same fields here. Without
       them this judged a PLAIN road fight against a screen that animated a
       blessed rung, and the verdict flipped on close boards. */
    var _localSimEngine = (opts.engineVer | 0) || (run.engine_ver | 0) || (E.ENGINE_VERSION | 0);
    var sim = E.run({ seed: run.seed, roster: (run.roster || []), foeScale: Number((run.foe && run.foe.scale) || 1), planId: opts.planId || null, planIds: opts.planIds || (run.plans || null), doubled: !!opts.doubled, carryA: (run.carry_a | 0) || 0, maxA: (window.__hohLocalLadder && window.__hohLocalLadder.max_hp | 0) || 0, depth: (run.depth | 0), engineVer: _localSimEngine, recruits: run.recruits || [], plays: opts.plays || [], foe: (run.rival && run.rival.roster) || undefined,
      stage: (opts.stage | 0) || undefined, rival: opts.rival || undefined,
      champ: opts.champ, stars: opts.stars,
      eventChoices: opts.eventChoices, draftPicks: opts.draftPicks, optIns: opts.optIns });
    var won = sim.winner === 'A', f = forceFlags();
    var ta = sim.ta, tb = sim.tb;
    if (f.outcome === 'win' || f.outcome === 'loss') {
      won = f.outcome === 'win';
      /* when an outcome is forced for debugging, present the scores so the
         result panel reads consistently (winner ahead) instead of the raw
         sim numbers that may disagree with the forced side */
      var hi = Math.max(ta, tb), lo = Math.min(ta, tb);
      ta = won ? hi : lo; tb = won ? lo : hi;
    }
    var raised = E.raisedFromSeed(run.seed);
    /* v28: no raise, no double, no pot that grows. The table you are on has a
       price and that is what it pays. */
    var pot = purseWin(opts.depth | 0);
    try { if (won && window.__hohLocalRun) window.__hohLocalRun._finalTa = ta; } catch (e) {}
    /* R450b: A RUNG ADVANCES THE SANDBOX LADDER. Until now the sandbox
       climb never moved: settleLocal answered a road-shaped reply, the win
       sheet drew the five-table recruit board on a rung, and stage 2 was
       unreachable in god mode — which made the entire R450 path chain
       (events, drafts, the crowning, the reclaim) untestable on a device.
       This mirrors hoh_bg_ladder_settle's own arithmetic, field for field:
       loss stashes the whole bench into fallen (the reclaim reads it),
       win advances the stage, decrements spent items, keeps answered
       allies, and pays first clears only. */
    var _lstg = opts.stage | 0;
    var _localFaces = _localSimEngine >= 71 ? unlimitedFaceSnapshot(runId, opts, _localSimEngine) : {};
    var _localAllies = won && _localSimEngine >= 71 ? localAidRows(sim, E, _localSimEngine, _localFaces) : [];
    if (_lstg > 0 && window.__hohLocalLadder) {
      var L = window.__hohLocalLadder;
      if (L.run_id != null && String(L.run_id) !== String(runId)) {
        return Promise.resolve({ ok: true, ladder: true, idempotent: true, status: won ? 'settled_win' : 'settled_loss', stage: L.stage | 0, ta: ta, tb: tb, pot: 0, packs: 0, hash: 'local', sandbox: true });
      }
      var _rep = { ok: true, ladder: true, status: won ? 'settled_win' : 'settled_loss', stage: _lstg, ta: ta, tb: tb, hash: 'local', sandbox: true };
      if (won) {
        var _first = _lstg > (L.best_stage | 0);
        L.best_stage = Math.max(L.best_stage | 0, _lstg);
        L.wins = (L.wins | 0) + 1;
        var _lk = null;
        try { _lk = (sim.events || []).filter(function (x) { return x.e === 'lock'; })[0]; } catch (e) {}
        var _open = (_lk && (_lk.ta | 0)) || ta || 0;
        L.max_hp = Math.max(L.max_hp | 0, _open);
        L.hp = Math.min(L.max_hp | 0, Math.max(1, (ta | 0) + 20));
        L.items = L.items || {};
        (opts.plays || []).forEach(function (p) {
          var k = String((p && p.id) || '');
          k = k.indexOf('mend') === 0 ? 'mend' : k.indexOf('bolt') === 0 ? 'bolt' : k.indexOf('ally') === 0 ? 'ally' : '';
          if (k) L.items[k] = Math.max(0, (L.items[k] | 0) - 1);
          if (_localSimEngine <= 70 && k === 'ally' && p.name) {
            L.recruits = L.recruits || [];
            var _legacyAnswer = addPersistentBench(L.recruits, {
              name: p.name, cls: p.cls || 'Fighter', hearts: 100,
              rar: Math.max(0, Math.min(3, Math.floor(_lstg / 8))), lvl: 1, aid: true
            }, _localSimEngine);
            L.recruits = _legacyAnswer.list;
          }
        });
        /* R491: god mode settles the same deaths the Worker derived and then
           fills at most one open seat. Doing it here, inside the judge, avoids
           the old UI-after-the-fact localFall race and gives the result sheet
           the same authoritative promoted body live play receives. */
        var _lf = applyLocalFalls(L, sim.fellBench || [], sim.fellHero || [], _lstg);
        if (_localAllies.length) {
          var _answer = addPersistentBench(L.recruits, _localAllies, _localSimEngine);
          L.recruits = _answer.list;
        }
        L.stage = _lstg + 1; L.run_id = null; L.stage_seed = null;
        /* the worker banks one champion XP per rung cleared (best effort,
           outside the payout path) — the sandbox star ladder needs the
           same drip or the tiers can never be tested locally */
        try {
          var _ck9 = L.path && L.path.champ_key;
          if (_ck9) {
            var C9 = window.__hohLocalChamp = window.__hohLocalChamp || {};
            var _cr9 = C9[_ck9] = C9[_ck9] || { xp: 0, stars: 0 };
            _cr9.xp = (_cr9.xp | 0) + 1;
          }
        } catch (e9) {}
        _rep.next = L.stage; _rep.next_kind = E.ladderKind ? E.ladderKind(L.stage, _localSimEngine) : 'fight';
        _rep.first_clear = _first;
        _rep.pot = _first && E.ladderHearts ? E.ladderHearts(_lstg) : 0;
        _rep.packs = _first && E.ladderPacks ? E.ladderPacks(_lstg) : 0;
        _rep.recruits = (L.recruits || []).slice();
        _rep.party = _lf.party;
        _rep.fallen_party = _lf.fallen_party;
        _rep.promoted = _lf.promoted;
      } else {
        L.live = false; L.run_id = null; L.hp = 0;
        L.fallen = (L.recruits || []).slice();
        L.recruits = []; L.fallen_party = [];
        _rep.pot = 0; _rep.packs = 0; _rep.retry_hearts = 0;
        _rep.fallen_party = [];
      }
      try {
        window.__hohLastSettle = { local: true, ladder: true, http: 0, run: runId, ok: true,
          status: _rep.status, packs: _rep.packs | 0, pot: _rep.pot | 0,
          grant: 'sandbox, never sent', at: Date.now() };
      } catch (e) {}
      return Promise.resolve(_rep);
    }
    if (won && _localSimEngine >= 71) {
      var _roadFell = {};
      (sim.fellBench || []).forEach(function (i) { _roadFell[i | 0] = 1; });
      var _roadBench = (run.recruits || []).filter(function (_row, i) { return !_roadFell[i]; });
      run.recruits = addPersistentBench(_roadBench, _localAllies, _localSimEngine).list;
      if (window.__hohLocalRun) window.__hohLocalRun.recruits = run.recruits.slice();
    }
    /* v39: stamp the sandbox on the record the pack screen reads, so a prize
       that cannot exist says why instead of blaming the vault. */
    try {
      window.__hohLastSettle = { local: true, http: 0, run: runId, ok: true,
        status: won ? 'settled_win' : 'settled_loss',
        packs: won ? pursePacks(opts.depth | 0) : 0, pot: won ? pot : 0,
        grant: 'sandbox, never sent', at: Date.now() };
    } catch (e) {}
    var _localResult = { ok: true, status: won ? 'settled_win' : 'settled_loss', ta: ta, tb: tb, pot: won ? pot : 0, packs: won ? pursePacks(opts.depth | 0) : 0, hash: 'local', sandbox: true };
    if (_localSimEngine >= 71) _localResult.recruits = (run.recruits || []).slice();
    return Promise.resolve(_localResult);
  }
  function settle(runId, outcome, opts) {
    opts = opts || {};
    var stake = opts.stake || 0;
    /* R348: a SANDBOX run can never be judged by the worker. Its id is
       'local-<ts>', which parseInt turns into NaN server-side, so the worker
       refused it three times and the player read "could not reach the
       ledger" -- a network message for what was actually a mode mismatch
       (local test toggled off mid-run in the debug panel). The id names the
       judge: local ids settle locally, whatever the toggle says now. */
    var isLocalRun = /^local-/.test(String(runId || ''));
    var settleScope = (localMode() || isLocalRun) ? null : captureCombatAccount();
    if (settleScope && opts.preparedAccount && opts.preparedAccount !== settleScope.account) {
      return Promise.reject(accountChangedError('the battle result was saving', false));
    }
    if (settleScope && opts.preparedGeneration != null &&
        (opts.preparedGeneration | 0) !== (settleScope.generation | 0)) {
      return Promise.reject(accountChangedError('the battle result was saving', false));
    }
    if (settleScope && outcome === 'battle' && !/^[0-9a-f]{32}$/.test(String(opts.stateToken || ''))) {
      return Promise.reject(new Error('This battle was not verified before it started. Return to setup and try again.'));
    }
    if (settleScope && ['battle', 'fled_half', 'fled_quarter'].indexOf(String(outcome || '')) >= 0) {
      try { rememberPendingSettlement(settleScope, runId, outcome, opts); }
      catch (pendingErr) { return Promise.reject(pendingErr); }
      /* A server-committed result remains here until the result UI explicitly
         finishes every required module. Re-entering settle during a recovered
         playback therefore returns the exact saved response and cannot pay,
         advance, or mutate the ladder a second time. */
      var committed = committedResponse(settleScope, runId, outcome);
      if (committed) return Promise.resolve(committed);
    }
    return ((localMode() || isLocalRun) ? settleLocal(runId, outcome, opts) : settleRemote(runId, outcome, opts, settleScope)).then(function (res) {
      if (!res || !res.ok) {
        /* R348: the worker ANSWERING with a refusal is not a network failure.
           Tag it so the UI stops blaming the connection for a verdict. */
        var eRef = new Error((res && res.error) || 'settle refused');
        eRef.__hocRefused = (res && res.error) || 'refused';
        /* R791: V142 answers an engine throw as 409 engine_refused with
           deterministic:true and the engine's leading code in detail. Keep
           all three so recovery can stop offering an endless Retry. */
        eRef.settleCode = String((res && res.error) || '');
        eRef.settleDetail = String((res && res.detail) || '').slice(0, 200);
        eRef.__hocDeterministic = !!(res && res.deterministic === true);
        if (eRef.__hocDeterministic) eRef.deterministic = true;
        /* ============ R622 · A result_safe REFUSAL KEEPS THE RECORD ========
           ⛔ THIS RECORD IS THE ONLY LOCAL COPY OF A DEATH RECEIPT, AND
           CLEARING IT DESTROYS IT. Every Unlimited 409 V81 can answer carries
           result_safe:true (unlimited_binding_mismatch,
           unlimited_engine_unsupported, client_run_engine_mismatch), and
           after a COMMITTED LOSS the binding 409 is the EXPECTED answer, not
           an error: the defeat unbinds the carry row (R611B:1956-1959) while
           V81 checks the binding BEFORE the RPC's own replay
           (AAA-DEPLOY-THIS-worker-V81.js:14791-14795), so every retry of a
           lost reply refuses by name. Clearing on that would throw away the
           receipt the end ceremony is about to read.
           result_safe is the Worker's own word for "nothing was decided by
           this refusal", so the record STAYS and the caller is routed to
           recovery. Only a refusal that is NOT result_safe is a genuine
           non-commit the player can correct by changing setup, and only that
           one releases the record. A 5xx never reaches here at all (settleOnce
           throws it as retryable), so this branch is exactly the parsed
           4xx/refusal case. */
        if (res && res.result_safe === true) {
          eRef.resultSafe = true;
          eRef.__hohRewardPending = true;
          eRef.__hocPendingRun = String(runId);
          throw eRef;
        }
        /* A concrete Worker refusal is a non-commit, not ambiguous recovery.
           Release only that matching request so the player can correct setup;
           transport failures never reach this branch and remain durable. */
        if (settleScope && !clearPendingSettlement(settleScope, runId)) {
          eRef.__hohRewardPending = true;
        }
        throw eRef;
      }
      /* ============ R622 · THE MODE BRANCH · THE ONE LINE THAT MATTERS =====
         ⛔ AN UNLIMITED RECEIPT MUST NEVER REACH THE DEVICE HEARTS APPLIER.
         The R611B reply carries `pot` because the receipt identity check ties
         it (sql/R611B-unlimited-foundation.sql:1996), and the RPC has ALREADY
         CREDITED THE SERVER WALLET through hoh_wallet_apply before it
         answered. Everything below this branch — the R333 expectPot rebuild,
         `potDue`, `claimSpecs`, `claimLocalReward` — exists to pay the OLD
         local scalar bank from `res.pot`. Letting an Unlimited receipt through
         would pay the same Hearts a second time, on this device, forever
         (the standing warning in the R611B header).

         KEYED ON res.mode, THE SERVER'S FIELD OFF THE REPLY, NEVER
         opts.mode ALONE (Part 2 rule 6). opts.mode is client state and rides
         the pending record; a forged or stale one must not be able to skip
         the applier for a ladder reply, and the reply's own mode must win
         when the two disagree. The disagreement is logged FLAT, the R559
         screenshot rule.

         FAIL CLOSED: an Unlimited reply with no receipt_id is not a receipt.
         Every path in R611B/R614B writes one before it returns, so a reply
         without it is a shape this file does not understand, and the safe
         answer is to refuse rather than to pay or to bank a ghost.

         What still happens: applyBattleXpReceipt (the card-level overlay is
         the same authority in either mode) and the identical pending-record
         bookkeeping, so resume, recovery and the one-record-per-account rule
         all behave exactly as they do for a climb. */
      var _resMode = String((res && res.mode) || '');
      var _optMode = String((opts && opts.mode) || '');
      if (_resMode !== _optMode && (_resMode === 'unlimited' || _optMode === 'unlimited')) {
        try { console.error('R622 SETTLE MODE MISMATCH run=' + runId +
          ' reply=' + (_resMode || '(none)') + ' client=' + (_optMode || '(none)') +
          ' status=' + String((res && res.status) || '')); } catch (_) {}
      }
      if (_resMode === 'unlimited') {
        if (!res.receipt_id) {
          var eNoReceipt = new Error('This round was judged but its receipt did not arrive. Reopen Battlegrounds to finish it safely.');
          eNoReceipt.__hocRefused = 'unlimited_receipt_missing';
          eNoReceipt.__hohRewardPending = true;
          eNoReceipt.__hocPendingRun = String(runId);
          throw eNoReceipt;
        }
        if (settleScope) applyBattleXpReceipt(res, settleScope);
        /* No claim journal, no local bank, no wallet arithmetic of any kind.
           These three fields exist so every reader downstream sees the same
           keys it sees on a climb reply and never has to branch. */
        res.local_reward_first = false;
        res.local_reward_amount = 0;
        res.local_reward_claims = null;
        res.local_reward_account = settleScope ? settleScope.account : 'sandbox';
        /* THE PACKS LATCH. An Unlimited pack is a durable server credit
           earned per ten completed Rounds and opened ONCE at the end of the
           run, not per segment. The result screen's packs-opened latch must
           therefore be released for this mode, or the next segment's settle
           hits the one-record-per-account rule (rememberPendingSettlement's
           `occupied` throw above) with a record the UI is still holding open
           for a pack opener that is not due yet. The data layer states it on
           the reply; the result screen reads it through
           D.settlementPacksDeferred. */
        res.__hocPacksDeferred = true;
        if (settleScope && !combatAccountIsCurrent(settleScope)) throw accountChangedError('the battle result was saving', true);
        if (settleScope && ['battle', 'fled_half', 'fled_quarter'].indexOf(String(outcome || '')) >= 0) {
          if (!savePendingResponse(settleScope, runId, outcome, res)) {
            var uSaveErr = new Error('Your result was saved, but this device could not preserve its result screen. Reopen Battlegrounds to finish safely.');
            uSaveErr.__hohRewardPending = true;
            uSaveErr.__hohCommitted = true;
            throw uSaveErr;
          }
          var uSaved = committedResponse(settleScope, runId, outcome);
          if (!uSaved) {
            var uReadErr = new Error('Your result was saved, but its recovery copy could not be read. Reopen Battlegrounds.');
            uReadErr.__hohRewardPending = true;
            uReadErr.__hohCommitted = true;
            throw uReadErr;
          }
          res.__hocResume = uSaved.__hocResume;
          res.__hocPendingRun = uSaved.__hocPendingRun;
          res.__hocPendingOutcome = uSaved.__hocPendingOutcome;
          res.__hocStateToken = uSaved.__hocStateToken;
          res.__hocRecoveryAccount = uSaved.__hocRecoveryAccount;
          res.__hocRecoveryGeneration = uSaved.__hocRecoveryGeneration;
        }
        /* the mirror is stale the moment a segment settles: the round moved,
           the badge may have risen, and a loss unbound the row */
        unlimitedForget();
        return res;
      }
      /* R333: THE IDEMPOTENT REPLAY MUST STILL PAY.
         When a retry lands on a run the worker already settled, it answers
         { ok:true, status:'settled_win', idempotent:true } with NO pot — the
         payout lives only in the reply that got lost. Without this the player
         wins, sees the win screen, and banks nothing. The UI hands us the pot
         it is about to display (the same number the worker computes from
         run.pot), so pay that instead. Capped like every other credit. */
      if (!res.receipt_id && res.status === 'settled_win' && !(res.pot > 0) && res.idempotent && opts.expectPot > 0) {
        /* R363: the packs have to be rebuilt too. The worker's idempotent
           branch answers before it computes anything, so `res.packs` is
           undefined here, and `|| 0` turned a lost REPLY into a lost prize:
           contract_settle had already recorded up to five packs and the player
           opened none, with no server-side balance to claim them from later.
           Depth 1-3 and the grand five are restored from depth just below;
           this line restores fight one's own stake-scaled table, using the
           same function the worker computed it with. */
        var _repPot = Math.min(POT_CAP, (opts.expectPot | 0) || purseWin(opts.depth | 0));
        /* R491: keep every authoritative rung field the Worker recovered on
           its idempotent path. Replacing the object here used to throw away
           party/promoted/recruits at the exact moment the retry had gone back
           to Supabase to recover them. Only the missing payout is rebuilt. */
        var _idem = {}, _ik;
        for (_ik in res) if (Object.prototype.hasOwnProperty.call(res, _ik)) _idem[_ik] = res[_ik];
        _idem.ok = true; _idem.status = res.status; _idem.idempotent = true;
        _idem.pot = _repPot;
        _idem.packs = (res.packs != null ? res.packs : pursePacks(opts.depth | 0));
        _idem.replayed = true;
        res = _idem;
      }
      /* Existing keyless runs can still finish on the legacy response shape.
         New schema-v1 runs always carry receipt_id and therefore never rebuild
         a server reward from UI state. */
      var _rd = (opts && opts.depth) | 0;
      if (!res.receipt_id && !res.ladder && res.status === 'settled_win' && _rd > 0) {
        res.pot = purseWin(_rd);
        res.packs = (res.packs != null) ? res.packs : pursePacks(_rd);
        if (_rd >= 4) res.grand = 1;
      }
      if (!res.receipt_id && res.owner_bonus == null) res.owner_bonus = Math.max(0, Math.min(250, opts.ownerBonus | 0));
      if (!res.receipt_id && res.recruit_dust == null) res.recruit_dust = Math.max(0, Math.min(120, opts.recruitDust | 0));
      /* R496: settlement owns permanent battle EXP. Copy only the exact saved
         receipt into the current account's cache; a switched generation gets
         no cache mutation and the browser never derives XP itself. */
      if (settleScope) applyBattleXpReceipt(res, settleScope);
      /* The climb mirror is display-only and must never survive an authoritative
         terminal receipt, even if the mounted UI has already been replaced. */
      if (res.ladder) ladderForget();
      /* Every local-wallet credit now wears a receipt/category key. Supabase is
         still the authority for the amount; this journal merely guarantees the
         old local bank consumes that amount once across retries and tabs. */
      var identity = claimIdentity(res, runId);
      var legacyIdentity = claimLegacyIdentity(res, runId), claimSpecs = [];
      var potDue = res.status === 'settled_win' ? Math.max(0, res.pot | 0) : 0;
      var ownerDue = res.status === 'settled_win' ? Math.max(0, res.owner_bonus | 0) : 0;
      var leaveDue = (res.status === 'fled_half' || res.status === 'fled_quarter')
        ? Math.max(0, Math.min(Math.max(0, stake | 0),
            (res.keep != null ? res.keep : (!res.receipt_id ? purseFlee(_rd, res.status === 'fled_quarter') : 0)) | 0))
        : (res.status === 'expired'
          ? Math.max(0, Math.min(Math.max(0, stake | 0),
              (res.refund_hearts != null ? res.refund_hearts : (!res.receipt_id && _rd === 0 && res.refund ? stake : 0)) | 0))
          : 0);
      if (potDue) claimSpecs.push({ category: 'payout', amount: potDue,
        reason: res.ladder ? 'earn_contract_run_win' : 'earn_contract_pot' });
      if (ownerDue) claimSpecs.push({ category: 'owner_bonus', amount: ownerDue, reason: 'earn_contract_own_bonus' });
      if (leaveDue) claimSpecs.push({ category: 'refund', amount: leaveDue, reason: 'earn_contract_flee_refund' });
      var dustEndsRoad = !res.ladder && (
        ((res.status === 'settled_loss' || res.status === 'expired') && _rd > 0) ||
        (res.status === 'settled_win' && (_rd >= 4 || res.grand)) ||
        ((res.status === 'fled_half' || res.status === 'fled_quarter') && _rd > 0)
      );
      if (opts.claimRecruitDust && dustEndsRoad && (res.recruit_dust | 0) > 0) {
        claimSpecs.push({ category: 'recruit_dust', amount: res.recruit_dust | 0, reason: 'earn_contract_recruit_dust' });
      }
      /* One local wallet means one serial queue. Parallel category writes could
         both read the same balance and one would overwrite the other. */
      var paid = [], chain = Promise.resolve();
      claimSpecs.forEach(function (spec) {
        chain = chain.then(function () {
          return claimLocalReward(settleScope, identity, spec.category, spec.amount, spec.reason, legacyIdentity).then(function (row) {
            row = row || { first: false, amount: spec.amount };
            row.category = spec.category;
            paid.push(row);
          });
        });
      });
      return chain.then(function () {
        var first = false, amount = 0;
        var byCategory = {
          payout: { first: false, amount: potDue },
          owner_bonus: { first: false, amount: ownerDue },
          refund: { first: false, amount: leaveDue },
          recruit_dust: { first: false, amount: (res.recruit_dust | 0) }
        };
        for (var ci = 0; ci < paid.length; ci++) {
          if (paid[ci] && paid[ci].first) { first = true; amount += paid[ci].amount | 0; }
          if (paid[ci] && paid[ci].category) byCategory[paid[ci].category] = paid[ci];
        }
        res.local_reward_first = first;
        res.local_reward_amount = amount;
        res.local_reward_claims = byCategory;
        res.local_reward_account = settleScope ? settleScope.account : 'sandbox';
        if (settleScope && !combatAccountIsCurrent(settleScope)) throw accountChangedError('the battle result was saving', true);
        if (settleScope && ['battle', 'fled_half', 'fled_quarter'].indexOf(String(outcome || '')) >= 0) {
          if (!savePendingResponse(settleScope, runId, outcome, res)) {
            var saveErr = new Error('Your result was saved, but this device could not preserve its result screen. Reopen Battlegrounds to finish safely.');
            saveErr.__hohRewardPending = true;
            saveErr.__hohCommitted = true;
            throw saveErr;
          }
          /* Return the same recovery metadata that a reload receives. The UI
             owns the later acknowledgement after packs and post-win modules. */
          var saved = committedResponse(settleScope, runId, outcome);
          if (!saved) {
            var readErr = new Error('Your result was saved, but its recovery copy could not be read. Reopen Battlegrounds.');
            readErr.__hohRewardPending = true;
            readErr.__hohCommitted = true;
            throw readErr;
          }
          res.__hocResume = saved.__hocResume;
          res.__hocPendingRun = saved.__hocPendingRun;
          res.__hocPendingOutcome = saved.__hocPendingOutcome;
          res.__hocStateToken = saved.__hocStateToken;
          res.__hocRecoveryAccount = saved.__hocRecoveryAccount;
          res.__hocRecoveryGeneration = saved.__hocRecoveryGeneration;
        }
        return res;
      }).catch(function (claimErr) {
        /* The server receipt already exists; a local-wallet/storage problem is
           recovery work, never permission to replay a speculative refund. */
        if (res && res.receipt_id) claimErr.__hohCommitted = true;
        throw claimErr;
      });
    });
  }

  /* open a contract-won pack through the shop's own opener. The server never
     charges (R235/R268: wallets are client-side), so a verified win's packs
     open exactly like the free pack: call the RPC, spend nothing. */
  function openPack() {
    if (localMode()) {
      /* the sandbox pack draws from the REAL cards the account owns (loaded
         via loadCollection), so test battles always field real heroes. Random
         3 for variety. Fabricated stand-ins only if the account truly owns
         nothing (a brand-new test account). */
      var cds = ownedCards().slice();
      var pick = [];
      while (pick.length < 3 && cds.length) {
        pick.push(cds.splice(Math.floor(Math.random() * cds.length), 1)[0]);
      }
      pick = pick.map(function (c) {
        return { character_id: c.id || c.character_id, name: c.name, character_class: c.character_class || 'Fighter',
                 hearts: c.hearts || 0, img: c.image_thumb_url || c.image_url || '',
                 ff: !!(c.is_ff_portrait || c.ff) };
      });
      var CLASSES = ['Fighter', 'Cleric', 'Rogue'];
      for (var fi = 0; pick.length < 3; fi++) {
        pick.push({ character_id: 'test-' + (fi + 1), name: 'Sparring ' + CLASSES[fi % 3], character_class: CLASSES[fi % 3], hearts: 120 * (fi + 1), img: '', ff: true });
      }
      return Promise.resolve({ cards: pick });
    }
    var packScope = captureCollectionAccount();
    return rpc('open_card_packs', {
      user_email: packScope.email, user_pin: packScope.pin, pay_currency: 'hearts',
      client_request_id: 'contract-' + Date.now() + '-' + Math.floor(Math.random() * 1e6)
    }).then(function (r) {
      /* the cards from a pack opened HERE are owned the moment the server
         answers — fold them into the collection cache so the party picker and
         the sandbox see them instantly, without waiting on a Shop refetch.
         The server grant still belongs to the frozen credentials when the
         player changes profile mid-open; in that case leave the current
         account's memory untouched and let the original account refetch it. */
      var normalizedPulls = [];
      try {
        var pulls = (r && (r.results || r.cards || r.pulls)) || (r && r.data && (r.data.results || r.data.cards || r.data.pulls)) || [];
        normalizedPulls = pulls.map(function (cd) {
          var id = cd && (cd.cid || cd.character_id || cd.id);
          if (id == null) return null;
          return {
            character_id: id, name: cd.name || 'Hero', character_class: cd.character_class || 'Fighter',
            hearts: cd.hearts || 0, img: cd.img || cd.image_thumb_url || cd.image_url || '',
            ff: (cd.ff != null ? !!cd.ff : !!cd.is_ff_portrait),
            copies: (cd.copies == null ? null : (cd.copies | 0))
          };
        }).filter(function (cd) { return !!cd; });
        if (normalizedPulls.length && collectionAccountIsCurrent(packScope)) {
          /* `hohShopOwnedCards` intentionally hides stacks whose every copy was
             dusted. That is correct for a party picker, but wrong as the merge
             base for a re-pull: losing the old row also loses its authoritative
             cashed_copies count and can make one new copy look like the entire
             historical stack is playable again. Prefer this module's complete
             collection cache, then the Shop's raw rows, and use the playable
             projection only as the last compatibility fallback. */
          var prior = collectionCache ? collectionCache.slice() : [];
          if (!prior.length) {
            try {
              var rawPrior = (typeof window.hohShopCollectionRows === 'function') ? (window.hohShopCollectionRows() || []) : [];
              if (rawPrior.length) prior = rawPrior.filter(function (row) {
                return row && row.character_id != null;
              }).map(enrichCard);
            } catch (rawErr) {}
          }
          if (!prior.length) prior = ownedCards().slice();
          collectionCache = prior;
          collectionCacheEmail = packScope.account;
          collectionCachePin = String(packScope.pin || '');
          var have = {};
          collectionCache.forEach(function (c) { have[String(c.id || c.character_id)] = c; });
          normalizedPulls.forEach(function (cd) {
            var id = String(cd.character_id), existing = have[id];
            if (existing) {
              var cashed = (existing.cashed_copies == null ? 0 : existing.cashed_copies | 0);
              existing.copies = cd.copies == null ? Math.max(0, existing.copies | 0) + 1 : cd.copies;
              existing.plays_left = Math.max(0, (existing.copies | 0) - cashed);
              /* The collection row's first-pull is_ff_snap is the namespace for
                 card_skills/card_xp. R422 deliberately does not rewrite it on
                 conflict, so preserve the existing client namespace too. This
                 is what makes dust -> re-pull return the exact same abilities
                 even if an administrator later changes the live hero flag. */
              if (cd.name) existing.name = cd.name;
              if (cd.img) existing.image_thumb_url = cd.img;
              if (cd.hearts != null) existing.hearts = Math.max(existing.hearts | 0, cd.hearts | 0);
              return;
            }
            var fresh = enrichCard({ character_id: cd.character_id, name: cd.name, character_class: cd.character_class,
              img: cd.img, hearts: cd.hearts, ff: cd.ff, copies: cd.copies == null ? 1 : cd.copies, cashed_copies: 0 });
            fresh.plays_left = Math.max(0, (fresh.copies | 0) - (fresh.cashed_copies | 0));
            have[id] = fresh; collectionCache.push(fresh);
          });
          /* The optimistic merge makes the re-pulled card available now. This
             background read replaces it with the authoritative collection and
             retains the same card_skills key; a transient refresh failure keeps
             the already-correct merged row instead of hiding the pull. */
          try { loadCollection(true); } catch (e2) {}
        }
      } catch (e) {}
      var out = (r && typeof r === 'object') ? Object.assign({}, r) : {};
      out.cards = normalizedPulls;
      return out;
    });
  }

  function randHex() {
    var s = ''; for (var i = 0; i < 32; i++) s += '0123456789abcdef'[Math.floor(Math.random() * 16)];
    return s;
  }

  /* Observe the Hall's real sign-in boundary, plus cross-tab token changes.
     This is what makes an A -> B -> A round trip advance generations even
     when no collection button was pressed during the brief B session. */
  function syncAccountBoundaries() {
    try { useCollectionAccount(); } catch (e) {}
    try { useCombatCacheAccount(); } catch (e2) {}
    try { flushAccountActions(); } catch (e3) {}
  }
  try {
    document.addEventListener('hoh:signed-in', syncAccountBoundaries);
    document.addEventListener('hoh:signed-out', syncAccountBoundaries);
  } catch (e4) {}
  try {
    window.addEventListener('storage', function (ev) {
      if (ev && ev.key === 'hoh-your-hall-token') syncAccountBoundaries();
    });
    window.addEventListener('online', function () {
      try { document.dispatchEvent(new CustomEvent('hoh:bg-settlement-pending')); } catch (_) {}
    });
  } catch (e5) {}
  syncAccountBoundaries();

  window.HOHContractData = {
    signedIn: signedIn, email: email, playedToday: playedToday,
    bankGet: bankGet, bankApply: bankApply, canAfford: canAfford,
    loadCollection: loadCollection, ownedCards: ownedCards,
    /* ⛔ R647: BOTH OF THESE ASK THE ACCOUNT BOUNDARY FIRST, and the harness
       caught the reason. These are module globals, so they outlive an in-page
       sign-out exactly the way collectionCache does (the R494/R495 lesson).
       Read bare, they answer for whoever was signed in last: after A -> B,
       collectionLoaded() said true, the new count trusted it, and then
       ownedCards() reset the cache underneath and returned B's empty list —
       so B read a confident ZERO. That is the same wrong number this whole
       wave exists to delete, in a narrower window and wearing the fix's own
       badge. useCollectionAccount() is the reset, and ownedCards() already
       calls it for precisely this reason. */
    collectionLoadFailed: function () { useCollectionAccount(); return collectionLoadFailed; },
    /* R647: "has a collection ever landed for this account", which is a
       different question from "did the last try break" and the one every
       count-driven gate actually needs to ask. */
    collectionLoaded: function () { useCollectionAccount(); return collectionLoadDone; },
    loadCardXp: loadCardXp, cardXpLoadFailed: function () { return xpLoadFailed; }, cardXpFor: cardXpFor, giveCardXp: giveCardXp,
    applyBattleXpReceipt: applyBattleXpReceipt, effectiveLevel: effectiveLevel,
    emberLine: emberLine,
    cardProgressionFor: cardProgressionFor, cardProgressionClaims: cardProgressionClaims,
    claimCardProgression: claimCardProgression,
    loadCardUltimateQuests: loadCardUltimateQuests, cardUltimateQuestFor: cardUltimateQuestFor,
    acceptCardUltimateQuest: acceptCardUltimateQuest,
    CARD_PROGRESSION_MILESTONES: CARD_PROGRESSION_MILESTONES.slice(),
    debugSetCardProgression: debugSetCardProgression,
    debugClearCardProgression: debugClearCardProgression,
    xpCost: xpCost, levelPower: levelPower, nextDieLevel: nextDieLevel, XP_PER_HEART: XP_PER_HEART,
    loadGear: loadGear, gearLoadFailed: function () { return gearLoadFailed; }, gearFor: gearFor, rollSlot: rollSlot,
    /* R643: exported so a surface can DISABLE the control and say why, instead
       of letting a player tap it and receive a rejected promise. */
    runLocksAbilities: runLocksAbilities, abilityRollErrorMessage: abilityRollErrorMessage,
    crystalRollPending: atomicSkillJournalPending,
    /* R616: read-only windows for the roll harness and the console */
    registryRollLive: function () { return REGISTRY_ROLL_LIVE; },
    fallbackHits: function () { return _fallbackHits; },
    /* R395: the simple reroll — meter readers + the stranded-offer healer */
    gearHealPending: gearHealPending,
    gearMetaFor: gearMetaFor, pityCapFor: pityCapFor,
    abilityTierBasePct: abilityTierBasePct, abilityTierChancePct: abilityTierChancePct,
    partiesGet: partiesGet, partySave: partySave, partyDelete: partyDelete, setPlans: setPlans,
    setRecruit: setRecruit, fetchRival: fetchRival, ladderRecruit: ladderRecruit,
    startContract: startContract,
    ladderState: ladderState, ladderAdopt: ladderAdopt, ladderShop: ladderShop,
    ladderParty: ladderParty, ladderForget: ladderForget, continueRun: continueRun,
    /* R622 · Battlegrounds Unlimited, the six doors plus the start journal */
    unlimitedState: unlimitedState, unlimitedStart: unlimitedStart,
    unlimitedContinue: unlimitedContinue, unlimitedStop: unlimitedStop,
    unlimitedExpire: unlimitedExpire, unlimitedForfeit: unlimitedForfeit,
    unlimitedForget: unlimitedForget,
    /* R791 · one reading of a failure, the words a player sees for it, and
       the one durable fault note */
    runErrorKind: runErrorKind, runErrorCode: runErrorCode, runSupportCode: runSupportCode,
    playerLine: playerLine, noteRunFault: noteRunFault,
    unlimitedStartRequestId: unlimitedStartRequestId,
    unlimitedClearStartRequest: unlimitedClearStartRequest,
    /* R778 · Away mode and one driver per run */
    deviceId: deviceId, deviceKind: deviceKind,
    awaySeen: awaySeen, awaySeenBeacon: awaySeenBeacon, awayClaim: awayClaim,
    awayState: awayState, awaySet: awaySet, awayStart: awayStart,
    awayLog: awayLog, awayEnd: awayEnd, unlimitedContinueN: unlimitedContinueN,
    awayRefresh: function () {
      try { var m = window.__hohBgUnlimited; return m && !m._sandbox ? awayMirrorFetch(m) : Promise.resolve(null); }
      catch (_) { return Promise.resolve(null); }
    },
    AWAY_EARNED_AT: AWAY_EARNED_AT,
    /* The packs latch the result screen must honour for this mode: an
       Unlimited pack is a durable server credit opened once at the END of a
       run, so the per-segment packs-opened latch is released and the pending
       record can be consumed before the next segment's settle. */
    settlementPacksDeferred: function (res) {
      return !!(res && (res.__hocPacksDeferred === true || String(res.mode || '') === 'unlimited'));
    },
    MAX_PERSISTENT_BENCH: MAX_PERSISTENT_BENCH,
    RECRUIT_REPLACE_ENGINE: RECRUIT_REPLACE_ENGINE,
    workerGate: workerGate,
    prepareSettle: prepareSettle, settle: settle, recoverPendingSettlement: recoverPendingSettlement,
    upgradeReconcile: upgradeReconcile114, upgradeOffer: upgradeOffer114, upgradeActivate: upgradeActivate114,
    serverWaitMs: serverWaitMs,
    upgradeReserve: upgradeReserve114, upgradeChoose: upgradeChoose114, upgradeExpire: upgradeExpire114,
    hasPendingSettlement: hasPendingSettlement, consumePendingSettlement: consumePendingSettlement,
    parkPendingSettlement: parkPendingSettlement,
    claimSettlementExtra: claimSettlementExtra, openPack: openPack,
    updateLoadout: updateLoadout, applyCarry: applyCarry,
    pathGet: pathGet, pathSet: pathSet, championState: championState, championStar: championStar,
    prepareEventDebit: prepareEventDebit, quarantineEventDebit: quarantineEventDebit, commitEventDebit: commitEventDebit,
    eventResolutionDone: eventResolutionDone, markEventResolutionDone: markEventResolutionDone,
    courtSkipped: courtSkipped, markCourtSkipped: markCourtSkipped,
    reclaimFallen: reclaimFallen, companionJoin: companionJoin, companionLose: companionLose, shopBuy: shopBuy, adminJump: adminJump,
    vigilRevive: vigilRevive, vigilCost: vigilCost, localFall: localFall,
    partyOut: partyOut, boardHire: boardHire, hireCost: hireCost,
    climbLine: climbLine, ticketRally: ticketRally, ladderRetire: ladderRetire,
    releaseExpiredLadder: releaseExpiredLadder,
    refreshCard: refreshCard,
    lossLockMsLeft: lossLockMsLeft, stampLoss: stampLoss, clearLossLock: clearLossLock,
    /* driven by the god-mode contract debug panel; inert for normal players */
    debug: {
      godMode: godMode,
      isLocal: localMode,
      isNewPlayerPreview: isNewPlayerPreview,
      setNewPlayerPreview: setNewPlayerPreview,
      /* R410: writes the NEW key, so the panel switch and localMode agree. Off
         is the default and off is real. Turning it on is a deliberate act for
         playing with no server, and nothing in the UI offers it. */
      setLocal: function (on) {
        try {
          if (on) localStorage.setItem('hoh-contract-sandbox-v2', '1');
          else localStorage.removeItem('hoh-contract-sandbox-v2');
        } catch (e) {}
      },
      grantHearts: function (n) { bankApply(n || 0, 'skip'); },   /* 'skip' keeps debug grants out of the ledger */
      resetDay: function () { try { localStorage.removeItem(DAY_KEY); } catch (e) {} window.__hohLocalRun = null; },
      setForce: function (obj) { var f = window.__hohContractForce || {}; obj = obj || {}; for (var k in obj) { if (obj[k] === null) delete f[k]; else f[k] = obj[k]; } window.__hohContractForce = f; },
      getForce: forceFlags,
      clearForce: function () { window.__hohContractForce = {}; },
      /* R337: the five minute cooling-off after a loss is a KINDNESS for
         players, and an obstacle for whoever is testing the loss path -- which
         is the path most worth testing. God mode can clear it. This only ever
         touches the local stamp; there is no server lock to desync from. */
      lockMsLeft: lossLockMsLeft,
      clearLock: clearLossLock
    }
  };
})();
